import axios from 'axios';
import { Room, RoomEvent, Track, ConnectionState, ParticipantEvent } from 'livekit-client';
import { collection, serverTimestamp, addDoc, doc, updateDoc, query, orderBy, onSnapshot, getDoc, setDoc } from 'firebase/firestore';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { BackgroundBlur, VirtualBackground } from '@livekit/track-processors';
import { createPortal } from 'react-dom';
import { toast as toast$1, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParticipants, useLocalParticipant, useRoomContext, useTracks, LiveKitRoom, VideoConference, RoomAudioRenderer } from '@livekit/components-react';
import '@livekit/components-styles/index.css';

/**
 * LiveKit Meeting SDK
 *
 * Core SDK for managing LiveKit meetings, tokens, and participants
 * Supports teachers, students, and guests
 */
class MeetingSDK {
    constructor(config) {
        this.currentRoom = null;
        this.currentToken = null;
        this.tokenCache = new Map();
        this.callbacks = {};
        this.apiClient = axios.create({
            baseURL: config.apiEndpoint,
            timeout: 30000,
        });
        this.livekitServerUrl = config.livekitServerUrl;
        this.tenantId = config.tenantId;
        this.userId = config.userId;
        this.userRole = config.userRole;
        this.participantName = config.participantName;
        this.metadata = config.metadata || {};
    }
    /**
     * Set callbacks for meeting events
     */
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }
    /**
     * Generate a LiveKit token for joining a meeting
     */
    async generateToken(roomName, options) {
        // Check cache first
        const cacheKey = `${roomName}-${this.userId}-${this.userRole}`;
        const cached = this.tokenCache.get(cacheKey);
        // Use cached token if still valid (not expired and less than 5 minutes old)
        if (cached && cached.expiresAt && cached.expiresAt > Date.now()) {
            const age = Date.now() - cached.generatedAt;
            if (age < 5 * 60 * 1000) { // 5 minutes
                return cached.token;
            }
        }
        // CRITICAL: Filter out 'record' from options - it's not a valid token permission
        // and causes participants to be kicked off if included. Recording is controlled via
        // API calls (startRecording/stopRecording), not token permissions.
        let sanitizedOptions = {};
        if (options) {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { record, ...rest } = options;
            sanitizedOptions = rest;
            if (record !== undefined) {
                console.warn('⚠️ "record" option detected in token generation - this is not a valid token permission and will be ignored. Recording is controlled via API calls, not token permissions.');
            }
        }
        const tokenOptions = {
            tenantId: sanitizedOptions?.tenantId || this.tenantId,
            userId: sanitizedOptions?.userId || this.userId,
            role: sanitizedOptions?.role || this.userRole,
            canPublish: sanitizedOptions?.canPublish ?? true,
            canSubscribe: sanitizedOptions?.canSubscribe ?? true,
            canPublishData: sanitizedOptions?.canPublishData ?? true,
            canUpdateOwnMetadata: sanitizedOptions?.canUpdateOwnMetadata ?? true,
            metadata: {
                ...this.metadata,
                ...sanitizedOptions?.metadata,
                participantName: this.participantName,
                joinedAt: new Date().toISOString(),
                role: sanitizedOptions?.role || this.userRole,
            },
        };
        try {
            const response = await this.apiClient.post('/api/meet/generateToken', {
                roomName,
                participantName: this.participantName,
                options: tokenOptions,
            });
            if (!response.data.token) {
                throw new Error('No token received from server');
            }
            const token = {
                token: response.data.token,
                roomName,
                expiresAt: response.data.expiresAt ? new Date(response.data.expiresAt).getTime() : undefined,
                generatedAt: Date.now(),
            };
            // Cache the token
            this.tokenCache.set(cacheKey, token);
            return token;
        }
        catch (error) {
            console.error('Error generating token:', error);
            throw new Error(error.response?.data?.message || 'Failed to generate meeting token');
        }
    }
    /**
     * Join a LiveKit meeting room
     */
    async joinRoom(roomName, token, options) {
        try {
            // Generate token if not provided
            if (!token) {
                token = await this.generateToken(roomName);
            }
            // Create room instance
            const room = new Room({
                adaptiveStream: true,
                dynacast: true,
                videoCaptureDefaults: {
                    resolution: { width: 1280, height: 720 },
                },
            });
            // Set up event listeners
            this.setupRoomListeners(room);
            // Connect to room
            await room.connect(this.livekitServerUrl, token, {
                autoSubscribe: options?.autoSubscribe ?? true,
            });
            this.currentRoom = room;
            this.currentToken = {
                token,
                roomName,
                generatedAt: Date.now(),
            };
            // Enable camera/mic if requested
            if (options?.enableCamera) {
                await room.localParticipant?.setCameraEnabled(true);
            }
            if (options?.enableMicrophone) {
                await room.localParticipant?.setMicrophoneEnabled(true);
            }
            this.callbacks.onConnected?.(room);
            return room;
        }
        catch (error) {
            console.error('Error joining room:', error);
            this.callbacks.onError?.(error);
            throw error;
        }
    }
    /**
     * Leave the current meeting room
     */
    async leaveRoom(isIntentional = true) {
        if (this.currentRoom) {
            try {
                this.currentRoom.disconnect(isIntentional);
            }
            catch (error) {
                console.error('Error disconnecting from room:', error);
            }
            this.currentRoom = null;
            this.currentToken = null;
        }
    }
    /**
     * Get the current room instance
     */
    getCurrentRoom() {
        return this.currentRoom;
    }
    /**
     * Get current token
     */
    getCurrentToken() {
        return this.currentToken;
    }
    /**
     * Set up room event listeners
     */
    setupRoomListeners(room) {
        room.on(RoomEvent.Connected, () => {
            console.log('✅ Connected to room');
        });
        room.on(RoomEvent.Disconnected, (reason) => {
            console.log('❌ Disconnected from room:', reason);
            this.currentRoom = null;
            this.currentToken = null;
            this.callbacks.onDisconnected?.(String(reason));
        });
        room.on(RoomEvent.ParticipantConnected, (participant) => {
            console.log('👤 Participant connected:', participant.identity);
            this.callbacks.onParticipantConnected?.(participant);
        });
        room.on(RoomEvent.ParticipantDisconnected, (participant) => {
            console.log('👋 Participant disconnected:', participant.identity);
            this.callbacks.onParticipantDisconnected?.(participant);
        });
        room.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
            this.callbacks.onTrackSubscribed?.(track, participant);
        });
        room.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
            this.callbacks.onTrackUnsubscribed?.(track, participant);
        });
        room.on(RoomEvent.TrackMuted, (publication, participant) => {
            console.log('🔇 Track muted:', publication.kind, participant.identity);
        });
        room.on(RoomEvent.TrackUnmuted, (publication, participant) => {
            console.log('🔊 Track unmuted:', publication.kind, participant.identity);
        });
        room.on(RoomEvent.LocalTrackPublished, (publication, participant) => {
            console.log('📹 Local track published:', publication.kind);
        });
        room.on(RoomEvent.LocalTrackUnpublished, (publication, participant) => {
            console.log('📹 Local track unpublished:', publication.kind);
        });
        room.on(RoomEvent.RoomMetadataChanged, (metadata) => {
            console.log('📝 Room metadata changed:', metadata);
        });
        room.on(RoomEvent.ConnectionQualityChanged, (quality, participant) => {
            console.log('📊 Connection quality changed:', quality, participant?.identity);
        });
    }
    /**
     * Clear token cache
     */
    clearTokenCache() {
        this.tokenCache.clear();
    }
    /**
     * Get cached token for a room
     */
    getCachedToken(roomName) {
        const cacheKey = `${roomName}-${this.userId}-${this.userRole}`;
        return this.tokenCache.get(cacheKey) || null;
    }
}

/**
 * LiveKit Recording SDK
 *
 * Core SDK for managing meeting recordings (start, stop, list, playback)
 */
class RecordingSDK {
    constructor(config) {
        this.callbacks = {};
        this.recordingsUnsubscribe = null;
        this.apiClient = axios.create({
            baseURL: config.apiEndpoint,
            timeout: 30000,
        });
        this.firestore = config.firestore;
        this.tenantId = config.tenantId;
    }
    /**
     * Set callbacks for recording events
     */
    setCallbacks(callbacks) {
        this.callbacks = { ...this.callbacks, ...callbacks };
    }
    /**
     * Start cloud recording
     */
    async startCloudRecording(options) {
        try {
            // CRITICAL: Use the provided layout, default to 'auto' only if not provided
            // This ensures the actual selected layout is sent to the backend
            const recordingLayout = options.layout ?? 'auto';
            console.log('📐 RecordingSDK: Using layout:', recordingLayout, '(provided:', options.layout, ')');
            const response = await this.apiClient.post('/api/meet/startRecording', {
                roomName: options.roomName,
                options: {
                    tenantId: this.tenantId,
                    originalRoomName: options.originalRoomName,
                    createdBy: options.createdBy,
                    resolution: options.resolution || '720p',
                    layout: recordingLayout, // Use the determined layout
                },
            });
            if (!response.data.recording_id) {
                throw new Error('No recording ID received');
            }
            // Store recording in Firestore
            const recordingsRef = collection(this.firestore, 'ServiceProviders', this.tenantId, 'Recordings');
            const recordingDoc = {
                recording_id: response.data.recording_id,
                room_name: options.roomName,
                original_room_name: options.originalRoomName,
                status: (response.data.status || 'active'),
                filename: response.data.filename || null,
                s3_bucket: response.data.s3_bucket || null,
                s3_key: response.data.s3_key || null,
                started_at: response.data.started_at || serverTimestamp(),
                stopped_at: null,
                tenant_id: this.tenantId,
                created_by: options.createdBy,
                meeting_title: options.meetingTitle,
                recording_type: 'cloud',
                resolution: (options.resolution || '720p'),
                layout: recordingLayout, // Use the same layout determined above
                created_at: serverTimestamp(),
                updated_at: serverTimestamp(),
            };
            const docRef = await addDoc(recordingsRef, recordingDoc);
            const recording = {
                id: docRef.id,
                ...recordingDoc,
            };
            this.callbacks.onRecordingStarted?.(recording);
            return recording;
        }
        catch (error) {
            console.error('Error starting cloud recording:', error);
            this.callbacks.onError?.(error);
            throw new Error(error.response?.data?.message || 'Failed to start recording');
        }
    }
    /**
     * Stop cloud recording
     */
    async stopCloudRecording(recordingId) {
        try {
            const response = await this.apiClient.post('/api/meet/stopRecording', {
                recordingId,
            });
            // Update Firestore document
            const recordingRef = doc(this.firestore, 'ServiceProviders', this.tenantId, 'Recordings', recordingId);
            await updateDoc(recordingRef, {
                status: 'stopped',
                stopped_at: serverTimestamp(),
                stop_response: response.data.message || 'Recording stopped',
                updated_at: serverTimestamp(),
            });
            // Fetch updated document
            // Note: In a real implementation, you might want to use the snapshot listener
            // For now, we'll just trigger the callback
            this.callbacks.onRecordingStopped?.({
                id: recordingId,
                recording_id: recordingId,
                room_name: '',
                original_room_name: '',
                status: 'stopped',
                filename: null,
                s3_bucket: null,
                s3_key: null,
                started_at: null,
                stopped_at: null,
                tenant_id: this.tenantId,
                created_by: '',
                meeting_title: '',
                recording_type: 'cloud',
                resolution: '720p',
                layout: 'auto',
                created_at: null,
                updated_at: null,
            });
        }
        catch (error) {
            console.error('Error stopping recording:', error);
            this.callbacks.onError?.(error);
            throw new Error(error.response?.data?.message || 'Failed to stop recording');
        }
    }
    /**
     * Get playback URL for a recording
     */
    async getPlaybackUrl(recordingDocId) {
        try {
            const response = await this.apiClient.post('/api/meet/getRecordingPlaybackUrl', {
                providerId: this.tenantId,
                recordingDocId,
            });
            return response.data;
        }
        catch (error) {
            console.error('Error getting playback URL:', error);
            this.callbacks.onError?.(error);
            throw new Error(error.response?.data?.message || 'Failed to get playback URL');
        }
    }
    /**
     * Delete a recording
     */
    async deleteRecording(recordingDocId) {
        try {
            const response = await this.apiClient.post('/api/meet/deleteRecording', {
                providerId: this.tenantId,
                recordingDocId,
            }, {
                validateStatus: (status) => {
                    return status === 200 || status === 400 || status === 404 || status === 500;
                },
            });
            if (response.status === 200 && response.data.success) {
                // Success - backend handles Firestore deletion
                return;
            }
            else {
                throw new Error(response.data.error || 'Failed to delete recording');
            }
        }
        catch (error) {
            console.error('Error deleting recording:', error);
            this.callbacks.onError?.(error);
            throw error;
        }
    }
    /**
     * Subscribe to recordings list (real-time updates)
     */
    subscribeToRecordings(callback, options) {
        const recordingsRef = collection(this.firestore, 'ServiceProviders', this.tenantId, 'Recordings');
        const q = query(recordingsRef, orderBy('created_at', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const recordings = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                const recording = {
                    id: doc.id,
                    recording_id: data.recording_id,
                    room_name: data.room_name,
                    original_room_name: data.original_room_name,
                    status: data.status,
                    filename: data.filename,
                    s3_bucket: data.s3_bucket,
                    s3_key: data.s3_key,
                    started_at: data.started_at,
                    stopped_at: data.stopped_at,
                    tenant_id: data.tenant_id,
                    created_by: data.created_by,
                    meeting_title: data.meeting_title,
                    recording_type: data.recording_type,
                    resolution: data.resolution,
                    layout: data.layout,
                    created_at: data.created_at,
                    updated_at: data.updated_at,
                    stop_response: data.stop_response,
                    playback_url: data.playback_url || (data.s3_bucket && data.s3_key
                        ? `https://${data.s3_bucket}.s3.amazonaws.com/${data.s3_key}`
                        : undefined),
                    transcoded_path: data.transcoded_path,
                    transcoding_job_id: data.transcoding_job_id,
                };
                // Apply filters
                if (options?.statusFilter && options.statusFilter !== 'all') {
                    if (recording.status !== options.statusFilter) {
                        return;
                    }
                }
                recordings.push(recording);
            });
            // Apply max items limit
            const limitedRecordings = options?.maxItems
                ? recordings.slice(0, options.maxItems)
                : recordings;
            callback(limitedRecordings);
            // Trigger status change callbacks
            recordings.forEach((recording) => {
                this.callbacks.onRecordingStatusChanged?.(recording);
            });
        }, (error) => {
            console.error('Error fetching recordings:', error);
            this.callbacks.onError?.(error);
        });
        this.recordingsUnsubscribe = unsubscribe;
        return unsubscribe;
    }
    /**
     * Unsubscribe from recordings
     */
    unsubscribeFromRecordings() {
        if (this.recordingsUnsubscribe) {
            this.recordingsUnsubscribe();
            this.recordingsUnsubscribe = null;
        }
    }
}

/**
 * Device Performance Detection
 *
 * Automatically detects device capabilities and recommends optimal
 * background processing settings for smooth performance.
 *
 * Similar to Google Meet's adaptive quality system.
 */
/**
 * Detect device performance tier based on:
 * - CPU cores
 * - Device memory
 * - GPU capabilities
 * - Browser performance metrics
 */
const detectPerformanceTier = () => {
    // Check if running in browser
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return 'medium'; // Default for SSR
    }
    let score = 0;
    // 1. CPU Cores (weight: 30%)
    const cores = navigator.hardwareConcurrency || 4;
    if (cores >= 8)
        score += 30;
    else if (cores >= 4)
        score += 20;
    else
        score += 10;
    // 2. Device Memory (weight: 30%)
    // @ts-ignore - deviceMemory is not in TypeScript types yet
    const memory = navigator.deviceMemory || 4; // GB
    if (memory >= 8)
        score += 30;
    else if (memory >= 4)
        score += 20;
    else
        score += 10;
    // 3. GPU Detection (weight: 20%)
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    if (gl) {
        // @ts-ignore
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
            // @ts-ignore
            const renderer = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
            // Check for dedicated GPU indicators
            if (renderer.match(/NVIDIA|AMD|Radeon|GeForce|RTX|GTX/i)) {
                score += 20; // Dedicated GPU
            }
            else if (renderer.match(/Intel|Iris|UHD/i)) {
                score += 15; // Integrated GPU (modern)
            }
            else {
                score += 10; // Basic GPU
            }
        }
        else {
            score += 10; // WebGL available but can't detect GPU
        }
    }
    else {
        score += 5; // No WebGL support
    }
    // 4. Connection Speed (weight: 10%)
    // @ts-ignore - connection is not in all TypeScript types
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (connection) {
        const effectiveType = connection.effectiveType;
        if (effectiveType === '4g')
            score += 10;
        else if (effectiveType === '3g')
            score += 7;
        else
            score += 5;
    }
    else {
        score += 7; // Unknown, assume decent
    }
    // 5. Platform Detection (weight: 10%)
    const platform = navigator.platform.toLowerCase();
    const userAgent = navigator.userAgent.toLowerCase();
    if (platform.includes('mac') || platform.includes('win')) {
        score += 10; // Desktop
    }
    else if (userAgent.includes('ipad') || userAgent.includes('tablet')) {
        score += 7; // Tablet
    }
    else if (userAgent.includes('mobile')) {
        score += 5; // Mobile
    }
    else {
        score += 8; // Unknown, assume decent
    }
    // Determine tier based on total score (max 100)
    if (score >= 75)
        return 'high';
    if (score >= 50)
        return 'medium';
    return 'low';
};
/**
 * Get recommended settings for detected performance tier
 */
const getPerformanceSettings = (tier) => {
    const detectedTier = tier || detectPerformanceTier();
    const settings = {
        high: {
            tier: 'high',
            maxFps: 30,
            blurRadius: 35,
            segmentationQuality: 'high',
            description: 'Maximum quality - 30 FPS, ultra-soft edges'
        },
        medium: {
            tier: 'medium',
            maxFps: 25,
            blurRadius: 30,
            segmentationQuality: 'medium',
            description: 'Balanced quality - 25 FPS, soft edges'
        },
        low: {
            tier: 'low',
            maxFps: 20,
            blurRadius: 25,
            segmentationQuality: 'low',
            description: 'Performance mode - 20 FPS, moderate edges'
        }
    };
    return settings[detectedTier];
};
/**
 * Get detailed device info for debugging
 */
const getDeviceInfo = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
        return null;
    }
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    let gpuInfo = 'Unknown';
    if (gl) {
        // @ts-ignore
        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (debugInfo) {
            // @ts-ignore
            gpuInfo = gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        }
    }
    return {
        cores: navigator.hardwareConcurrency || 'Unknown',
        // @ts-ignore
        memory: navigator.deviceMemory ? `${navigator.deviceMemory} GB` : 'Unknown',
        gpu: gpuInfo,
        platform: navigator.platform,
        userAgent: navigator.userAgent,
        // @ts-ignore
        connection: navigator.connection?.effectiveType || 'Unknown',
        tier: detectPerformanceTier()
    };
};
/**
 * Log device performance info to console
 */
const logDevicePerformance = () => {
    const info = getDeviceInfo();
    const settings = getPerformanceSettings();
    console.log('🖥️ Device Performance Detection:');
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log(`📊 Performance Tier: ${settings.tier.toUpperCase()}`);
    console.log(`⚙️  CPU Cores: ${info?.cores}`);
    console.log(`💾 Memory: ${info?.memory}`);
    console.log(`🎮 GPU: ${info?.gpu}`);
    console.log(`📱 Platform: ${info?.platform}`);
    console.log(`📡 Connection: ${info?.connection}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    console.log('🎯 Recommended Settings:');
    console.log(`   FPS: ${settings.maxFps}`);
    console.log(`   Blur Radius: ${settings.blurRadius}`);
    console.log(`   Quality: ${settings.segmentationQuality}`);
    console.log(`   ${settings.description}`);
    console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
};
/**
 * Monitor real-time FPS and adjust settings if needed
 */
class PerformanceMonitor {
    constructor() {
        this.frameCount = 0;
        this.lastTime = performance.now();
        this.currentFps = 0;
        this.fpsHistory = [];
        this.historySize = 30; // Track last 30 FPS readings
    }
    updateFrame() {
        this.frameCount++;
        const currentTime = performance.now();
        const elapsed = currentTime - this.lastTime;
        if (elapsed >= 1000) {
            this.currentFps = Math.round((this.frameCount * 1000) / elapsed);
            this.fpsHistory.push(this.currentFps);
            if (this.fpsHistory.length > this.historySize) {
                this.fpsHistory.shift();
            }
            this.frameCount = 0;
            this.lastTime = currentTime;
        }
    }
    getCurrentFps() {
        return this.currentFps;
    }
    getAverageFps() {
        if (this.fpsHistory.length === 0)
            return 0;
        const sum = this.fpsHistory.reduce((a, b) => a + b, 0);
        return Math.round(sum / this.fpsHistory.length);
    }
    isPerformanceDegraded() {
        const avgFps = this.getAverageFps();
        return avgFps > 0 && avgFps < 15; // Below 15 FPS is considered degraded
    }
    shouldDowngrade() {
        // Suggest downgrade if average FPS is below 20 for extended period
        return this.fpsHistory.length >= 10 && this.getAverageFps() < 20;
    }
    reset() {
        this.frameCount = 0;
        this.lastTime = performance.now();
        this.currentFps = 0;
        this.fpsHistory = [];
    }
}

/**
 * Background Effects Utilities
 *
 * Helper functions for applying background blur and virtual backgrounds
 */
// ADAPTIVE QUALITY SETTINGS - Automatically adjust based on device performance
// Detects device capabilities and sets optimal FPS and blur radius
const performanceSettings = getPerformanceSettings();
// Log device performance on module load (helps with debugging)
if (typeof window !== 'undefined') {
    logDevicePerformance();
}
// Use adaptive settings based on device tier
const DEFAULT_BLUR_RADIUS = performanceSettings.blurRadius; // Adaptive: 35 (high), 30 (medium), 25 (low)
const PROCESSOR_FRAME_CAP = performanceSettings.maxFps; // Adaptive: 30 (high), 25 (medium), 20 (low)
// EDGE QUALITY PRESETS - Users can choose their preference
const BLUR_PRESETS = {
    LIGHT: 15, // Subtle blur, sharper edges
    MEDIUM: 25, // Balanced (good for most)
    SOFT: 35, // Ultra-soft, hides gaps (default for high-end)
    MAXIMUM: 45 // Maximum softness, perfect for challenging lighting
};
// Export performance info for UI display
const getDevicePerformanceInfo = () => performanceSettings;
// Segmentation options - GPU acceleration for best performance
const segmenterOptions = {
    delegate: 'GPU'
};
// Processor options - adaptive FPS based on device
const processorOptions = {
    maxFps: PROCESSOR_FRAME_CAP
};
// Processor cache for instant switching
const processorCache = new Map();
// Pre-warm processors to eliminate loading delays
let blurProcessorPromise = null;
const imageProcessorPromises = new Map();
// Create blur processor with optimized settings
const createBlurProcessor = (blurRadius = DEFAULT_BLUR_RADIUS) => {
    console.log('🎭 Creating blur processor:', { blurRadius, fps: PROCESSOR_FRAME_CAP });
    return BackgroundBlur(blurRadius, segmenterOptions, undefined, processorOptions);
};
// Create virtual background processor with optimized settings
const createImageProcessor = (imageUrl) => {
    console.log('🖼️ Creating virtual background processor:', { imageUrl, fps: PROCESSOR_FRAME_CAP });
    return VirtualBackground(imageUrl, segmenterOptions, undefined, processorOptions);
};
// Pre-warm processors for instant application
const prewarmBlurProcessor = () => {
    if (!blurProcessorPromise) {
        console.log('🔥 Pre-warming blur processor...');
        blurProcessorPromise = Promise.resolve(createBlurProcessor());
    }
    return blurProcessorPromise;
};
const prewarmImageProcessor = (imageUrl) => {
    const cacheKey = `image_${imageUrl}`;
    if (!imageProcessorPromises.has(cacheKey)) {
        console.log('🔥 Pre-warming image processor for:', imageUrl);
        imageProcessorPromises.set(cacheKey, Promise.resolve(createImageProcessor(imageUrl)));
    }
    return imageProcessorPromises.get(cacheKey);
};
// Get cached processor or create new one
const getCachedProcessor = async (effect, imageUrl) => {
    const cacheKey = effect === 'blur' ? 'blur' :
        effect === 'image' ? `image_${imageUrl}` : 'none';
    // Check cache first
    if (processorCache.has(cacheKey)) {
        console.log('⚡ Using cached processor:', cacheKey);
        return processorCache.get(cacheKey);
    }
    // Create new processor
    let processor;
    if (effect === 'blur') {
        processor = await prewarmBlurProcessor();
    }
    else if (effect === 'image' && imageUrl) {
        processor = await prewarmImageProcessor(imageUrl);
    }
    // Cache it for future use
    if (processor) {
        processorCache.set(cacheKey, processor);
        console.log('💾 Cached processor:', cacheKey);
    }
    return processor;
};
// Seamless background processor application
const ensureBackgroundProcessor = async (track, effect, imageUrl) => {
    if (!track || track.readyState === 'ended')
        return; // CRITICAL: Ensure track is active and not ended
    // CRITICAL FIX: Check mediaStreamTrack.readyState to prevent "InvalidStateError: Stream closed"
    // This happens when the track processor is being asked to work on a track that has been detached
    // but not yet re-attached during shard migrations or reconnections
    if (track.mediaStreamTrack && track.mediaStreamTrack.readyState === 'ended') {
        console.warn("🚫 Aborting background effect: Track is ended/migrating.");
        return;
    }
    const typedTrack = track;
    const existingState = typedTrack._meetplusBackground;
    if (effect === 'none') {
        if (existingState?.effect !== 'none') {
            console.log('🚫 Removing background effect...');
            await track.stopProcessor().catch(() => {
                // ignore stop failures
            });
            typedTrack._meetplusBackground = { effect: 'none' };
        }
        return;
    }
    const normalizedImageUrl = effect === 'image' ? imageUrl : undefined;
    // Check if effect is already applied
    if (existingState &&
        existingState.effect === effect &&
        (effect !== 'image' || existingState.imageUrl === normalizedImageUrl)) {
        console.log('⚠️ Effect already applied, skipping');
        return;
    }
    console.log('🎭 Applying background effect:', effect, normalizedImageUrl);
    // Get cached processor INSTANTLY (pre-warmed)
    const newProcessor = await getCachedProcessor(effect, normalizedImageUrl);
    if (!newProcessor) {
        throw new Error(`Failed to create ${effect} processor`);
    }
    // SEAMLESS TRANSITION: Apply new processor BEFORE stopping old one
    console.log('⚡ Applying processor seamlessly...');
    await track.setProcessor(newProcessor);
    // Update metadata
    if (effect === 'blur') {
        typedTrack._meetplusBackground = { effect };
    }
    else if (effect === 'image' && normalizedImageUrl) {
        typedTrack._meetplusBackground = { effect, imageUrl: normalizedImageUrl };
    }
    console.log('✅ Background effect applied seamlessly');
};
// HIGH-QUALITY Professional Backgrounds (1920x1080 Full HD)
// All images are now bundled locally for fast loading
// Using absolute paths starting with / - works in both localhost and Amplify
// Localhost: http://localhost:5173/assets/backgrounds/office.jpg
// Amplify: https://your-domain.com/assets/backgrounds/office.jpg
// CRITICAL: Images must be in public/assets/backgrounds/ for Amplify deployment
const BACKGROUND_BASE_URL = '/assets/backgrounds';
const getBuiltInBackgrounds = () => {
    // Helper to construct URL without double slashes
    // CRITICAL FIX: Ensure paths work correctly in Amplify by using absolute paths
    const getUrl = (filename) => {
        // Remove any leading/trailing slashes and ensure proper format
        const cleanBase = BACKGROUND_BASE_URL.replace(/^\/+|\/+$/g, '');
        const cleanFilename = filename.replace(/^\/+|\/+$/g, '');
        // Always return absolute path starting with /
        return `/${cleanBase}/${cleanFilename}`;
    };
    const backgrounds = [
        {
            id: 'office',
            name: 'Modern Office',
            url: getUrl('office.jpg'),
            thumbnail: getUrl('office.jpg')
        },
        {
            id: 'library',
            name: 'Classic Library',
            url: getUrl('library.jpg'),
            thumbnail: getUrl('library.jpg')
        },
        {
            id: 'classroom',
            name: 'Modern Classroom',
            url: getUrl('classroom.jpg'),
            thumbnail: getUrl('classroom.jpg')
        },
        {
            id: 'study',
            name: 'Study Room',
            url: getUrl('study.jpg'),
            thumbnail: getUrl('study.jpg')
        },
        {
            id: 'home-office',
            name: 'Home Office',
            url: getUrl('home-office.jpg'),
            thumbnail: getUrl('home-office.jpg')
        },
        {
            id: 'living-room',
            name: 'Living Room',
            url: getUrl('living-room.jpg'),
            thumbnail: getUrl('living-room.jpg')
        },
        {
            id: 'minimalist',
            name: 'Minimalist',
            url: getUrl('minimalist.jpg'),
            thumbnail: getUrl('minimalist.jpg')
        },
        {
            id: 'nature',
            name: 'Green Plants',
            url: getUrl('nature.jpg'),
            thumbnail: getUrl('nature.jpg')
        },
        {
            id: 'beach',
            name: 'Beach Sunset',
            url: getUrl('beach.jpg'),
            thumbnail: getUrl('beach.jpg')
        },
        {
            id: 'mountains',
            name: 'Mountains',
            url: getUrl('mountains.jpg'),
            thumbnail: getUrl('mountains.jpg')
        },
        {
            id: 'gradient-blue',
            name: 'Blue Gradient',
            url: getUrl('gradient-blue.jpg'),
            thumbnail: getUrl('gradient-blue.jpg')
        },
        {
            id: 'abstract',
            name: 'Purple Gradient',
            url: getUrl('abstract.jpg'),
            thumbnail: getUrl('abstract.jpg')
        }
    ];
    // CRITICAL: Ensure all backgrounds have thumbnails (fallback to url if missing)
    backgrounds.forEach(bg => {
        if (!bg.thumbnail) {
            bg.thumbnail = bg.url;
            console.warn(`⚠️ Background ${bg.id} missing thumbnail, using url as fallback`);
        }
    });
    // Log background paths for debugging (only in dev)
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        console.log('🖼️ Background images configured:', backgrounds.map(bg => ({
            id: bg.id,
            url: bg.url,
            thumbnail: bg.thumbnail
        })));
    }
    return backgrounds;
};
// Initialize processor pre-warming on module load
const initializeProcessorPrewarming = () => {
    console.log('🚀 Initializing background processor pre-warming...');
    // Pre-warm blur processor immediately
    prewarmBlurProcessor().then(() => {
        console.log('✅ Blur processor pre-warmed and ready');
    }).catch(error => {
        console.warn('⚠️ Failed to pre-warm blur processor:', error);
    });
    // Pre-warm common background images
    const getUrl = (filename) => {
        const base = BACKGROUND_BASE_URL.endsWith('/')
            ? BACKGROUND_BASE_URL.slice(0, -1)
            : BACKGROUND_BASE_URL;
        return `${base}/${filename}`;
    };
    const commonBackgrounds = [
        getUrl('office.jpg'),
        getUrl('library.jpg'),
        getUrl('classroom.jpg'),
        getUrl('study.jpg')
    ];
    commonBackgrounds.forEach(url => {
        prewarmImageProcessor(url).then(() => {
            console.log('✅ Pre-warmed background:', url);
        }).catch(error => {
            console.warn('⚠️ Failed to pre-warm background:', url, error);
        });
    });
};

// Custom scrollbar styles for background selection
const scrollbarStyles = `
.custom-scrollbar::-webkit-scrollbar {
    width: 6px;
}
.custom-scrollbar::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 10px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
}
`;
// Inject scrollbar styles
if (typeof document !== 'undefined' && !document.getElementById('prejoin-scrollbar-styles')) {
    const styleSheet = document.createElement('style');
    styleSheet.id = 'prejoin-scrollbar-styles';
    styleSheet.textContent = scrollbarStyles;
    document.head.appendChild(styleSheet);
}
// HIGH-QUALITY Professional Backgrounds (1920x1080 Full HD)
// Now imported from shared backgroundHelpers for consistency across all apps
const builtInBackgrounds = getBuiltInBackgrounds();
const PreJoinScreen = ({ participantName, meetingTitle, onJoin, onCancel, token, isGeneratingToken = false }) => {
    const [micEnabled, setMicEnabled] = useState(false); // Default: muted (like Zoom)
    const [cameraEnabled, setCameraEnabled] = useState(true); // Default: camera on
    const [selectedBackground, setSelectedBackground] = useState('none');
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const localStreamRef = useRef(null);
    const backgroundProcessorRef = useRef(null); // Pre-initialized processor for instant use
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedAudioDevice, setSelectedAudioDevice] = useState('');
    const [selectedVideoDevice, setSelectedVideoDevice] = useState('');
    const [isVideoLoading, setIsVideoLoading] = useState(true);
    const [isApplyingBackground, setIsApplyingBackground] = useState(false);
    const [backgroundProcessingProgress, setBackgroundProcessingProgress] = useState(0);
    const initializationInProgressRef = useRef(false);
    const hasJoinedRef = useRef(false); // Track if user has joined meeting (to avoid cleanup on unmount)
    const liveKitTrackRef = useRef(null); // Move to top level so cleanup can access it
    // Get user media for preview
    useEffect(() => {
        let isMounted = true;
        const initPreview = async () => {
            console.log('🎬 initPreview called, waiting for debounce...');
            // Small delay to debounce rapid effect triggers
            await new Promise(resolve => setTimeout(resolve, 50));
            if (!isMounted) {
                console.log('⏭️ Component unmounted during debounce, skipping');
                return;
            }
            // Prevent multiple simultaneous initializations
            if (initializationInProgressRef.current) {
                console.log('⏭️ Skipping initialization - already in progress');
                return;
            }
            initializationInProgressRef.current = true;
            console.log('🎬 Starting media initialization', {
                cameraEnabled,
                micEnabled,
                hasLocalStream: !!localStreamRef.current,
                hasLiveKitTrack: !!liveKitTrackRef.current
            });
            try {
                // CRITICAL FIX: Only stop and recreate tracks when camera/video settings change
                // Don't touch video when only mic changes!
                const needsVideoRecreation = cameraEnabled && (!localStreamRef.current ||
                    localStreamRef.current.getVideoTracks().length === 0);
                // Stop existing tracks ONLY if we need to recreate them
                if (needsVideoRecreation && localStreamRef.current) {
                    console.log('🔄 Recreating video stream due to camera/device change');
                    localStreamRef.current.getTracks().forEach(track => track.stop());
                    localStreamRef.current = null;
                }
                const constraints = {
                    video: cameraEnabled ? {
                        deviceId: selectedVideoDevice ? { exact: selectedVideoDevice } : undefined,
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 }
                    } : false,
                    audio: micEnabled ? {
                        deviceId: selectedAudioDevice ? { exact: selectedAudioDevice } : undefined,
                        echoCancellation: true,
                        noiseSuppression: true
                    } : false
                };
                console.log('🎥 Requesting media with constraints:', constraints);
                const stream = await navigator.mediaDevices.getUserMedia(constraints);
                if (!isMounted) {
                    // Component unmounted, stop the stream
                    stream.getTracks().forEach(track => track.stop());
                    return;
                }
                console.log('✅ Media stream obtained:', {
                    videoTracks: stream.getVideoTracks().length,
                    audioTracks: stream.getAudioTracks().length
                });
                // CRITICAL: If we already have a processed video (with background), don't replace it!
                // Only update the audio track
                if (localStreamRef.current && liveKitTrackRef.current && !needsVideoRecreation) {
                    console.log('🎤 Only updating audio track, keeping processed video');
                    // Remove old audio tracks
                    localStreamRef.current.getAudioTracks().forEach(track => {
                        localStreamRef.current?.removeTrack(track);
                        track.stop();
                    });
                    // Add new audio tracks
                    stream.getAudioTracks().forEach(track => {
                        localStreamRef.current?.addTrack(track);
                    });
                    // Stop the new video track since we're not using it
                    stream.getVideoTracks().forEach(track => track.stop());
                    // Don't touch the video element - it's showing the processed stream!
                }
                else {
                    // First time or video recreation needed
                    console.log('📹 Setting video element to new stream');
                    if (videoRef.current) {
                        const video = videoRef.current;
                        video.srcObject = stream;
                        localStreamRef.current = stream;
                        // ✅ Immediately allow joining once we have a stream bound
                        setIsVideoLoading(false);
                        // Check if video is already ready (immediate check)
                        const checkVideoReady = () => {
                            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                                console.log('✅ Video already ready, enabling join immediately');
                                setIsVideoLoading(false);
                                return true;
                            }
                            return false;
                        };
                        // Immediate check
                        if (checkVideoReady()) {
                            // Video is already ready, just play it
                            video.play().catch(err => console.warn('Video play error:', err));
                        }
                        else {
                            // Set up event handlers for when video becomes ready
                            const handleCanPlay = () => {
                                console.log('✅ Video can play, enabling join button');
                                setIsVideoLoading(false);
                                video.removeEventListener('canplay', handleCanPlay);
                                video.removeEventListener('loadedmetadata', handleLoadedMetadata);
                            };
                            const handleLoadedMetadata = () => {
                                console.log('✅ Video metadata loaded');
                                // Check again when metadata loads
                                if (checkVideoReady()) {
                                    handleCanPlay();
                                }
                            };
                            video.addEventListener('canplay', handleCanPlay, { once: true });
                            video.addEventListener('loadedmetadata', handleLoadedMetadata, { once: true });
                            // Force video to play immediately
                            video.play().catch(err => console.warn('Video play error:', err));
                            // Fallback: Hide loading after 500ms (reduced from 2s) even if events don't fire
                            setTimeout(() => {
                                if (isMounted && isVideoLoading) {
                                    console.log('⏰ Loading timeout reached, enabling join button');
                                    setIsVideoLoading(false);
                                }
                            }, 500);
                        }
                    }
                }
                // Get available devices only on first load
                if (audioDevices.length === 0 && videoDevices.length === 0) {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const audioInputs = devices.filter(d => d.kind === 'audioinput');
                    const videoInputs = devices.filter(d => d.kind === 'videoinput');
                    if (isMounted) {
                        setAudioDevices(audioInputs);
                        setVideoDevices(videoInputs);
                        if (audioInputs.length > 0 && !selectedAudioDevice) {
                            setSelectedAudioDevice(audioInputs[0].deviceId);
                        }
                        if (videoInputs.length > 0 && !selectedVideoDevice) {
                            setSelectedVideoDevice(videoInputs[0].deviceId);
                        }
                    }
                }
            }
            catch (error) {
                console.error('Error accessing media devices:', error);
            }
            finally {
                initializationInProgressRef.current = false;
            }
        };
        initPreview();
        return () => {
            isMounted = false;
            // CRITICAL: Only stop tracks if component is truly unmounting AND user hasn't joined
            // If user has joined, the meeting component will take ownership of the tracks
            if (localStreamRef.current && !hasJoinedRef.current) {
                console.log('🧹 Cleaning up local stream tracks on unmount');
                localStreamRef.current.getTracks().forEach(track => {
                    track.stop();
                });
                localStreamRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cameraEnabled, micEnabled, selectedAudioDevice, selectedVideoDevice]);
    // Reset loading state when camera is toggled
    useEffect(() => {
        if (cameraEnabled) {
            setIsVideoLoading(true);
        }
    }, [cameraEnabled]);
    // ULTIMATE OPTIMIZATION: Apply background processor using LiveKit track!
    // Create LiveKit track from browser stream, apply processor, show in preview
    // Note: liveKitTrackRef is declared at component top level for cleanup access
    useEffect(() => {
        const applyBackgroundToPreJoinStream = async () => {
            // If camera is disabled, just hide the video but keep the processor ready
            if (!cameraEnabled) {
                setIsApplyingBackground(false);
                console.log('📹 Camera disabled, keeping processor ready for when camera is enabled');
                if (liveKitTrackRef.current) {
                    liveKitTrackRef.current.stop();
                    liveKitTrackRef.current = null;
                }
                backgroundProcessorRef.current = null;
                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.srcObject = null;
                }
                return;
            }
            // CRITICAL FIX: Wait for stream to be ready before processing
            // If stream isn't ready yet, just return - the first useEffect will handle setting the video element
            if (!localStreamRef.current || localStreamRef.current.getVideoTracks().length === 0) {
                console.log('⏳ Stream not ready yet, background processing will wait');
                backgroundProcessorRef.current = null;
                setIsApplyingBackground(false);
                // Don't interfere with video element - let first useEffect handle it
                return;
            }
            // Only apply if a background is selected
            if (selectedBackground === 'none' || !selectedBackground) {
                console.log('🚫 No background selected');
                setIsApplyingBackground(false);
                // Use original stream without processor
                // CRITICAL: Always ensure video element is set to the original stream when no background
                if (videoRef.current && localStreamRef.current) {
                    const video = videoRef.current;
                    const currentSrcObject = video.srcObject;
                    // Check if video already has the correct stream by comparing track IDs
                    const currentVideoTrack = currentSrcObject?.getVideoTracks()[0];
                    const expectedVideoTrack = localStreamRef.current.getVideoTracks()[0];
                    const hasCorrectStream = currentVideoTrack && expectedVideoTrack &&
                        currentVideoTrack.id === expectedVideoTrack.id;
                    if (!hasCorrectStream || !currentSrcObject) {
                        console.log('📹 Setting video element to original stream (no background)', {
                            hasCurrentSrc: !!currentSrcObject,
                            hasCorrectStream,
                            currentTrackId: currentVideoTrack?.id,
                            expectedTrackId: expectedVideoTrack?.id
                        });
                        video.srcObject = localStreamRef.current;
                        // Check if video is ready immediately
                        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                            console.log('✅ Video ready (no background), enabling join immediately');
                            setIsVideoLoading(false);
                        }
                        else {
                            // Wait for video to be ready
                            const handleNoBgCanPlay = () => {
                                console.log('✅ Video ready (no background), enabling join');
                                setIsVideoLoading(false);
                                video.removeEventListener('canplay', handleNoBgCanPlay);
                            };
                            video.addEventListener('canplay', handleNoBgCanPlay, { once: true });
                            video.play().catch(err => console.warn('Video play error:', err));
                        }
                    }
                    else {
                        console.log('✅ Video already showing correct stream (no background)');
                        // Ensure video is playing
                        if (video.paused) {
                            video.play().catch(err => console.warn('Video play error:', err));
                        }
                        // Ensure loading state is cleared
                        setIsVideoLoading(false);
                    }
                }
                else if (videoRef.current && !localStreamRef.current) {
                    console.warn('⚠️ Video element exists but stream is not ready yet');
                }
                // Clean up old LiveKit track if switching to no background
                if (liveKitTrackRef.current) {
                    console.log('🧹 Stopping old LiveKit track (switching to no background)');
                    liveKitTrackRef.current.stop();
                    liveKitTrackRef.current = null;
                }
                backgroundProcessorRef.current = null;
                return;
            }
            // CRITICAL: Stop old LiveKit track before creating new one (prevent memory leak)
            if (liveKitTrackRef.current) {
                console.log('🧹 Stopping old LiveKit track before creating new one');
                liveKitTrackRef.current.stop();
                liveKitTrackRef.current = null;
            }
            // Show loading animation
            setIsApplyingBackground(true);
            setBackgroundProcessingProgress(0);
            console.log('🚀 ULTIMATE OPTIMIZATION: Creating LiveKit track with background processor!');
            try {
                // Simulate progress for better UX
                const progressInterval = setInterval(() => {
                    setBackgroundProcessingProgress(prev => {
                        if (prev >= 90)
                            return prev;
                        return prev + 10;
                    });
                }, 300);
                // Import LiveKit
                const { createLocalVideoTrack } = await import('livekit-client');
                setBackgroundProcessingProgress(40);
                // Get the device ID from current stream
                const browserTrack = localStreamRef.current.getVideoTracks()[0];
                const deviceId = browserTrack.getSettings().deviceId;
                // Create LiveKit track (this supports processors!)
                console.log('📹 Creating LiveKit track from device:', deviceId);
                const liveKitTrack = await createLocalVideoTrack({
                    deviceId: deviceId,
                    resolution: { width: 1280, height: 720 },
                    facingMode: 'user'
                });
                setBackgroundProcessingProgress(60);
                // Apply processor to LiveKit track with optimized settings
                if (selectedBackground === 'blur') {
                    console.log('🌫️ Applying blur to LiveKit track...');
                    await ensureBackgroundProcessor(liveKitTrack, 'blur');
                    const processor = liveKitTrack.getProcessor?.();
                    if (!processor) {
                        throw new Error('Processor not available after blur application');
                    }
                    backgroundProcessorRef.current = { type: 'blur', processor, applied: true, track: liveKitTrack };
                    console.log('✅ Optimized blur APPLIED to LiveKit track!');
                }
                else {
                    console.log('🖼️ Applying virtual background to LiveKit track...');
                    await ensureBackgroundProcessor(liveKitTrack, 'image', selectedBackground);
                    const processor = liveKitTrack.getProcessor?.();
                    if (!processor) {
                        throw new Error('Processor not available after background application');
                    }
                    backgroundProcessorRef.current = { type: 'image', processor, imageUrl: selectedBackground, applied: true, track: liveKitTrack };
                    console.log('✅ Virtual background APPLIED to LiveKit track!');
                }
                // Store LiveKit track
                liveKitTrackRef.current = liveKitTrack;
                // Create MediaStream from LiveKit track and show in video element
                const processedStream = new MediaStream([liveKitTrack.mediaStreamTrack]);
                if (videoRef.current) {
                    const video = videoRef.current;
                    video.srcObject = processedStream;
                    console.log('✅ Video preview now showing PROCESSED stream with background!');
                    // Check if processed video is ready immediately
                    const checkProcessedVideoReady = () => {
                        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
                            console.log('✅ Processed video already ready, enabling join');
                            setIsVideoLoading(false);
                            return true;
                        }
                        return false;
                    };
                    if (!checkProcessedVideoReady()) {
                        // Wait for processed video to be ready
                        const handleProcessedCanPlay = () => {
                            console.log('✅ Processed video can play, enabling join');
                            setIsVideoLoading(false);
                            video.removeEventListener('canplay', handleProcessedCanPlay);
                        };
                        video.addEventListener('canplay', handleProcessedCanPlay, { once: true });
                        video.play().catch(err => console.warn('Processed video play error:', err));
                    }
                    else {
                        video.play().catch(err => console.warn('Processed video play error:', err));
                    }
                }
                clearInterval(progressInterval);
                setBackgroundProcessingProgress(100);
                // Hide loading after a brief moment
                setTimeout(() => {
                    setIsApplyingBackground(false);
                }, 500);
            }
            catch (error) {
                console.error('❌ Error applying background to prejoin stream:', error);
                backgroundProcessorRef.current = null;
                setIsApplyingBackground(false);
                // Fallback to original stream
                if (videoRef.current && localStreamRef.current) {
                    videoRef.current.srcObject = localStreamRef.current;
                }
            }
        };
        // Apply when background selection changes or stream is ready
        applyBackgroundToPreJoinStream();
        // Cleanup - Only stop LiveKit track if user hasn't joined
        // If user has joined, the track will be passed to the meeting component
        return () => {
            if (!hasJoinedRef.current && liveKitTrackRef.current) {
                console.log('🧹 Cleaning up LiveKit track on unmount (user canceled/closed prejoin)');
                try {
                    liveKitTrackRef.current.stop();
                }
                catch (error) {
                    console.warn('⚠️ Error stopping LiveKit track in cleanup:', error);
                }
                liveKitTrackRef.current = null;
                backgroundProcessorRef.current = null;
            }
            else if (hasJoinedRef.current) {
                console.log('🔒 Keeping LiveKit track alive - user is joining meeting');
            }
        };
    }, [selectedBackground, cameraEnabled]);
    // Background image cache for instant loading
    const backgroundImageCache = useRef(new Map());
    // Preload all background images when component mounts (like Google Meet)
    useEffect(() => {
        console.log('🔄 Preloading all background images...');
        const preloadPromises = builtInBackgrounds.map(bg => {
            return new Promise((resolve) => {
                // Use thumbnail for preloading (smaller, faster)
                const imageUrl = bg.thumbnail || bg.url;
                if (backgroundImageCache.current.has(imageUrl)) {
                    console.log('⚡ Already cached:', bg.name);
                    resolve();
                    return;
                }
                const img = new Image();
                // Only set crossOrigin for remote URLs, not local paths
                if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                    img.crossOrigin = 'anonymous';
                }
                img.onload = () => {
                    console.log('✅ Preloaded:', bg.name, imageUrl);
                    backgroundImageCache.current.set(imageUrl, img);
                    // Also cache by URL for lookup
                    if (bg.url !== imageUrl) {
                        backgroundImageCache.current.set(bg.url, img);
                    }
                    resolve();
                };
                img.onerror = (error) => {
                    console.error('❌ Failed to preload background image:', bg.name, imageUrl, error);
                    // Try fallback to full URL if thumbnail failed
                    if (bg.thumbnail && bg.thumbnail !== bg.url) {
                        console.log('🔄 Trying fallback URL for:', bg.name);
                        const fallbackImg = new Image();
                        if (bg.url.startsWith('http://') || bg.url.startsWith('https://')) {
                            fallbackImg.crossOrigin = 'anonymous';
                        }
                        fallbackImg.onload = () => {
                            console.log('✅ Preloaded (fallback):', bg.name, bg.url);
                            backgroundImageCache.current.set(bg.url, fallbackImg);
                            resolve();
                        };
                        fallbackImg.onerror = () => {
                            console.error('❌ Fallback also failed for:', bg.name);
                            resolve(); // Continue even if both fail
                        };
                        fallbackImg.src = bg.url;
                    }
                    else {
                        resolve(); // Continue even if one fails
                    }
                };
                img.src = imageUrl;
            });
        });
        // Wait for all images to preload
        Promise.all(preloadPromises).then(() => {
            console.log('✅ All background images preloaded!', {
                total: builtInBackgrounds.length,
                cached: backgroundImageCache.current.size
            });
        });
    }, []); // Only run once on mount
    // Preload background images when selected (for caching)
    useEffect(() => {
        if (selectedBackground !== 'none' && selectedBackground !== 'blur') {
            // Check if already cached
            const cachedImage = backgroundImageCache.current.get(selectedBackground);
            if (cachedImage && cachedImage.complete) {
                console.log('⚡ Background image already cached:', selectedBackground);
                return;
            }
            // Preload and cache
            console.log('🖼️ Preloading background image:', selectedBackground);
            const img = new Image();
            // Only set crossOrigin for remote URLs (Firebase Hosting), not local paths
            if (selectedBackground.startsWith('http://') || selectedBackground.startsWith('https://')) {
                img.crossOrigin = 'anonymous';
            }
            img.onload = () => {
                backgroundImageCache.current.set(selectedBackground, img);
                console.log('✅ Background image cached:', selectedBackground);
            };
            img.onerror = (error) => {
                console.error('❌ Failed to load background image:', selectedBackground, error);
            };
            img.src = selectedBackground;
        }
    }, [selectedBackground]);
    // NO CANVAS DRAWING NEEDED!
    // The video element automatically shows the processed stream because
    // LiveKit's setProcessor() modifies the MediaStreamTrack itself.
    // The video element displays whatever the track outputs - which is the processed video with background!
    // This is the ULTIMATE optimization - preview shows the ACTUAL stream that will be used in meeting!
    const handleJoin = () => {
        if (!token) {
            // Token should be generated before showing pre-join screen
            console.error('Token not available');
            return;
        }
        console.log('🚀 Joining meeting with settings:', {
            micEnabled,
            cameraEnabled,
            selectedBackground,
            selectedAudioDevice,
            selectedVideoDevice,
            hasMediaStream: !!localStreamRef.current
        });
        // Mark that user is joining - this prevents cleanup from stopping tracks
        hasJoinedRef.current = true;
        // Pass the LiveKit track with background already applied!
        const processorInfo = cameraEnabled ? backgroundProcessorRef.current : null;
        // CRITICAL: Verify processor is actually attached to the track
        const trackHasProcessor = processorInfo?.track ? processorInfo.track.processor : null;
        console.log('🚀 PreJoin: Preparing to join with:', {
            hasProcessor: !!processorInfo,
            type: processorInfo?.type,
            hasTrack: !!processorInfo?.track,
            applied: processorInfo?.applied,
            selectedBackground,
            liveKitTrackExists: !!liveKitTrackRef.current,
            processorAttachedToTrack: !!trackHasProcessor,
            processorObject: processorInfo?.processor ? 'exists' : 'missing'
        });
        // Store in window for meeting to access
        if (cameraEnabled && processorInfo && processorInfo.track) {
            window.__preInitializedProcessor = processorInfo;
            console.log('✅ Stored pre-initialized processor in window for meeting access', {
                trackId: processorInfo.track.sid,
                processorAttached: !!trackHasProcessor
            });
        }
        else {
            console.warn('⚠️ No processor info to store! Background will not be applied in meeting.');
        }
        onJoin({
            micEnabled,
            cameraEnabled,
            selectedBackground,
            token,
            mediaStream: localStreamRef.current || undefined,
            selectedAudioDevice: selectedAudioDevice || undefined,
            selectedVideoDevice: selectedVideoDevice || undefined,
            // ULTIMATE OPTIMIZATION: Pass LiveKit track with processor already applied!
            preInitializedProcessor: cameraEnabled ? processorInfo : undefined
        });
    };
    const toggleMic = () => {
        setMicEnabled(!micEnabled);
        if (localStreamRef.current) {
            const audioTrack = localStreamRef.current.getAudioTracks()[0];
            if (audioTrack) {
                audioTrack.enabled = !micEnabled;
            }
        }
    };
    const toggleCamera = () => {
        setCameraEnabled(prev => {
            const next = !prev;
            if (!next) {
                setIsVideoLoading(false);
                if (localStreamRef.current) {
                    const audioTracks = localStreamRef.current.getAudioTracks();
                    localStreamRef.current.getVideoTracks().forEach(track => {
                        track.stop();
                        localStreamRef.current?.removeTrack(track);
                    });
                    const audioOnlyStream = new MediaStream();
                    audioTracks.forEach(track => audioOnlyStream.addTrack(track));
                    localStreamRef.current = audioOnlyStream;
                }
                if (videoRef.current) {
                    videoRef.current.pause();
                    videoRef.current.srcObject = null;
                }
                if (liveKitTrackRef.current) {
                    try {
                        liveKitTrackRef.current.stop();
                    }
                    catch (error) {
                        console.warn('⚠️ Error stopping LiveKit track on camera toggle:', error);
                    }
                    liveKitTrackRef.current = null;
                }
                backgroundProcessorRef.current = null;
                try {
                    delete window.__preInitializedProcessor;
                }
                catch (error) {
                    console.warn('Failed to clear pre-initialized processor:', error);
                }
            }
            else {
                setIsVideoLoading(true);
            }
            return next;
        });
    };
    // CRITICAL: Cleanup all camera/mic tracks when component unmounts (if user canceled/closed)
    useEffect(() => {
        // Capture refs at effect setup time for cleanup
        // We intentionally capture these values at mount time for use in cleanup
        // eslint-disable-next-line react-hooks/exhaustive-deps
        const videoElement = videoRef.current;
        return () => {
            console.log('🧹 PreJoinScreen unmounting - cleaning up all media tracks');
            // Only cleanup if user hasn't joined (if they joined, meeting component owns the tracks)
            if (!hasJoinedRef.current) {
                // Stop local stream tracks
                const localStream = localStreamRef.current;
                if (localStream) {
                    console.log('🧹 Stopping local stream tracks');
                    localStream.getTracks().forEach(track => {
                        try {
                            track.stop();
                            console.log('✅ Stopped track:', track.kind, track.id);
                        }
                        catch (error) {
                            console.warn('⚠️ Error stopping track:', error);
                        }
                    });
                    localStreamRef.current = null;
                }
                // Stop LiveKit track
                const liveKitTrack = liveKitTrackRef.current;
                if (liveKitTrack) {
                    console.log('🧹 Stopping LiveKit track');
                    try {
                        liveKitTrack.stop();
                        console.log('✅ Stopped LiveKit track');
                    }
                    catch (error) {
                        console.warn('⚠️ Error stopping LiveKit track:', error);
                    }
                    liveKitTrackRef.current = null;
                }
                // Clear processor ref
                backgroundProcessorRef.current = null;
                // Clear video element using captured reference
                if (videoElement) {
                    try {
                        videoElement.srcObject = null;
                        videoElement.pause();
                    }
                    catch (error) {
                        console.warn('⚠️ Error clearing video element:', error);
                    }
                }
                // Clear window processor
                try {
                    delete window.__preInitializedProcessor;
                }
                catch (error) {
                    console.warn('⚠️ Error clearing window processor:', error);
                }
                console.log('✅ PreJoinScreen cleanup completed');
            }
            else {
                console.log('🔒 PreJoinScreen unmounting - tracks passed to meeting, skipping cleanup');
            }
        };
    }, []); // Run only on mount/unmount
    return (jsx("div", { className: "w-full", children: jsxs("div", { className: "bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100", style: {
                backgroundColor: '#ffffff !important',
                background: '#ffffff !important',
            }, children: [jsxs("div", { className: "bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 px-8 py-5", children: [jsx("h2", { className: "text-white text-2xl font-bold tracking-tight", children: meetingTitle }), jsxs("p", { className: "text-purple-50 text-sm mt-1.5 font-medium", children: ["Join as ", participantName] })] }), jsxs("div", { className: "grid md:grid-cols-2 gap-8 p-8", children: [jsxs("div", { className: "space-y-5", children: [jsx("h3", { className: "text-gray-900 font-semibold text-lg", children: "Preview" }), jsx("div", { className: "relative bg-gray-900 rounded-xl overflow-hidden aspect-video shadow-lg border border-gray-800", children: cameraEnabled ? (jsxs(Fragment, { children: [isVideoLoading && (jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gray-900 z-10", children: jsxs("div", { className: "text-center", children: [jsxs("div", { className: "relative w-20 h-20 mx-auto mb-4", children: [jsx("div", { className: "absolute inset-0 border-4 border-purple-500/30 rounded-full" }), jsx("div", { className: "absolute inset-0 border-4 border-transparent border-t-purple-500 rounded-full animate-spin" }), jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-purple-400", children: [jsx("path", { d: "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" }), jsx("circle", { cx: "12", cy: "13", r: "4" })] }) })] }), jsx("p", { className: "text-purple-400 text-sm font-medium", children: "Loading camera..." }), jsx("p", { className: "text-gray-500 text-xs mt-1", children: "Please wait" })] }) })), isApplyingBackground && !isVideoLoading && (jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gradient-to-br from-purple-900/95 via-purple-800/95 to-indigo-900/95 z-20 backdrop-blur-sm", children: jsxs("div", { className: "text-center max-w-md px-6", children: [jsxs("div", { className: "relative w-24 h-24 mx-auto mb-6", children: [jsx("div", { className: "absolute inset-0 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-pulse opacity-20" }), jsx("div", { className: "absolute inset-0 border-4 border-purple-400/30 rounded-full" }), jsx("div", { className: "absolute inset-0 border-4 border-transparent border-t-purple-400 border-r-pink-400 rounded-full animate-spin" }), jsx("div", { className: "absolute inset-3 border-4 border-purple-300/20 rounded-full" }), jsx("div", { className: "absolute inset-3 border-4 border-transparent border-b-purple-300 border-l-pink-300 rounded-full animate-spin", style: { animationDirection: 'reverse', animationDuration: '1.5s' } }), jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "40", height: "40", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-purple-300 animate-pulse", children: jsx("path", { d: "M12 3v18M3 12h18M5.6 5.6l12.8 12.8M5.6 18.4L18.4 5.6" }) }) })] }), jsx("h3", { className: "text-white text-lg font-bold mb-2 animate-pulse", children: "\u2728 Hosaksham is enhancing your visual experience" }), jsx("p", { className: "text-purple-200 text-sm mb-4", children: "Applying AI-powered background effects..." }), jsx("div", { className: "w-full bg-purple-900/50 rounded-full h-2 mb-2 overflow-hidden", children: jsx("div", { className: "bg-gradient-to-r from-purple-400 via-pink-400 to-purple-400 h-full rounded-full transition-all duration-300 ease-out relative", style: { width: `${backgroundProcessingProgress}%` }, children: jsx("div", { className: "absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent animate-shimmer" }) }) }), jsxs("p", { className: "text-purple-300 text-xs", children: [backgroundProcessingProgress, "% complete"] }), jsx("div", { className: "mt-6 p-3 bg-white/10 rounded-lg border border-purple-400/30", children: jsxs("p", { className: "text-purple-100 text-xs", children: ["\uD83D\uDCA1 ", jsx("span", { className: "font-semibold", children: "Pro Tip:" }), " Your background will be instantly active when you join!"] }) })] }) })), jsx("video", { ref: videoRef, autoPlay: true, playsInline: true, muted: true, className: "absolute inset-0 w-full h-full object-cover", style: {
                                                    // No mirror - match meeting room default state to prevent flip on join
                                                    zIndex: 1,
                                                    display: cameraEnabled ? 'block' : 'none'
                                                } }), jsx("canvas", { ref: canvasRef, className: "absolute inset-0 w-full h-full object-cover", style: {
                                                    display: 'none'
                                                } })] })) : (jsx("div", { className: "w-full h-full flex items-center justify-center bg-gray-800", children: jsxs("div", { className: "text-center", children: [jsx("div", { className: "w-20 h-20 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-3", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-gray-400", children: [jsx("path", { d: "M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10" }), jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" })] }) }), jsx("p", { className: "text-gray-400 text-sm", children: "Camera is off" })] }) })) }), jsxs("div", { className: "flex items-center justify-center gap-3", children: [jsx("button", { onClick: toggleMic, className: `px-5 py-2.5 rounded-full flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg font-medium ${micEnabled
                                                ? 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 hover:border-gray-300'
                                                : 'bg-red-500 hover:bg-red-600 text-white border border-red-600'}`, title: micEnabled ? 'Mute microphone' : 'Unmute microphone', children: micEnabled ? (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }), "Unmuted"] })) : (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), jsx("path", { d: "M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" }), jsx("path", { d: "M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }), "Muted"] })) }), jsx("button", { onClick: toggleCamera, className: `px-5 py-2.5 rounded-full flex items-center gap-2 transition-all duration-200 shadow-md hover:shadow-lg font-medium ${cameraEnabled
                                                ? 'bg-white hover:bg-gray-50 text-gray-900 border border-gray-200 hover:border-gray-300'
                                                : 'bg-red-500 hover:bg-red-600 text-white border border-red-600'}`, title: cameraEnabled ? 'Turn off camera' : 'Turn on camera', children: cameraEnabled ? (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" }), jsx("circle", { cx: "12", cy: "13", r: "4" })] }), "Camera On"] })) : (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), jsx("path", { d: "M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h3m3-3h6l2 3h4a2 2 0 0 1 2 2v9.34" }), jsx("path", { d: "M9 9v1a3 3 0 0 0 4.58 2.64" })] }), "Camera Off"] })) })] })] }), jsxs("div", { className: "space-y-6", children: [(audioDevices.length > 1 || videoDevices.length > 1) && (jsxs("div", { className: "space-y-3", children: [audioDevices.length > 1 && (jsxs("div", { children: [jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-2", children: "Microphone" }), jsx("select", { value: selectedAudioDevice, onChange: (e) => setSelectedAudioDevice(e.target.value), className: "w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white text-gray-900", children: audioDevices.map(device => (jsx("option", { value: device.deviceId, children: device.label || `Microphone ${audioDevices.indexOf(device) + 1}` }, device.deviceId))) })] })), videoDevices.length > 1 && (jsxs("div", { children: [jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-2", children: "Camera" }), jsx("select", { value: selectedVideoDevice, onChange: (e) => setSelectedVideoDevice(e.target.value), className: "w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all bg-white text-gray-900", children: videoDevices.map(device => (jsx("option", { value: device.deviceId, children: device.label || `Camera ${videoDevices.indexOf(device) + 1}` }, device.deviceId))) })] }))] })), jsx("h3", { className: "text-gray-900 font-semibold text-lg", children: "Settings" }), jsxs("div", { children: [jsx("label", { className: "block text-sm font-semibold text-gray-700 mb-3", children: "Background" }), jsx("div", { className: "max-h-64 overflow-y-auto pr-2 custom-scrollbar", children: jsxs("div", { className: "grid grid-cols-3 gap-3", children: [jsx("button", { onClick: () => setSelectedBackground('none'), className: `p-3 rounded-xl border-2 transition-all duration-200 aspect-square hover:scale-105 ${selectedBackground === 'none'
                                                            ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200 shadow-sm'
                                                            : 'border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-50/50'}`, children: jsx("div", { className: "text-xs font-semibold text-gray-700", children: "None" }) }), jsx("button", { onClick: () => setSelectedBackground('blur'), className: `p-3 rounded-xl border-2 transition-all duration-200 aspect-square hover:scale-105 ${selectedBackground === 'blur'
                                                            ? 'border-purple-600 bg-purple-50 ring-2 ring-purple-200 shadow-sm'
                                                            : 'border-gray-300 bg-white hover:border-purple-300 hover:bg-purple-50/50'}`, children: jsx("div", { className: "text-xs font-semibold text-gray-700", children: "Blur" }) }), builtInBackgrounds.map(bg => (jsxs("button", { onClick: () => setSelectedBackground(bg.url), className: `p-0.5 rounded-xl border-2 transition-all duration-200 relative overflow-hidden aspect-square hover:scale-105 ${selectedBackground === bg.url
                                                            ? 'border-purple-600 ring-2 ring-purple-200 shadow-md'
                                                            : 'border-gray-300 hover:border-purple-300'}`, children: [jsx("img", { src: bg.thumbnail || bg.url, alt: bg.name, className: "w-full h-full object-cover rounded-lg", onError: (e) => {
                                                                    console.error('❌ Failed to load background image:', bg.name, bg.thumbnail || bg.url);
                                                                    // Show a placeholder instead of hiding completely
                                                                    const img = e.target;
                                                                    img.style.backgroundColor = '#e5e7eb';
                                                                    img.style.display = 'flex';
                                                                    img.style.alignItems = 'center';
                                                                    img.style.justifyContent = 'center';
                                                                    // Try fallback URL if thumbnail failed
                                                                    if (bg.thumbnail && bg.thumbnail !== bg.url) {
                                                                        img.src = bg.url;
                                                                    }
                                                                }, onLoad: () => {
                                                                    console.log('✅ Loaded background image:', bg.name, bg.thumbnail || bg.url);
                                                                }, loading: "lazy" }), jsx("div", { className: "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/50 to-transparent text-white text-[10px] font-medium px-2 py-1.5 rounded-b-lg", children: bg.name })] }, bg.id)))] }) })] })] })] }), jsxs("div", { className: "px-8 py-5 bg-gradient-to-b from-gray-50 to-white border-t border-gray-200 flex justify-end gap-3", children: [onCancel && (jsx("button", { onClick: () => {
                                // CRITICAL: Clean up camera/mic tracks immediately when cancel is clicked
                                console.log('🧹 Cancel clicked - cleaning up camera/mic tracks');
                                // Stop local stream tracks
                                if (localStreamRef.current) {
                                    localStreamRef.current.getTracks().forEach(track => {
                                        try {
                                            track.stop();
                                            console.log('✅ Stopped track on cancel:', track.kind);
                                        }
                                        catch (error) {
                                            console.warn('⚠️ Error stopping track on cancel:', error);
                                        }
                                    });
                                    localStreamRef.current = null;
                                }
                                // Stop LiveKit track
                                if (liveKitTrackRef.current) {
                                    try {
                                        liveKitTrackRef.current.stop();
                                        console.log('✅ Stopped LiveKit track on cancel');
                                    }
                                    catch (error) {
                                        console.warn('⚠️ Error stopping LiveKit track on cancel:', error);
                                    }
                                    liveKitTrackRef.current = null;
                                }
                                // Clear processor ref
                                backgroundProcessorRef.current = null;
                                // Clear video element
                                if (videoRef.current) {
                                    videoRef.current.srcObject = null;
                                    videoRef.current.pause();
                                }
                                // Clear window processor
                                try {
                                    delete window.__preInitializedProcessor;
                                }
                                catch (error) {
                                    console.warn('⚠️ Error clearing window processor on cancel:', error);
                                }
                                // Call original onCancel callback
                                onCancel();
                            }, className: "px-6 py-2.5 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-100 hover:border-gray-400 transition-all duration-200 font-medium shadow-sm hover:shadow", children: "Cancel" })), jsx("button", { onClick: handleJoin, disabled: !token || isGeneratingToken || isVideoLoading, className: "px-8 py-2.5 bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 text-white rounded-xl hover:from-purple-700 hover:via-purple-600 hover:to-indigo-700 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-lg", children: isGeneratingToken ? (jsxs("span", { className: "flex items-center gap-2", children: [jsxs("svg", { className: "animate-spin h-4 w-4", viewBox: "0 0 24 24", fill: "none", children: [jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Preparing..."] })) : isVideoLoading ? (jsxs("span", { className: "flex items-center gap-2", children: [jsxs("svg", { className: "animate-spin h-4 w-4", viewBox: "0 0 24 24", fill: "none", children: [jsx("circle", { className: "opacity-25", cx: "12", cy: "12", r: "10", stroke: "currentColor", strokeWidth: "4" }), jsx("path", { className: "opacity-75", fill: "currentColor", d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" })] }), "Loading camera..."] })) : ('Join Meeting') })] })] }) }));
};

/**
 * React Hook for Meeting Management
 */
function useMeeting(config, options = {}) {
    const [meetingSDK, setMeetingSDK] = useState(null);
    const [room, setRoom] = useState(null);
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const sdkRef = useRef(null);
    // Store callbacks in ref to avoid recreating SDK on callback changes
    const callbacksRef = useRef(options);
    useEffect(() => {
        callbacksRef.current = options;
    }, [options.onConnected, options.onDisconnected, options.onError]);
    // Initialize SDK
    useEffect(() => {
        if (!config) {
            setMeetingSDK(null);
            sdkRef.current = null;
            return;
        }
        // Clean up previous SDK if it exists
        if (sdkRef.current) {
            sdkRef.current.leaveRoom(true).catch(console.error);
        }
        const sdk = new MeetingSDK(config);
        sdk.setCallbacks({
            onConnected: (room) => {
                setRoom(room);
                setStatus('connected');
                setError(null);
                callbacksRef.current.onConnected?.(room);
            },
            onDisconnected: (reason) => {
                setRoom(null);
                setStatus('disconnected');
                callbacksRef.current.onDisconnected?.(reason);
            },
            onError: (err) => {
                setError(err);
                setStatus('error');
                callbacksRef.current.onError?.(err);
            },
        });
        sdkRef.current = sdk;
        setMeetingSDK(sdk);
        return () => {
            if (sdkRef.current) {
                sdkRef.current.leaveRoom(true).catch(console.error);
                sdkRef.current = null;
            }
        };
    }, [config]);
    // Auto-join if enabled
    useEffect(() => {
        if (options.autoJoin &&
            meetingSDK &&
            options.roomName &&
            status === 'idle' &&
            options.token) {
            joinRoom(options.roomName, options.token).catch((err) => {
                console.error('Auto-join failed:', err);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options.autoJoin, options.roomName, options.token, meetingSDK, status]);
    // Store media options in ref to prevent unnecessary recreations
    const mediaOptionsRef = useRef({ enableCamera: options.enableCamera, enableMicrophone: options.enableMicrophone });
    useEffect(() => {
        mediaOptionsRef.current = { enableCamera: options.enableCamera, enableMicrophone: options.enableMicrophone };
    }, [options.enableCamera, options.enableMicrophone]);
    const joinRoom = useCallback(async (roomName, token) => {
        if (!meetingSDK) {
            throw new Error('Meeting SDK not initialized');
        }
        try {
            setStatus('connecting');
            setError(null);
            const joinedRoom = await meetingSDK.joinRoom(roomName, token, {
                autoSubscribe: true,
                enableCamera: mediaOptionsRef.current.enableCamera,
                enableMicrophone: mediaOptionsRef.current.enableMicrophone,
            });
            setRoom(joinedRoom);
            setStatus('connected');
        }
        catch (err) {
            setError(err);
            setStatus('error');
            throw err;
        }
    }, [meetingSDK]);
    const leaveRoom = useCallback(async (isIntentional = true) => {
        if (!meetingSDK) {
            return;
        }
        try {
            await meetingSDK.leaveRoom(isIntentional);
            setRoom(null);
            setStatus('idle');
            setError(null);
        }
        catch (err) {
            setError(err);
            setStatus('error');
        }
    }, [meetingSDK]);
    const generateToken = useCallback(async (roomName) => {
        if (!meetingSDK) {
            throw new Error('Meeting SDK not initialized');
        }
        return meetingSDK.generateToken(roomName);
    }, [meetingSDK]);
    return {
        meetingSDK,
        room,
        status,
        isConnecting: status === 'connecting',
        isConnected: status === 'connected',
        joinRoom,
        leaveRoom,
        generateToken,
        error,
    };
}

// Toast notification - use react-hot-toast or react-toastify
// For compatibility, we'll use a simple console-based approach if toast is not available
let toast = {
    success: (msg) => console.log('✅', msg),
    error: (msg) => console.error('❌', msg),
    info: (msg) => console.info('ℹ️', msg),
};
// Try to load toast library if available
try {
    const reactToastify = require('react-toastify');
    if (reactToastify?.toast) {
        toast = reactToastify.toast;
    }
}
catch {
    // Use console fallback
}
const GuestJoin = ({ apiEndpoint, livekitServerUrl, tenantId: propTenantId, roomName: propRoomName, meetingTitle: propMeetingTitle, subscriptionName: propSubscriptionName, guestName: propGuestName, onJoin, onDisconnect, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
customBackgrounds, // Reserved for future use
showFeedback = true, className = '', logoUrl, renderMeetingRoom, }) => {
    // Get URL params - use native URLSearchParams for compatibility
    // If react-router-dom is available, it will be handled by the parent component
    const searchParams = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
    // Get params from URL or props
    const roomName = propRoomName || searchParams.get('room') || '';
    const meetingTitle = propMeetingTitle || searchParams.get('title') || 'Meeting';
    const subscriptionName = propSubscriptionName || searchParams.get('subscription') || '';
    const tenantId = propTenantId || searchParams.get('tenant') || '';
    // Initialize guestName from prop, sessionStorage, or empty string
    const [guestName, setGuestName] = useState(() => {
        if (propGuestName) {
            return propGuestName;
        }
        const saved = sessionStorage.getItem('meetplus_guest_name');
        return saved || '';
    });
    // Auto-join flow: if guestName is provided via prop and we have roomName/tenantId, auto-show pre-join
    const [isJoining, setIsJoining] = useState(false);
    const [showPreJoin, setShowPreJoin] = useState(false);
    const [preJoinToken, setPreJoinToken] = useState(null);
    const [isGeneratingToken, setIsGeneratingToken] = useState(false);
    const [preJoinOptions, setPreJoinOptions] = useState(null);
    // Keep the pre-join MediaStream in memory (cannot store in sessionStorage)
    const [preJoinMediaStream, setPreJoinMediaStream] = useState(null);
    const [showThankYou, setShowThankYou] = useState(false);
    const [disconnectReason, setDisconnectReason] = useState(null);
    const [rating, setRating] = useState(null);
    const [feedback, setFeedback] = useState('');
    const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
    // Meeting config - ALWAYS null to completely disable useMeeting hook
    // We use MeetingSDK directly for token generation and custom renderMeetingRoom for connection
    // This prevents premature LiveKit connections during pre-join phase
    const meetingConfig = null;
    // Create MeetingSDK instance for token generation ONLY
    // Use useRef to prevent unnecessary recreation
    const meetingSDKRef = React.useRef(null);
    const [meetingSDK, setMeetingSDK] = useState(null);
    // Use a stable userId
    const userIdRef = React.useRef(`guest_${Date.now()}`);
    // Track if SDK has been initialized to prevent re-creation
    const sdkInitializedRef = React.useRef(false);
    useEffect(() => {
        // Only create SDK once if we have tenantId and guestName
        if (tenantId && guestName && guestName.trim() && !sdkInitializedRef.current) {
            sdkInitializedRef.current = true;
            const sdk = new MeetingSDK({
                apiEndpoint,
                livekitServerUrl,
                tenantId,
                userId: userIdRef.current,
                userRole: 'student',
                participantName: guestName,
                metadata: {
                    subscriptionName,
                    joinedAt: new Date().toISOString(),
                    meetingTitle,
                    isGuest: true,
                    role: 'student',
                },
            });
            meetingSDKRef.current = sdk;
            setMeetingSDK(sdk);
        }
        return () => {
            // Cleanup on unmount
            if (meetingSDKRef.current) {
                meetingSDKRef.current.leaveRoom(true).catch(console.error);
                meetingSDKRef.current = null;
                sdkInitializedRef.current = false;
            }
        };
    }, [tenantId, guestName, apiEndpoint, livekitServerUrl, subscriptionName, meetingTitle]);
    // DISABLE useMeeting hook completely - we use MeetingSDK directly for token generation
    // and renderMeetingRoom (or custom component) handles the actual connection
    // Memoize options to prevent infinite re-renders
    const useMeetingOptions = React.useMemo(() => ({
        enableCamera: false,
        enableMicrophone: false,
    }), []);
    const { room, status, isConnected, joinRoom: _unusedJoinRoom, leaveRoom: _unusedLeaveRoom, generateToken: _unusedGenerateToken, error: meetingError, } = useMeeting(meetingConfig, useMeetingOptions);
    // Token generation function - ALWAYS use MeetingSDK directly
    const generateTokenForRoom = useCallback(async (roomName) => {
        if (!meetingSDK) {
            throw new Error('Meeting SDK not initialized');
        }
        return await meetingSDK.generateToken(roomName);
    }, [meetingSDK]);
    useEffect(() => {
        if (!roomName || !tenantId) {
            toast.error('Invalid meeting link');
        }
    }, [roomName, tenantId]);
    // Track if auto-join has been triggered to prevent multiple calls
    const autoJoinTriggeredRef = React.useRef(false);
    // Auto-join effect: trigger pre-join when propGuestName is provided (for logged-in students)
    useEffect(() => {
        if (propGuestName &&
            propGuestName.trim() &&
            roomName &&
            tenantId &&
            meetingSDK &&
            !autoJoinTriggeredRef.current &&
            !showPreJoin &&
            !showThankYou &&
            !preJoinToken) {
            autoJoinTriggeredRef.current = true;
            const autoJoin = async () => {
                try {
                    setIsGeneratingToken(true);
                    setShowPreJoin(true);
                    console.log('🔑 Auto-generating guest token for room:', roomName);
                    const token = await generateTokenForRoom(roomName);
                    if (!token) {
                        throw new Error('No token received');
                    }
                    setPreJoinToken(token);
                    setIsGeneratingToken(false);
                    console.log('✅ Token generated, ready to join');
                }
                catch (error) {
                    console.error('Error auto-generating token:', error);
                    setIsGeneratingToken(false);
                    setShowPreJoin(false);
                    autoJoinTriggeredRef.current = false; // Reset on error so user can retry
                    toast.error(error.message || 'Failed to prepare meeting');
                }
            };
            autoJoin();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propGuestName, roomName, tenantId, meetingSDK, showPreJoin, showThankYou, preJoinToken]);
    const handleJoin = async () => {
        if (!guestName.trim()) {
            toast.error('Please enter your name');
            return;
        }
        if (!roomName || !tenantId) {
            toast.error('Invalid meeting link');
            return;
        }
        // Save guest name to sessionStorage
        sessionStorage.setItem('meetplus_guest_name', guestName.trim());
        // Generate token immediately when opening pre-join screen
        try {
            setIsGeneratingToken(true);
            setShowPreJoin(true);
            console.log('🔑 Generating guest token for room:', roomName);
            const token = await generateTokenForRoom(roomName);
            if (!token) {
                throw new Error('No token received');
            }
            setPreJoinToken(token);
            setIsGeneratingToken(false);
            console.log('✅ Token generated, ready to join');
        }
        catch (error) {
            console.error('Error generating token:', error);
            setIsGeneratingToken(false);
            setShowPreJoin(false);
            toast.error(error.message || 'Failed to prepare meeting');
        }
    };
    const handlePreJoinConfirm = async (options) => {
        if (!options.token) {
            toast.error('Token not available');
            return;
        }
        try {
            setShowPreJoin(false);
            setIsJoining(true);
            // Store pre-join options
            setPreJoinOptions({
                micEnabled: options.micEnabled,
                cameraEnabled: options.cameraEnabled,
                selectedBackground: options.selectedBackground,
                selectedAudioDevice: options.selectedAudioDevice,
                selectedVideoDevice: options.selectedVideoDevice,
            });
            // Keep media stream in memory for instant reuse in meeting
            setPreJoinMediaStream(options.mediaStream || null);
            // Persist meeting state to sessionStorage
            const meetingInfo = {
                roomName,
                meetingTitle,
                subscriptionName: subscriptionName || undefined
            };
            sessionStorage.setItem('meetplus_guest_token', options.token);
            sessionStorage.setItem('meetplus_guest_meeting_info', JSON.stringify(meetingInfo));
            sessionStorage.setItem('meetplus_guest_prejoin_options', JSON.stringify({
                micEnabled: options.micEnabled,
                cameraEnabled: options.cameraEnabled,
                selectedBackground: options.selectedBackground,
                selectedAudioDevice: options.selectedAudioDevice,
                selectedVideoDevice: options.selectedVideoDevice
            }));
            // When renderMeetingRoom is provided, let the custom component handle connection
            // Otherwise, we're not using useMeeting anymore, so just show success
            setPreJoinToken(options.token);
            setIsJoining(false);
            toast.success('Ready to join meeting');
        }
        catch (error) {
            console.error('Error joining meeting:', error);
            toast.error('Failed to join meeting');
            setShowPreJoin(true);
            setIsJoining(false);
        }
    };
    // Restore meeting on mount
    useEffect(() => {
        const savedToken = sessionStorage.getItem('meetplus_guest_token');
        const savedInfo = sessionStorage.getItem('meetplus_guest_meeting_info');
        const savedOptions = sessionStorage.getItem('meetplus_guest_prejoin_options');
        if (savedToken && savedInfo && roomName) {
            try {
                const info = JSON.parse(savedInfo);
                if (info.roomName === roomName) {
                    setPreJoinToken(savedToken);
                    if (savedOptions) {
                        setPreJoinOptions(JSON.parse(savedOptions));
                    }
                    // Don't auto-join - let renderMeetingRoom handle connection
                }
            }
            catch (error) {
                console.error('Error restoring meeting:', error);
                sessionStorage.removeItem('meetplus_guest_token');
                sessionStorage.removeItem('meetplus_guest_meeting_info');
            }
        }
    }, [roomName]);
    const handleDisconnect = (reason = 'ended') => {
        // Clean up MeetingSDK connection
        if (meetingSDKRef.current) {
            meetingSDKRef.current.leaveRoom(true).catch(console.error);
        }
        // Clear persisted meeting state
        sessionStorage.removeItem('meetplus_guest_token');
        sessionStorage.removeItem('meetplus_guest_meeting_info');
        sessionStorage.removeItem('meetplus_guest_prejoin_options');
        setPreJoinOptions(null);
        setPreJoinToken(null);
        setDisconnectReason(reason);
        setShowThankYou(true);
        toast.success(reason === 'ended' ? 'Meeting ended' : 'Disconnected from meeting');
        onDisconnect?.(reason);
    };
    const handleRejoin = () => {
        setShowThankYou(false);
        setDisconnectReason(null);
        handleJoin();
    };
    // If already in meeting, show meeting UI
    // CRITICAL FIX: When renderMeetingRoom is provided, show it when we have token and options
    // Don't wait for useMeeting connection since the custom component handles it
    if (renderMeetingRoom && preJoinToken && preJoinOptions && !showThankYou && !showPreJoin) {
        return (jsx(Fragment, { children: renderMeetingRoom({
                roomName: roomName,
                meetingTitle,
                token: preJoinToken,
                participantName: guestName,
                tenantId,
                subscriptionId: roomName,
                subscriptionName,
                initialMediaStream: preJoinMediaStream || undefined,
                preJoinOptions,
                onDisconnect: () => handleDisconnect('ended'),
            }) }));
    }
    // We no longer use the default useMeeting flow
    // All connections are handled by renderMeetingRoom or custom components
    const handleFeedbackSubmit = () => {
        // Here you can send feedback to your backend
        console.log('Feedback submitted:', { rating, feedback, meetingTitle, guestName });
        setFeedbackSubmitted(true);
        // Optional: Send to API
        // if (apiEndpoint) {
        //   fetch(`${apiEndpoint}/feedback`, {
        //     method: 'POST',
        //     headers: { 'Content-Type': 'application/json' },
        //     body: JSON.stringify({ rating, feedback, meetingTitle, guestName, tenantId, roomName })
        //   });
        // }
    };
    // Show Thank You / Feedback page after meeting ends
    if (showThankYou) {
        return (jsx("div", { className: `guest-thank-you ${className}`, style: {
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px',
                background: 'linear-gradient(to bottom right, #f3e8ff, #dbeafe, #fce7f3)',
            }, children: jsx("div", { style: {
                    maxWidth: '700px',
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: '24px',
                    padding: '48px',
                    textAlign: 'center',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                }, children: !feedbackSubmitted ? (jsxs(Fragment, { children: [jsxs("div", { style: { marginBottom: '32px' }, children: [jsx("div", { style: {
                                        width: '80px',
                                        height: '80px',
                                        margin: '0 auto 20px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '40px',
                                    }, children: "\u2713" }), jsx("h1", { style: {
                                        fontSize: '32px',
                                        fontWeight: 'bold',
                                        marginBottom: '12px',
                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                    }, children: disconnectReason === 'disconnected' ? 'Disconnected' : 'Thank You!' }), jsx("p", { style: { color: '#666', fontSize: '16px', lineHeight: '1.6' }, children: disconnectReason === 'disconnected'
                                        ? 'You were disconnected from the meeting. We hope to see you again soon!'
                                        : `Thank you for joining "${meetingTitle}"! We'd love to hear your feedback.` })] }), showFeedback && disconnectReason === 'ended' && (jsxs("div", { style: { marginBottom: '32px', textAlign: 'left' }, children: [jsx("label", { style: {
                                        display: 'block',
                                        marginBottom: '12px',
                                        fontWeight: '600',
                                        color: '#333',
                                        fontSize: '16px'
                                    }, children: "How would you rate this meeting?" }), jsx("div", { style: {
                                        display: 'flex',
                                        gap: '8px',
                                        justifyContent: 'center',
                                        marginBottom: '24px'
                                    }, children: [1, 2, 3, 4, 5].map((star) => (jsx("button", { onClick: () => setRating(star), style: {
                                            width: '48px',
                                            height: '48px',
                                            borderRadius: '12px',
                                            border: '2px solid',
                                            borderColor: rating && star <= rating ? '#667eea' : '#e5e7eb',
                                            backgroundColor: rating && star <= rating ? '#667eea' : 'white',
                                            color: rating && star <= rating ? 'white' : '#9ca3af',
                                            fontSize: '24px',
                                            cursor: 'pointer',
                                            transition: 'all 0.2s',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }, onMouseEnter: (e) => {
                                            if (!rating) {
                                                e.currentTarget.style.borderColor = '#667eea';
                                                e.currentTarget.style.backgroundColor = '#f3f4f6';
                                            }
                                        }, onMouseLeave: (e) => {
                                            if (!rating) {
                                                e.currentTarget.style.borderColor = '#e5e7eb';
                                                e.currentTarget.style.backgroundColor = 'white';
                                            }
                                        }, children: "\u2605" }, star))) }), jsx("label", { style: {
                                        display: 'block',
                                        marginBottom: '12px',
                                        fontWeight: '600',
                                        color: '#333',
                                        fontSize: '16px'
                                    }, children: "Additional feedback (optional):" }), jsx("textarea", { value: feedback, onChange: (e) => setFeedback(e.target.value), placeholder: "Share your thoughts about the meeting...", style: {
                                        width: '100%',
                                        minHeight: '120px',
                                        padding: '16px',
                                        borderRadius: '12px',
                                        border: '2px solid #e5e7eb',
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        resize: 'vertical',
                                        transition: 'border-color 0.2s',
                                    }, onFocus: (e) => e.currentTarget.style.borderColor = '#667eea', onBlur: (e) => e.currentTarget.style.borderColor = '#e5e7eb' })] })), jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }, children: [showFeedback && disconnectReason === 'ended' && (jsx("button", { onClick: handleFeedbackSubmit, disabled: !rating, style: {
                                        padding: '14px 32px',
                                        backgroundColor: rating ? '#667eea' : '#d1d5db',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: rating ? 'pointer' : 'not-allowed',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                        boxShadow: rating ? '0 4px 6px -1px rgba(102, 126, 234, 0.3)' : 'none',
                                    }, onMouseEnter: (e) => {
                                        if (rating) {
                                            e.currentTarget.style.transform = 'translateY(-2px)';
                                            e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(102, 126, 234, 0.4)';
                                        }
                                    }, onMouseLeave: (e) => {
                                        if (rating) {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(102, 126, 234, 0.3)';
                                        }
                                    }, children: "Submit Feedback" })), jsx("button", { onClick: handleRejoin, style: {
                                        padding: '14px 32px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.transform = 'translateY(-2px)';
                                        e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(16, 185, 129, 0.4)';
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(16, 185, 129, 0.3)';
                                    }, children: "Rejoin Meeting" }), jsx("button", { onClick: () => {
                                        setShowThankYou(false);
                                        setDisconnectReason(null);
                                        setRating(null);
                                        setFeedback('');
                                        setFeedbackSubmitted(false);
                                    }, style: {
                                        padding: '14px 32px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#374151',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                    }, onMouseEnter: (e) => {
                                        e.currentTarget.style.backgroundColor = '#e5e7eb';
                                    }, onMouseLeave: (e) => {
                                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                                    }, children: "Close" })] })] })) : (jsxs(Fragment, { children: [jsxs("div", { style: { marginBottom: '32px' }, children: [jsx("div", { style: {
                                        width: '80px',
                                        height: '80px',
                                        margin: '0 auto 20px',
                                        borderRadius: '50%',
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '40px',
                                    }, children: "\u2713" }), jsx("h1", { style: {
                                        fontSize: '28px',
                                        fontWeight: 'bold',
                                        marginBottom: '12px',
                                        color: '#10b981',
                                    }, children: "Feedback Submitted!" }), jsx("p", { style: { color: '#666', fontSize: '16px', lineHeight: '1.6' }, children: "Thank you for your feedback. We appreciate your input!" })] }), jsxs("div", { style: { display: 'flex', gap: '12px', justifyContent: 'center' }, children: [jsx("button", { onClick: handleRejoin, style: {
                                        padding: '14px 32px',
                                        backgroundColor: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        transition: 'all 0.2s',
                                        boxShadow: '0 4px 6px -1px rgba(16, 185, 129, 0.3)',
                                    }, children: "Rejoin Meeting" }), jsx("button", { onClick: () => {
                                        setShowThankYou(false);
                                        setDisconnectReason(null);
                                        setRating(null);
                                        setFeedback('');
                                        setFeedbackSubmitted(false);
                                    }, style: {
                                        padding: '14px 32px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#374151',
                                        border: 'none',
                                        borderRadius: '12px',
                                        cursor: 'pointer',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                    }, children: "Close" })] })] })) }) }));
    }
    // Show pre-join screen
    if (showPreJoin && preJoinToken) {
        return (jsx("div", { className: `guest-prejoin ${className}`, style: {
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.75)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9999,
                padding: '20px',
            }, children: jsx("div", { style: {
                    backgroundColor: 'white',
                    borderRadius: '16px',
                    maxWidth: '1200px',
                    width: '100%',
                    maxHeight: '90vh',
                    overflow: 'auto',
                }, children: jsx(PreJoinScreen, { participantName: guestName, meetingTitle: meetingTitle, onJoin: handlePreJoinConfirm, onCancel: () => {
                        setShowPreJoin(false);
                        setIsJoining(false);
                    }, token: preJoinToken, isGeneratingToken: isGeneratingToken }) }) }));
    }
    // Show name input screen
    return (jsxs("div", { className: `guest-join ${className}`, style: {
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            background: 'linear-gradient(to bottom right, #f3e8ff, #dbeafe, #fce7f3)',
            position: 'relative',
        }, children: [jsxs("div", { style: {
                    position: 'absolute',
                    top: '20px',
                    left: '20px',
                    right: '20px',
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    borderRadius: '16px',
                    padding: '16px 20px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    maxWidth: '1200px',
                    margin: '0 auto',
                    zIndex: 10,
                }, children: [logoUrl && (jsx("img", { src: logoUrl, alt: "HoSaksham Logo", style: {
                            height: '40px',
                            width: 'auto',
                            objectFit: 'contain',
                        } })), jsxs("div", { style: { flex: 1 }, children: [jsxs("div", { style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    marginBottom: '4px',
                                }, children: [jsx("span", { style: {
                                            display: 'inline-block',
                                            width: '8px',
                                            height: '8px',
                                            borderRadius: '50%',
                                            backgroundColor: '#10b981',
                                            animation: 'pulse 2s infinite',
                                        } }), jsx("span", { style: {
                                            fontSize: '12px',
                                            fontWeight: '600',
                                            color: '#10b981',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px',
                                        }, children: "Live Gameplay" })] }), jsxs("p", { style: {
                                    fontSize: '14px',
                                    color: '#374151',
                                    margin: 0,
                                    fontWeight: '500',
                                }, children: [jsx("strong", { children: "HoSaksham" }), " - Nimble meeting software for academies with Recording & Live Sessions"] })] }), jsxs("div", { style: {
                            display: 'flex',
                            gap: '8px',
                            flexWrap: 'wrap',
                        }, children: [jsx("span", { style: {
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    backgroundColor: '#f0f9ff',
                                    color: '#0369a1',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                }, children: "\uD83C\uDFA5 Recording" }), jsx("span", { style: {
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    backgroundColor: '#f0fdf4',
                                    color: '#166534',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                }, children: "\uD83C\uDFAE Interactive" }), jsx("span", { style: {
                                    fontSize: '11px',
                                    padding: '4px 10px',
                                    backgroundColor: '#fef3c7',
                                    color: '#92400e',
                                    borderRadius: '12px',
                                    fontWeight: '600',
                                }, children: "\u26A1 Real-time" })] })] }), jsxs("div", { style: {
                    maxWidth: '520px',
                    width: '100%',
                    backgroundColor: 'white',
                    borderRadius: '24px',
                    padding: '48px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    marginTop: '100px',
                }, children: [jsxs("div", { style: { textAlign: 'center', marginBottom: '32px' }, children: [logoUrl ? (jsx("img", { src: logoUrl, alt: "HoSaksham", style: {
                                    height: '80px',
                                    width: 'auto',
                                    margin: '0 auto 20px',
                                    objectFit: 'contain',
                                } })) : (jsx("div", { style: {
                                    width: '80px',
                                    height: '80px',
                                    margin: '0 auto 20px',
                                    borderRadius: '20px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '40px',
                                    color: 'white',
                                    fontWeight: 'bold',
                                }, children: "HS" })), jsx("h1", { style: {
                                    fontSize: '32px',
                                    fontWeight: 'bold',
                                    marginBottom: '8px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }, children: "Join Meeting" }), jsx("h2", { style: { fontSize: '18px', color: '#666', fontWeight: '500' }, children: meetingTitle }), jsx("p", { style: {
                                    fontSize: '14px',
                                    color: '#9ca3af',
                                    marginTop: '8px',
                                    fontStyle: 'italic',
                                }, children: "Powered by HoSaksham" })] }), jsxs("div", { style: { marginBottom: '24px' }, children: [jsx("label", { style: {
                                    display: 'block',
                                    marginBottom: '10px',
                                    fontWeight: '600',
                                    color: '#374151',
                                    fontSize: '15px'
                                }, children: "Your Name" }), jsx("input", { type: "text", value: guestName, onChange: (e) => setGuestName(e.target.value), placeholder: "Enter your name", style: {
                                    width: '100%',
                                    padding: '14px 16px',
                                    borderRadius: '12px',
                                    border: '2px solid #e5e7eb',
                                    fontSize: '16px',
                                    transition: 'border-color 0.2s',
                                    outline: 'none',
                                }, disabled: isJoining, onFocus: (e) => e.currentTarget.style.borderColor = '#667eea', onBlur: (e) => e.currentTarget.style.borderColor = '#e5e7eb', onKeyPress: (e) => {
                                    if (e.key === 'Enter' && guestName.trim() && !isJoining && roomName && tenantId) {
                                        handleJoin();
                                    }
                                } })] }), meetingError && (jsxs("div", { style: {
                            color: '#dc2626',
                            marginBottom: '20px',
                            padding: '14px',
                            backgroundColor: '#fef2f2',
                            borderRadius: '12px',
                            border: '1px solid #fecaca',
                            fontSize: '14px',
                        }, children: [jsx("strong", { children: "Error:" }), " ", meetingError.message] })), jsx("button", { onClick: handleJoin, disabled: isJoining || !guestName.trim() || !roomName || !tenantId, style: {
                            width: '100%',
                            padding: '16px',
                            fontSize: '16px',
                            fontWeight: '600',
                            backgroundColor: (isJoining || !guestName.trim() || !roomName || !tenantId)
                                ? '#d1d5db'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            background: (isJoining || !guestName.trim() || !roomName || !tenantId)
                                ? '#d1d5db'
                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            border: 'none',
                            borderRadius: '12px',
                            cursor: (isJoining || !guestName.trim() || !roomName || !tenantId) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.2s',
                            boxShadow: (isJoining || !guestName.trim() || !roomName || !tenantId)
                                ? 'none'
                                : '0 4px 6px -1px rgba(102, 126, 234, 0.3)',
                        }, onMouseEnter: (e) => {
                            if (!isJoining && guestName.trim() && roomName && tenantId) {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 12px -1px rgba(102, 126, 234, 0.4)';
                            }
                        }, onMouseLeave: (e) => {
                            if (!isJoining && guestName.trim() && roomName && tenantId) {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(102, 126, 234, 0.3)';
                            }
                        }, children: isJoining ? 'Preparing to join...' : 'Join Meeting' }), jsxs("div", { style: {
                            marginTop: '32px',
                            padding: '20px',
                            backgroundColor: '#f9fafb',
                            borderRadius: '16px',
                            border: '1px solid #e5e7eb',
                        }, children: [jsx("p", { style: {
                                    fontSize: '13px',
                                    color: '#6b7280',
                                    margin: '0 0 12px 0',
                                    fontWeight: '600',
                                    textAlign: 'center',
                                }, children: "\u2728 What you'll experience:" }), jsxs("div", { style: {
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    gap: '12px',
                                }, children: [jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }, children: [jsx("span", { style: { fontSize: '18px' }, children: "\uD83C\uDFA5" }), jsx("span", { style: { fontSize: '12px', color: '#4b5563' }, children: "HD Recording" })] }), jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }, children: [jsx("span", { style: { fontSize: '18px' }, children: "\uD83C\uDFAE" }), jsx("span", { style: { fontSize: '12px', color: '#4b5563' }, children: "Live Games" })] }), jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }, children: [jsx("span", { style: { fontSize: '18px' }, children: "\uD83D\uDCAC" }), jsx("span", { style: { fontSize: '12px', color: '#4b5563' }, children: "Real-time Chat" })] }), jsxs("div", { style: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px',
                                        }, children: [jsx("span", { style: { fontSize: '18px' }, children: "\uD83D\uDCDA" }), jsx("span", { style: { fontSize: '12px', color: '#4b5563' }, children: "Interactive Learning" })] })] })] })] }), jsx("style", { children: `
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
            transform: scale(1);
          }
          50% {
            opacity: 0.5;
            transform: scale(1.2);
          }
        }
      ` })] }));
};

// GMEET OPTIMIZATION: Memoize VideoTile to prevent unnecessary re-renders
// Only re-render when props actually change
const VideoTile = React.memo(({ participant, isLocal, isTeacher, isPinned, isActiveSpeaker, onClick, className = '', isMirrored = false, selectedAvatar }) => {
    const videoRef = useRef(null);
    const getCameraState = (p) => {
        if (typeof p?.isCameraEnabled === 'boolean') {
            return p.isCameraEnabled;
        }
        if (typeof p?.isCameraMuted === 'boolean') {
            return !p.isCameraMuted;
        }
        return true;
    };
    // CRITICAL: For screen share MAIN tiles (not sidebar), always show video regardless of camera state
    // Must check for 'screenshare-video' specifically, not 'screenshare-sidebar-video'
    const isScreenShareMainTile = className?.includes('screenshare-video') && !className?.includes('sidebar');
    // Detect if this is a filmstrip tile (thumbnail in bottom strip)
    const isFilmstripTile = className?.includes('filmstrip') || className?.includes('gmeet-filmstrip');
    // Detect if this is a grid tile (gallery view)
    const isGridTile = className?.includes('gmeet-video') && !isFilmstripTile && !isScreenShareMainTile;
    const [isVideoEnabled, setIsVideoEnabled] = React.useState(() => isScreenShareMainTile ? true : getCameraState(participant));
    useEffect(() => {
        // CRITICAL: Screen share MAIN tiles are ALWAYS enabled, all other tiles depend on camera state
        if (isScreenShareMainTile) {
            console.log(`🖥️ Screen share MAIN tile for ${participant.identity} - forcing isVideoEnabled = true`);
            setIsVideoEnabled(true);
        }
        else {
            // CRITICAL FIX: For sidebar tiles during screen share, check if camera track exists
            // Don't rely solely on isCameraEnabled/isCameraMuted which might not be updated
            const isSidebarTile = className?.includes('sidebar');
            if (isSidebarTile) {
                // For sidebar, check if camera track actually exists
                const allTracks = participant.trackPublications ? [...participant.trackPublications.values()] : [];
                const hasCameraTrack = allTracks.some((pub) => {
                    const isCamera = pub.source === 'camera' || (!pub.source && pub.kind === 'video');
                    const hasTrack = pub.track || pub.videoTrack;
                    return isCamera && hasTrack;
                });
                console.log(`📹 Sidebar tile for ${participant.identity} - hasCameraTrack = ${hasCameraTrack}`);
                setIsVideoEnabled(hasCameraTrack);
            }
            else {
                const cameraState = getCameraState(participant);
                console.log(`📹 Regular tile for ${participant.identity} - isVideoEnabled = ${cameraState}, className: ${className}`);
                setIsVideoEnabled(cameraState);
            }
        }
    }, [participant, isScreenShareMainTile, className]);
    // CRITICAL: Create stable track identifiers to prevent unnecessary re-renders
    // Only re-run video attachment when the SPECIFIC track we care about changes
    const relevantTrackInfo = useMemo(() => {
        if (!participant.trackPublications)
            return null;
        const allTracks = [...participant.trackPublications.values()];
        const isScreenShareTile = isScreenShareMainTile;
        console.log(`🔍 relevantTrackInfo memoizing for ${participant.identity}:`, {
            isScreenShareTile,
            className,
            tracksCount: allTracks.length,
            tracks: allTracks.map(pub => ({ source: pub.source, kind: pub.kind }))
        });
        if (isScreenShareTile) {
            // For screen share main tiles, only track the screen share publication
            // CRITICAL: Check for Track.Source.ScreenShare enum value
            const screenSharePub = allTracks.find(pub => pub.source === Track.Source.ScreenShare
                || pub.source === 'screen_share'
                || pub.source === 'screen'
                || pub.source === Track.Source.ScreenShare);
            const result = screenSharePub ? {
                type: 'screen_share',
                trackSid: screenSharePub.trackSid,
                hasTrack: !!screenSharePub.track,
                isSubscribed: screenSharePub.isSubscribed
            } : null;
            console.log(`🎯 Screen share tile relevantTrackInfo:`, result);
            return result;
        }
        else {
            // For camera tiles, only track the camera publication
            const cameraPub = allTracks.find(pub => pub.kind === 'video' && (pub.source === 'camera' || !pub.source));
            const result = cameraPub ? {
                type: 'camera',
                trackSid: cameraPub.trackSid,
                hasTrack: !!cameraPub.track,
                isMuted: cameraPub.isMuted
            } : null;
            console.log(`📹 Camera tile relevantTrackInfo:`, result);
            return result;
        }
    }, [participant.trackPublications, isScreenShareMainTile, className, participant.identity]);
    // Avatar options matching PreJoinScreen and BackgroundEffectsControl
    const avatarOptions = [
        {
            id: 'avatar1',
            name: 'Professional',
            emoji: '🧑‍💼',
            gradient: 'from-blue-500 to-blue-700'
        },
        {
            id: 'avatar2',
            name: 'Student',
            emoji: '🧑‍🎓',
            gradient: 'from-green-500 to-green-700'
        },
        {
            id: 'avatar3',
            name: 'Teacher',
            emoji: '🧑‍🏫',
            gradient: 'from-purple-500 to-purple-700'
        },
        {
            id: 'avatar4',
            name: 'Creative',
            emoji: '🧑‍🎨',
            gradient: 'from-pink-500 to-pink-700'
        },
        {
            id: 'avatar5',
            name: 'Tech',
            emoji: '🧑‍💻',
            gradient: 'from-indigo-500 to-indigo-700'
        },
        {
            id: 'avatar6',
            name: 'Science',
            emoji: '🧑‍🔬',
            gradient: 'from-teal-500 to-teal-700'
        },
        {
            id: 'avatar7',
            name: 'Medical',
            emoji: '🧑‍⚕️',
            gradient: 'from-red-500 to-red-700'
        },
        {
            id: 'avatar8',
            name: 'Engineer',
            emoji: '🧑‍🔧',
            gradient: 'from-orange-500 to-orange-700'
        },
        {
            id: 'avatar9',
            name: 'Artist',
            emoji: '🎭',
            gradient: 'from-yellow-500 to-yellow-700'
        },
        {
            id: 'avatar10',
            name: 'Chef',
            emoji: '🧑‍🍳',
            gradient: 'from-amber-500 to-amber-700'
        },
        {
            id: 'avatar11',
            name: 'Astronaut',
            emoji: '🧑‍🚀',
            gradient: 'from-slate-500 to-slate-700'
        },
        {
            id: 'avatar12',
            name: 'Musician',
            emoji: '🎵',
            gradient: 'from-violet-500 to-violet-700'
        }
    ];
    // Get selected avatar from localStorage
    const getSelectedAvatar = () => {
        if (selectedAvatar) {
            const match = avatarOptions.find(a => a.id === selectedAvatar);
            if (match) {
                return match;
            }
        }
        try {
            const savedAvatarId = localStorage.getItem('meetplus_selected_avatar');
            if (savedAvatarId) {
                const avatar = avatarOptions.find(a => a.id === savedAvatarId);
                return avatar || avatarOptions[0];
            }
        }
        catch (error) {
            console.error('Error reading avatar from localStorage:', error);
        }
        return avatarOptions[0]; // Default to first avatar
    };
    const currentAvatarData = getSelectedAvatar();
    useEffect(() => {
        console.log(`🔄 VideoTile useEffect triggered for ${participant.identity}:`, {
            className,
            isScreenShareMainTile,
            relevantTrackInfo,
            isLocal,
            timestamp: Date.now()
        });
        let retryCount = 0;
        const maxRetries = 5;
        let retryInterval = null;
        let debounceTimeout = null;
        // Calculate isScreenShare at useEffect level so it's available throughout
        const isScreenShare = (className?.includes('screen-share-video') || className?.includes('screenshare-video'))
            && !className?.includes('sidebar');
        const attachVideoTrack = async () => {
            if (!videoRef.current || !participant.trackPublications)
                return;
            // Get all tracks and find the appropriate one to display
            const allTracks = [...participant.trackPublications.values()];
            console.log(`🎥 Track debug for ${participant.identity}:`, {
                allTracks: allTracks.map(pub => ({
                    kind: pub.kind,
                    source: pub.source,
                    isMuted: pub.isMuted,
                    trackName: pub.trackName,
                    hasTrack: !!pub.track,
                    hasVideoTrack: !!pub.videoTrack,
                    trackState: pub.track?.state,
                    streamActive: pub.track?.mediaStream?.active
                }))
            });
            // For screen share layout, prioritize screen share track
            const screenShareTrack = allTracks.find(pub => {
                // CRITICAL: Check for screen share using Track.Source.ScreenShare enum (not just strings)
                // LiveKit uses Track.Source.ScreenShare enum value, but also check string values for compatibility
                const isScreenShare = pub.source === Track.Source.ScreenShare
                    || pub.source === 'screen_share'
                    || pub.source === 'screen'
                    || pub.source === Track.Source.ScreenShare;
                if (!isScreenShare)
                    return false;
                // CRITICAL: For remote participants, accept ANY screen share track publication
                // LiveKit will auto-subscribe, but the track might not be available immediately
                // Accept the publication if it exists and is not explicitly unsubscribed
                const isUnsubscribed = pub.isSubscribed === false;
                const hasTrack = pub.track || pub.videoTrack;
                const isActive = pub.track?.mediaStream?.active || pub.videoTrack?.mediaStream?.active;
                // CRITICAL: For remote participants, accept the publication even if track is not available yet
                // The track will become available once subscribed - we'll retry attachment
                // Accept if:
                // 1. Local participant: must have active track
                // 2. Remote participant: accept ANY screen share publication (not explicitly unsubscribed) - track will auto-subscribe
                const hasValidTrack = isLocal
                    ? (hasTrack && isActive) // Local: must have active track
                    : (pub.isSubscribed !== false); // Remote: accept if not explicitly unsubscribed (undefined or true is OK)
                console.log(`🖥️ Screen share track check for ${pub.trackName || 'unnamed'}:`, {
                    isScreenShare,
                    isLocal,
                    hasTrack: !!hasTrack,
                    isUnsubscribed,
                    isActive,
                    hasValidTrack,
                    source: pub.source,
                    kind: pub.kind,
                    trackState: pub.track?.state,
                    streamActive: pub.track?.mediaStream?.active || pub.videoTrack?.mediaStream?.active,
                    isSubscribed: pub.isSubscribed
                });
                return hasValidTrack;
            });
            // For regular video, use camera track
            const cameraTrack = allTracks.find(pub => {
                const isVideoTrack = pub.kind === 'video';
                const isCameraSource = pub.source === 'camera' || !pub.source;
                // CRITICAL FIX: Don't require mediaStream.active - track might be valid but in different state
                // Just check if track exists and is not explicitly ended
                const hasTrack = pub.track || pub.videoTrack;
                const trackState = pub.track?.state || pub.videoTrack?.state;
                const isNotEnded = trackState !== 'ended' && trackState !== 'stopped';
                const hasValidTrack = hasTrack && isNotEnded;
                console.log(`📷 Camera track check for ${pub.trackName || 'unnamed'}:`, {
                    isVideoTrack,
                    isCameraSource,
                    hasValidTrack,
                    hasTrack: !!hasTrack,
                    trackState,
                    isNotEnded,
                    source: pub.source,
                    kind: pub.kind,
                    streamActive: pub.track?.mediaStream?.active
                });
                return isVideoTrack && isCameraSource && hasValidTrack;
            });
            // Choose track based on className
            // CRITICAL: Must match isScreenShareMainTile check - only main screenshare tiles, not sidebar
            // isScreenShare is already defined at useEffect level
            const videoTrackPublication = isScreenShare ? screenShareTrack : cameraTrack;
            console.log(`🎯 Track selection for ${participant.identity}:`, {
                className,
                isScreenShare,
                willUseScreenShare: isScreenShare && !!screenShareTrack,
                willUseCamera: !isScreenShare && !!cameraTrack
            });
            console.log(`🎥 Selected track for ${participant.identity}:`, {
                isScreenShare,
                selectedTrack: videoTrackPublication ? {
                    source: videoTrackPublication.source,
                    kind: videoTrackPublication.kind,
                    trackName: videoTrackPublication.trackName,
                    trackState: videoTrackPublication.track?.state,
                    streamActive: videoTrackPublication.track?.mediaStream?.active
                } : null,
                screenShareAvailable: !!screenShareTrack,
                cameraAvailable: !!cameraTrack
            });
            console.log(`🎥 Selected track for ${participant.identity}:`, {
                isScreenShare,
                hasScreenShare: !!screenShareTrack,
                hasCameraTrack: !!cameraTrack,
                selectedTrack: videoTrackPublication ? {
                    kind: videoTrackPublication.kind,
                    source: videoTrackPublication.source,
                    isMuted: videoTrackPublication.isMuted,
                    hasTrack: !!videoTrackPublication.track,
                    hasVideoTrack: !!(videoTrackPublication.videoTrack),
                    trackType: typeof videoTrackPublication.videoTrack,
                    trackType2: typeof videoTrackPublication.track
                } : null
            });
            // CRITICAL: Check both videoTrack and track properties - LiveKit uses different properties in different contexts
            // For screen share, also check if track is available via publication's trackSid or by waiting for subscription
            let videoTrack = videoTrackPublication ?
                (videoTrackPublication.videoTrack || videoTrackPublication.track) : null;
            // CRITICAL: For remote screen share tracks, the track might be subscribed but not yet available
            // Try to get the track from the participant's trackPublications if not directly available
            if (!videoTrack && videoTrackPublication && isScreenShare && !isLocal) {
                // Check if track is available via participant's getTrackPublication
                try {
                    const trackSid = videoTrackPublication.trackSid;
                    if (trackSid && participant.getTrackPublication) {
                        const pub = participant.getTrackPublication(trackSid);
                        if (pub && (pub.track || pub.videoTrack)) {
                            videoTrack = pub.videoTrack || pub.track;
                            console.log(`✅ Found screen share track via getTrackPublication for ${participant.identity}`);
                        }
                    }
                }
                catch (error) {
                    console.warn(`⚠️ Error getting track via getTrackPublication:`, error);
                }
            }
            if (videoTrack) {
                const videoElement = videoRef.current;
                // Log track debug info
                console.log(`🎥 Video track debug for ${participant.identity}:`, {
                    isScreenShare,
                    allTracks: allTracks.map(pub => ({
                        source: pub.source,
                        kind: pub.kind,
                        isMuted: pub.isMuted,
                        hasVideoTrack: !!pub.videoTrack,
                        hasTrack: !!pub.track
                    })),
                    selectedTrack: videoTrackPublication ? {
                        source: videoTrackPublication.source,
                        kind: videoTrackPublication.kind,
                        isMuted: videoTrackPublication.isMuted,
                        hasVideoTrack: !!(videoTrackPublication.videoTrack),
                        hasTrack: !!videoTrackPublication.track,
                        resolvedVideoTrack: !!videoTrack
                    } : null
                });
                try {
                    // Check if video element already has a source to prevent conflicts
                    if (videoElement.srcObject) {
                        console.log(`🔄 Video element already has source, detaching first for ${participant.identity}`);
                        videoTrack.detach(videoElement);
                        await new Promise(resolve => setTimeout(resolve, 100)); // Small delay
                    }
                    await videoTrack.attach(videoElement);
                    videoElement.autoplay = true;
                    videoElement.playsInline = true;
                    videoElement.muted = true;
                    // Apply mirror effect for local camera based on settings
                    if (isLocal) {
                        videoElement.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                    }
                    console.log(`✅ Video track attached successfully for ${participant.identity}`);
                    if (retryInterval) {
                        clearInterval(retryInterval);
                        retryInterval = null;
                    }
                }
                catch (error) {
                    console.error(`❌ Failed to attach video track for ${participant.identity}:`, error);
                    if (retryCount < maxRetries) {
                        retryCount++;
                        console.log(`🔄 Retrying video attachment (${retryCount}/${maxRetries})...`);
                        if (!retryInterval) {
                            retryInterval = setInterval(attachVideoTrack, 2000); // Increased delay
                        }
                    }
                }
                // Listen for track mute/unmute
                // CRITICAL: Track mute/unmute does NOT mean camera is disabled
                // Only update isVideoEnabled based on actual camera state, not track mute state
                // Track mute is just a temporary state (e.g., when speaking), not a camera disable
                const handleMute = () => {
                    console.log(`🔇 Video track muted for ${participant.identity} - but camera may still be enabled`);
                    // Don't change isVideoEnabled - track mute doesn't mean camera is disabled
                    // Keep video element visible - it will show last frame or black, but no avatar overlay
                    // Ensure video element doesn't get paused
                    if (videoElement && !videoElement.paused) ;
                };
                const handleUnmute = () => {
                    console.log(`🔊 Video track unmuted for ${participant.identity}`);
                    // Don't change isVideoEnabled - track unmute doesn't mean camera is enabled
                    // Check actual camera state instead
                    const cameraState = getCameraState(participant);
                    setIsVideoEnabled(cameraState);
                    // Ensure video element plays if track is unmuted
                    if (videoElement && videoElement.paused && cameraState) {
                        videoElement.play().catch(err => {
                            console.warn(`⚠️ Could not play video after unmute:`, err);
                        });
                    }
                };
                videoTrack.on('muted', handleMute);
                videoTrack.on('unmuted', handleUnmute);
                // Initial state: use actual camera state, not track mute state
                // Track mute is temporary (e.g., when speaking), not a camera disable
                const cameraState = getCameraState(participant);
                setIsVideoEnabled(cameraState);
                return () => {
                    if (retryInterval) {
                        clearInterval(retryInterval);
                    }
                    if (videoElement && videoTrack) {
                        try {
                            // Properly close any VideoFrames before detaching
                            if (videoElement.srcObject) {
                                const stream = videoElement.srcObject;
                                stream.getVideoTracks().forEach(track => {
                                    if (track.readyState === 'live') {
                                        track.stop();
                                    }
                                });
                                // Clear the srcObject to release VideoFrames
                                videoElement.srcObject = null;
                            }
                            // Pause and clear video element
                            videoElement.pause();
                            videoElement.load(); // Reset video element
                            videoTrack.off('muted', handleMute);
                            videoTrack.off('unmuted', handleUnmute);
                            videoTrack.detach(videoElement);
                            console.log(`🔌 Video track detached for ${participant.identity}`);
                        }
                        catch (error) {
                            console.warn(`⚠️ Error detaching video track for ${participant.identity}:`, error);
                        }
                    }
                };
            }
            else if (videoTrackPublication && isScreenShare) {
                // CRITICAL: For screen share tracks (especially remote), the track might not be available yet
                // Ensure subscription is enabled, then wait for track to become available
                const isRemote = !isLocal;
                console.log(`⏳ Screen share track not ready yet for ${participant.identity} (${isRemote ? 'remote' : 'local'}), waiting for track...`, {
                    hasPublication: !!videoTrackPublication,
                    isSubscribed: videoTrackPublication.isSubscribed,
                    source: videoTrackPublication.source,
                    hasTrack: !!(videoTrackPublication.track || videoTrackPublication.videoTrack),
                    trackState: videoTrackPublication.track?.state
                });
                // CRITICAL: Explicitly subscribe to the track if not already subscribed (for remote participants)
                if (isRemote && videoTrackPublication.setSubscribed && videoTrackPublication.isSubscribed !== true) {
                    try {
                        console.log(`📡 Explicitly subscribing to screen share track for ${participant.identity}`);
                        videoTrackPublication.setSubscribed(true);
                    }
                    catch (error) {
                        console.error(`❌ Error subscribing to screen share track for ${participant.identity}:`, error);
                    }
                }
                // CRITICAL: Listen for track subscription event if track becomes available
                if (isRemote && videoTrackPublication.track) {
                    const track = videoTrackPublication.track;
                    const handleTrackSubscribed = () => {
                        console.log(`✅ Screen share track became available for ${participant.identity}, retrying attachment...`);
                        // Retry attachment when track becomes available
                        setTimeout(() => {
                            attachVideoTrack();
                        }, 100);
                    };
                    if (track.state === 'live') {
                        // Track is already live, retry immediately
                        handleTrackSubscribed();
                    }
                    else {
                        // Wait for track to become live
                        track.on('subscribed', handleTrackSubscribed);
                        track.on('stateChanged', (state) => {
                            if (state === 'live') {
                                handleTrackSubscribed();
                            }
                        });
                    }
                }
                // Set up retry with longer interval and more retries for remote tracks
                const maxRetriesRemote = isRemote ? 30 : 10; // More retries for remote tracks
                if (retryCount < maxRetriesRemote && !retryInterval) {
                    retryCount++;
                    retryInterval = setInterval(attachVideoTrack, 500); // Check every 500ms for screen share
                }
            }
            else {
                console.log(`⏳ Video track not ready yet for ${participant.identity}, will retry...`);
                if (retryCount < maxRetries && !retryInterval) {
                    retryCount++;
                    retryInterval = setInterval(attachVideoTrack, 1000);
                }
            }
        };
        attachVideoTrack();
        // CRITICAL: For screen share tracks, also listen for track published/subscribed events
        // This ensures we catch the track as soon as it becomes available
        if (isScreenShare && !isLocal && participant.trackPublications) {
            const handleTrackSubscribed = (track, publication) => {
                // Check if this is the screen share track we're waiting for
                const isScreenSharePub = publication.source === Track.Source.ScreenShare
                    || publication.source === 'screen_share'
                    || publication.source === 'screen';
                if (isScreenSharePub && track && track.kind === 'video') {
                    console.log(`✅ Screen share track subscribed event received for ${participant.identity}, retrying attachment...`);
                    // Retry attachment when track becomes available
                    setTimeout(() => {
                        attachVideoTrack();
                    }, 100);
                }
            };
            // Listen for track subscribed events on the participant
            if (participant.on) {
                participant.on('trackSubscribed', handleTrackSubscribed);
                return () => {
                    if (retryInterval) {
                        clearInterval(retryInterval);
                    }
                    if (participant.off) {
                        participant.off('trackSubscribed', handleTrackSubscribed);
                    }
                };
            }
        }
        // GMEET OPTIMIZATION: Debounce video track attachment to prevent rapid re-attachments
        // This improves performance by batching multiple updates
        debounceTimeout = setTimeout(() => {
            attachVideoTrack();
        }, 50); // 50ms debounce - fast enough to feel instant, slow enough to batch updates
        return () => {
            if (retryInterval) {
                clearInterval(retryInterval);
            }
            if (debounceTimeout) {
                clearTimeout(debounceTimeout);
            }
        };
        // CRITICAL: We use relevantTrackInfo instead of participant.trackPublications to prevent
        // unnecessary re-renders when unrelated tracks (like audio) change.
        // isMirrored is handled by a separate useEffect to prevent unnecessary video re-attachments.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [participant.identity, relevantTrackInfo, isLocal, className]);
    // Update mirror effect when isMirrored prop changes - prevent initial flip
    const lastMirrorStateRef = useRef(null);
    useEffect(() => {
        if (isLocal && videoRef.current) {
            // Only apply if the state actually changed from the last known state
            if (lastMirrorStateRef.current !== null && lastMirrorStateRef.current !== isMirrored) {
                videoRef.current.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                console.log(`🔄 Mirror state changed for ${participant.identity}: ${isMirrored ? 'flipped' : 'normal'}`);
            }
            else if (lastMirrorStateRef.current === null) {
                // First render - set initial state without animation
                videoRef.current.style.transform = isMirrored ? 'scaleX(-1)' : 'scaleX(1)';
                lastMirrorStateRef.current = isMirrored;
                console.log(`🎬 Initial mirror state for ${participant.identity}: ${isMirrored ? 'flipped' : 'normal'}`);
            }
            lastMirrorStateRef.current = isMirrored;
        }
    }, [isMirrored, isLocal, participant.identity]);
    return (jsxs("div", { className: `custom-video-tile ${isScreenShareMainTile ? 'screenshare-main-tile' : 'bg-gray-800'} cursor-pointer ${isPinned ? 'pinned' : ''} ${isActiveSpeaker ? 'active-speaker' : ''} ${className}`, onClick: onClick, style: {
            borderRadius: '12px',
            overflow: 'hidden',
            position: 'relative',
            border: 'none', // Removed purple/colored borders to prevent recording outline
            boxShadow: isScreenShareMainTile ? 'none' : '0 4px 12px rgba(0, 0, 0, 0.3)',
            transition: 'all 0.3s ease',
            backgroundColor: isScreenShareMainTile ? 'transparent' : undefined,
            background: isScreenShareMainTile ? 'transparent' : undefined
        }, children: [jsx("video", { ref: videoRef, className: `w-full h-full ${isScreenShareMainTile ? 'object-contain' : 'object-cover'}`, "data-local": isLocal ? "true" : "false", style: {
                    width: '100%',
                    height: '100%',
                    objectFit: isScreenShareMainTile ? 'contain' : 'cover', // CRITICAL: contain for screen share, cover for camera
                    backgroundColor: '#000',
                    background: '#000',
                    // CRITICAL: 
                    // - Screen share MAIN tile: ALWAYS visible (shows shared screen)
                    // - Other tiles: visible when camera is enabled (isVideoEnabled)
                    // Note: Track mute/unmute doesn't affect visibility - that's just temporary state
                    display: (isScreenShareMainTile || isVideoEnabled) ? 'block' : 'none',
                    transition: 'opacity 0.2s ease-in-out',
                    // Ensure video is always visible when track is attached, even if temporarily muted
                    opacity: (isScreenShareMainTile || isVideoEnabled) ? 1 : 0,
                    // CRITICAL: For screen share, ensure video is on top
                    zIndex: isScreenShareMainTile ? 200 : 'auto',
                    position: isScreenShareMainTile ? 'relative' : 'static'
                }, onLoadStart: () => console.log(`🎬 Video load started for ${participant.identity}`), onLoadedData: () => console.log(`✅ Video data loaded for ${participant.identity}`), onError: (e) => console.error(`❌ Video error for ${participant.identity}:`, e), onStalled: () => console.warn(`⚠️ Video stalled for ${participant.identity}`), onSuspend: () => console.warn(`⚠️ Video suspended for ${participant.identity}`), onWaiting: () => console.warn(`⏳ Video waiting for data for ${participant.identity}`), onCanPlay: () => console.log(`▶️ Video can play for ${participant.identity}`), onPlay: () => console.log(`▶️ Video playing for ${participant.identity}`) }), isVideoEnabled && !isScreenShareMainTile && (jsx("div", { className: "absolute inset-0 pointer-events-none", style: {
                    background: `
                            radial-gradient(ellipse 95% 95% at 50% 50%, transparent 60%, rgba(0,0,0,0.02) 75%, rgba(0,0,0,0.04) 85%, rgba(0,0,0,0.06) 92%, rgba(0,0,0,0.08) 97%, rgba(0,0,0,0.1) 100%)
                        `,
                    mixBlendMode: 'multiply',
                    opacity: 0.6,
                    zIndex: 10
                } })), !isVideoEnabled && !isScreenShareMainTile && !isFilmstripTile && (jsx("div", { className: `absolute inset-0 flex items-center justify-center bg-gradient-to-br ${currentAvatarData.gradient}`, children: isGridTile ? (
                // Compact avatar for grid tiles
                jsxs("div", { className: "text-center", children: [jsx("div", { className: "text-5xl mb-2", children: currentAvatarData.emoji }), jsx("p", { className: "text-white text-sm font-semibold truncate px-2", children: participant.name || participant.identity })] })) : (
                // Full avatar for main view (focus)
                jsxs("div", { className: "text-center", children: [jsx("div", { className: "text-9xl mb-4", children: currentAvatarData.emoji }), jsx("p", { className: "text-white text-xl font-semibold", children: participant.name || participant.identity }), jsx("p", { className: "text-white/60 text-sm mt-1", children: currentAvatarData.name })] })) })), !isScreenShareMainTile && !isFilmstripTile && (
            // For grid tiles: always show overlay; for main view (focus): hide when active speaker/pinned
            (isGridTile || (!isActiveSpeaker && !isPinned)) && (jsx("div", { className: "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/40 to-transparent p-2", children: jsxs("div", { className: "flex items-center justify-between", children: [jsxs("div", { className: "flex items-center gap-2", children: [jsx("span", { className: "text-white text-xs font-medium truncate drop-shadow-lg", children: participant.name || participant.identity }), isTeacher && (jsx("span", { className: "bg-purple-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium", children: "Teacher" })), isLocal && (jsx("span", { className: "bg-blue-600 text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium", children: "You" }))] }), jsxs("div", { className: "flex items-center gap-1", children: [jsx("div", { className: `w-2 h-2 rounded-full ${participant.videoTrackPublications.size > 0 ? 'bg-green-500' : 'bg-red-500'}` }), jsx("div", { className: `w-2 h-2 rounded-full ${participant.audioTrackPublications.size > 0 && !participant.isMicrophoneEnabled ? 'bg-green-500' : 'bg-red-500'}` })] })] }) }))), !isScreenShareMainTile && isFilmstripTile && (jsx("div", { className: "absolute bottom-1 left-1 right-1", children: jsx("span", { className: "text-white text-[10px] font-medium truncate drop-shadow-lg", children: participant.name || participant.identity }) })), isActiveSpeaker && !isScreenShareMainTile && (jsx("div", { className: "absolute top-2 right-2", children: jsx("div", { className: "w-3 h-3 bg-green-500 rounded-full animate-pulse" }) })), isPinned && !isScreenShareMainTile && (jsx("div", { className: "absolute top-2 left-2", children: jsx("div", { className: "w-6 h-6 bg-purple-600 rounded-full flex items-center justify-center", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("path", { d: "M12 2l3 3-3 3 3 3-3 3-3-3 3-3-3-3z" }) }) }) }))] }));
}, (prevProps, nextProps) => {
    // GMEET OPTIMIZATION: Custom comparison function for React.memo
    // Only re-render if these specific props change
    return (prevProps.participant.identity === nextProps.participant.identity &&
        prevProps.isLocal === nextProps.isLocal &&
        prevProps.isTeacher === nextProps.isTeacher &&
        prevProps.isPinned === nextProps.isPinned &&
        prevProps.isActiveSpeaker === nextProps.isActiveSpeaker &&
        prevProps.className === nextProps.className &&
        prevProps.isMirrored === nextProps.isMirrored &&
        prevProps.selectedAvatar === nextProps.selectedAvatar &&
        // Check if participant's camera state changed
        prevProps.participant.isCameraEnabled === nextProps.participant.isCameraEnabled &&
        prevProps.participant.isMicrophoneEnabled === nextProps.participant.isMicrophoneEnabled);
});

const CustomLayoutManager = ({ currentLayout, userRole, teacherParticipantId, onParticipantPin, pinnedParticipant, onScreenShareChange, onTeacherIdentified, isMirrored = false, selectedAvatar, disableActiveSpeakerSwitching = false }) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    const room = useRoomContext();
    // Track screen share tracks
    const screenShareTracks = useTracks([Track.Source.ScreenShare, Track.Source.ScreenShareAudio], {
        onlySubscribed: false,
    }).filter(track => track.publication.kind === 'video');
    // CRITICAL: Zoom-like stable focus participant tracking
    // These must be at the top level (before any early returns)
    const stableFocusRef = useRef(null);
    const speakingTimeoutRef = useRef(null);
    const lastSpeakingParticipantRef = useRef(null);
    const speakingStartTimeRef = useRef(null);
    const lastSwitchTimeRef = useRef(null); // Track last switch time to prevent rapid flipping
    const [focusedParticipantId, setFocusedParticipantId] = useState(null);
    const [activeSpeakerId, setActiveSpeakerId] = useState(null); // Real-time active speaker from LiveKit events
    // Memoize allParticipants for all layouts (must be before any early returns)
    const allParticipants = useMemo(() => {
        if (!localParticipant)
            return [];
        return participants.length > 0 ? participants : [localParticipant];
    }, [participants, localParticipant]);
    // Memoize allParticipants for focus layout
    const allParticipantsForFocus = useMemo(() => {
        if (!localParticipant)
            return [];
        return participants.length > 0 ? participants : [localParticipant];
    }, [participants, localParticipant]);
    // CRITICAL: Auto-detect teacher for guests if teacherParticipantId is not set
    // Teacher is identified as: participant who is NOT a guest (doesn't start with "guest_")
    // NOTE: This must come AFTER allParticipantsForFocus is defined
    useEffect(() => {
        if (userRole === 'student' && !teacherParticipantId && allParticipantsForFocus.length > 0) {
            const localIdentity = localParticipant?.identity || '';
            const isGuest = localIdentity.startsWith('guest_');
            if (isGuest) {
                // Find teacher: participant who is NOT a guest
                const teacher = allParticipantsForFocus.find(p => {
                    const pIdentity = p.identity || '';
                    // Teacher is the participant who doesn't start with "guest_"
                    return !pIdentity.startsWith('guest_') && pIdentity !== localIdentity;
                });
                if (teacher && onTeacherIdentified) {
                    console.log('🎯 Auto-detected teacher for guest:', {
                        teacherId: teacher.identity,
                        teacherName: teacher.name,
                        localIdentity,
                        allParticipants: allParticipantsForFocus.map(p => ({
                            identity: p.identity,
                            name: p.name,
                            isGuest: (p.identity || '').startsWith('guest_')
                        }))
                    });
                    onTeacherIdentified(teacher.identity);
                }
            }
        }
    }, [userRole, teacherParticipantId, allParticipantsForFocus, localParticipant, onTeacherIdentified]);
    // REAL-TIME ACTIVE SPEAKER DETECTION: Listen to LiveKit's activeSpeakersChanged event
    // This is much more responsive than polling isSpeaking property
    useEffect(() => {
        if (!room)
            return;
        const handleActiveSpeakersChanged = (speakers) => {
            console.log('🔊 Active speakers changed (LiveKit event):', {
                speakers: speakers.map(s => ({
                    identity: s.identity,
                    name: s.name,
                    audioLevel: s.audioLevel
                })),
                timestamp: new Date().toISOString()
            });
            // Get the loudest speaker (first in the array)
            if (speakers.length > 0) {
                const loudestSpeaker = speakers[0];
                setActiveSpeakerId(loudestSpeaker.identity);
                console.log('🎤 Set active speaker:', loudestSpeaker.identity);
            }
            else {
                setActiveSpeakerId(null);
                console.log('🔇 No active speakers');
            }
        };
        // Subscribe to active speakers changed event
        room.on('activeSpeakersChanged', handleActiveSpeakersChanged);
        return () => {
            room.off('activeSpeakersChanged', handleActiveSpeakersChanged);
        };
    }, [room]);
    // ZOOM-LIKE BEHAVIOR: Initialize stable focus on mount
    // For guests/students: Initialize to teacher, but allow active speaker switching
    useEffect(() => {
        if (currentLayout !== 'focus' || allParticipantsForFocus.length === 0)
            return;
        // ZOOM BEHAVIOR: For students/guests, initialize to teacher (but allow switching)
        if (userRole === 'student' && teacherParticipantId) {
            const teacher = allParticipantsForFocus.find(p => p.identity === teacherParticipantId);
            const localIdentity = localParticipant?.identity;
            const currentFocus = stableFocusRef.current || focusedParticipantId;
            if (teacher) {
                // Initialize to teacher only if no focus is set yet
                // Once initialized, allow active speaker switching (including guest's own video)
                if (!currentFocus) {
                    console.log('🎯 Initializing teacher as focus for guest/student', {
                        currentFocus,
                        localIdentity,
                        teacherId: teacherParticipantId
                    });
                    stableFocusRef.current = teacherParticipantId;
                    setFocusedParticipantId(teacherParticipantId);
                }
                // If focus is already set (to any participant), allow it
                // This enables active speaker switching, including showing guest when they speak
                return;
            }
            else {
                // Teacher not found yet - initialize to null if no focus is set
                if (!currentFocus) {
                    console.log('⏳ Teacher not found yet - initializing to null');
                    stableFocusRef.current = null;
                    setFocusedParticipantId(null);
                }
            }
            return;
        }
        // For teachers: initialize only if not already set
        if (!stableFocusRef.current) {
            const teacher = allParticipantsForFocus.find(p => p.identity === teacherParticipantId);
            if (teacher) {
                stableFocusRef.current = teacherParticipantId;
                setFocusedParticipantId(teacherParticipantId);
            }
            else if (allParticipantsForFocus.length > 0) {
                stableFocusRef.current = allParticipantsForFocus[0].identity;
                setFocusedParticipantId(allParticipantsForFocus[0].identity);
            }
        }
    }, [currentLayout, allParticipantsForFocus, teacherParticipantId, userRole, focusedParticipantId, localParticipant]);
    // CRITICAL: Recovery logic - ensure focused participant always exists in the list
    // ZOOM-LIKE BEHAVIOR: Handle pinned participant (immediate switch, no debounce)
    useEffect(() => {
        if (currentLayout !== 'focus')
            return;
        if (pinnedParticipant) {
            const pinned = allParticipantsForFocus.find(p => p.identity === pinnedParticipant);
            if (pinned && stableFocusRef.current !== pinnedParticipant) {
                // Clear any speaking timers
                if (speakingTimeoutRef.current) {
                    clearTimeout(speakingTimeoutRef.current);
                    speakingTimeoutRef.current = null;
                }
                lastSpeakingParticipantRef.current = null;
                speakingStartTimeRef.current = null;
                // Immediately switch to pinned participant
                stableFocusRef.current = pinnedParticipant;
                setFocusedParticipantId(pinnedParticipant);
            }
        }
    }, [pinnedParticipant, allParticipantsForFocus, currentLayout]);
    // ZOOM-LIKE BEHAVIOR: Handle active speaker with 2-second buffer (like Zoom)
    // For guests: Enable active speaker switching but prioritize teacher when teacher is speaking
    useEffect(() => {
        if (currentLayout !== 'focus')
            return;
        if (pinnedParticipant)
            return; // Don't switch if someone is pinned
        if (disableActiveSpeakerSwitching)
            return; // Don't switch in chess/puzzle/screen-share modes
        if (currentLayout === 'screen-share')
            return; // Don't switch in screen-share mode
        localParticipant?.identity;
        const currentFocusId = stableFocusRef.current || focusedParticipantId;
        // CRITICAL: Check if teacher is speaking FIRST (highest priority for guests)
        // For teachers, also check if teacher is speaking to prioritize teacher
        const teacher = teacherParticipantId ? allParticipantsForFocus.find(p => p.identity === teacherParticipantId) : null;
        // PRIORITY 1: If teacher is the active speaker, always show teacher immediately (highest priority)
        if (teacher && activeSpeakerId === teacherParticipantId && currentFocusId !== teacherParticipantId) {
            console.log('👨‍🏫 Teacher is active speaker - IMMEDIATELY prioritizing teacher in main view', {
                currentFocusId,
                teacherId: teacherParticipantId,
                activeSpeakerId,
                userRole
            });
            // Clear any existing active speaker timer
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
                speakingTimeoutRef.current = null;
            }
            lastSpeakingParticipantRef.current = null;
            speakingStartTimeRef.current = null;
            // Immediately switch to teacher (no delay)
            stableFocusRef.current = teacherParticipantId;
            setFocusedParticipantId(teacherParticipantId);
            lastSwitchTimeRef.current = Date.now();
            return;
        }
        // Use real-time active speaker from LiveKit events (much more responsive than polling isSpeaking)
        // Find the active speaker participant (excluding current focus)
        const currentlySpeaking = activeSpeakerId && activeSpeakerId !== currentFocusId
            ? allParticipantsForFocus.find(p => p.identity === activeSpeakerId)
            : null;
        // CRITICAL DEBUG: Enhanced logging for active speaker detection (disabled to reduce console noise)
        // console.log('🎤 ACTIVE SPEAKER CHECK (Real-time):', ...);
        if (currentlySpeaking) {
            const now = Date.now();
            // If this is a new speaker, start tracking
            if (lastSpeakingParticipantRef.current !== currentlySpeaking.identity) {
                lastSpeakingParticipantRef.current = currentlySpeaking.identity;
                speakingStartTimeRef.current = now;
                // Clear any existing timer
                if (speakingTimeoutRef.current) {
                    clearTimeout(speakingTimeoutRef.current);
                }
                // GMEET-LIKE: Instant switching with minimal delay (300ms) for natural feel
                // Teacher gets instant switching (0ms), students get 300ms to avoid flicker
                const switchDelay = currentlySpeaking.identity === teacherParticipantId ? 0 : 300;
                speakingTimeoutRef.current = setTimeout(() => {
                    // Validate participant still exists and is still the active speaker
                    const stillSpeaking = activeSpeakerId === currentlySpeaking.identity &&
                        allParticipantsForFocus.find(p => p.identity === currentlySpeaking.identity);
                    // CRITICAL: Check cooldown - don't switch if we switched recently (within 500ms)
                    // Reduced from 1000ms to 500ms for faster responsiveness like GMeet
                    const now = Date.now();
                    const timeSinceLastSwitch = lastSwitchTimeRef.current ? now - lastSwitchTimeRef.current : Infinity;
                    // CRITICAL: Don't switch if:
                    // 1. Someone is pinned
                    // 2. Participant no longer exists
                    // 3. We switched recently (cooldown period - 500ms)
                    // 4. Teacher started speaking (check again - teacher always has priority)
                    const teacherIsSpeakingNow = activeSpeakerId === teacherParticipantId;
                    // SPECIAL CASE: If switching TO teacher, ignore cooldown (teacher has highest priority)
                    const isSwitchingToTeacher = currentlySpeaking.identity === teacherParticipantId;
                    const cooldownOk = isSwitchingToTeacher || timeSinceLastSwitch >= 500;
                    if (stillSpeaking && !pinnedParticipant && cooldownOk && !teacherIsSpeakingNow) {
                        console.log(`🎤 ✅ INSTANT SWITCH to active speaker: ${currentlySpeaking.identity} (${currentlySpeaking.name || 'unnamed'})`, {
                            switchDelay: `${switchDelay}ms`,
                            stillSpeaking: !!stillSpeaking,
                            pinnedParticipant,
                            timeSinceLastSwitch,
                            currentFocusId,
                            newFocusId: currentlySpeaking.identity,
                            userRole,
                            teacherIsSpeakingNow
                        });
                        // CRITICAL: Set both ref and state to ensure the switch happens
                        stableFocusRef.current = currentlySpeaking.identity;
                        setFocusedParticipantId(currentlySpeaking.identity);
                        lastSwitchTimeRef.current = now; // Update last switch time
                    }
                    else {
                        console.log(`⏸️ ❌ NOT switching to active speaker:`, {
                            stillSpeaking: !!stillSpeaking,
                            pinnedParticipant,
                            timeSinceLastSwitch,
                            teacherIsSpeakingNow,
                            cooldownOk,
                            isSwitchingToTeacher,
                            reason: !stillSpeaking ? 'not speaking anymore' :
                                pinnedParticipant ? 'someone pinned' :
                                    !cooldownOk ? `cooldown active (${timeSinceLastSwitch}ms < 500ms)` :
                                        teacherIsSpeakingNow ? 'teacher is speaking' :
                                            'unknown'
                        });
                    }
                    speakingTimeoutRef.current = null;
                    lastSpeakingParticipantRef.current = null;
                    speakingStartTimeRef.current = null;
                }, switchDelay); // Teacher: 0ms (instant), Students: 300ms (smooth)
            }
        }
        else {
            // No one is speaking - clear tracking
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
                speakingTimeoutRef.current = null;
            }
            lastSpeakingParticipantRef.current = null;
            speakingStartTimeRef.current = null;
        }
        return () => {
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
            }
        };
        // CRITICAL: Don't include focusedParticipantId in dependencies - it causes the effect to re-run
        // when we set focusedParticipantId, which resets the timer. We only want to react to:
        // - activeSpeakerId (real-time active speaker from LiveKit events - MOST IMPORTANT)
        // - allParticipantsForFocus (when participants join/leave)
        // - currentLayout (when layout changes)
        // - pinnedParticipant (when someone is pinned)
        // - teacherParticipantId (when teacher is identified)
        // - userRole (when role changes)
        // - localParticipant (when local participant changes)
        // - disableActiveSpeakerSwitching (when switching is disabled)
    }, [activeSpeakerId, allParticipantsForFocus, currentLayout, pinnedParticipant, teacherParticipantId, userRole, localParticipant, disableActiveSpeakerSwitching]);
    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (speakingTimeoutRef.current) {
                clearTimeout(speakingTimeoutRef.current);
            }
        };
    }, []);
    // Determine focused participant from stable state
    // CRITICAL: ZOOM BEHAVIOR - For guests, teacher is ALWAYS in main view
    const focusedParticipant = useMemo(() => {
        if (currentLayout !== 'focus')
            return null;
        if (allParticipantsForFocus.length === 0)
            return null;
        // CRITICAL: For guests/students, ALWAYS show teacher (no exceptions except pinned)
        const localIdentity = localParticipant?.identity;
        // Priority 1: Explicitly pinned participant (for teachers or when guest pins someone)
        if (pinnedParticipant) {
            const pinned = allParticipantsForFocus.find(p => p.identity === pinnedParticipant);
            if (pinned) {
                // For guests, ensure pinned participant is not themselves
                if (userRole === 'student' && pinned.identity === localIdentity) ;
                else {
                    return pinned;
                }
            }
        }
        // Priority 2: For guests/students, prioritize teacher when teacher is speaking
        // Otherwise, allow active speaker switching (handled by focusedParticipantId/stableRef)
        // ZOOM-LIKE: Guests can see active speakers, but teacher takes priority when speaking
        if (userRole === 'student') {
            const localIdentity = localParticipant?.identity || '';
            const isGuest = localIdentity.startsWith('guest_');
            // Try to find teacher: first by teacherParticipantId, then auto-detect
            let teacher = teacherParticipantId
                ? allParticipantsForFocus.find(p => p.identity === teacherParticipantId)
                : null;
            // If teacherParticipantId not set yet, auto-detect teacher (non-guest participant)
            if (!teacher && isGuest && allParticipantsForFocus.length > 0) {
                teacher = allParticipantsForFocus.find(p => {
                    const pIdentity = p.identity || '';
                    // Teacher is the participant who doesn't start with "guest_"
                    return !pIdentity.startsWith('guest_') && pIdentity !== localIdentity;
                });
                // If we found teacher but teacherParticipantId is null, notify parent to set it
                if (teacher && onTeacherIdentified) {
                    console.log('🎯 Auto-detecting teacher in focusedParticipant useMemo:', teacher.identity);
                    // Call in next tick to avoid state updates during render
                    setTimeout(() => onTeacherIdentified(teacher.identity), 0);
                }
            }
            const currentFocusId = focusedParticipantId || stableFocusRef.current;
            // ZOOM-LIKE: If teacher is speaking, prioritize teacher over active speaker
            if (teacher && teacher.isSpeaking && currentFocusId !== teacher.identity) {
                console.log('👨‍🏫 Teacher is speaking - prioritizing teacher in focusedParticipant useMemo');
                return teacher;
            }
            // If teacher not found yet, wait for teacher - don't show guest's own video
            if (!teacher) {
                console.log('⏳ Teacher not found yet for guest, returning null', {
                    teacherParticipantId,
                    allParticipants: allParticipantsForFocus.map(p => ({
                        identity: p.identity,
                        name: p.name,
                        isGuest: (p.identity || '').startsWith('guest_')
                    }))
                });
                return null;
            }
            // CRITICAL: When teacher is NOT speaking, allow active speaker switching
            // PRIORITY: Check stableRef FIRST (it's updated by active speaker switching)
            // Then check focusedParticipantId (state might lag behind ref)
            if (stableFocusRef.current) {
                const stable = allParticipantsForFocus.find(p => p.identity === stableFocusRef.current);
                if (stable && stable.identity !== localIdentity && stable.identity !== teacherParticipantId) {
                    // Stable ref points to valid participant (not guest's own video, not teacher) - use it
                    console.log('🎤 Guest view - showing stable focus (active speaker):', stable.identity, {
                        stableRef: stableFocusRef.current,
                        teacherIsSpeaking: teacher?.isSpeaking
                    });
                    return stable;
                }
            }
            // Check focusedParticipantId as backup
            if (focusedParticipantId) {
                const focused = allParticipantsForFocus.find(p => p.identity === focusedParticipantId);
                if (focused && focused.identity !== localIdentity && focused.identity !== teacherParticipantId) {
                    // Active speaker is set and it's not guest's own video, not teacher - use it
                    console.log('🎤 Guest view - showing focused participant (active speaker):', focused.identity);
                    return focused;
                }
            }
            // If no active speaker is set yet, default to teacher (but allow switching when someone speaks)
            console.log('👨‍🏫 Guest view - defaulting to teacher (no active speaker yet)', {
                stableRef: stableFocusRef.current,
                focusedParticipantId,
                teacherIsSpeaking: teacher?.isSpeaking
            });
            return teacher;
        }
        // Priority 3: Use the stable focused participant ID (for teachers and active speakers)
        // This allows active speaker switching to work for teachers
        if (focusedParticipantId) {
            const focused = allParticipantsForFocus.find(p => p.identity === focusedParticipantId);
            if (focused) {
                console.log('🎤 Teacher view - showing focused participant (active speaker):', focused.identity);
                return focused;
            }
        }
        // Priority 4: Use stable ref (for teachers and active speakers)
        if (stableFocusRef.current) {
            const stable = allParticipantsForFocus.find(p => p.identity === stableFocusRef.current);
            if (stable) {
                console.log('🎤 Teacher view - showing stable focus:', stable.identity);
                return stable;
            }
        }
        // Priority 5: Check if any participant is currently speaking (for teachers - show active speaker)
        // This ensures teachers see active speakers even if focusedParticipantId hasn't been set yet
        if (userRole === 'teacher') {
            const activeSpeaker = allParticipantsForFocus.find(p => p.isSpeaking && p.identity !== localParticipant?.identity);
            if (activeSpeaker) {
                console.log('🎤 Teacher view - showing active speaker:', activeSpeaker.identity);
                return activeSpeaker;
            }
        }
        // Priority 6: Prefer teacher for anyone (default fallback)
        if (teacherParticipantId) {
            const teacher = allParticipantsForFocus.find(p => p.identity === teacherParticipantId);
            if (teacher) {
                console.log('👨‍🏫 Default fallback - showing teacher');
                return teacher;
            }
        }
        // Priority 7: Fallback to first participant (should never be null at this point)
        if (allParticipantsForFocus.length > 0) {
            console.log('👤 Final fallback - showing first participant');
            return allParticipantsForFocus[0];
        }
        return null;
    }, [focusedParticipantId, allParticipantsForFocus, currentLayout, teacherParticipantId, userRole, pinnedParticipant, localParticipant, onTeacherIdentified]);
    // Reduced logging - only log on significant changes
    // console.log('🎥 Layout:', currentLayout, 'Role:', userRole, 'Participants:', participants.length, 'ScreenShares:', screenShareTracks.length);
    // console.log('🎯 Focused Participant:', {
    //     identity: focusedParticipant?.identity,
    //     name: focusedParticipant?.name,
    //     isLocal: focusedParticipant?.identity === localParticipant?.identity,
    //     isTeacher: focusedParticipant?.identity === teacherParticipantId,
    //     userRole,
    //     teacherParticipantId
    // });
    // ZOOM-LIKE BEHAVIOR: Include guest's own video in filmstrip (self-view)
    // Guests should see their own video tile like Zoom
    // This must be defined before any early returns (React Hook rules)
    const otherParticipants = useMemo(() => {
        if (!localParticipant || currentLayout !== 'focus')
            return [];
        // Filter out the focused participant (main view), but include guest's own video in filmstrip
        const filtered = allParticipantsForFocus.filter(p => p.identity !== focusedParticipant?.identity);
        // ZOOM-LIKE: Guests can see their own video in filmstrip (self-view)
        // No longer filtering out guest's own video - they should see themselves
        return filtered;
    }, [allParticipantsForFocus, focusedParticipant, userRole, localParticipant, currentLayout]);
    // CRITICAL: Detect screen share and notify parent
    // Also check for screen share publications that may not be subscribed yet (for guests joining during active screen share)
    const previousScreenShareStateRef = useRef({ hasScreenShare: false, participantId: null });
    useEffect(() => {
        // Reduced logging - only log on state changes
        // console.log('🔍 Screen share check:', {
        //     tracksCount: screenShareTracks.length,
        //     tracks: screenShareTracks.map(t => ({
        //         participant: t.participant.identity,
        //         source: t.publication.source,
        //         kind: t.publication.kind
        //     }))
        // });
        // First, check subscribed tracks (from useTracks)
        if (screenShareTracks.length > 0) {
            const sharingParticipant = screenShareTracks[0].participant;
            const participantId = sharingParticipant.identity;
            // Only log and notify if state changed
            if (!previousScreenShareStateRef.current.hasScreenShare || previousScreenShareStateRef.current.participantId !== participantId) {
                console.log('📺 Screen share detected via tracks - notifying parent:', participantId);
                previousScreenShareStateRef.current = { hasScreenShare: true, participantId };
            }
            onScreenShareChange?.(true, participantId);
            return;
        }
        // Fallback: Check for screen share publications that may not be subscribed yet
        // This is critical for guests joining during active screen share
        const allParticipantsToCheck = localParticipant ? [localParticipant, ...participants] : participants;
        for (const participant of allParticipantsToCheck) {
            if (participant.trackPublications) {
                const tracks = [...participant.trackPublications.values()];
                const screenSharePub = tracks.find((pub) => {
                    // CRITICAL: Check for Track.Source.ScreenShare enum value (primary check)
                    const isScreenShare = pub.source === Track.Source.ScreenShare
                        || pub.source === 'screen_share'
                        || pub.source === 'screen';
                    const isVideo = pub.kind === 'video';
                    // Accept if it's a screen share publication (even if not subscribed yet for remote participants)
                    const isLocal = participant.identity === localParticipant?.identity;
                    // For local: must have track, for remote: accept if not explicitly unsubscribed
                    return isScreenShare && isVideo && (isLocal ? !!pub.track : (pub.isSubscribed !== false));
                });
                if (screenSharePub) {
                    const participantId = participant.identity;
                    // Only log and notify if state changed
                    if (!previousScreenShareStateRef.current.hasScreenShare || previousScreenShareStateRef.current.participantId !== participantId) {
                        console.log('📺 Screen share detected via publication (not yet subscribed) - notifying parent:', participantId);
                        previousScreenShareStateRef.current = { hasScreenShare: true, participantId };
                    }
                    onScreenShareChange?.(true, participantId);
                    return;
                }
            }
        }
        // Only log and notify if state changed
        // CRITICAL: Don't notify screen share stopped during reconnection/disconnection
        // Screen share tracks may temporarily disappear during network issues, but they will be restored
        // This prevents false positives that cause screen share to stop incorrectly
        const isNetworkIssue = room.state === ConnectionState.Reconnecting ||
            room.state === ConnectionState.Disconnected ||
            room.state === ConnectionState.Connecting;
        if (previousScreenShareStateRef.current.hasScreenShare && !isNetworkIssue) {
            const previousParticipantId = previousScreenShareStateRef.current.participantId;
            console.log('📺 No screen share - notifying parent (was:', previousParticipantId, ')');
            previousScreenShareStateRef.current = { hasScreenShare: false, participantId: null };
            // Pass the previous participantId so handler knows who stopped sharing
            onScreenShareChange?.(false, previousParticipantId ?? undefined);
        }
        else if (previousScreenShareStateRef.current.hasScreenShare && isNetworkIssue) {
            // Screen share tracks temporarily unavailable during network issue - preserve state
            console.log('🌐 Network issue detected - preserving screen share state (tracks temporarily unavailable)');
            // Don't notify parent - screen share should resume after reconnection
        }
    }, [screenShareTracks.length, screenShareTracks, onScreenShareChange, participants, localParticipant, room.state]);
    // ============================================
    // SCREEN SHARE LAYOUT (Google Meet Style)
    // ============================================
    if (currentLayout === 'screen-share') {
        // Reduced logging - only log on significant changes
        // console.log('📺 Screen share layout mode - finding sharer...', {
        //     screenShareTracksCount: screenShareTracks.length,
        //     participantsCount: participants.length,
        //     hasLocal: !!localParticipant
        // });
        let sharingParticipant = null;
        if (screenShareTracks.length > 0) {
            sharingParticipant = screenShareTracks[0].participant;
            // console.log('✅ Found sharer via screenShareTracks:', sharingParticipant.identity);
        }
        else {
            // Fallback: manually check all participants
            // console.log('🔍 Manually checking participants for screen share...');
            const allParticipantsToCheck = [localParticipant, ...participants].filter(Boolean);
            for (const participant of allParticipantsToCheck) {
                // console.log(`🔍 Checking ${participant.identity} for screen share...`);
                if (participant.trackPublications) {
                    const tracks = [...participant.trackPublications.values()];
                    // console.log(`   - Has ${tracks.length} track publications`);
                    // CRITICAL: Check for Track.Source.ScreenShare enum value (with string fallback)
                    const screenSharePub = tracks.find(pub => {
                        const isScreenShare = pub.source === Track.Source.ScreenShare
                            || pub.source === 'screen_share'
                            || pub.source === 'screen';
                        return isScreenShare && pub.kind === 'video';
                    });
                    if (screenSharePub) {
                        sharingParticipant = participant;
                        // console.log('✅ Found sharer via manual check:', participant.identity);
                        break;
                    }
                }
            }
        }
        if (!sharingParticipant || !localParticipant) {
            console.log('⚠️ Screen share layout: Missing participant', {
                hasSharer: !!sharingParticipant,
                hasLocal: !!localParticipant,
                screenShareTracks: screenShareTracks.map(t => t.participant.identity),
                allParticipants: [localParticipant, ...participants].filter(Boolean).map(p => p.identity)
            });
            return null;
        }
        // CRITICAL: Include ALL participants - local participant + remote participants
        // This ensures everyone sees all camera feeds in the sidebar
        // DEDUPLICATE: Use a Map to ensure unique participants by identity
        const participantMap = new Map();
        participantMap.set(localParticipant.identity, localParticipant);
        participants.forEach(p => participantMap.set(p.identity, p));
        const allParticipants = Array.from(participantMap.values());
        // CRITICAL: Include ALL participants in sidebar, including the sharing participant
        // The main area shows their screen share, sidebar shows their camera feed
        // This allows teacher to see their own camera in the sidebar when presenting
        const sidebarParticipants = allParticipants;
        // Disabled excessive logging to reduce console noise
        // console.log('✅ Rendering screen share layout:', ...);
        return (jsxs("div", { className: "gmeet-screenshare-layout", style: {
                position: 'absolute',
                top: '50px',
                left: 0,
                width: '100%',
                height: 'calc(100% - 50px)',
                zIndex: 1000,
                display: 'flex',
                flexDirection: 'row',
                gap: '8px',
                padding: '8px',
                backgroundColor: '#202124'
            }, children: [jsxs("div", { className: "screenshare-main-area", style: {
                        flex: 1,
                        minWidth: 0,
                        height: '100%',
                        backgroundColor: '#000',
                        borderRadius: '8px',
                        overflow: 'hidden',
                        position: 'relative',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        zIndex: 100
                    }, children: [jsx(VideoTile, { participant: sharingParticipant, isLocal: sharingParticipant.identity === localParticipant.identity, isTeacher: sharingParticipant.identity === teacherParticipantId, isPinned: true, isActiveSpeaker: false, onClick: () => { }, className: "screenshare-video", isMirrored: false, selectedAvatar: undefined }, `screenshare-main-${sharingParticipant.identity}`), jsxs("div", { className: "screenshare-label", children: [jsx("span", { className: "presenter-icon", children: "\uD83D\uDCFA" }), sharingParticipant.name || sharingParticipant.identity, " is presenting"] })] }), jsx("div", { className: "screenshare-sidebar", children: sidebarParticipants.map((participant) => (jsx("div", { className: "screenshare-participant-tile", children: jsx(VideoTile, { participant: participant, isLocal: participant.identity === localParticipant.identity, isTeacher: participant.identity === teacherParticipantId, isPinned: false, isActiveSpeaker: participant.isSpeaking, onClick: () => onParticipantPin(participant.identity), className: "screenshare-sidebar-video", isMirrored: participant.identity === localParticipant.identity && isMirrored, selectedAvatar: participant.identity === localParticipant.identity ? selectedAvatar : undefined }, `screenshare-sidebar-${participant.identity}`) }, participant.identity))) })] }));
    }
    if (!localParticipant) {
        return null;
    }
    // ============================================
    // GRID/GALLERY VIEW (Google Meet Style)
    // ============================================
    if (currentLayout === 'grid') {
        return (jsx("div", { className: "gmeet-layout gmeet-grid", children: jsx("div", { className: "gmeet-grid-container", children: allParticipants.map((participant) => (jsx("div", { className: `gmeet-grid-tile ${participant.isSpeaking ? 'speaking' : ''}`, onClick: () => onParticipantPin(participant.identity), children: jsx(VideoTile, { participant: participant, isLocal: participant.identity === localParticipant.identity, isTeacher: participant.identity === teacherParticipantId, isPinned: pinnedParticipant === participant.identity, isActiveSpeaker: participant.isSpeaking, onClick: () => onParticipantPin(participant.identity), className: "gmeet-video", isMirrored: participant.identity === localParticipant.identity && isMirrored, selectedAvatar: participant.identity === localParticipant.identity ? selectedAvatar : undefined }, `grid-${participant.identity}`) }, participant.identity))) }) }));
    }
    // ============================================
    // FOCUS/SPOTLIGHT VIEW (Google Meet Style)
    // ============================================
    // CRITICAL: Debug logging for guests to see what's being rendered
    if (userRole === 'student' && focusedParticipant) {
        console.log('🎥 RENDERING focused participant for guest:', {
            focusedParticipantIdentity: focusedParticipant.identity,
            focusedParticipantName: focusedParticipant.name,
            teacherParticipantId,
            localIdentity: localParticipant?.identity,
            isTeacher: focusedParticipant.identity === teacherParticipantId,
            isLocal: focusedParticipant.identity === localParticipant?.identity,
            allParticipants: allParticipantsForFocus.map(p => ({
                identity: p.identity,
                name: p.name,
                isTeacher: p.identity === teacherParticipantId,
                isLocal: p.identity === localParticipant?.identity
            }))
        });
    }
    return (jsxs("div", { className: "gmeet-layout gmeet-spotlight", children: [jsxs("div", { className: "gmeet-spotlight-main", children: [focusedParticipant && (jsx(VideoTile, { participant: focusedParticipant, isLocal: focusedParticipant.identity === localParticipant.identity, isTeacher: focusedParticipant.identity === teacherParticipantId, isPinned: true, isActiveSpeaker: focusedParticipant.isSpeaking, onClick: () => onParticipantPin(focusedParticipant.identity), className: "gmeet-video", isMirrored: focusedParticipant.identity === localParticipant.identity && isMirrored, selectedAvatar: focusedParticipant.identity === localParticipant.identity ? selectedAvatar : undefined }, `focus-main-${focusedParticipant.identity}`)), !focusedParticipant && userRole === 'student' && (jsx("div", { className: "flex items-center justify-center h-full text-white", children: jsx("p", { children: "Waiting for teacher to join..." }) }))] }), otherParticipants.length > 0 && (jsx("div", { className: "gmeet-filmstrip", children: jsx("div", { className: "gmeet-filmstrip-scroll", children: otherParticipants.map((participant) => (jsx("div", { className: `gmeet-filmstrip-tile ${participant.isSpeaking ? 'speaking' : ''}`, onClick: () => onParticipantPin(participant.identity), children: jsx(VideoTile, { participant: participant, isLocal: participant.identity === localParticipant.identity, isTeacher: participant.identity === teacherParticipantId, isPinned: false, isActiveSpeaker: participant.isSpeaking, onClick: () => onParticipantPin(participant.identity), className: "gmeet-video gmeet-filmstrip-video", isMirrored: participant.identity === localParticipant.identity && isMirrored, selectedAvatar: participant.identity === localParticipant.identity ? selectedAvatar : undefined }, `focus-filmstrip-${participant.identity}`) }, participant.identity))) }) }))] }));
};

// Background Effects Control Component (inside room context)
const BackgroundEffectsControl = ({ onEffectChange, currentEffect, showPanel, onClose, onProcessingChange, onVideoEnhancementChange, isMirrored, onMirrorToggle, initialBackgroundEffect, selectedAvatar, onAvatarChange, hqEnabled = false // Default to false if not provided
 }) => {
    const { localParticipant } = useLocalParticipant();
    const [isProcessing, setIsProcessing] = useState(false);
    const [selectedBackground, setSelectedBackground] = useState('');
    const [videoEnhancement, setVideoEnhancement] = useState(false);
    const [enhancementLevel, setEnhancementLevel] = useState(2);
    const [activeTab, setActiveTab] = useState('effects');
    const [currentAvatar, setCurrentAvatar] = useState(() => {
        if (selectedAvatar)
            return selectedAvatar;
        try {
            return localStorage.getItem('meetplus_selected_avatar') || 'avatar1';
        }
        catch {
            return 'avatar1';
        }
    });
    useEffect(() => {
        if (selectedAvatar) {
            setCurrentAvatar(selectedAvatar);
        }
    }, [selectedAvatar]);
    // const hasRestoredRef = useRef<boolean>(false); // Disabled with auto-restore
    // Image cache for instant background switching (like Google Meet)
    const backgroundImageCache = useRef(new Map());
    // Built-in backgrounds - shared with pre-join screen and hosted on Firebase CDN
    const builtInBackgrounds = getBuiltInBackgrounds();
    // Avatar options for when camera is off - Inclusive & Diverse (matching PreJoinScreen)
    const avatarOptions = [
        {
            id: 'avatar1',
            name: 'Professional',
            emoji: '🧑‍💼',
            gradient: 'from-blue-500 to-blue-700'
        },
        {
            id: 'avatar2',
            name: 'Student',
            emoji: '🧑‍🎓',
            gradient: 'from-green-500 to-green-700'
        },
        {
            id: 'avatar3',
            name: 'Teacher',
            emoji: '🧑‍🏫',
            gradient: 'from-purple-500 to-purple-700'
        },
        {
            id: 'avatar4',
            name: 'Creative',
            emoji: '🧑‍🎨',
            gradient: 'from-pink-500 to-pink-700'
        },
        {
            id: 'avatar5',
            name: 'Tech',
            emoji: '🧑‍💻',
            gradient: 'from-indigo-500 to-indigo-700'
        },
        {
            id: 'avatar6',
            name: 'Science',
            emoji: '🧑‍🔬',
            gradient: 'from-teal-500 to-teal-700'
        },
        {
            id: 'avatar7',
            name: 'Medical',
            emoji: '🧑‍⚕️',
            gradient: 'from-red-500 to-red-700'
        },
        {
            id: 'avatar8',
            name: 'Engineer',
            emoji: '🧑‍🔧',
            gradient: 'from-orange-500 to-orange-700'
        },
        {
            id: 'avatar9',
            name: 'Artist',
            emoji: '🎭',
            gradient: 'from-yellow-500 to-yellow-700'
        },
        {
            id: 'avatar10',
            name: 'Chef',
            emoji: '🧑‍🍳',
            gradient: 'from-amber-500 to-amber-700'
        },
        {
            id: 'avatar11',
            name: 'Astronaut',
            emoji: '🧑‍🚀',
            gradient: 'from-slate-500 to-slate-700'
        },
        {
            id: 'avatar12',
            name: 'Musician',
            emoji: '🎵',
            gradient: 'from-violet-500 to-violet-700'
        }
    ];
    // Storage keys
    const STORAGE_KEYS = {
        BACKGROUND_EFFECT: 'meetplus_background_effect',
        BACKGROUND_URL: 'meetplus_background_url',
        VIDEO_ENHANCEMENT: 'meetplus_video_enhancement',
        ENHANCEMENT_LEVEL: 'meetplus_enhancement_level'
    };
    // Save preferences to localStorage
    const savePreferences = (prefs) => {
        Object.entries(prefs).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
                localStorage.setItem(STORAGE_KEYS[key.toUpperCase()], value.toString());
            }
        });
    };
    // Initialize processor pre-warming and preload background images on mount
    useEffect(() => {
        console.log('🚀 Initializing background processing for seamless application...');
        // Initialize processor pre-warming (blur + common backgrounds)
        initializeProcessorPrewarming();
        // Preload ALL background images for instant switching
        console.log('🔄 Preloading all background images for instant switching...');
        const preloadPromises = builtInBackgrounds.map(bg => {
            return new Promise((resolve) => {
                if (backgroundImageCache.current.has(bg.url)) {
                    resolve();
                    return;
                }
                const img = new Image();
                // Only set crossOrigin for remote URLs (Firebase Hosting), not local paths
                if (bg.url.startsWith('http://') || bg.url.startsWith('https://')) {
                    img.crossOrigin = 'anonymous';
                }
                img.onload = () => {
                    console.log('✅ Cached:', bg.name);
                    backgroundImageCache.current.set(bg.url, img);
                    // Also pre-warm the processor for this image
                    prewarmImageProcessor(bg.url).catch(error => {
                        console.warn('⚠️ Failed to pre-warm processor for:', bg.name, error);
                    });
                    resolve();
                };
                img.onerror = () => {
                    console.warn('⚠️ Failed to cache:', bg.name);
                    resolve(); // Continue even if one fails
                };
                img.src = bg.url;
            });
        });
        // Wait for all images to preload
        Promise.all(preloadPromises).then(() => {
            console.log('✅ All background images cached and processors pre-warmed! Ready for instant switching.');
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Only run once on mount - builtInBackgrounds is static
    // Preload image function for custom images
    const preloadImage = (imageUrl) => {
        return new Promise((resolve) => {
            // Check cache first
            const cached = backgroundImageCache.current.get(imageUrl);
            if (cached && cached.complete && cached.naturalWidth > 0) {
                console.log('⚡ Using cached image:', imageUrl);
                resolve(cached);
                return;
            }
            const img = new Image();
            // Only set crossOrigin for remote URLs (Firebase Hosting), not local paths
            if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                img.crossOrigin = 'anonymous';
            }
            img.onload = () => {
                console.log('✅ Image loaded and cached:', imageUrl);
                backgroundImageCache.current.set(imageUrl, img);
                resolve(img);
            };
            img.onerror = () => {
                console.warn('⚠️ Failed to load image:', imageUrl);
                resolve(null);
            };
            img.src = imageUrl;
        });
    };
    const handleEffectChange = async (effect, imageUrl) => {
        if (!localParticipant) {
            toast$1.error('Not connected to meeting');
            return;
        }
        // Prevent re-applying the same effect
        if (effect === currentEffect && effect !== 'image') {
            console.log('⚠️ Effect already applied, skipping');
            return;
        }
        // For image effects, only skip if it's the same image
        if (effect === 'image' && currentEffect === 'image' && selectedBackground === imageUrl) {
            console.log('⚠️ Same background image already applied, skipping');
            return;
        }
        try {
            setIsProcessing(true);
            onProcessingChange?.(true); // Notify parent about processing start
            // Get the video track
            const videoTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track;
            if (!videoTrack) {
                console.log('⏳ Video track not ready yet, will retry...');
                toast$1.error('Please enable your camera first');
                setIsProcessing(false);
                onProcessingChange?.(false); // Notify parent about processing end
                return;
            }
            console.log('🎥 Applying effect:', effect, imageUrl);
            // GOOGLE MEET APPROACH: Seamless switching
            if (effect === 'blur') {
                console.log('🌫️ Applying background blur...');
                try {
                    await ensureBackgroundProcessor(videoTrack, 'blur');
                    setSelectedBackground('');
                    console.log('✅ Background blur applied');
                    toast$1.success('Background blur applied', { autoClose: 1000 });
                }
                catch (error) {
                    console.error('Error applying blur:', error);
                    toast$1.error('Failed to apply blur effect');
                }
            }
            else if (effect === 'image' && imageUrl) {
                console.log('🖼️ Applying background image...');
                try {
                    // CRITICAL: Preload image from cache (instant if cached)
                    const cachedImage = await preloadImage(imageUrl);
                    if (!cachedImage) {
                        throw new Error('Failed to load background image');
                    }
                    await ensureBackgroundProcessor(videoTrack, 'image', imageUrl);
                    setSelectedBackground(imageUrl);
                    console.log('✅ Background image applied');
                    toast$1.success('Background applied', { autoClose: 1000 });
                }
                catch (error) {
                    console.error('Error applying image:', error);
                    toast$1.error('Failed to apply background image');
                }
            }
            else if (effect === 'none') {
                console.log('🚫 Removing background effect...');
                try {
                    await ensureBackgroundProcessor(videoTrack, 'none');
                    setSelectedBackground('');
                    console.log('✅ Background effect removed');
                    toast$1.success('Background removed', { autoClose: 1000 });
                }
                catch (error) {
                    console.error('Error removing effect:', error);
                }
            }
            // Save preferences
            savePreferences({
                effect,
                backgroundUrl: imageUrl || '',
                videoEnhancement,
                enhancementLevel
            });
            onEffectChange(effect, imageUrl);
        }
        catch (error) {
            console.error('❌ Error applying background effect:', error);
            toast$1.error('Failed to apply background effect');
        }
        finally {
            setIsProcessing(false);
            onProcessingChange?.(false); // Notify parent about processing end
        }
    };
    // DISABLED: Auto-restore removed to prevent dark overlay flashing
    /*
    // Auto-restore last applied background effect on mount - ONCE ONLY
    useEffect(() => {
        // Only restore once, even if localParticipant changes
        if (hasRestoredRef.current) {
            console.log('⏭️ Background already restored once, skipping');
            return;
        }

        const restoreBackground = async () => {
            if (!localParticipant) {
                console.log('⏳ Local participant not ready yet');
                return;
            }

            const prefs = loadPreferences();
            const savedEffect = prefs.effect;
            const savedBackgroundUrl = prefs.backgroundUrl;

            if (savedEffect && savedEffect !== 'none') {
                console.log('🔄 Restoring saved background effect:', savedEffect, savedBackgroundUrl);
                
                // Wait for video track to be available
                let retries = 0;
                const maxRetries = 10;
                while (retries < maxRetries) {
                    const videoTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track as LocalVideoTrack | undefined;
                    if (videoTrack) {
                        await handleEffectChange(savedEffect as 'none' | 'blur' | 'image', savedBackgroundUrl || undefined);
                        console.log('✅ Background effect restored successfully');
                        hasRestoredRef.current = true; // Mark as restored
                        break;
                    }
                    await new Promise(resolve => setTimeout(resolve, 200));
                    retries++;
                }
                
                if (retries >= maxRetries) {
                    console.log('⚠️ Video track not available, skipping background restore');
                }
            } else {
                // No saved effect, mark as "restored" to prevent future attempts
                hasRestoredRef.current = true;
            }
        };

        restoreBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [localParticipant]); // Only run when localParticipant is available
    */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const applyEnhancementFilter = (_enabled, _level) => {
        // Do nothing - video enhancement disabled to prevent flickering
        console.log('Video enhancement disabled to prevent flickering');
        onVideoEnhancementChange(false);
    };
    const changeEnhancementLevel = (level) => {
        setEnhancementLevel(level);
        localStorage.setItem('meetplus_enhancement_level', level.toString());
        if (videoEnhancement) {
            applyEnhancementFilter();
        }
    };
    const toggleVideoEnhancement = (enabled) => {
        setVideoEnhancement(enabled);
        localStorage.setItem('meetplus_video_enhancement', enabled.toString());
        applyEnhancementFilter();
        toast$1.success(enabled ? '✨ Video enhancement enabled' : '📷 Video enhancement disabled');
    };
    // REMOVED: Auto-apply initial background effect
    // Background is already applied in MediaEnabler component (LiveKitRoom.tsx)
    // No need to apply twice - just sync the UI state
    useEffect(() => {
        if (initialBackgroundEffect && initialBackgroundEffect !== 'none') {
            // Just update UI state to match what was applied in MediaEnabler
            if (initialBackgroundEffect === 'blur') {
                // Blur is applied, just update state
                console.log('🎨 Syncing UI: Blur effect from prejoin');
            }
            else {
                // Image background, update selectedBackground for UI
                setSelectedBackground(initialBackgroundEffect);
                console.log('🎨 Syncing UI: Background image from prejoin:', initialBackgroundEffect);
            }
        }
    }, [initialBackgroundEffect]);
    // DISABLED: Monitor video track changes to prevent screen darkening
    // useEffect(() => {
    //     if (!localParticipant) return;
    //     // No video track monitoring to prevent any darkening issues
    // }, [localParticipant]);
    useEffect(() => {
        return () => {
            // Only cleanup if we're actually unmounting the entire component
            // Don't cleanup when settings panel closes - we want effects to persist
            console.log('🧹 BackgroundEffectsControl component unmounting - preserving effects');
        };
    }, []);
    if (!showPanel)
        return null;
    return (jsxs("div", { className: "absolute top-16 right-4 z-50 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 h-[85vh] flex flex-col", style: { width: '400px' }, children: [jsxs("div", { className: "sticky top-0 bg-gray-800 z-10 border-b border-gray-700", children: [jsxs("div", { className: "flex justify-between items-center px-4 pt-3 pb-2", children: [jsx("h3", { className: "text-white font-semibold text-sm", children: "Video Settings" }), jsx("button", { onClick: onClose, className: "text-gray-400 hover:text-white transition-colors", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }), jsxs("div", { className: "flex px-4 pb-2 gap-2", children: [jsx("button", { onClick: () => setActiveTab('effects'), className: `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'effects'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`, children: "Effects" }), jsx("button", { onClick: () => setActiveTab('avatar'), className: `px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${activeTab === 'avatar'
                                    ? 'bg-purple-600 text-white'
                                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`, children: "Avatar" })] })] }), activeTab === 'effects' && (jsx("div", { className: "flex-1 overflow-y-auto", children: jsxs("div", { className: "p-4 space-y-4", children: [jsxs("div", { children: [jsx("h4", { className: "text-gray-300 text-xs font-semibold mb-2", children: "QUICK EFFECTS" }), jsxs("div", { className: "space-y-2", children: [jsx("button", { onClick: () => handleEffectChange('none'), disabled: isProcessing, className: `w-full p-2.5 rounded-lg text-left transition-all ${currentEffect === 'none'
                                                ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`, children: jsxs("div", { className: "flex items-center gap-2", children: [jsx("div", { className: "w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) }), jsxs("div", { children: [jsx("div", { className: "text-sm font-medium", children: "None" }), jsx("div", { className: "text-xs text-gray-400", children: "Original background" })] })] }) }), jsx("button", { onClick: () => handleEffectChange('blur'), disabled: isProcessing, className: `w-full p-2.5 rounded-lg text-left transition-all ${currentEffect === 'blur'
                                                ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`, children: jsxs("div", { className: "flex items-center gap-2", children: [jsx("div", { className: "w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "3" }), jsx("path", { d: "M12 1v6m0 6v6m11-7h-6m-6 0H1" })] }) }), jsxs("div", { children: [jsx("div", { className: "text-sm font-medium", children: "Blur" }), jsx("div", { className: "text-xs text-gray-400", children: "Blur background" })] })] }) }), jsx("button", { onClick: onMirrorToggle, className: `w-full p-2.5 rounded-lg text-left transition-all ${isMirrored
                                                ? 'bg-purple-600 text-white ring-2 ring-purple-400'
                                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`, children: jsxs("div", { className: "flex items-center gap-2", children: [jsx("div", { className: "w-8 h-8 rounded bg-gray-900 flex items-center justify-center flex-shrink-0", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M12 2v20M12 2l-3 3m3-3l3 3" }), jsx("path", { d: "M3 12h7m4 0h7" })] }) }), jsxs("div", { children: [jsx("div", { className: "text-sm font-medium", children: "Video Flip" }), jsx("div", { className: "text-xs text-gray-400", children: "180\u00B0 video rotation" })] })] }) }), jsx("div", { className: "mb-6", children: jsxs("div", { className: "relative w-full h-40 rounded-lg overflow-hidden bg-gray-900", children: [currentEffect === 'image' && selectedBackground ? (jsx("img", { src: selectedBackground, alt: "Current background", className: "w-full h-full object-cover", onError: (e) => {
                                                            console.error('Failed to load preview image:', selectedBackground);
                                                            e.currentTarget.style.display = 'none';
                                                        } })) : currentEffect === 'blur' ? (jsx("div", { className: "absolute inset-0 backdrop-blur-md bg-black/30" })) : null, jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: jsx("div", { className: "w-20 h-20 rounded-full bg-gray-800 border-2 border-gray-700 flex items-center justify-center", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "32", height: "32", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" }), jsx("circle", { cx: "12", cy: "7", r: "4" })] }) }) })] }) }), jsxs("div", { children: [jsx("h4", { className: "text-gray-300 text-xs font-semibold mb-3", children: "BUILT-IN BACKGROUNDS" }), jsx("div", { className: "grid grid-cols-2 gap-3", children: builtInBackgrounds.map((bg) => (jsxs("button", { onClick: () => handleEffectChange('image', bg.url), disabled: isProcessing, className: `group relative overflow-hidden h-24 rounded-lg transition-all ${currentEffect === 'image' && selectedBackground === bg.url
                                                            ? 'ring-2 ring-purple-500'
                                                            : 'ring-1 ring-gray-600 hover:ring-purple-400'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`, children: [jsx("img", { src: bg.thumbnail, alt: bg.name, className: "w-full h-full object-cover", onError: (e) => {
                                                                    console.error(`Failed to load background image: ${bg.name}`);
                                                                    const target = e.target;
                                                                    target.style.display = 'none';
                                                                } }), jsx("div", { className: "absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" }), jsx("div", { className: "absolute bottom-2 left-2 text-white text-xs font-medium", children: bg.name }), currentEffect === 'image' && selectedBackground === bg.url && (jsx("div", { className: "absolute top-2 right-2 w-6 h-6 bg-purple-500 rounded-full flex items-center justify-center", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("polyline", { points: "20 6 9 17 4 12" }) }) }))] }, bg.id))) })] }), jsxs("div", { className: "mt-4", children: [jsx("h4", { className: "text-gray-300 text-xs font-semibold mb-3", children: "CUSTOM BACKGROUND" }), jsx("input", { type: "file", accept: "image/*", onChange: async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (file) {
                                                            const reader = new FileReader();
                                                            reader.onloadend = () => {
                                                                const imageUrl = reader.result;
                                                                handleEffectChange('image', imageUrl);
                                                            };
                                                            reader.readAsDataURL(file);
                                                        }
                                                    }, className: "hidden", id: "background-image-upload" }), jsx("div", { className: "relative", children: jsx("label", { htmlFor: "background-image-upload", className: `group w-full h-24 rounded-lg border-2 border-dashed transition-all flex items-center justify-center cursor-pointer ${currentEffect === 'image' && !builtInBackgrounds.some(bg => bg.url === selectedBackground)
                                                            ? 'border-purple-500 bg-purple-500/10 hover:bg-purple-500/20'
                                                            : 'border-gray-600 hover:border-purple-400 hover:bg-purple-500/10'} ${isProcessing ? 'opacity-50 cursor-not-allowed' : ''}`, children: jsxs("div", { className: "text-center", children: [jsx("div", { className: "w-10 h-10 rounded-full bg-gray-700 group-hover:bg-gray-600 flex items-center justify-center mx-auto mb-2", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" }), jsx("polyline", { points: "17 8 12 3 7 8" }), jsx("line", { x1: "12", y1: "3", x2: "12", y2: "15" })] }) }), jsx("div", { className: "text-sm font-medium", children: "Upload Image" }), jsx("div", { className: "text-xs text-gray-400 mt-1", children: "Click to choose a file" })] }) }) })] })] })] }), jsxs("div", { children: [jsx("h4", { className: "text-gray-300 text-xs font-semibold mb-2", children: "VIDEO ENHANCEMENT" }), jsxs("div", { className: "space-y-3", children: [jsxs("div", { className: "flex items-center justify-between", children: [jsx("span", { className: "text-sm text-gray-300", children: "Enable Enhancement" }), jsx("button", { onClick: () => toggleVideoEnhancement(!videoEnhancement), className: `w-12 h-6 rounded-full transition-colors ${videoEnhancement ? 'bg-purple-600' : 'bg-gray-600'}`, children: jsx("div", { className: `w-5 h-5 bg-white rounded-full transition-transform ${videoEnhancement ? 'translate-x-6' : 'translate-x-0.5'}` }) })] }), videoEnhancement && (jsxs("div", { children: [jsx("label", { className: "text-xs text-gray-400 block mb-2", children: "Enhancement Level" }), jsx("div", { className: "space-y-2", children: [1, 2, 3].map((level) => (jsx("button", { onClick: () => changeEnhancementLevel(level), className: `w-full p-2 rounded text-xs transition-all ${enhancementLevel === level
                                                            ? 'bg-purple-600 text-white'
                                                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`, children: level === 1 ? 'Light' : level === 2 ? 'Medium (Recommended)' : 'Strong' }, level))) })] }))] })] })] }) })), activeTab === 'avatar' && (jsx("div", { className: "flex-1 overflow-y-auto", children: jsxs("div", { className: "p-4 space-y-4", children: [jsx("h4", { className: "text-gray-300 text-xs font-semibold mb-2", children: "CHOOSE YOUR AVATAR" }), jsx("p", { className: "text-gray-400 text-xs mb-4", children: "Select an avatar to display when your camera is off" }), jsx("div", { className: "grid grid-cols-3 gap-3", children: avatarOptions.map(avatar => (jsxs("button", { onClick: () => {
                                    setCurrentAvatar(avatar.id);
                                    onAvatarChange(avatar.id);
                                    try {
                                        localStorage.setItem('meetplus_selected_avatar', avatar.id);
                                    }
                                    catch (error) {
                                        console.error('Failed to persist avatar selection:', error);
                                    }
                                    toast$1.success(`Avatar changed to ${avatar.name}`, { autoClose: 1000 });
                                }, className: `p-4 rounded-lg border-2 ${currentAvatar === avatar.id
                                    ? 'border-purple-500 bg-gray-700 ring-2 ring-purple-400'
                                    : 'border-gray-600 bg-gray-700 hover:border-purple-500'} transition-all group`, children: [jsx("div", { className: `text-5xl mb-2 bg-gradient-to-br ${avatar.gradient} rounded-lg p-3 group-hover:scale-110 transition-transform`, children: avatar.emoji }), jsx("div", { className: "text-xs font-medium text-gray-300", children: avatar.name })] }, avatar.id))) }), jsx("div", { className: "mt-6 p-3 bg-gray-700 rounded-lg border border-gray-600", children: jsxs("p", { className: "text-gray-300 text-xs", children: ["\uD83D\uDCA1 ", jsx("span", { className: "font-semibold", children: "Tip:" }), " Your avatar will be displayed to other participants when your camera is turned off."] }) })] }) }))] }));
};

/**
 * LiveKit Room Configuration
 *
 * This file allows consuming apps to inject their dependencies
 * (Firebase, API endpoints, custom components) into the LiveKit SDK.
 */
// Global config storage
let sdkConfig = null;
/**
 * Initialize the LiveKit SDK with app-specific configuration
 * Call this once in your app's initialization (e.g., App.tsx or main entry point)
 */
const initializeLiveKitSDK = (config) => {
    sdkConfig = config;
    console.log('✅ LiveKit SDK initialized with config:', {
        hasFirebase: !!config.firebaseDb,
        apiEndpoint: config.apiEndpoint,
        hasChessComponent: !!config.ChessComponent,
        hasPollsComponent: !!config.PollsComponent,
        hqEnabled: config.hqEnabled || false
    });
};
/**
 * Get the current SDK configuration
 * Throws an error if SDK is not initialized
 */
const getLiveKitSDKConfig = () => {
    if (!sdkConfig) {
        throw new Error('LiveKit SDK not initialized! Call initializeLiveKitSDK() before using LiveKit components.');
    }
    return sdkConfig;
};
/**
 * Check if SDK is initialized
 */
const isSDKInitialized = () => {
    return sdkConfig !== null;
};
/**
 * Reset SDK configuration (useful for testing)
 */
const resetSDKConfig = () => {
    sdkConfig = null;
};

// Get SDK configuration (injected by consuming app)
const getSDKConfig = () => {
    try {
        return getLiveKitSDKConfig();
    }
    catch (error) {
        console.error('❌ LiveKit SDK not initialized!', error);
        throw error;
    }
};
// Comprehensive logging helper with special tag for filtering
// Use this tag [LIVEKIT_DEBUG] to filter logs in browser console
const logLiveKitEvent = (event, data, level = 'info') => {
    const timestamp = new Date().toISOString();
    const logData = {
        timestamp,
        event,
        ...data
    };
    const logMessage = `[LIVEKIT_DEBUG] ${event}`;
    const jsonString = JSON.stringify(logData);
    // Log with both human-readable format and JSON for easy filtering
    switch (level) {
        case 'error':
            console.error(logMessage, logData);
            console.error(`[LIVEKIT_DEBUG]`, jsonString);
            break;
        case 'warn':
            console.warn(logMessage, logData);
            console.warn(`[LIVEKIT_DEBUG]`, jsonString);
            break;
        default:
            console.log(logMessage, logData);
            console.log(`[LIVEKIT_DEBUG]`, jsonString);
    }
};
// Internal helper to expose LiveKit room instance via callback and handle track subscriptions
const RoomRefCollector = ({ onRoom, serverUrl, token }) => {
    const room = useRoomContext();
    useEffect(() => {
        if (room) {
            onRoom(room);
            // Prepare connection to preselect region if not already connected
            if (serverUrl && token && typeof room.prepareConnection === 'function' && room.state === 'disconnected') {
                try {
                    room.prepareConnection(serverUrl, token);
                    console.log('✅ Prepared connection on LiveKitRoom instance for region preselection:', serverUrl);
                }
                catch (error) {
                    console.warn('⚠️ Failed to prepare connection on LiveKitRoom instance:', error);
                }
            }
            // CRITICAL: Listen for remote track publications to auto-subscribe to screen share tracks
            // This ensures students/guests automatically receive screen share tracks
            const handleTrackPublished = (publication, participant) => {
                // Only handle remote participants' screen share tracks
                if (participant && participant !== room.localParticipant) {
                    // CRITICAL: Check for Track.Source.ScreenShare enum value (not just strings)
                    const isScreenShare = publication.source === Track.Source.ScreenShare
                        || publication.source === 'screen_share'
                        || publication.source === 'screen';
                    const isVideo = publication.kind === 'video';
                    if (isScreenShare && isVideo) {
                        // Explicitly subscribe to remote screen share tracks
                        try {
                            // Use setSubscribed if available, otherwise try to subscribe via room
                            if (publication.setSubscribed) {
                                if (publication.isSubscribed !== true) {
                                    console.log('📡 Auto-subscribing to remote screen share track from:', participant.identity);
                                    publication.setSubscribed(true);
                                }
                            }
                            else if (room.localParticipant) {
                                // Fallback: try to subscribe via room's subscribe method
                                console.log('📡 Attempting to subscribe to screen share via room:', participant.identity);
                                room.localParticipant.subscribe(publication).catch((err) => {
                                    console.warn('⚠️ Could not subscribe to screen share track:', err);
                                });
                            }
                        }
                        catch (error) {
                            console.error('❌ Error auto-subscribing to screen share track:', error);
                        }
                    }
                }
            };
            // Listen for track published events
            room.on('trackPublished', handleTrackPublished);
            // CRITICAL: Also listen for when tracks are subscribed (become available)
            // This helps detect when screen share tracks become available for remote participants
            const handleTrackSubscribed = (track, publication, participant) => {
                if (participant && participant !== room.localParticipant) {
                    // CRITICAL: Check for Track.Source.ScreenShare enum value
                    const isScreenShare = publication.source === Track.Source.ScreenShare
                        || publication.source === 'screen_share'
                        || publication.source === 'screen';
                    if (isScreenShare && track && track.kind === 'video') {
                        console.log('✅ Remote screen share track subscribed and available for:', participant.identity);
                    }
                }
            };
            room.on('trackSubscribed', handleTrackSubscribed);
            // Also subscribe to any existing screen share tracks from remote participants
            // Use remoteParticipants Map if available, otherwise iterate through all participants
            const remoteParticipants = room.remoteParticipants || new Map();
            remoteParticipants.forEach((participant) => {
                if (participant && participant.trackPublications) {
                    participant.trackPublications.forEach((pub) => {
                        // CRITICAL: Check for Track.Source.ScreenShare enum value
                        const isScreenShare = pub.source === Track.Source.ScreenShare
                            || pub.source === 'screen_share'
                            || pub.source === 'screen';
                        if (isScreenShare && pub.kind === 'video') {
                            try {
                                // Subscribe if not already subscribed
                                if (pub.isSubscribed !== true) {
                                    if (pub.setSubscribed) {
                                        console.log('📡 Subscribing to existing screen share track from:', participant.identity);
                                        pub.setSubscribed(true);
                                    }
                                    else if (room.localParticipant) {
                                        // Fallback: try to subscribe via room
                                        console.log('📡 Attempting to subscribe to existing screen share via room:', participant.identity);
                                        room.localParticipant.subscribe(pub).catch((err) => {
                                            console.warn('⚠️ Could not subscribe to existing screen share track:', err);
                                        });
                                    }
                                }
                            }
                            catch (error) {
                                console.error('❌ Error subscribing to existing screen share track:', error);
                            }
                        }
                    });
                }
            });
            return () => {
                room.off('trackPublished', handleTrackPublished);
                room.off('trackSubscribed', handleTrackSubscribed);
            };
        }
    }, [room, onRoom, serverUrl, token]);
    return null;
};
// Media enablement component that runs inside LiveKitRoom context
const MediaEnabler = ({ initialCameraEnabled, initialMicEnabled, initialBackgroundEffect, initialMediaStream, selectedAudioDevice, selectedVideoDevice, onCameraEnabled, onMicEnabled, onCurrentEffect, isConnected }) => {
    const room = useRoomContext();
    const [hasRun, setHasRun] = useState(false);
    const lastConnectedStateRef = useRef(false);
    // CRITICAL: DO NOT reset MediaEnabler after reconnection
    // LiveKit's republishOnReconnect: true handles track republishing automatically
    // Resetting MediaEnabler would create NEW tracks, losing the background processor
    // and causing the video to freeze/flicker
    useEffect(() => {
        if (isConnected && !lastConnectedStateRef.current && hasRun) {
            // Connection restored - LiveKit will automatically republish existing tracks
            console.log('✅ Connection restored - LiveKit will auto-republish existing tracks (republishOnReconnect: true)');
            console.log('ℹ️ NOT resetting MediaEnabler to preserve background processor and avoid video freeze');
        }
        lastConnectedStateRef.current = isConnected;
    }, [isConnected, hasRun]);
    useEffect(() => {
        if (!room || !room.localParticipant || hasRun)
            return;
        // CRITICAL: If we are already connected and have tracks, don't re-enable
        // This prevents MediaEnabler from creating duplicate tracks during reconnection
        // During shard migrations, tracks may be temporarily unpublished but will auto-republish
        const existingCameraTrack = room.localParticipant.getTrackPublication(Track.Source.Camera);
        if (existingCameraTrack && isConnected) {
            console.log('📹 MediaEnabler: Camera already published, skipping init (reconnection)');
            setHasRun(true);
            return;
        }
        const enableMedia = async () => {
            try {
                // Ensure devices are available and valid
                let validVideoDevice = selectedVideoDevice;
                let validAudioDevice = selectedAudioDevice;
                try {
                    const devices = await navigator.mediaDevices.enumerateDevices();
                    const audioInputs = devices.filter(device => device.kind === 'audioinput');
                    const videoInputs = devices.filter(device => device.kind === 'videoinput');
                    console.log('🔍 MediaEnabler: Available devices:', {
                        audioDevices: audioInputs.map(d => ({ id: d.deviceId, label: d.label })),
                        videoDevices: videoInputs.map(d => ({ id: d.deviceId, label: d.label })),
                        requestedAudio: selectedAudioDevice,
                        requestedVideo: selectedVideoDevice
                    });
                    // Validate selected devices exist
                    const audioDeviceExists = selectedAudioDevice && audioInputs.some(d => d.deviceId === selectedAudioDevice);
                    const videoDeviceExists = selectedVideoDevice && videoInputs.some(d => d.deviceId === selectedVideoDevice);
                    if (!audioDeviceExists) {
                        console.warn('⚠️ Selected audio device not found, using default');
                        validAudioDevice = audioInputs.length > 0 ? audioInputs[0].deviceId : '';
                    }
                    if (!videoDeviceExists) {
                        console.warn('⚠️ Selected video device not found, using default');
                        validVideoDevice = videoInputs.length > 0 ? videoInputs[0].deviceId : '';
                    }
                    console.log('✅ MediaEnabler: Using devices:', {
                        audio: validAudioDevice,
                        video: validVideoDevice
                    });
                }
                catch (enumError) {
                    console.warn('⚠️ Could not enumerate devices:', enumError);
                }
                console.log('🎬 MediaEnabler: Starting media enable with prejoin stream...');
                console.log('Initial settings:', {
                    cameraEnabled: initialCameraEnabled,
                    micEnabled: initialMicEnabled,
                    background: initialBackgroundEffect,
                    hasMediaStream: !!initialMediaStream,
                    videoTracks: initialMediaStream?.getVideoTracks().length || 0,
                    audioTracks: initialMediaStream?.getAudioTracks().length || 0,
                    selectedVideoDevice,
                    selectedAudioDevice,
                    validVideoDevice,
                    validAudioDevice
                });
                // ULTIMATE OPTIMIZATION: Check for pre-created LiveKit track FIRST!
                const preInitProcessor = window.__preInitializedProcessor;
                console.log('🔍 MediaEnabler: Checking for pre-created track:', {
                    hasPreInitProcessor: !!preInitProcessor,
                    applied: preInitProcessor?.applied,
                    hasTrack: !!preInitProcessor?.track,
                    trackType: preInitProcessor?.type,
                    initialCameraEnabled,
                    windowKeys: Object.keys(window).filter(k => k.includes('preInit'))
                });
                if (preInitProcessor && preInitProcessor.applied && preInitProcessor.track && initialCameraEnabled !== false) {
                    console.log('⚡⚡⚡ ULTIMATE OPTIMIZATION: Using pre-created LiveKit track with background!');
                    // CRITICAL: Verify processor is on track BEFORE publishing
                    const trackHasProcessor = preInitProcessor.track.processor;
                    console.log('Track details BEFORE publish:', {
                        trackId: preInitProcessor.track.sid,
                        trackKind: preInitProcessor.track.kind,
                        trackSource: preInitProcessor.track.source,
                        isMuted: preInitProcessor.track.isMuted,
                        hasProcessor: !!preInitProcessor.processor,
                        processorAttachedToTrack: !!trackHasProcessor
                    });
                    // If processor is not attached to track, attach it now
                    if (!trackHasProcessor && preInitProcessor.processor) {
                        console.log('⚠️ Processor not attached to track, attaching now...');
                        try {
                            await preInitProcessor.track.setProcessor(preInitProcessor.processor);
                            console.log('✅ Processor attached to track before publishing');
                        }
                        catch (e) {
                            console.error('❌ Failed to attach processor before publishing:', e, {
                                errorName: e.name,
                                errorMessage: e.message,
                                trackStopped: preInitProcessor.track.isMuted,
                                trackState: preInitProcessor.track.mediaStreamTrack?.readyState,
                                processorType: preInitProcessor.type
                            });
                            // If we can't attach the processor, don't publish this track
                            // Fall through to normal flow instead
                            console.warn('⚠️ Skipping pre-created track due to processor attachment failure');
                            delete window.__preInitializedProcessor;
                            throw new Error('Processor attachment failed');
                        }
                    }
                    try {
                        // CRITICAL: Publish with options to preserve the processor!
                        const publication = await room.localParticipant.publishTrack(preInitProcessor.track, {
                            name: 'camera',
                            source: Track.Source.Camera,
                            // Don't let LiveKit modify the track - keep our processor!
                            simulcast: false,
                            videoCodec: 'vp9' // Using VP9 (same as Google Meet) for better compression
                        });
                        onCameraEnabled(true);
                        console.log('✅ Published pre-created LiveKit track with background!', {
                            publicationSid: publication.trackSid,
                            trackName: publication.trackName
                        });
                        // CRITICAL: Preserve mic state from prejoin screen
                        await room.localParticipant.setMicrophoneEnabled(initialMicEnabled !== false);
                        onMicEnabled(initialMicEnabled !== false);
                        console.log(`✅ Mic ${initialMicEnabled !== false ? 'enabled' : 'disabled'} (preserved from prejoin)`);
                        // Sync the UI state
                        onCurrentEffect(preInitProcessor.type === 'blur' ? 'blur' : 'image', preInitProcessor.type === 'image' ? preInitProcessor.imageUrl : undefined);
                        // Clear the processor from window
                        delete window.__preInitializedProcessor;
                        console.log('🎉 INSTANT MEETING JOIN WITH BACKGROUND - ZERO DELAY!');
                        setHasRun(true);
                        return; // Done! Exit early
                    }
                    catch (error) {
                        console.error('❌ Error publishing pre-created track:', error, {
                            errorName: error.name,
                            errorMessage: error.message,
                            errorStack: error.stack
                        });
                        delete window.__preInitializedProcessor;
                        // Fall through to normal flow
                    }
                }
                else {
                    console.log('⚠️ MediaEnabler: Pre-created track not available or not ready:', {
                        reason: !preInitProcessor ? 'No preInitProcessor' :
                            !preInitProcessor.applied ? 'Not applied' :
                                !preInitProcessor.track ? 'No track' :
                                    initialCameraEnabled === false ? 'Camera disabled' :
                                        'Unknown'
                    });
                }
                // Fallback: Normal flow if no pre-created track
                if (initialMediaStream) {
                    console.log('📹 Using prejoin media stream for faster startup...');
                    const shouldEnableCamera = initialCameraEnabled !== false;
                    if (shouldEnableCamera && initialBackgroundEffect && initialBackgroundEffect !== 'none') {
                        try {
                            console.log('🎯 Creating processed track before publishing to avoid flicker...');
                            const { createLocalVideoTrack } = await import('livekit-client');
                            const videoTrack = await createLocalVideoTrack({
                                resolution: { width: 1280, height: 720 },
                                facingMode: 'user',
                                deviceId: validVideoDevice ? { exact: validVideoDevice } : undefined
                            });
                            if (initialBackgroundEffect === 'blur') {
                                await ensureBackgroundProcessor(videoTrack, 'blur');
                                onCurrentEffect('blur');
                                console.log('✅ Blur applied before publishing track.');
                            }
                            else {
                                await ensureBackgroundProcessor(videoTrack, 'image', initialBackgroundEffect);
                                onCurrentEffect('image', initialBackgroundEffect);
                                console.log('✅ Virtual background applied before publishing track.');
                            }
                            await room.localParticipant.publishTrack(videoTrack, {
                                name: 'camera',
                                source: Track.Source.Camera,
                                simulcast: false,
                                videoCodec: 'vp9' // Using VP9 (same as Google Meet) for better compression
                            });
                            onCameraEnabled(true);
                            console.log('✅ Published processed track (camera enabled) without interim flicker.');
                        }
                        catch (trackError) {
                            console.error('❌ Failed to pre-process track, falling back to LiveKit enable flow:', trackError);
                            await room.localParticipant.setCameraEnabled(true);
                            onCameraEnabled(true);
                            console.log('✅ Camera enabled via LiveKit fallback.');
                        }
                    }
                    else {
                        await room.localParticipant.setCameraEnabled(shouldEnableCamera);
                        onCameraEnabled(shouldEnableCamera);
                        console.log(`✅ Camera ${shouldEnableCamera ? 'enabled' : 'disabled'} (preserved from prejoin)`);
                    }
                    // CRITICAL: Preserve mic state from prejoin screen
                    const shouldEnableMic = initialMicEnabled !== false;
                    // CRITICAL: Set microphone state correctly based on prejoin preference
                    if (validAudioDevice) {
                        if (shouldEnableMic) {
                            // Enable mic with specific device
                            await room.localParticipant.setMicrophoneEnabled(true, { deviceId: validAudioDevice });
                            console.log(`✅ Mic enabled with device ${validAudioDevice} (preserved from prejoin)`);
                        }
                        else {
                            // Ensure mic is disabled if user muted in prejoin
                            await room.localParticipant.setMicrophoneEnabled(false);
                            console.log(`✅ Mic disabled (preserved from prejoin)`);
                        }
                    }
                    else {
                        await room.localParticipant.setMicrophoneEnabled(shouldEnableMic);
                        console.log(`✅ Mic ${shouldEnableMic ? 'enabled' : 'disabled'} (preserved from prejoin)`);
                    }
                    onMicEnabled(shouldEnableMic);
                    // Apply background if needed (fallback)
                    if (initialBackgroundEffect && initialBackgroundEffect !== 'none' && initialCameraEnabled !== false) {
                        const preInitProcessor = window.__preInitializedProcessor;
                        if (preInitProcessor) {
                            console.log('⚡ INSTANT BACKGROUND: Using pre-initialized processor from prejoin!');
                            const applyPreInitProcessor = async () => {
                                for (let attempt = 0; attempt < 50; attempt++) {
                                    const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
                                    if (publication && publication.track) {
                                        const track = publication.track;
                                        console.log(`✅ Track ready on attempt ${attempt + 1}, applying PRE-INITIALIZED background...`);
                                        try {
                                            const startTime = performance.now();
                                            // Use the pre-initialized processor directly!
                                            await track.setProcessor(preInitProcessor.processor);
                                            onCurrentEffect(preInitProcessor.type === 'blur' ? 'blur' : 'image', preInitProcessor.type === 'image' ? preInitProcessor.imageUrl : undefined);
                                            const duration = ((performance.now() - startTime) / 1000).toFixed(2);
                                            console.log(`⚡ INSTANT BACKGROUND APPLIED in ${duration}s (ML model was pre-loaded)!`);
                                            // Clear the processor from window
                                            delete window.__preInitializedProcessor;
                                            return;
                                        }
                                        catch (bgError) {
                                            console.error('❌ Error applying pre-initialized background:', bgError);
                                            // Fall back to normal initialization
                                            delete window.__preInitializedProcessor;
                                            break;
                                        }
                                    }
                                    await new Promise(resolve => setTimeout(resolve, 30));
                                }
                            };
                            applyPreInitProcessor();
                        }
                        else {
                            // No pre-initialized processor, use normal method
                            console.log('🎨 Applying background from prejoin selection (ML model will take 3-5s to load)...');
                            const applyBackgroundNow = async () => {
                                for (let attempt = 0; attempt < 50; attempt++) {
                                    const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
                                    if (publication && publication.track) {
                                        const track = publication.track;
                                        console.log(`✅ Track ready on attempt ${attempt + 1}, applying background NOW...`);
                                        try {
                                            const startTime = performance.now();
                                            if (initialBackgroundEffect === 'blur') {
                                                await ensureBackgroundProcessor(track, 'blur');
                                                onCurrentEffect('blur');
                                                const duration = ((performance.now() - startTime) / 1000).toFixed(2);
                                                console.log(`✅ BLUR APPLIED FROM PREJOIN in ${duration}s!`);
                                            }
                                            else {
                                                await ensureBackgroundProcessor(track, 'image', initialBackgroundEffect);
                                                onCurrentEffect('image', initialBackgroundEffect);
                                                const duration = ((performance.now() - startTime) / 1000).toFixed(2);
                                                console.log(`✅ BACKGROUND APPLIED FROM PREJOIN in ${duration}s!`);
                                            }
                                            return;
                                        }
                                        catch (bgError) {
                                            console.error('❌ Error applying background:', bgError);
                                            return;
                                        }
                                    }
                                    await new Promise(resolve => setTimeout(resolve, 30));
                                }
                                console.error('❌ Failed to apply background after 50 attempts');
                            };
                            applyBackgroundNow();
                        }
                    }
                }
                else {
                    // Fallback: No prejoin stream, use LiveKit's built-in methods
                    console.log('⚠️ No prejoin stream, using LiveKit built-in methods...');
                    // CRITICAL: Preserve camera state from prejoin screen
                    await room.localParticipant.setCameraEnabled(initialCameraEnabled !== false);
                    onCameraEnabled(initialCameraEnabled !== false);
                    console.log(`✅ Camera ${initialCameraEnabled !== false ? 'enabled' : 'disabled'} (preserved from prejoin)`);
                    // CRITICAL: Preserve mic state from prejoin screen
                    const shouldEnableMic = initialMicEnabled !== false;
                    // CRITICAL: Set microphone state correctly based on prejoin preference
                    if (validAudioDevice) {
                        if (shouldEnableMic) {
                            // Enable mic with specific device
                            await room.localParticipant.setMicrophoneEnabled(true, { deviceId: validAudioDevice });
                            console.log(`✅ Mic enabled with device ${validAudioDevice} (preserved from prejoin)`);
                        }
                        else {
                            // Ensure mic is disabled if user muted in prejoin
                            await room.localParticipant.setMicrophoneEnabled(false);
                            console.log(`✅ Mic disabled (preserved from prejoin)`);
                        }
                    }
                    else {
                        await room.localParticipant.setMicrophoneEnabled(shouldEnableMic);
                        console.log(`✅ Mic ${shouldEnableMic ? 'enabled' : 'disabled'} (preserved from prejoin)`);
                    }
                    onMicEnabled(shouldEnableMic);
                    // Apply background after track is available
                    if (initialBackgroundEffect && initialBackgroundEffect !== 'none' && initialCameraEnabled !== false) {
                        console.log('🎨 Applying background to new track...');
                        const applyBackgroundImmediately = async () => {
                            for (let attempt = 0; attempt < 40; attempt++) {
                                const publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
                                if (publication && publication.track) {
                                    const track = publication.track;
                                    console.log(`✅ Track found on attempt ${attempt + 1}, applying background...`);
                                    try {
                                        if (initialBackgroundEffect === 'blur') {
                                            await ensureBackgroundProcessor(track, 'blur');
                                            onCurrentEffect('blur');
                                            console.log('✅ BLUR APPLIED!');
                                        }
                                        else {
                                            await ensureBackgroundProcessor(track, 'image', initialBackgroundEffect);
                                            onCurrentEffect('image', initialBackgroundEffect);
                                            console.log('✅ BACKGROUND APPLIED!');
                                        }
                                        return;
                                    }
                                    catch (bgError) {
                                        console.error('❌ Error applying background:', bgError);
                                        return;
                                    }
                                }
                                await new Promise(resolve => setTimeout(resolve, 50));
                            }
                            console.error('❌ Failed to apply background after 40 attempts');
                        };
                        applyBackgroundImmediately();
                    }
                }
                setHasRun(true);
            }
            catch (error) {
                console.error('❌ Error in MediaEnabler:', error);
            }
        };
        enableMedia();
    }, [room, hasRun, initialCameraEnabled, initialMicEnabled, initialBackgroundEffect, initialMediaStream, selectedAudioDevice, selectedVideoDevice, onCameraEnabled, onMicEnabled, onCurrentEffect, isConnected]);
    return null;
};
// Chess component wrapper that uses room context from LiveKit
const ChessInMeeting = (props) => {
    const room = useRoomContext();
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    // Validate required props
    if (!props.subscriptionId || !props.subscriptionName || !props.participantName) {
        console.error('⚠️ Missing required props for ChessInMeeting:', {
            subscriptionId: props.subscriptionId,
            subscriptionName: props.subscriptionName,
            participantName: props.participantName,
            userRole: props.userRole
        });
        return null;
    }
    // Validate room context
    if (!room) {
        console.warn('⚠️ Room context not available for chess');
        return null;
    }
    // Validate room state
    if (!localParticipant) {
        console.warn('⚠️ Local participant not ready');
        return null;
    }
    console.log('♟️ Initializing ChessInMeeting:', {
        userRole: props.userRole,
        participantName: props.participantName,
        roomName: room.name,
        roomState: room.state,
        hasLocalParticipant: !!localParticipant,
        participantCount: participants.length,
        localParticipantId: localParticipant.identity,
        localParticipantMetadata: localParticipant.metadata,
        participants: participants.map(p => p.identity)
    });
    try {
        // Get Chess component from SDK config
        const sdkConfig = getSDKConfig();
        const ChessComp = sdkConfig.ChessComponent;
        if (!ChessComp) {
            console.warn('⚠️ Chess component not configured in SDK');
            return null;
        }
        const normalizedRole = props.userRole === 'teacher'
            ? 'teacher'
            : props.userRole === 'student'
                ? 'student'
                : undefined;
        return (jsx(ChessComp, { ...props, isInMeeting: true, room: room, disableTeachingMode: true, userRole: normalizedRole, serviceProviderId: props.serviceProviderId }));
    }
    catch (error) {
        console.error('❌ Error rendering ChessInMeeting:', error);
        return (jsxs("div", { className: "p-4 bg-red-100 text-red-700 rounded-lg", children: [jsx("p", { className: "font-semibold", children: "Error loading chess component" }), jsx("p", { className: "text-sm opacity-75", children: "Please try refreshing the page" })] }));
    }
};
// Polls component wrapper
const PollsInMeeting = (props) => {
    const room = useRoomContext();
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    if (!room) {
        console.warn('⚠️ Room context not available for polls');
        return null;
    }
    console.log('📊 Initializing PollsQuizzes with room:', {
        roomName: room.name,
        roomState: room.state,
        hasLocalParticipant: !!localParticipant,
        participantCount: participants.length
    });
    // Get Polls component from SDK config
    const sdkConfig = getSDKConfig();
    const PollsComp = sdkConfig.PollsComponent;
    if (!PollsComp) {
        console.warn('⚠️ Polls component not configured in SDK');
        return null;
    }
    return (jsx(PollsComp, { ...props, isInMeeting: true, room: room, serviceProviderId: props.tenantId }));
};
// Activity Notification Listener Component
const ActivityNotificationListener = ({ userRole, onPuzzleShared, onPollStarted, onChatMessage, onShowChessBoard, onShowPollsQuizzes, onDisconnect, stopRecordingIfActive, setIntentionalDisconnect, onRaiseHand, onAcknowledgeHand, onMicStateChange }) => {
    const room = useRoomContext();
    const participants = useParticipants();
    // Log initial component state
    useEffect(() => {
        console.log('🏁 ActivityNotificationListener initial state:', {
            userRole,
            hasRoom: !!room,
            roomName: room?.name || 'not connected',
            roomState: room?.state || 'not connected',
            participantCount: participants.length
        });
    }, [room, userRole, participants.length]);
    useEffect(() => {
        if (!room)
            return;
        const handleData = (data) => {
            try {
                console.log('📨 Raw data received:', {
                    dataType: typeof data,
                    hasPayload: !!data.payload,
                    payloadType: typeof data.payload,
                    isArray: Array.isArray(data.payload),
                    isUint8: data.payload instanceof Uint8Array,
                    fullData: data
                });
                let message;
                // Handle different data formats
                console.log('🔍 Processing data:', {
                    dataKeys: Object.keys(data),
                    dataValues: Object.values(data),
                    dataEntries: Object.entries(data)
                });
                // Try to extract the actual payload from LiveKit's data event
                // The data might be in data.data, data.message, or just data itself
                const rawPayload = data.data || data.message || data.payload || data;
                console.log('🔍 Extracted raw payload:', {
                    rawPayload,
                    rawPayloadType: typeof rawPayload,
                    isUint8: rawPayload instanceof Uint8Array,
                    isArray: Array.isArray(rawPayload),
                    length: rawPayload?.length
                });
                if (rawPayload instanceof Uint8Array || Array.isArray(rawPayload)) {
                    // Raw Uint8Array or Array
                    if (rawPayload.length === 0) {
                        console.warn('⚠️ Empty data received, skipping');
                        return;
                    }
                    const decoder = new TextDecoder();
                    const payload = rawPayload instanceof Uint8Array ? rawPayload : new Uint8Array(rawPayload);
                    const jsonString = decoder.decode(payload);
                    console.log('🔍 Decoded JSON string:', jsonString);
                    message = JSON.parse(jsonString);
                }
                else if (typeof rawPayload === 'string') {
                    // Already a string
                    if (rawPayload.length === 0) {
                        console.warn('⚠️ Empty string payload, skipping');
                        return;
                    }
                    console.log('🔍 Parsing JSON string:', rawPayload);
                    message = JSON.parse(rawPayload);
                }
                else {
                    // Already parsed or unknown format
                    console.log('🔍 Using raw payload as message:', rawPayload);
                    message = rawPayload;
                }
                console.log('📨 Received data:', {
                    type: message.type,
                    action: message.action,
                    sender: message.name || message.playerName,
                    timestamp: new Date().toISOString(),
                    fullMessage: message
                });
                // Handle meeting control messages (type: 'meeting' with action)
                if (message.type === 'meeting' && message.action === 'END_MEETING') {
                    console.log('🔴 End meeting message received:', message);
                    // Removed toast - no toasts in LiveKit room except Chequered
                    // CRITICAL: Mark as intentional disconnect BEFORE disconnecting
                    // This ensures onDisconnected handler treats it as intentional (no errors)
                    if (setIntentionalDisconnect) {
                        setIntentionalDisconnect(true);
                        console.log('✅ Marked disconnect as intentional for END_MEETING');
                    }
                    // Disconnect all participants after a short delay to show the message
                    setTimeout(async () => {
                        try {
                            // CRITICAL: Stop recording if active (for students who might be recording locally)
                            if (stopRecordingIfActive) {
                                try {
                                    console.log('🛑 Stopping recording due to END_MEETING message...');
                                    await stopRecordingIfActive();
                                    console.log('✅ Recording stopped due to END_MEETING');
                                }
                                catch (recordingError) {
                                    console.warn('⚠️ Error stopping recording on END_MEETING:', recordingError);
                                    // Continue with disconnect even if recording stop fails
                                }
                            }
                            // CRITICAL: Stop camera and mic tracks to clear browser indicators
                            if (room?.localParticipant) {
                                try {
                                    // Stop camera track
                                    const videoTrack = room.localParticipant.getTrackPublication(Track.Source.Camera)?.track;
                                    if (videoTrack) {
                                        try {
                                            await videoTrack.stop();
                                            console.log('📹 Camera track stopped (END_MEETING)');
                                        }
                                        catch (error) {
                                            console.warn('⚠️ Error stopping camera track:', error);
                                        }
                                    }
                                    // Stop microphone track
                                    const audioTrack = room.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
                                    if (audioTrack) {
                                        try {
                                            await audioTrack.stop();
                                            console.log('🎤 Microphone track stopped (END_MEETING)');
                                        }
                                        catch (error) {
                                            console.warn('⚠️ Error stopping microphone track:', error);
                                        }
                                    }
                                    // CRITICAL: Stop screen share track (if any) - must use setScreenShareEnabled to clear browser indicator
                                    const screenPub = room.localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                    if (screenPub?.track) {
                                        try {
                                            console.log('🖥️ Stopping screen share via setScreenShareEnabled to clear browser indicator (END_MEETING)...');
                                            // Step 1: Stop via LiveKit API to clear browser indicator
                                            await room.localParticipant.setScreenShareEnabled(false);
                                            // Step 2: Stop the underlying MediaStream track directly
                                            const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                            if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                                console.log('🛑 Stopping underlying MediaStream track to clear browser indicators (END_MEETING)...');
                                                mediaStreamTrack.stop();
                                                console.log('✅ MediaStream track stopped (END_MEETING)');
                                            }
                                            // Step 3: Also stop the LiveKit track if it's still active
                                            if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                                                try {
                                                    await screenPub.track.stop();
                                                    console.log('✅ LiveKit screen share track stopped (END_MEETING)');
                                                }
                                                catch (trackError) {
                                                    console.warn('⚠️ Error stopping LiveKit track (END_MEETING):', trackError);
                                                }
                                            }
                                            console.log('✅ Screen share stopped and browser indicator should be cleared (END_MEETING)');
                                        }
                                        catch (error) {
                                            console.warn('⚠️ Error stopping screen share track (END_MEETING):', error);
                                            // Fallback: try to stop the track directly
                                            try {
                                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                                if (mediaStreamTrack) {
                                                    mediaStreamTrack.stop();
                                                }
                                                await screenPub.track.stop();
                                            }
                                            catch (fallbackError) {
                                                console.warn('⚠️ Error in fallback screen share stop (END_MEETING):', fallbackError);
                                            }
                                        }
                                    }
                                    // CRITICAL: Also find and stop any remaining screen share MediaStreams
                                    // This catches any streams that might not be tracked by LiveKit
                                    try {
                                        const allVideoElements = document.querySelectorAll('video');
                                        allVideoElements.forEach(video => {
                                            if (video.srcObject && video.srcObject instanceof MediaStream) {
                                                const stream = video.srcObject;
                                                stream.getVideoTracks().forEach(track => {
                                                    const label = track.label.toLowerCase();
                                                    if (track.kind === 'video' && (label.includes('screen') ||
                                                        label.includes('window') ||
                                                        label.includes('display') ||
                                                        label.includes('monitor'))) {
                                                        console.log('🛑 Stopping screen share MediaStream from video element (END_MEETING):', track.label);
                                                        try {
                                                            track.stop();
                                                        }
                                                        catch (e) {
                                                            console.warn('⚠️ Error stopping track from video element (END_MEETING):', e);
                                                        }
                                                    }
                                                });
                                            }
                                        });
                                    }
                                    catch (screenShareStreamError) {
                                        console.warn('⚠️ Error stopping screen share streams (END_MEETING):', screenShareStreamError);
                                    }
                                }
                                catch (trackError) {
                                    console.warn('⚠️ Error stopping tracks in END_MEETING:', trackError);
                                }
                                // Also stop underlying MediaStream tracks
                                try {
                                    const allVideoElements = document.querySelectorAll('video');
                                    allVideoElements.forEach(video => {
                                        if (video.srcObject && video.srcObject instanceof MediaStream) {
                                            const stream = video.srcObject;
                                            stream.getTracks().forEach(track => {
                                                console.log(`🛑 Stopping ${track.kind} track from video element (END_MEETING):`, track.label);
                                                try {
                                                    track.stop();
                                                }
                                                catch (error) {
                                                    console.warn(`⚠️ Error stopping ${track.kind} track:`, error);
                                                }
                                            });
                                            video.srcObject = null;
                                        }
                                    });
                                    const allAudioElements = document.querySelectorAll('audio');
                                    allAudioElements.forEach(audio => {
                                        if (audio.srcObject && audio.srcObject instanceof MediaStream) {
                                            const stream = audio.srcObject;
                                            stream.getTracks().forEach(track => {
                                                console.log(`🛑 Stopping ${track.kind} track from audio element (END_MEETING):`, track.label);
                                                try {
                                                    track.stop();
                                                }
                                                catch (error) {
                                                    console.warn(`⚠️ Error stopping ${track.kind} track:`, error);
                                                }
                                            });
                                            audio.srcObject = null;
                                        }
                                    });
                                }
                                catch (mediaError) {
                                    console.warn('⚠️ Error stopping media tracks:', mediaError);
                                }
                            }
                            // Trigger the leave/disconnect handler
                            if (room && room.state !== ConnectionState.Disconnected) {
                                console.log('🔌 Disconnecting due to end meeting message...');
                                await room.disconnect();
                                // CRITICAL: Call onDisconnect to clean up and exit meeting
                                // DO NOT reload page as it causes auto-rejoin for guests
                                if (onDisconnect) {
                                    console.log('🔴 Calling onDisconnect after END_MEETING (intentional disconnect)');
                                    onDisconnect(true); // Pass true to indicate intentional disconnect
                                }
                                else {
                                    console.error('❌ onDisconnect callback not provided to ActivityNotificationListener');
                                }
                            }
                        }
                        catch (error) {
                            console.error('❌ Error disconnecting from end meeting message:', error);
                            // Call onDisconnect anyway to exit meeting
                            if (onDisconnect) {
                                console.log('🔴 Calling onDisconnect after END_MEETING error (intentional disconnect)');
                                onDisconnect(true); // Pass true to indicate intentional disconnect
                            }
                            else {
                                console.error('❌ onDisconnect callback not provided to ActivityNotificationListener');
                            }
                        }
                    }, 2000);
                    return; // Don't process further
                }
                // Handle poll messages (type: 'poll' with action)
                if (message.type === 'poll' && message.action === 'POLL_STARTED') {
                    console.log('📊 Poll started:', message);
                    // Both teachers and students receive poll messages
                    if (onShowPollsQuizzes) {
                        onShowPollsQuizzes(true);
                    }
                    if (onPollStarted) {
                        onPollStarted({
                            ...message,
                            isTeacherView: userRole === 'teacher'
                        });
                    }
                    // Removed non-critical toast - UI shows poll panel
                }
                switch (message.type) {
                    case 'chess':
                        // Handle chess/puzzle messages
                        if (message.action === 'PUZZLE_SHARED') {
                            console.log('♟️ Puzzle shared:', message);
                            // Both students/guests and teacher receive puzzle
                            if (onShowChessBoard) {
                                onShowChessBoard(true);
                            }
                            if (onPuzzleShared) {
                                onPuzzleShared({
                                    ...message,
                                    isTeacherView: userRole === 'teacher',
                                    isGuest: userRole === 'student' && message.metadata?.isGuest
                                });
                            }
                            if (userRole === 'student') {
                                // Removed non-critical toast - UI shows chess panel
                                console.log('🎯 Opening chess board for student/guest');
                            }
                        }
                        else if (message.action === 'BOARD_VIEW_SHARED') {
                            console.log('📺 Board view shared:', message);
                            if (onShowChessBoard) {
                                onShowChessBoard(true);
                            }
                            // Removed non-critical toast - UI shows board view
                        }
                        else if (message.action === 'PUZZLE_STOPPED' || message.action === 'BOARD_VIEW_STOPPED') {
                            console.log('♟️ Puzzle stopped');
                            if (onShowChessBoard) {
                                onShowChessBoard(false);
                            }
                            // Removed non-critical toast - UI reflects state change
                        }
                        else if (message.action === 'STUDENT_MOVE') {
                            console.log('♟️ Student move:', message);
                            // Teacher sees moves
                            if (userRole === 'teacher') {
                                if (onPuzzleShared) {
                                    onPuzzleShared({
                                        ...message,
                                        isTeacherView: true
                                    });
                                }
                                // Removed non-critical toast - moves shown in UI
                            }
                        }
                        else if (message.action === 'PUZZLE_COMPLETED') {
                            console.log('♟️ Puzzle completed:', message);
                            // Teacher sees completion
                            if (userRole === 'teacher') {
                                if (onPuzzleShared) {
                                    onPuzzleShared({
                                        ...message,
                                        isTeacherView: true
                                    });
                                }
                                // Removed non-critical toast - completion shown in UI
                            }
                        }
                        break;
                    case 'puzzle_shared':
                        // Legacy support
                        console.log('♟️ Legacy puzzle shared:', message);
                        onPuzzleShared({
                            ...message,
                            isTeacherView: userRole === 'teacher'
                        });
                        break;
                    case 'chat_message':
                        console.log('💬 Chat message received by ActivityNotificationListener:', {
                            messageType: message.type,
                            from: message.name,
                            messageText: message.message,
                            isTeacher: message.isTeacher,
                            userRole,
                            hasOnChatMessage: !!onChatMessage
                        });
                        // Everyone receives chat messages
                        if (onChatMessage) {
                            console.log('📞 Calling onChatMessage callback');
                            onChatMessage(message);
                        }
                        else {
                            console.error('❌ onChatMessage callback is not defined!');
                        }
                        break;
                    case 'raise_hand':
                        console.log('✋ Raise hand message received:', message);
                        if (onRaiseHand && message.participantId && message.participantName) {
                            onRaiseHand(message.participantId, message.participantName, message.isRaised || false);
                        }
                        break;
                    case 'acknowledge_hand':
                        console.log('✅ Hand acknowledged:', message);
                        if (onAcknowledgeHand && message.participantId) {
                            onAcknowledgeHand(message.participantId);
                        }
                        break;
                    case 'mute_request':
                        console.log('🎤 Mute request received:', message);
                        // Only act if this message is for the current participant
                        if (message.targetParticipantId && room?.localParticipant?.identity === message.targetParticipantId) {
                            const shouldMute = message.muted !== false; // Default to mute if not specified
                            const targetMicState = !shouldMute; // Enable mic if shouldMute is false, disable if true
                            console.log(`🎤 Acting on mute request: ${shouldMute ? 'mute' : 'unmute'}`);
                            // Mute/unmute self
                            if (room?.localParticipant) {
                                room.localParticipant.setMicrophoneEnabled(targetMicState).then(() => {
                                    console.log(`✅ Successfully ${shouldMute ? 'muted' : 'unmuted'} microphone as requested by teacher`);
                                    // Sync UI state with actual microphone state
                                    if (onMicStateChange) {
                                        onMicStateChange(targetMicState);
                                    }
                                }).catch((error) => {
                                    console.error('❌ Error muting/unmuting microphone:', error);
                                });
                            }
                        }
                        break;
                    default:
                        console.log('❓ Unknown message type:', message.type);
                }
            }
            catch (error) {
                console.error('Error parsing data message:', error);
            }
        };
        room.on('dataReceived', handleData);
        return () => {
            room.off('dataReceived', handleData);
        };
    }, [room, userRole, onPuzzleShared, onPollStarted, onChatMessage, onShowChessBoard, onShowPollsQuizzes, onDisconnect, setIntentionalDisconnect, stopRecordingIfActive, onRaiseHand, onAcknowledgeHand, onMicStateChange]);
    return null;
};
// Participant count tracker - updates ref with current participant count
const ParticipantCountTracker = ({ onCountChange }) => {
    const participants = useParticipants();
    useEffect(() => {
        onCountChange(participants.length);
    }, [participants.length, onCountChange]);
    return null;
};
const LiveKitRoomComponent = ({ roomName, meetingTitle, token: initialToken, serverUrl, apiEndpoint, participantName, userRole = 'student', tenantId, subscriptionId = '', subscriptionName = '', onDisconnect, onError, initialMicEnabled, initialCameraEnabled, initialBackgroundEffect, initialMediaStream, initialAudioDevice, initialVideoDevice }) => {
    // --- Meeting SDK Integration ---
    const meetingConfig = useMemo(() => ({
        tenantId,
        userId: participantName, // Assuming participantName is the userId
        userRole,
        livekitServerUrl: serverUrl,
        apiEndpoint,
        participantName,
        metadata: {
            meetingTitle,
            roomName,
            subscriptionId,
            subscriptionName,
            userRole,
        },
    }), [tenantId, participantName, userRole, serverUrl, meetingTitle, roomName, subscriptionId, subscriptionName, apiEndpoint]);
    const { meetingSDK, room, generateToken } = useMeeting(meetingConfig, {
        roomName,
        token: typeof initialToken === 'string' ? initialToken : initialToken.token,
        enableCamera: initialCameraEnabled,
        enableMicrophone: initialMicEnabled,
        onConnected: (connectedRoom) => {
            // Existing onConnected logic can be added here if needed
        },
        onDisconnected: (reason) => {
            // Existing onDisconnected logic can be added here if needed
            onDisconnect(reason === 'intentional');
        },
        onError: (err) => {
            // Existing onError logic can be added here if needed
            onError?.(err);
        },
    });
    // Prepare connection before mounting to preselect region and prevent region hopping
    useEffect(() => {
        const token = typeof initialToken === 'string' ? initialToken : initialToken.token;
        if (!serverUrl || !token) {
            return;
        }
        // Prepare connection on the room from useMeeting if it exists
        // This preselects the region before the actual connection happens
        if (room && typeof room.prepareConnection === 'function') {
            try {
                room.prepareConnection(serverUrl, token);
                console.log('✅ Prepared connection on useMeeting room instance for region preselection:', serverUrl);
            }
            catch (error) {
                console.warn('⚠️ Failed to prepare connection on useMeeting room:', error);
            }
        }
    }, [room, serverUrl, initialToken]);
    // Token refresh mechanism
    useEffect(() => {
        if (!room || !initialToken || typeof initialToken === 'string' || !initialToken.expiresAt) {
            return;
        }
        const expiresInMs = initialToken.expiresAt - Date.now();
        const refreshThresholdMs = 5 * 60 * 1000; // Refresh 5 minutes before expiration
        let refreshTimer;
        if (expiresInMs > refreshThresholdMs) {
            refreshTimer = setTimeout(async () => {
                console.log('🔄 LiveKit token expiring soon, refreshing...');
                try {
                    const newToken = await generateToken(roomName);
                    // Disconnect and reconnect with new token
                    if (room) {
                        await room.disconnect();
                        await room.connect(serverUrl, newToken.token, {
                            autoSubscribe: true,
                            // Re-add other initial connection options from useMeeting if needed
                            // For simplicity, assuming default options or passing only token and url
                        });
                    }
                    console.log('✅ LiveKit token refreshed successfully by reconnecting.');
                }
                catch (err) {
                    console.error('❌ Failed to refresh LiveKit token by reconnecting:', err);
                    onError?.(new Error('Failed to refresh LiveKit token by reconnecting'));
                }
            }, expiresInMs - refreshThresholdMs);
        }
        else if (expiresInMs > 0) {
            // If already within the refresh threshold, refresh immediately or very soon
            refreshTimer = setTimeout(async () => {
                console.log('🔄 LiveKit token already past refresh threshold, refreshing now...');
                try {
                    const newToken = await generateToken(roomName);
                    // Disconnect and reconnect with new token
                    if (room) {
                        await room.disconnect();
                        await room.connect(serverUrl, newToken.token, {
                            autoSubscribe: true,
                        });
                    }
                    console.log('✅ LiveKit token refreshed successfully by reconnecting immediately.');
                }
                catch (err) {
                    console.error('❌ Failed to refresh LiveKit token immediately by reconnecting:', err);
                    onError?.(new Error('Failed to refresh LiveKit token immediately by reconnecting'));
                }
            }, 1000); // Refresh after 1 second
        }
        return () => {
            if (refreshTimer) {
                clearTimeout(refreshTimer);
            }
        };
    }, [room, initialToken, generateToken, roomName, onError, meetingSDK, serverUrl]);
    // Get SDK configuration (injected by consuming app)
    const sdkConfig = getSDKConfig();
    const db = sdkConfig.firebaseDb;
    const API_ENDPOINT = sdkConfig.apiEndpoint;
    const HQ_Enabled = sdkConfig.hqEnabled || false;
    sdkConfig.ChessComponent;
    sdkConfig.PollsComponent;
    const lkRoomRef = useRef(null);
    const [isConnected, setIsConnected] = useState(false);
    const [isLoading, setIsLoading] = useState(false); // Start as false to avoid black overlay
    const [isRecording, setIsRecording] = useState(false);
    const [showLeaveConfirmDialog, setShowLeaveConfirmDialog] = useState(false);
    const [leaveAction, setLeaveAction] = useState(null);
    // Debug: Log when dialog state changes
    useEffect(() => {
        console.log('🔴 Dialog state changed:', showLeaveConfirmDialog);
    }, [showLeaveConfirmDialog]);
    // Browser tab indicator cleanup function
    const clearBrowserMeetingIndicators = useCallback(() => {
        try {
            // Clear any media session indicators
            if ('mediaSession' in navigator) {
                const mediaSession = navigator.mediaSession;
                if (mediaSession) {
                    mediaSession.metadata = null;
                    mediaSession.playbackState = 'none';
                }
            }
            // Clear document title if it was modified for meeting
            const originalTitle = document.title;
            if (originalTitle.includes('🔴') || originalTitle.includes('Live') || originalTitle.includes('Meeting')) {
                // Try to restore original title (this is a best-effort approach)
                const baseTitle = originalTitle.replace(/🔴|Live|Meeting/g, '').trim();
                if (baseTitle) {
                    document.title = baseTitle;
                }
            }
            // Force clear any active media streams that might be lingering
            if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
                // Try to enumerate devices to refresh permission state
                navigator.mediaDevices.enumerateDevices().catch(() => {
                    // Ignore enumeration errors
                });
            }
            // Clear any stored media permissions state
            if ('permissions' in navigator) {
                // Check camera/microphone permissions state
                Promise.all([
                    navigator.permissions.query({ name: 'camera' }).then(permission => {
                        console.log('📷 Camera permission state:', permission.state);
                        return permission;
                    }).catch(() => { }),
                    navigator.permissions.query({ name: 'microphone' }).then(permission => {
                        console.log('🎤 Microphone permission state:', permission.state);
                        return permission;
                    }).catch(() => { })
                ]).catch(() => {
                    // Ignore permission query errors
                });
            }
            // Force cleanup of any WebRTC peer connections that might be lingering
            try {
                // Get all RTCPeerConnection instances that might be active
                const peerConnections = window.RTCPeerConnection ?
                    // Try to find any active peer connections
                    [] : []; // We can't easily enumerate all peer connections
                // For LiveKit, ensure room is fully disconnected
                if (lkRoomRef.current) {
                    console.log('🔌 Ensuring LiveKit room is fully disconnected...');
                    // The disconnect should already be called, but ensure cleanup
                }
            }
            catch (rtcError) {
                console.warn('⚠️ Error during WebRTC cleanup:', rtcError);
            }
            console.log('🧹 Cleared browser meeting indicators');
        }
        catch (error) {
            console.warn('⚠️ Error clearing browser indicators:', error);
        }
    }, []);
    // Complete media cleanup function
    const cleanupAllMediaAndIndicators = useCallback(async () => {
        try {
            // CRITICAL: If we are migrating or reconnecting, DO NOT destroy media contexts
            // This prevents WebGL context destruction during shard migrations (reason 10)
            // Managed LiveKit performs shard migrations during high-bandwidth activities like screen sharing
            // If we destroy the WebGL context during migration, it causes InvalidStateError when tracks try to republish
            const roomState = lkRoomRef.current?.state;
            if (roomState === ConnectionState.Reconnecting || roomState === ConnectionState.Connecting) {
                console.log('🧹 Cleanup aborted: Room is reconnecting/migrating - preserving media contexts');
                console.log('🌐 Managed LiveKit shard migration in progress - WebGL context must stay alive');
                return;
            }
            console.log('🧹 Starting complete media and indicator cleanup...');
            // CRITICAL: This function ONLY cleans up screen share and browser indicators
            // NEVER stops camera/mic streams - they should only be stopped when explicitly leaving the meeting
            // This ensures camera stays on like Google Meet/Zoom - camera never goes off during meeting
            // Stop screen sharing ONLY - to clear browser indicator
            if (lkRoomRef.current?.localParticipant) {
                const localParticipant = lkRoomRef.current.localParticipant;
                // CRITICAL: Stop screen sharing FIRST using setScreenShareEnabled to clear browser indicator
                const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                if (screenPub?.track) {
                    console.log('🖥️ Stopping screen share via setScreenShareEnabled to clear browser indicator...');
                    try {
                        // Step 1: Stop via LiveKit API
                        await localParticipant.setScreenShareEnabled(false);
                        // Step 2: CRITICAL - Also stop the underlying MediaStream track directly
                        // This ensures all browser indicators are cleared (tab notification, floating notification, control panel)
                        const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                        if (mediaStreamTrack) {
                            console.log('🛑 Stopping underlying MediaStream track to clear browser indicators...');
                            mediaStreamTrack.stop();
                            console.log('✅ MediaStream track stopped');
                        }
                        // Step 3: Also stop the LiveKit track if it's still active
                        if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                            try {
                                await screenPub.track.stop();
                                console.log('✅ LiveKit screen share track stopped');
                            }
                            catch (trackError) {
                                console.warn('⚠️ Error stopping LiveKit track:', trackError);
                            }
                        }
                        // Step 4: Find and stop any remaining screen share MediaStreams
                        try {
                            const allVideoElements = document.querySelectorAll('video');
                            allVideoElements.forEach(video => {
                                if (video.srcObject && video.srcObject instanceof MediaStream) {
                                    const stream = video.srcObject;
                                    stream.getVideoTracks().forEach(track => {
                                        // ONLY stop screen share tracks, never camera tracks
                                        if (track.kind === 'video' && (track.label.toLowerCase().includes('screen') ||
                                            track.label.toLowerCase().includes('window') ||
                                            track.label.toLowerCase().includes('display'))) {
                                            console.log('🛑 Stopping screen share MediaStream from video element:', track.label);
                                            track.stop();
                                        }
                                    });
                                }
                            });
                        }
                        catch (streamError) {
                            console.warn('⚠️ Error stopping screen share streams:', streamError);
                        }
                        console.log('✅ Screen share stopped and all browser indicators should be cleared');
                    }
                    catch (screenShareError) {
                        console.warn('⚠️ Error stopping screen share via setScreenShareEnabled:', screenShareError);
                        // Fallback: try to stop the track directly
                        if (screenPub?.track) {
                            try {
                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                if (mediaStreamTrack) {
                                    mediaStreamTrack.stop();
                                }
                                await screenPub.track.stop();
                            }
                            catch (trackError) {
                                console.warn('⚠️ Error stopping screen share track:', trackError);
                            }
                        }
                    }
                }
                // CRITICAL: DO NOT stop camera and mic tracks here
                // Only stop them when meeting actually ends (handled in disconnect handler)
                // This ensures camera stays on like Google Meet/Zoom - camera never goes off during meeting
                console.log('ℹ️ Skipping camera/mic track stop - camera must stay on during meeting');
            }
            // Clear browser meeting indicators
            clearBrowserMeetingIndicators();
            // CRITICAL: DO NOT stop initialMediaStream camera/mic tracks
            // Only stop screen share tracks if any exist
            if (initialMediaStream) {
                initialMediaStream.getTracks().forEach(track => {
                    // ONLY stop screen share tracks, never camera/mic tracks
                    if (track.kind === 'video' && (track.label.toLowerCase().includes('screen') ||
                        track.label.toLowerCase().includes('window') ||
                        track.label.toLowerCase().includes('display'))) {
                        console.log('🛑 Stopping screen share from initial media stream:', track.kind);
                        track.stop();
                    }
                    else {
                        console.log(`ℹ️ Preserving ${track.kind} track from initial media stream - camera/mic must stay on`);
                    }
                });
            }
            // Clean up any lingering screen share streams ONLY
            if (navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices) {
                try {
                    const allVideoElements = document.querySelectorAll('video');
                    allVideoElements.forEach(video => {
                        if (video.srcObject && video.srcObject instanceof MediaStream) {
                            const stream = video.srcObject;
                            stream.getVideoTracks().forEach(track => {
                                // ONLY stop screen share tracks, never camera tracks
                                if (track.kind === 'video' && (track.label.toLowerCase().includes('screen') ||
                                    track.label.toLowerCase().includes('window') ||
                                    track.label.toLowerCase().includes('display'))) {
                                    console.log('🛑 Stopping screen share MediaStream from video element:', track.label);
                                    track.stop();
                                }
                                else {
                                    console.log(`ℹ️ Preserving ${track.kind} track from video element - camera must stay on`);
                                }
                            });
                            // Only clear srcObject if we stopped all tracks (shouldn't happen for camera tracks)
                            const activeTracks = stream.getTracks().filter(t => t.readyState === 'live');
                            if (activeTracks.length === 0) {
                                video.srcObject = null;
                            }
                        }
                        // CRITICAL: Remove any overlays or translucent elements that might be stuck
                        video.style.opacity = '';
                        video.style.visibility = '';
                        video.style.display = '';
                    });
                    // CRITICAL: Remove any stuck overlay divs that might be covering video
                    const overlaySelectors = [
                        '.absolute.inset-0.z-20',
                        '.absolute.inset-0.z-10',
                        '[class*="overlay"]',
                        '[class*="loading"]'
                    ];
                    overlaySelectors.forEach(selector => {
                        try {
                            const overlays = document.querySelectorAll(selector);
                            overlays.forEach(overlay => {
                                const el = overlay;
                                // Only remove if it's in the video area and has translucent styling
                                if (el.classList.contains('bg-black') ||
                                    el.classList.contains('backdrop-blur') ||
                                    el.style.opacity === '0.5' ||
                                    getComputedStyle(el).opacity < '1') {
                                    // Check if it's within the meeting container
                                    const meetingContainer = document.querySelector('.lk-room-container') ||
                                        document.querySelector('[class*="meeting"]') ||
                                        document.querySelector('[class*="LiveKit"]');
                                    if (meetingContainer && meetingContainer.contains(el)) {
                                        console.log('🧹 Removing stuck overlay element');
                                        el.style.display = 'none';
                                        el.style.opacity = '0';
                                        el.style.visibility = 'hidden';
                                        el.style.pointerEvents = 'none';
                                    }
                                }
                            });
                        }
                        catch (e) {
                            // Ignore errors
                        }
                    });
                    // CRITICAL: DO NOT stop audio tracks - mic must stay on during meeting
                    // Only clean up audio elements that are not in use
                    const allAudioElements = document.querySelectorAll('audio');
                    allAudioElements.forEach(audio => {
                        // Don't stop audio tracks - mic must stay on
                        // Only clear srcObject if it's already null or empty
                        if (audio.srcObject && audio.srcObject instanceof MediaStream) {
                            const stream = audio.srcObject;
                            const activeTracks = stream.getTracks().filter(t => t.readyState === 'live');
                            // Only clear if there are no active tracks (shouldn't happen for mic)
                            if (activeTracks.length === 0) {
                                audio.srcObject = null;
                            }
                            else {
                                console.log(`ℹ️ Preserving audio stream - mic must stay on`);
                            }
                        }
                    });
                    // Try to stop any MediaStream objects that might be stored globally
                    // This is a more aggressive cleanup
                    if (window.MediaStream && window.MediaStream.activeStreams) {
                        // Some browsers expose active streams, try to clean them up
                        console.log('🔍 Checking for globally active media streams...');
                    }
                }
                catch (streamError) {
                    console.warn('⚠️ Error stopping remaining media streams:', streamError);
                }
            }
            // Small delay to ensure browser updates indicators
            setTimeout(() => {
                console.log('⏳ Waiting for browser to clear indicators...');
                // Try to trigger browser indicator refresh by dispatching focus/blur events
                try {
                    // Dispatch a blur event followed by focus to refresh browser state
                    window.dispatchEvent(new Event('blur'));
                    setTimeout(() => {
                        window.dispatchEvent(new Event('focus'));
                        console.log('🔄 Dispatched focus/blur events to refresh browser indicators');
                    }, 50);
                }
                catch (eventError) {
                    console.warn('⚠️ Error dispatching focus/blur events:', eventError);
                }
                // Final attempt: try to refresh media device enumeration
                if (navigator.mediaDevices && navigator.mediaDevices.enumerateDevices) {
                    navigator.mediaDevices.enumerateDevices().then(devices => {
                        console.log('🔄 Refreshed media device enumeration:', devices.length, 'devices');
                    }).catch(() => {
                        // Ignore enumeration errors
                    });
                }
            }, 500);
            console.log('✅ Complete media and indicator cleanup finished');
        }
        catch (error) {
            console.warn('⚠️ Error during media cleanup:', error);
        }
    }, [clearBrowserMeetingIndicators, initialMediaStream]);
    const [isRecordingLoading, setIsRecordingLoading] = useState(false);
    const recordingIdRef = useRef(null);
    const [recordingMode, setRecordingMode] = useState(null);
    const [meetingStartTime, setMeetingStartTime] = useState(null);
    const [subscriptionHoursRemaining, setSubscriptionHoursRemaining] = useState(null);
    const [initialHoursAtMeetingStart, setInitialHoursAtMeetingStart] = useState(null);
    const [showPurchaseHoursDialog, setShowPurchaseHoursDialog] = useState(false);
    const [showHoursInfoDialog, setShowHoursInfoDialog] = useState(false);
    // Unified resolution for both cloud and local recording
    const [selectedResolution, setSelectedResolution] = useState(() => {
        const saved = localStorage.getItem('meetplus_recording_resolution');
        // If HQ is disabled, force 480p (720p and 1080p are not available)
        if (!HQ_Enabled) {
            if (saved !== '480p') {
                localStorage.setItem('meetplus_recording_resolution', '480p');
            }
            return '480p';
        }
        // When HQ is enabled, allow saved preference
        if (saved === '1080p' || saved === '720p' || saved === '480p') {
            return saved;
        }
        // Migrate old cloud resolution if exists
        const oldCloudRes = localStorage.getItem('meetplus_cloud_resolution');
        if (oldCloudRes === '720p' || oldCloudRes === '480p') {
            return oldCloudRes;
        }
        return '480p'; // Default to 480p (changed from 720p)
    });
    // CRITICAL: ALWAYS use 'pip-circle' layout for recording
    // This ensures backgrounds are properly recorded and layout is consistent
    // Side-by-side has issues with background effects not being applied to both sides
    const FORCED_RECORDING_LAYOUT = 'pip-circle';
    const [recordLayout, setRecordLayout] = useState(FORCED_RECORDING_LAYOUT);
    // Ensure localStorage is always set to pip-circle
    useEffect(() => {
        localStorage.setItem('meetplus_recording_layout', FORCED_RECORDING_LAYOUT);
        localStorage.setItem('meetplus_cloud_layout', FORCED_RECORDING_LAYOUT);
        console.log('📐 Recording layout LOCKED to pip-circle (backgrounds work correctly in this mode)');
    }, []);
    const idleTimerRef = useRef(null);
    const maxTimerRef = useRef(null);
    const [showBackgroundEffects, setShowBackgroundEffects] = useState(false);
    const [recordingStartMs, setRecordingStartMs] = useState(null);
    const [currentEffect, setCurrentEffect] = useState('none');
    const [backgroundConfig, setBackgroundConfig] = useState({
        effect: 'none'
    });
    const backgroundConfigRef = useRef(backgroundConfig);
    const [showChessBoard, setShowChessBoard] = useState(false);
    const [showPollsQuizzes, setShowPollsQuizzes] = useState(false);
    const [chequeredEnabled, setChequeredEnabled] = useState(false);
    const participantCountRef = useRef(1); // Start with 1 (local participant)
    const [teacherParticipantId, setTeacherParticipantId] = useState(null);
    const [pinnedParticipant, setPinnedParticipant] = useState(null);
    const [currentLayout, setCurrentLayout] = useState('focus');
    // CRITICAL: Persist screen share state to survive page refresh (like Google Meet)
    const [isScreenSharing, setIsScreenSharing] = useState(() => {
        // Restore from localStorage on mount
        const saved = sessionStorage.getItem('meetplus_screen_sharing');
        return saved === 'true';
    });
    const screenShareOperationInProgressRef = useRef(false); // Prevent concurrent screen share operations
    const screenShareWatchdogRef = useRef(null); // Detect silent screen-share drops
    const activeTimeoutsRef = useRef(new Set()); // Track all active timeouts for memory cleanup
    const [screenSharingParticipantId, setScreenSharingParticipantId] = useState(null);
    const [isMirrored, setIsMirrored] = useState(false); // Disabled by default to prevent auto-flip
    const [showChat, setShowChat] = useState(false);
    const [showParticipants, setShowParticipants] = useState(false);
    const [minimizeVideos, setMinimizeVideos] = useState(false);
    const [chatExpanded, setChatExpanded] = useState(false); // New state for chat expansion
    const [isMinimized, setIsMinimized] = useState(false); // Picture-in-picture mode when screen sharing
    const [minimizedPosition, setMinimizedPosition] = useState({ x: window.innerWidth - 380, y: 100 }); // Default position for minimized window
    const [chatMessages, setChatMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');
    const [showControls, setShowControls] = useState(true); // Always visible to prevent dark overlay
    const [unreadMessageCount, setUnreadMessageCount] = useState(0);
    const [showMeetingInfo, setShowMeetingInfo] = useState(false);
    const hideControlsTimerRef = useRef(null);
    const [micEnabled, setMicEnabled] = useState(initialMicEnabled !== false);
    const [cameraEnabled, setCameraEnabled] = useState(initialCameraEnabled !== false);
    const [, setForceUpdate] = useState(0);
    const [selectedAvatar, setSelectedAvatar] = useState(() => {
        try {
            return localStorage.getItem('meetplus_selected_avatar') || 'avatar1';
        }
        catch {
            return 'avatar1';
        }
    });
    const handleAvatarSelection = useCallback((avatarId) => {
        setSelectedAvatar(avatarId);
        try {
            localStorage.setItem('meetplus_selected_avatar', avatarId);
        }
        catch (error) {
            console.error('Failed to persist avatar selection:', error);
        }
        try {
            const savedOptions = sessionStorage.getItem('meetplus_prejoin_options');
            const parsed = savedOptions ? JSON.parse(savedOptions) : {};
            parsed.selectedAvatar = avatarId;
            sessionStorage.setItem('meetplus_prejoin_options', JSON.stringify(parsed));
        }
        catch (error) {
            console.error('Failed to persist avatar selection in session storage:', error);
        }
    }, []);
    // Helper to track timeouts for cleanup (prevents memory leaks)
    useCallback((fn, delay) => {
        const timeout = setTimeout(() => {
            activeTimeoutsRef.current.delete(timeout);
            fn();
        }, delay);
        activeTimeoutsRef.current.add(timeout);
        return timeout;
    }, []);
    // Screen share watchdog helpers (no wake lock; detect silent drop/stall)
    const stopScreenShareWatchdog = useCallback(() => {
        if (screenShareWatchdogRef.current) {
            clearInterval(screenShareWatchdogRef.current);
            screenShareWatchdogRef.current = null;
        }
    }, []);
    // Track consecutive failures to avoid false positives (like Google Meet)
    // Increased threshold to be more lenient during network blips
    const screenShareFailureCountRef = useRef(0);
    const MAX_FAILURES = 5; // Only stop after 5 consecutive failures (125 seconds) - more lenient for network blips
    const handleScreenShareDropped = useCallback(() => {
        const room = lkRoomRef.current;
        // CRITICAL: Don't stop screen share during network blips (like Google Meet)
        // Check if room is reconnecting or disconnected - this is a network issue
        const isNetworkIssue = room && (room.state === ConnectionState.Reconnecting ||
            room.state === ConnectionState.Disconnected ||
            room.state === ConnectionState.Connecting);
        if (isNetworkIssue) {
            console.log('🌐 Network blip detected - preserving screen share (like Google Meet)');
            screenShareFailureCountRef.current = 0; // Reset counter
            return; // Don't stop screen share during network issues
        }
        // CRITICAL: Don't stop screen share during recording (like Google Meet)
        // Screen share must work reliably for hours during recording
        if (isRecording) {
            console.warn('⚠️ Screen share watchdog detected issue during recording - preserving screen share (like Google Meet)');
            screenShareFailureCountRef.current = 0; // Reset counter
            return;
        }
        // Only stop if room is fully connected and track is truly ended
        if (room && room.state === ConnectionState.Connected) {
            console.warn('🛑 Screen share track truly ended - resetting state (watchdog)');
            stopScreenShareWatchdog();
            setIsScreenSharing(false);
            sessionStorage.removeItem('meetplus_screen_sharing');
            setScreenSharingParticipantId(null);
            setCurrentLayout(prev => prev === 'screen-share' ? 'focus' : prev);
            screenShareOperationInProgressRef.current = false;
            screenShareFailureCountRef.current = 0; // Reset counter
        }
        else {
            console.log('🌐 Room not fully connected - preserving screen share during network issue');
            screenShareFailureCountRef.current = 0; // Reset counter
        }
    }, [stopScreenShareWatchdog, isRecording]);
    const startScreenShareWatchdog = useCallback(() => {
        stopScreenShareWatchdog();
        screenShareFailureCountRef.current = 0; // Reset failure counter
        // Google Meet approach: Check less frequently and only stop if truly ended
        // CRITICAL: Don't stop screen share during network blips (like Google Meet)
        // Screen share must continue working reliably even during temporary network issues
        screenShareWatchdogRef.current = setInterval(() => {
            try {
                const room = lkRoomRef.current;
                const participant = room?.localParticipant;
                if (!room || !participant || !isScreenSharing) {
                    return;
                }
                // CRITICAL: Don't stop screen share during network blips (like Google Meet)
                // If room is reconnecting or disconnected, it's a network issue - preserve screen share
                const isNetworkIssue = room.state === ConnectionState.Reconnecting ||
                    room.state === ConnectionState.Disconnected ||
                    room.state === ConnectionState.Connecting ||
                    room.state === ConnectionState.Disconnected;
                if (isNetworkIssue) {
                    console.log('🌐 Network blip detected - preserving screen share (like Google Meet)');
                    screenShareFailureCountRef.current = 0; // Reset counter during network issues
                    return; // Don't check track state during network issues
                }
                const pub = participant.getTrackPublication(Track.Source.ScreenShare);
                const track = pub?.track;
                const mediaTrack = track?.mediaStreamTrack;
                // CRITICAL: Only check if track is TRULY ended (not just muted or in transient state)
                // Google Meet doesn't stop screen share just because track appears muted
                // Muted tracks can still be active - only stop if readyState is 'ended'
                const trulyEnded = !pub || !track || !mediaTrack || mediaTrack.readyState === 'ended';
                // Also check if track state is 'ended' (LiveKit track state)
                const trackEnded = track?.state === 'ended' || track?.state === 'stopped';
                // CRITICAL FIX: Only consider it ended if BOTH conditions are met (more reliable)
                // Changed from OR to AND to prevent false positives during tab switches
                const isEnded = trulyEnded && trackEnded;
                // 🔍 DIAGNOSTIC LOGGING
                console.log('🔍 WATCHDOG CHECK:', {
                    timestamp: new Date().toISOString(),
                    hasPub: !!pub,
                    hasTrack: !!track,
                    hasMediaTrack: !!mediaTrack,
                    mediaTrackReadyState: mediaTrack?.readyState,
                    trackState: track?.state,
                    trulyEnded,
                    trackEnded,
                    isEnded,
                    failureCount: screenShareFailureCountRef.current,
                    maxFailures: MAX_FAILURES,
                    documentHidden: document.hidden,
                    roomState: room.state
                });
                if (isEnded) {
                    screenShareFailureCountRef.current++;
                    console.warn(`⚠️⚠️⚠️ WATCHDOG ALERT: Track appears ended (failure count: ${screenShareFailureCountRef.current}/${MAX_FAILURES})`);
                    console.warn('📊 TRACK STATE DETAILS:', {
                        publication: pub ? {
                            trackSid: pub.trackSid,
                            trackName: pub.trackName,
                            source: pub.source,
                            isMuted: pub.isMuted,
                            isSubscribed: pub.isSubscribed
                        } : null,
                        track: track ? {
                            sid: track.sid,
                            state: track.state,
                            kind: track.kind,
                            source: track.source
                        } : null,
                        mediaTrack: mediaTrack ? {
                            id: mediaTrack.id,
                            kind: mediaTrack.kind,
                            label: mediaTrack.label,
                            readyState: mediaTrack.readyState,
                            enabled: mediaTrack.enabled,
                            muted: mediaTrack.muted
                        } : null
                    });
                    // Only stop after multiple consecutive failures (like Google Meet - more reliable)
                    // Increased threshold to be more lenient during network issues
                    if (screenShareFailureCountRef.current >= MAX_FAILURES) {
                        // Double-check room is still connected before stopping
                        if (room.state === ConnectionState.Connected) {
                            console.error('🛑🛑🛑 WATCHDOG STOPPING SCREEN SHARE - Track confirmed ended after multiple checks');
                            console.error('📊 FINAL STATE BEFORE STOP:', {
                                timestamp: new Date().toISOString(),
                                failureCount: screenShareFailureCountRef.current,
                                roomState: room.state,
                                documentHidden: document.hidden,
                                isRecording
                            });
                            handleScreenShareDropped();
                        }
                        else {
                            console.log('🌐 Room not connected - preserving screen share during network issue');
                            screenShareFailureCountRef.current = 0; // Reset counter
                        }
                    }
                    else {
                        console.log(`⏳ Screen share track may be in transient state - waiting for next check (${screenShareFailureCountRef.current}/${MAX_FAILURES})...`);
                    }
                }
                else {
                    // Track is active - reset failure counter
                    if (screenShareFailureCountRef.current > 0) {
                        console.log('✅ Screen share track recovered - resetting failure counter');
                        screenShareFailureCountRef.current = 0;
                    }
                }
            }
            catch (err) {
                console.warn('⚠️ Screen share watchdog error:', err);
                // Don't increment failure count on errors - might be transient network issues
                screenShareFailureCountRef.current = 0; // Reset on errors (likely network blip)
            }
        }, 30000); // ~30s - increased from 25s to be more lenient and prevent false positives
    }, [handleScreenShareDropped, isScreenSharing, stopScreenShareWatchdog]);
    // CRITICAL: Comprehensive memory cleanup to prevent Chrome hangs
    const comprehensiveMemoryCleanup = useCallback(() => {
        console.log('🧹 Starting comprehensive memory cleanup...');
        // 1. Clear all active timeouts
        activeTimeoutsRef.current.forEach(timeout => {
            try {
                clearTimeout(timeout);
            }
            catch (e) {
                // Ignore errors
            }
        });
        activeTimeoutsRef.current.clear();
        // 2. Stop screen share watchdog
        stopScreenShareWatchdog();
        // 3. Stop all background processors
        try {
            const room = lkRoomRef.current;
            if (room?.localParticipant) {
                const videoTrack = room.localParticipant.getTrackPublication(Track.Source.Camera);
                if (videoTrack?.track) {
                    const track = videoTrack.track;
                    if (track.stopProcessor) {
                        track.stopProcessor().catch(() => {
                            // Ignore errors
                        });
                    }
                }
            }
        }
        catch (e) {
            console.warn('⚠️ Error stopping background processor:', e);
        }
        // Local recording cleanup removed - only cloud recording is supported
        // 12. Clear all video/audio elements' srcObject
        try {
            document.querySelectorAll('video, audio').forEach(el => {
                const mediaEl = el;
                if (mediaEl.srcObject) {
                    try {
                        const stream = mediaEl.srcObject;
                        stream.getTracks().forEach(t => {
                            if (t.readyState !== 'ended') {
                                t.stop();
                            }
                        });
                    }
                    catch (e) {
                        // Ignore errors
                    }
                    mediaEl.srcObject = null;
                }
                if (mediaEl.load) {
                    mediaEl.load(); // Reset element
                }
            });
        }
        catch (e) {
            console.warn('⚠️ Error cleaning up media elements:', e);
        }
        // 13. Clear all refs
        hideControlsTimerRef.current = null;
        console.log('✅ Comprehensive memory cleanup completed');
    }, [stopScreenShareWatchdog]);
    // Cleanup on component unmount
    useEffect(() => {
        // Reset loading state on mount to prevent persisting from previous meetings
        setIsLoading(false);
        return () => {
            console.log('🧹 LiveKitRoom component unmounting - cleaning up...');
            setIsLoading(false);
            comprehensiveMemoryCleanup();
            cleanupAllMediaAndIndicators();
        };
    }, [cleanupAllMediaAndIndicators, comprehensiveMemoryCleanup]);
    const [, setRecordingTimer] = useState(0); // Force re-render for recording timer
    const [pollsPanelWidth, setPollsPanelWidth] = useState(400); // Polls panel width state
    const [isHandRaised, setIsHandRaised] = useState(false);
    const [raisedHands, setRaisedHands] = useState(new Map());
    // Removed inactivity dialog - using 70-minute auto-stop instead
    useRef(null);
    const isIntentionalDisconnectRef = useRef(false); // Track intentional exits
    const hasEverConnectedRef = useRef(false); // Track if we've successfully connected at least once
    const isInitialConnectionRef = useRef(true); // Track if we're in the initial connection phase
    const reconnectAttemptCountRef = useRef(0); // Track reconnection attempts (for logging only - LiveKit handles actual reconnection)
    // CRITICAL: Store token and participant identity to ensure same identity on reconnection (Zoom-like behavior)
    // This ensures recording sees the same participant, not a new one
    const tokenRef = useRef(null); // Store token for reconnection
    const participantIdentityRef = useRef(null); // Store participant identity to verify it's maintained
    // Recording state persistence across disconnections (like Zoom)
    // CRITICAL: Store screen sharing state before disconnection to restore on reconnection
    const screenShareStateBeforeDisconnectRef = useRef(null);
    // CRITICAL: Track recent visibility changes to prevent screen share from stopping during tab switches
    const wasScreenSharingBeforeHiddenRef = useRef(false);
    const lastVisibilityChangeTimeRef = useRef(0);
    const isVisibilityChangingRef = useRef(false); // Track visibility change state globally
    const recordingStateBeforeDisconnectRef = useRef(null);
    // Device selection states - initialize with props from prejoin
    const [audioDevices, setAudioDevices] = useState([]);
    const [videoDevices, setVideoDevices] = useState([]);
    const [selectedAudioDevice, setSelectedAudioDevice] = useState(initialAudioDevice || '');
    const [selectedVideoDevice, setSelectedVideoDevice] = useState(initialVideoDevice || '');
    // Debug logging for device initialization
    useEffect(() => {
        console.log('🎥 LiveKitRoom: Device initialization:', {
            initialAudioDevice,
            initialVideoDevice,
            selectedAudioDevice,
            selectedVideoDevice
        });
    }, [initialAudioDevice, initialVideoDevice, selectedAudioDevice, selectedVideoDevice]);
    const [showAudioDevices, setShowAudioDevices] = useState(false);
    const [showVideoDevices, setShowVideoDevices] = useState(false);
    useEffect(() => {
        backgroundConfigRef.current = backgroundConfig;
    }, [backgroundConfig]);
    const handleEffectState = useCallback((effect, imageUrl) => {
        setCurrentEffect(effect);
        setBackgroundConfig({ effect, imageUrl });
    }, []);
    const reapplyBackground = useCallback(async () => {
        try {
            const room = lkRoomRef.current;
            if (!room)
                return;
            const { effect, imageUrl } = backgroundConfigRef.current;
            if (!room.localParticipant || effect === 'none')
                return;
            // CRITICAL FIX: Wait for track to be fully ready after reconnection/migration
            // After shard migrations, the track might be republished but MediaStreamTrack might still be closed
            // We need to wait for the track to be fully restored before applying the processor
            let track;
            let publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
            track = publication?.track;
            // Retry mechanism: wait for track to be fully ready (MediaStreamTrack must be 'live')
            if (!track) {
                // Track not published yet, wait a bit
                await new Promise(resolve => setTimeout(resolve, 100));
                publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
                track = publication?.track;
            }
            if (!track) {
                console.warn('⚠️ Camera track not available for background reapplication');
                return;
            }
            // CRITICAL: Check if track's MediaStreamTrack is ready
            // After shard migrations, the MediaStreamTrack might be closed even though the LiveKit track exists
            if (track.mediaStreamTrack && track.mediaStreamTrack.readyState === 'ended') {
                console.warn('🚫 Track MediaStreamTrack is ended - waiting for track to be restored...');
                // Wait and retry up to 10 times (2 seconds total)
                for (let attempt = 0; attempt < 10; attempt++) {
                    await new Promise(resolve => setTimeout(resolve, 200));
                    publication = room.localParticipant.getTrackPublication(Track.Source.Camera);
                    track = publication?.track;
                    if (track && track.mediaStreamTrack && track.mediaStreamTrack.readyState === 'live') {
                        console.log(`✅ Track MediaStreamTrack is now live after ${attempt + 1} attempts`);
                        break;
                    }
                }
                // Final check - if still ended, abort
                if (!track || !track.mediaStreamTrack || track.mediaStreamTrack.readyState === 'ended') {
                    console.warn('🚫 Track MediaStreamTrack still ended after retries - aborting background reapplication');
                    return;
                }
            }
            // Additional safety check: ensure track state is live (using type assertion for TypeScript)
            const trackState = track.state;
            if (trackState && trackState !== 'live' && trackState !== 'starting') {
                console.warn('🚫 Track state is not live - aborting background reapplication', { state: trackState });
                return;
            }
            await ensureBackgroundProcessor(track, effect, imageUrl);
            console.log('🔁 Reapplied background effect on local track', { effect, imageUrl });
        }
        catch (error) {
            console.warn('⚠️ Failed to reapply background effect:', error);
        }
    }, []);
    useEffect(() => {
        if (backgroundConfig.effect === 'none')
            return;
        reapplyBackground();
    }, [backgroundConfig.effect, backgroundConfig.imageUrl, reapplyBackground]);
    useEffect(() => {
        const room = lkRoomRef.current;
        if (!room)
            return;
        const participant = room.localParticipant;
        if (!participant)
            return;
        const handleLocalTrackPublished = (publication) => {
            if (publication.source === Track.Source.Camera) {
                reapplyBackground();
            }
            // CRITICAL: Protect screen share track from being unpublished during tab visibility changes
            // CRITICAL: Screen share tracks should NEVER have processors applied (causes WASM/WebGL conflicts)
            if (publication.source === Track.Source.ScreenShare && publication.kind === 'video') {
                console.log('🛡️ Screen share track published - installing unpublish protection and ensuring no processors');
                // Ensure no processor is applied to screen share track (causes browser throttling issues)
                const screenShareTrack = publication.track;
                if (screenShareTrack && screenShareTrack.processor) {
                    console.warn('⚠️ Screen share track has a processor - this can cause tab switching issues. Removing...');
                    // Remove processor asynchronously (non-blocking)
                    screenShareTrack.stopProcessor?.().then(() => {
                        console.log('✅ Removed processor from screen share track');
                    }).catch((e) => {
                        console.warn('⚠️ Could not remove processor from screen share track:', e);
                    });
                }
                // Install unpublish protection on the newly published track
                const originalUnpublish = publication.unpublish;
                if (originalUnpublish && typeof originalUnpublish === 'function') {
                    publication.unpublish = async (...args) => {
                        const timeSinceVisibilityChange = Date.now() - lastVisibilityChangeTimeRef.current;
                        const recentVisibilityChange = timeSinceVisibilityChange < 5000;
                        const isDuringVisibilityChange = isVisibilityChangingRef.current || recentVisibilityChange;
                        // Prevent unpublish if it's during a visibility change
                        if ((document.hidden || recentVisibilityChange || isDuringVisibilityChange) && wasScreenSharingBeforeHiddenRef.current) {
                            console.log('🚫 Prevented track unpublish during tab switch (published track protection)', {
                                documentHidden: document.hidden,
                                recentVisibilityChange,
                                timeSinceVisibilityChange,
                                isDuringVisibilityChange
                            });
                            return; // Don't unpublish - keep screen share active
                        }
                        return originalUnpublish.apply(publication, args);
                    };
                    console.log('✅ Installed unpublish protection on newly published screen share track');
                }
            }
        };
        const handleTrackUnmuted = (publication) => {
            if (publication.source === Track.Source.Camera) {
                reapplyBackground();
            }
        };
        // CRITICAL: Listen for when screen share track is unpublished (e.g., user stops from browser tab)
        // This ensures camera is re-enabled when screen share stops unexpectedly
        // IMPORTANT: Screen share should work reliably for hours (like Google Meet) - only stop if user explicitly stops it
        const handleLocalTrackUnpublished = async (publication) => {
            if (publication.source === Track.Source.ScreenShare && publication.kind === 'video') {
                console.warn('🔔🔔🔔 SCREEN SHARE TRACK UNPUBLISHED EVENT 🔔🔔🔔');
                const room = lkRoomRef.current;
                const roomState = room?.state;
                console.warn('📊 DIAGNOSTIC INFO:', {
                    timestamp: new Date().toISOString(),
                    publicationSid: publication.trackSid,
                    publicationName: publication.trackName,
                    isMuted: publication.isMuted,
                    isLocal: participant === lkRoomRef.current?.localParticipant,
                    isRecording,
                    documentHidden: document.hidden,
                    documentVisibility: document.visibilityState,
                    currentLayout,
                    isScreenSharing,
                    screenSharingParticipantId,
                    roomState,
                    wasIntentional: isIntentionalDisconnectRef.current
                });
                // CRITICAL FIX: Managed LiveKit Shard Migration Check
                // Reason 10 is often a backend-initiated move (shard migration)
                // If the room is Reconnecting or Connecting, DO NOT run cleanup
                // This prevents WebGL context destruction during migration
                if (roomState === ConnectionState.Reconnecting || roomState === ConnectionState.Connecting) {
                    console.log('🌐 Shard migration/reconnect in progress. Preserving screen share state.');
                    console.warn('📊 STOPPING REASON: PREVENTED - Managed LiveKit shard migration detected (room state:', roomState, ')');
                    return; // IMPORTANT: Prevent any state changes - screen share will resume after migration
                }
                // CRITICAL: Check if room is disconnected (must be checked before any state changes)
                // If room is in a disconnected state AND it was NOT intentional, it's a network issue/migration
                // This prevents false positives where disconnection-forced unpublish is mistaken for user stopping share
                const roomIsDisconnected = roomState === ConnectionState.Disconnected;
                // CRITICAL: If room is disconnected AND it was NOT intentional, it's a network issue/migration
                // This is the most reliable check - isIntentionalDisconnectRef is set synchronously in disconnect handler
                // Note: We can't rely on isConnected state because React state updates are async
                const isUnintentionalDisconnect = roomIsDisconnected && !isIntentionalDisconnectRef.current;
                // Combined check: room state shows disconnect AND it was not intentional
                const isNetworkIssue = isUnintentionalDisconnect;
                if (participant === lkRoomRef.current?.localParticipant) {
                    // CRITICAL: Priority 0 - Skip all screen share stop logic during network issues/migrations
                    // This includes:
                    // 1. Network blips (temporary connection loss)
                    // 2. Managed LiveKit shard migrations (reason 10 - backend optimization moving session between regions)
                    // 3. Server-side rebalancing (load balancing during high bandwidth)
                    // LiveKit's auto-republish will restore tracks after reconnection/migration completes
                    if (isNetworkIssue) {
                        console.warn('🌐 Screen share unpublished during network issue/migration - preserving state (like Google Meet)');
                        console.log('🔄 Room is reconnecting/disconnected or unintentional disconnect detected - track will auto-republish after reconnection/migration');
                        console.warn('📊 STOPPING REASON: PREVENTED - Network issue or Managed LiveKit migration detected', {
                            roomState,
                            isConnected,
                            wasIntentional: isIntentionalDisconnectRef.current
                        });
                        return; // IMPORTANT: Prevent any further state changes - screen share will resume after reconnection/migration
                    }
                    // CRITICAL: Priority 1 - Check if recording is active
                    // During recording, NEVER reset screen share state due to transient unpublish
                    // Recording must continue reliably for hours (like Google Meet)
                    if (isRecording) {
                        console.warn('⚠️ Screen share unpublished during recording - preserving state (like Google Meet)');
                        console.log('🎥 Recording continues - screen share will auto-resume when track republishes');
                        console.warn('📊 STOPPING REASON: PREVENTED - Recording active');
                        return; // IMPORTANT: Prevent any further state changes
                    }
                    // CRITICAL: Priority 2 - Check if this is due to visibility change (tab switch)
                    // If the document is hidden (tab switched), this is likely a transient unpublish
                    // and we should NOT reset the screen share state (like Google Meet/Zoom)
                    if (document.hidden) {
                        console.log('⏸️ Screen share unpublished during tab switch - preserving state (like Google Meet)');
                        console.warn('📊 STOPPING REASON: PREVENTED - Tab switch detected');
                        return; // IMPORTANT: Prevent any further state changes
                    }
                    // CRITICAL: Priority 3 - Check if the underlying MediaStreamTrack is actually ended
                    // Only stop screen share if the track is truly ended (user clicked stop)
                    // Transient unpublishes during normal operation should NOT stop screen share
                    // Note: When track is unpublished, publication.track may be null, so we can't check readyState
                    // In that case, we preserve state - only stop if we can definitively verify track is ended
                    const mediaStreamTrack = publication.track?.mediaStreamTrack;
                    const hasTrack = !!publication.track;
                    const isTrackActuallyEnded = hasTrack && mediaStreamTrack?.readyState === 'ended';
                    if (!isTrackActuallyEnded) {
                        // If track is null or not ended, preserve state - this is likely a transient unpublish
                        console.log('⏸️ Screen share unpublished but track is NOT confirmed ended - preserving state (transient unpublish)', {
                            hasTrack,
                            readyState: mediaStreamTrack?.readyState,
                            roomState
                        });
                        console.warn('📊 STOPPING REASON: PREVENTED - Cannot verify track is ended (track exists:', hasTrack, ', readyState:', mediaStreamTrack?.readyState, ')');
                        console.log('🔄 This is likely a transient unpublish or track cleanup - screen share will resume when track republishes');
                        return; // IMPORTANT: Prevent any further state changes
                    }
                    // CRITICAL: Only reset if this was truly intentional AND track is actually ended
                    // This means user clicked "Stop sharing" in browser or closed the shared window/tab
                    console.error('🛑🛑🛑 STOPPING SCREEN SHARE - Track unpublished AND ended (user action confirmed)');
                    console.error('📊 STOPPING REASON: Track unpublished event with track readyState = ended (user clicked stop or closed shared window)');
                    setIsScreenSharing(false);
                    sessionStorage.removeItem('meetplus_screen_sharing');
                    setScreenSharingParticipantId(null);
                    // Use functional update to access current layout state
                    setCurrentLayout(prev => prev === 'screen-share' ? 'focus' : prev);
                    // Clear the operation flag to allow new screen share operations
                    screenShareOperationInProgressRef.current = false;
                }
                // CRITICAL: Re-enable camera immediately when screen share stops
                // This handles the case where user stops sharing from browser tab
                // Camera MUST be re-enabled - no exceptions
                // BUT: Skip during network issues - camera will be handled during reconnection
                const shouldReenableCamera = cameraEnabled &&
                    participant === lkRoomRef.current?.localParticipant &&
                    !isNetworkIssue; // Don't re-enable during reconnection
                if (shouldReenableCamera) {
                    // Multiple attempts to ensure camera is re-enabled
                    const reenableCamera = async (attempt = 1) => {
                        try {
                            const localParticipant = lkRoomRef.current?.localParticipant;
                            if (!localParticipant)
                                return;
                            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                            const hasValidCamera = cameraPub && cameraPub.track && !cameraPub.isMuted && cameraPub.track.state === 'live';
                            // CRITICAL: Re-enable camera if it's missing, muted, or not active
                            if (!hasValidCamera) {
                                console.log(`📹 Camera track lost after screen share stopped from tab (attempt ${attempt}) - re-enabling IMMEDIATELY...`);
                                await localParticipant.setCameraEnabled(true);
                                // CRITICAL: Reapply background effect after re-enabling camera
                                // setCameraEnabled creates a new track without the background processor
                                // We must reapply it to preserve the user's background selection
                                if (currentEffect !== 'none') {
                                    console.log(`🎨 Reapplying background effect after camera re-enable: ${currentEffect}`);
                                    setTimeout(async () => {
                                        try {
                                            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                            if (cameraPub?.track) {
                                                const track = cameraPub.track;
                                                await ensureBackgroundProcessor(track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                console.log('✅ Background effect reapplied after camera re-enable');
                                            }
                                        }
                                        catch (bgError) {
                                            console.error('❌ Error reapplying background after camera re-enable:', bgError);
                                        }
                                    }, 500); // Wait for track to be fully ready
                                }
                                // Verify and retry if needed
                                setTimeout(async () => {
                                    const cameraPubAfter = localParticipant.getTrackPublication(Track.Source.Camera);
                                    const hasValidCameraAfter = cameraPubAfter && cameraPubAfter.track && !cameraPubAfter.isMuted && cameraPubAfter.track.state === 'live';
                                    if (!hasValidCameraAfter && attempt < 3) {
                                        console.log(`📹 Camera still not active after screen share stop, retrying (attempt ${attempt + 1})...`);
                                        reenableCamera(attempt + 1);
                                    }
                                    else if (hasValidCameraAfter) {
                                        console.log('✅ Camera re-enabled after screen share stopped from tab');
                                    }
                                }, 300);
                            }
                            else {
                                console.log('📹 Camera track is still active after screen share stop');
                            }
                        }
                        catch (error) {
                            console.error(`❌ Error re-enabling camera after screen share stop from tab (attempt ${attempt}):`, error);
                            // Retry if not the last attempt
                            if (attempt < 3) {
                                setTimeout(() => reenableCamera(attempt + 1), 500);
                            }
                        }
                    };
                    // Start immediately, then retry with delays
                    setTimeout(() => reenableCamera(1), 50);
                    setTimeout(() => reenableCamera(2), 400);
                    setTimeout(() => reenableCamera(3), 1000);
                }
            }
        };
        participant.on(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);
        participant.on(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);
        participant.on(ParticipantEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
        return () => {
            participant.off(ParticipantEvent.LocalTrackPublished, handleLocalTrackPublished);
            participant.off(ParticipantEvent.TrackUnmuted, handleTrackUnmuted);
            participant.off(ParticipantEvent.LocalTrackUnpublished, handleLocalTrackUnpublished);
        };
    }, [reapplyBackground, isConnected, cameraEnabled]);
    // CRITICAL: Clean up any stuck overlays when connection state changes
    useEffect(() => {
        // When connected, immediately remove any loading overlays
        if (isConnected) {
            // Force remove any loading overlays that might be stuck
            const loadingOverlays = document.querySelectorAll('[class*="loading"], [class*="overlay"]');
            loadingOverlays.forEach(overlay => {
                const el = overlay;
                // Only remove if it's a loading/connecting overlay in the video area
                if (el.classList.contains('bg-gray-900') ||
                    el.classList.contains('bg-black') ||
                    el.style.backgroundColor === 'rgb(17, 24, 39)' || // bg-gray-900
                    el.style.backgroundColor === 'rgb(0, 0, 0)') { // bg-black
                    const meetingContainer = document.querySelector('.lk-room-container') ||
                        document.querySelector('[class*="meeting"]') ||
                        document.querySelector('[class*="LiveKit"]');
                    if (meetingContainer && meetingContainer.contains(el)) {
                        console.log('🧹 Removing stuck loading overlay after connection');
                        el.style.display = 'none';
                        el.style.opacity = '0';
                        el.style.visibility = 'hidden';
                        el.style.pointerEvents = 'none';
                    }
                }
            });
        }
    }, [isConnected, isLoading]);
    // Persist unified resolution to localStorage and enforce HQ restrictions
    useEffect(() => {
        // Enforce 480p when HQ is disabled
        if (!HQ_Enabled) {
            if (selectedResolution !== '480p') {
                console.log('⚠️ HQ_Enabled is false, forcing resolution to 480p');
                setSelectedResolution('480p');
                localStorage.setItem('meetplus_recording_resolution', '480p');
            }
            else {
                localStorage.setItem('meetplus_recording_resolution', '480p');
            }
        }
        else {
            // HQ enabled - save the selected resolution
            localStorage.setItem('meetplus_recording_resolution', selectedResolution);
            console.log('💾 Saved recording resolution to localStorage:', selectedResolution);
        }
    }, [selectedResolution]); // eslint-disable-line react-hooks/exhaustive-deps
    // CRITICAL: Ensure layout is always 'pip-circle' - enforce it
    useEffect(() => {
        if (recordLayout !== 'pip-circle') {
            console.log('📐 Enforcing pip-circle layout (only supported layout)');
            setRecordLayout('pip-circle');
            localStorage.setItem('meetplus_recording_layout', 'pip-circle');
            localStorage.setItem('meetplus_cloud_layout', 'pip-circle');
        }
        else {
            localStorage.setItem('meetplus_recording_layout', 'pip-circle');
            localStorage.setItem('meetplus_cloud_layout', 'pip-circle');
            console.log('💾 Saved recording layout to localStorage: pip-circle');
        }
    }, [recordLayout]);
    // Recording timer - updates every second when recording  
    useEffect(() => {
        if (!isRecording)
            return;
        const interval = setInterval(() => {
            setRecordingTimer(prev => prev + 1);
        }, 1000);
        return () => clearInterval(interval);
    }, [isRecording, setRecordingTimer]);
    // Fetch meetPlusHours from serviceProviders document
    const fetchSubscriptionHours = useCallback(async () => {
        if (!tenantId || userRole !== 'teacher')
            return { canStart: true, hours: null };
        try {
            const serviceProviderRef = doc(db, 'ServiceProviders', tenantId);
            const serviceProviderSnap = await getDoc(serviceProviderRef);
            if (serviceProviderSnap.exists()) {
                const data = serviceProviderSnap.data();
                let meetPlusHours = data.meetPlusHours || 0;
                // If no hours configured, assume 5 trial hours (display only, will be granted on first meeting end)
                if (meetPlusHours === 0 || meetPlusHours === null || meetPlusHours === undefined) {
                    meetPlusHours = 5; // Display 5 hours, but haven't granted yet
                }
                setSubscriptionHoursRemaining(meetPlusHours);
                console.log('📊 Meet+ hours remaining:', { meetPlusHours });
                // Check if meeting can start
                // Allow if no hours configured (will get 5 trial on first meeting end)
                // Or if they have at least 1 hour remaining
                const hasNoConfig = (data.meetPlusHours === 0 || data.meetPlusHours === null || data.meetPlusHours === undefined);
                if (!hasNoConfig && meetPlusHours < 1) {
                    // Show custom dialog instead of browser alert
                    setShowPurchaseHoursDialog(true);
                    onDisconnect(false);
                    return { canStart: false, hours: meetPlusHours };
                }
                return { canStart: true, hours: meetPlusHours };
            }
        }
        catch (error) {
            console.error('Error fetching service provider hours:', error);
        }
        return { canStart: true, hours: null };
    }, [tenantId, userRole, onDisconnect]);
    // Periodically refresh subscription hours balance during active meetings (teachers only)
    // Also update hours in real-time by subtracting elapsed meeting time
    useEffect(() => {
        if (!isConnected || userRole !== 'teacher' || !tenantId)
            return;
        // Refresh immediately, then every minute
        fetchSubscriptionHours();
        const interval = setInterval(async () => {
            // If meeting is active, calculate real-time remaining hours using initial balance
            if (meetingStartTime && initialHoursAtMeetingStart !== null) {
                const elapsedHours = (Date.now() - meetingStartTime) / (1000 * 60 * 60); // hours
                const realTimeRemaining = initialHoursAtMeetingStart - elapsedHours;
                // Update display with real-time remaining
                setSubscriptionHoursRemaining(Math.max(0, realTimeRemaining));
                // Note: We DON'T update Firestore during the meeting to avoid double-deduction
                // Firestore is only updated when the meeting ends (in stopMeetingHoursTracking)
                // This ensures accurate deduction and prevents race conditions
            }
            else {
                // If no active meeting, just refresh from Firestore
                fetchSubscriptionHours();
            }
        }, 60000); // Every 60 seconds
        return () => clearInterval(interval);
    }, [isConnected, userRole, tenantId, fetchSubscriptionHours, meetingStartTime, initialHoursAtMeetingStart]);
    // CRITICAL: Cleanup on component unmount - ensure ALL screen share tracks are stopped
    // This runs when the component is unmounted (e.g., when navigating away from the page)
    useEffect(() => {
        // Define a global cleanup function that can be called immediately
        const aggressiveScreenShareCleanup = () => {
            console.log('🧹 [LiveKitRoom] AGGRESSIVE COMPREHENSIVE screen share cleanup...');
            let stoppedCount = 0;
            // Step 1: Stop via LiveKit if room still exists (synchronous operations only)
            if (lkRoomRef.current?.localParticipant) {
                const localParticipant = lkRoomRef.current.localParticipant;
                const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                if (screenPub?.track) {
                    try {
                        // CRITICAL: Stop the underlying MediaStream track FIRST (synchronous)
                        const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                        if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                            console.log(`🛑 [LiveKitRoom] Stopping LiveKit MediaStream track: ${mediaStreamTrack.label} (state: ${mediaStreamTrack.readyState})`);
                            mediaStreamTrack.stop();
                            stoppedCount++;
                            console.log(`✅ [LiveKitRoom] LiveKit MediaStream track stopped (new state: ${mediaStreamTrack.readyState})`);
                        }
                        // Then stop the LiveKit track (synchronous)
                        if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                            screenPub.track.stop();
                            console.log('✅ [LiveKitRoom] LiveKit track stopped');
                        }
                        // Call setScreenShareEnabled asynchronously (non-blocking)
                        localParticipant.setScreenShareEnabled(false).catch(() => { });
                    }
                    catch (error) {
                        console.error('⚠️ [LiveKitRoom] Error stopping via LiveKit:', error);
                    }
                }
            }
            // Step 2: Stop ALL screen share tracks from ALL media elements (video AND audio) (synchronous)
            const allMediaElements = document.querySelectorAll('video, audio');
            console.log(`🔍 [LiveKitRoom] Checking ${allMediaElements.length} media elements`);
            allMediaElements.forEach((element, idx) => {
                const mediaEl = element;
                if (mediaEl.srcObject && mediaEl.srcObject instanceof MediaStream) {
                    const stream = mediaEl.srcObject;
                    stream.getTracks().forEach(track => {
                        const label = track.label.toLowerCase();
                        const isScreenShare = label.includes('screen') ||
                            label.includes('window') ||
                            label.includes('display') ||
                            label.includes('monitor') ||
                            label.includes('web contents') ||
                            label.includes('capture');
                        if (isScreenShare && track.readyState !== 'ended') {
                            console.log(`🛑 [LiveKitRoom] STOPPING track from element ${idx}: ${track.label} (${track.kind}, state: ${track.readyState})`);
                            try {
                                track.stop();
                                stoppedCount++;
                                console.log(`✅ [LiveKitRoom] Stopped track (new state: ${track.readyState})`);
                            }
                            catch (e) {
                                console.error(`⚠️ [LiveKitRoom] Error stopping track:`, e);
                            }
                        }
                    });
                    // Clear srcObject if all tracks are stopped
                    const activeTracks = stream.getTracks().filter(t => t.readyState === 'live');
                    if (activeTracks.length === 0) {
                        console.log(`🧹 [LiveKitRoom] Clearing srcObject for element ${idx}`);
                        mediaEl.srcObject = null;
                    }
                }
            });
            // Step 3: Stop any screen share tracks from initialMediaStream if it exists (synchronous)
            if (initialMediaStream) {
                console.log('🔍 [LiveKitRoom] Checking initialMediaStream...');
                initialMediaStream.getTracks().forEach(track => {
                    const label = track.label.toLowerCase();
                    const isScreenShare = label.includes('screen') ||
                        label.includes('window') ||
                        label.includes('display') ||
                        label.includes('monitor') ||
                        label.includes('web contents') ||
                        label.includes('capture');
                    if (isScreenShare && track.readyState !== 'ended') {
                        console.log(`🛑 [LiveKitRoom] STOPPING initialMediaStream track: ${track.label} (state: ${track.readyState})`);
                        try {
                            track.stop();
                            stoppedCount++;
                            console.log(`✅ [LiveKitRoom] Stopped initialMediaStream track (new state: ${track.readyState})`);
                        }
                        catch (e) {
                            console.error('⚠️ [LiveKitRoom] Error stopping initialMediaStream track:', e);
                        }
                    }
                });
            }
            console.log(`📊 [LiveKitRoom] Cleanup summary: Stopped ${stoppedCount} screen share tracks`);
            // Force browser to update indicator and clear stuck red dot
            if (stoppedCount > 0) {
                console.log('🔄 [LiveKitRoom] Forcing browser UI update to clear red dot...');
                try {
                    window.dispatchEvent(new Event('focus'));
                    window.dispatchEvent(new Event('blur'));
                    window.dispatchEvent(new Event('visibilitychange'));
                    // Force multiple reflows to ensure browser updates
                    document.body.offsetHeight;
                    document.documentElement.offsetHeight;
                }
                catch (e) {
                    console.warn('⚠️ [LiveKitRoom] Error forcing browser update:', e);
                }
            }
        };
        return () => {
            // This cleanup function runs when component unmounts
            console.log('🧹 [LiveKitRoom] Component unmounting - running cleanup...');
            try {
                // Run cleanup immediately (synchronous)
                aggressiveScreenShareCleanup();
                // Also run cleanup again after a tiny delay to catch anything that might have been created during unmount
                setTimeout(aggressiveScreenShareCleanup, 50);
                setTimeout(aggressiveScreenShareCleanup, 200);
            }
            catch (error) {
                console.warn('⚠️ [LiveKitRoom] Error in unmount cleanup:', error);
            }
        };
    }, []); // Empty dependency array - runs only on mount/unmount
    // Fetch chequeredEnabled flag from service provider document
    useEffect(() => {
        if (!tenantId)
            return;
        const fetchChequeredEnabled = async () => {
            try {
                const serviceProviderRef = doc(db, 'ServiceProviders', tenantId);
                const serviceProviderSnap = await getDoc(serviceProviderRef);
                if (serviceProviderSnap.exists()) {
                    const data = serviceProviderSnap.data();
                    const enabled = data.chequeredEnabled === true;
                    setChequeredEnabled(enabled);
                    console.log('♟️ Chequered enabled status:', enabled);
                }
                else {
                    setChequeredEnabled(false);
                }
            }
            catch (error) {
                console.error('Error fetching chequeredEnabled:', error);
                setChequeredEnabled(false);
            }
        };
        fetchChequeredEnabled();
    }, [tenantId]);
    // DISABLED: Auto-restore background effect to prevent first-time blur
    // User will apply effects manually
    // useEffect(() => {
    //     const savedEffect = localStorage.getItem('meetplus_background_effect');
    //     if (savedEffect && ['none', 'blur', 'image'].includes(savedEffect)) {
    //         setCurrentEffect(savedEffect as 'none' | 'blur' | 'image');
    //         console.log('🔄 Restored saved background effect:', savedEffect);
    //     }
    // }, []);
    useEffect(() => {
        const handleLiveKitError = (event) => {
            console.warn('LiveKit internal error (non-critical):', event);
        };
        // Add global error handler for LiveKit
        window.addEventListener('error', (event) => {
            if (event.message?.includes('Element not part of the array') ||
                event.message?.includes('camera_placeholder')) {
                console.warn('LiveKit placeholder error (non-critical):', event.message);
                event.preventDefault(); // Prevent error from showing in console
            }
        });
        return () => {
            window.removeEventListener('error', handleLiveKitError);
        };
    }, []);
    useEffect(() => {
        const handleKeyDown = (event) => {
            // Only handle spacebar when not typing in input fields
            if (event.code === 'Space' &&
                !['INPUT', 'TEXTAREA'].includes(event.target?.tagName)) {
                event.preventDefault();
                // Toggle microphone
                if (lkRoomRef.current?.localParticipant) {
                    const participant = lkRoomRef.current.localParticipant;
                    const currentState = participant.isMicrophoneEnabled;
                    const newState = !currentState;
                    participant.setMicrophoneEnabled(newState).then(() => {
                        setMicEnabled(newState);
                        setForceUpdate(prev => prev + 1);
                        // Removed non-critical toast - UI shows mute state
                    }).catch((error) => {
                        console.error('Error toggling microphone:', error);
                        // Removed toast - no toasts in LiveKit room except Chequered
                    });
                }
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, []);
    // Sync micEnabled state with LiveKit's microphone state (event-driven only)
    // This ensures control panel stays in sync when teacher mutes/unmutes
    useEffect(() => {
        if (!lkRoomRef.current?.localParticipant)
            return;
        const localParticipant = lkRoomRef.current.localParticipant;
        // Function to sync UI state with actual microphone state
        const syncMicState = () => {
            const actualMicState = localParticipant.isMicrophoneEnabled;
            if (actualMicState !== micEnabled) {
                setMicEnabled(actualMicState);
                console.log(`🔄 Synced micEnabled state with LiveKit: ${actualMicState}`);
            }
        };
        // Initial sync
        syncMicState();
        // Listen to track published/unpublished events for microphone
        const handleTrackPublished = (publication) => {
            if (publication.source === Track.Source.Microphone) {
                syncMicState();
            }
        };
        const handleTrackUnpublished = (publication) => {
            if (publication.source === Track.Source.Microphone) {
                syncMicState();
            }
        };
        // Listen to participant events
        localParticipant.on('trackPublished', handleTrackPublished);
        localParticipant.on('trackUnpublished', handleTrackUnpublished);
        // REMOVED: Polling every 5 seconds - causes unnecessary re-renders and conflicts
        // Event-driven approach is sufficient and more efficient
        return () => {
            localParticipant.off('trackPublished', handleTrackPublished);
            localParticipant.off('trackUnpublished', handleTrackUnpublished);
        };
    }, [isConnected, micEnabled]); // Added micEnabled to deps to prevent stale closures
    const enumerateDevices = async () => {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(device => device.kind === 'audioinput');
            const videoInputs = devices.filter(device => device.kind === 'videoinput');
            setAudioDevices(audioInputs);
            setVideoDevices(videoInputs);
            console.log('🎤 Audio devices:', audioInputs.map(d => ({ id: d.deviceId, label: d.label })));
            console.log('📹 Video devices:', videoInputs.map(d => ({ id: d.deviceId, label: d.label })));
            console.log('📋 Current selected devices:', { selectedAudioDevice, selectedVideoDevice });
            // Validate that selected devices still exist
            const selectedAudioExists = selectedAudioDevice && audioInputs.some(d => d.deviceId === selectedAudioDevice);
            const selectedVideoExists = selectedVideoDevice && videoInputs.some(d => d.deviceId === selectedVideoDevice);
            console.log('✅ Device validation:', {
                selectedAudioExists,
                selectedVideoExists,
                selectedAudioDevice,
                selectedVideoDevice
            });
            // Only set defaults if no device is selected OR if selected device doesn't exist anymore
            if ((!selectedAudioDevice || !selectedAudioExists) && audioInputs.length > 0) {
                console.log('🎤 Setting default audio device:', audioInputs[0].deviceId);
                setSelectedAudioDevice(audioInputs[0].deviceId);
            }
            if ((!selectedVideoDevice || !selectedVideoExists) && videoInputs.length > 0) {
                console.log('📹 Setting default video device:', videoInputs[0].deviceId);
                setSelectedVideoDevice(videoInputs[0].deviceId);
            }
        }
        catch (error) {
            console.error('Error enumerating devices:', error);
        }
    };
    // Switch audio device
    const switchAudioDevice = async (deviceId) => {
        if (!lkRoomRef.current?.localParticipant) {
            console.log('❌ No room or participant available');
            return;
        }
        try {
            console.log('🎤 Switching to audio device:', deviceId);
            await lkRoomRef.current.localParticipant.setMicrophoneEnabled(false);
            await lkRoomRef.current.localParticipant.setMicrophoneEnabled(true, { deviceId });
            setSelectedAudioDevice(deviceId);
            setShowAudioDevices(false);
            // Removed non-critical toast - device change is immediate
        }
        catch (error) {
            console.error('Error switching audio device:', error);
            // Removed non-critical toast - error already logged
        }
    };
    // Switch video device
    const switchVideoDevice = async (deviceId) => {
        if (!lkRoomRef.current?.localParticipant) {
            console.log('❌ No room or participant available');
            return;
        }
        try {
            console.log('📹 Switching to video device:', deviceId);
            await lkRoomRef.current.localParticipant.setCameraEnabled(false);
            await lkRoomRef.current.localParticipant.setCameraEnabled(true, { deviceId });
            setSelectedVideoDevice(deviceId);
            setShowVideoDevices(false);
            // Removed non-critical toast - device change is immediate
        }
        catch (error) {
            console.error('Error switching video device:', error);
            // Removed non-critical toast - error already logged
        }
    };
    // Layout management - Google Meet-like behavior
    useEffect(() => {
        // Both teacher and students use spotlight/focus layout by default
        // Shows main speaker big with thumbnails below (like Google Meet)
        setCurrentLayout('focus');
    }, [userRole]);
    // Track join time to prevent layout switches during initial join
    const joinTimeRef = useRef(null);
    // Handle screen share detection - with safeguards to prevent false positives
    // CRITICAL: Add ref to prevent rapid repeated calls to handleScreenShareDetected
    const screenShareProcessingRef = useRef(false);
    const handleScreenShareDetected = useCallback((isSharing, participantId) => {
        // CRITICAL: If CustomLayoutManager detected screen share, trust it immediately (before flag check)
        // CustomLayoutManager uses useTracks hook which is more reliable than checking trackPublications
        // This is especially important for guests who join during active screen share
        // Skip flag check for CustomLayoutManager detections to ensure they're not blocked
        if (isSharing && participantId) {
            const room = lkRoomRef.current;
            if (room && room.state !== ConnectionState.Disconnected && isConnected) {
                // Only trust if we're not already processing (to prevent infinite loops)
                if (!screenShareProcessingRef.current) {
                    // console.log('🖥️ Screen share detected by CustomLayoutManager - trusting detection and switching layout:', participantId);
                    screenShareProcessingRef.current = true;
                    setScreenSharingParticipantId(participantId);
                    setCurrentLayout('screen-share');
                    if (participantId === room.localParticipant?.identity) {
                        setIsScreenSharing(true);
                        // CRITICAL: Persist screen share state to survive refresh (like Google Meet)
                        sessionStorage.setItem('meetplus_screen_sharing', 'true');
                        startScreenShareWatchdog();
                    }
                    else {
                        setIsScreenSharing(false);
                        sessionStorage.removeItem('meetplus_screen_sharing');
                    }
                    // Reset flag after a delay to allow future detections
                    setTimeout(() => {
                        screenShareProcessingRef.current = false;
                    }, 500);
                    return; // Early return - trust CustomLayoutManager's detection
                }
            }
        }
        // CRITICAL: Prevent rapid repeated calls that cause flickering
        if (screenShareProcessingRef.current) {
            // console.log('⏸️ Screen share detection already processing, skipping...');
            return;
        }
        screenShareProcessingRef.current = true;
        // CRITICAL: Only process if we're actually connected
        if (!isConnected) {
            // console.log('⏭️ Ignoring screen share detection - not connected');
            screenShareProcessingRef.current = false; // Reset flag
            return;
        }
        // CRITICAL: Prevent layout switches during initial join (first 3 seconds)
        // EXCEPTION: Allow detection of existing screen shares from remote participants
        // This ensures guests joining during active screen share see it immediately
        const isInitialJoin = joinTimeRef.current && (Date.now() - joinTimeRef.current < 3000);
        if (isInitialJoin && isSharing) {
            const room = lkRoomRef.current;
            const isRemoteParticipant = participantId && room && participantId !== room.localParticipant?.identity;
            // Allow detection if it's a remote participant (existing screen share)
            // Block only if it's the local participant starting a new screen share
            if (!isRemoteParticipant) {
                console.log('⏭️ Ignoring local screen share detection during initial join - preventing layout blip');
                return;
            }
            else {
                console.log('✅ Allowing existing remote screen share detection during initial join');
            }
        }
        // CRITICAL: Verify the participant actually exists before switching layout
        if (isSharing && participantId) {
            const room = lkRoomRef.current;
            if (!room) {
                console.log('⏭️ Ignoring screen share detection - no room');
                return;
            }
            // CRITICAL: Verify room state is valid before accessing properties
            if (room.state === ConnectionState.Disconnected) {
                console.log('⏭️ Ignoring screen share detection - room is disconnected:', room.state);
                return;
            }
            // CRITICAL: Get participant using remoteParticipants Map
            // LiveKit exposes remoteParticipants as a Map, not participants directly
            const participantsMap = room.remoteParticipants || new Map();
            console.log('🔍 Checking participants map:', {
                hasRemoteParticipants: !!room.remoteParticipants,
                remoteParticipantsSize: participantsMap.size,
                isLocalParticipant: participantId === room.localParticipant?.identity,
                participantId
            });
            // Verify participant exists and has active screen share track
            let participant = null;
            try {
                // CRITICAL: Check if it's local participant or remote participant
                if (participantId === room.localParticipant?.identity) {
                    participant = room.localParticipant;
                    console.log('✅ Found local participant for screen share');
                }
                else {
                    participant = participantsMap.get(participantId);
                    console.log('🔍 Found remote participant for screen share:', !!participant);
                }
            }
            catch (error) {
                console.error('❌ Error accessing participants:', error);
                return;
            }
            if (!participant) {
                console.log('⏭️ Ignoring screen share detection - participant not found:', participantId);
                return;
            }
            // CRITICAL: Ensure trackPublications exists before accessing it
            if (!participant.trackPublications) {
                console.log('⏭️ Ignoring screen share detection - participant.trackPublications is undefined');
                return;
            }
            // Double-check that they actually have a screen share track
            let tracks = [];
            try {
                tracks = [...participant.trackPublications.values()];
            }
            catch (error) {
                console.error('❌ Error accessing participant.trackPublications:', error);
                return;
            }
            // CRITICAL: Use relaxed validation to match CustomLayoutManager
            // For remote participants, accept ANY screen share track publication (LiveKit auto-subscribes)
            const hasScreenShareTrack = tracks.some(pub => {
                if (!pub)
                    return false;
                // CRITICAL: Check for Track.Source.ScreenShare enum value (not just strings)
                const isScreenShare = pub.source === Track.Source.ScreenShare
                    || pub.source === 'screen_share'
                    || pub.source === 'screen';
                if (!isScreenShare)
                    return false;
                // CRITICAL: For remote participants, accept ANY screen share track publication
                // LiveKit will auto-subscribe, but the track might not be available immediately
                // Accept the publication if it exists and is not explicitly unsubscribed
                pub.isSubscribed === false;
                const isLocalParticipant = participantId === room.localParticipant?.identity;
                const hasTrack = pub.track || pub.videoTrack;
                const isActive = pub.track?.mediaStream?.active || pub.videoTrack?.mediaStream?.active;
                // Accept if:
                // 1. Local participant: must have active track
                // 2. Remote participant: accept ANY screen share publication (not explicitly unsubscribed) - track will auto-subscribe
                const hasValidTrack = isLocalParticipant
                    ? (hasTrack && isActive)
                    : (pub.isSubscribed !== false); // Remote: accept if not explicitly unsubscribed (undefined or true is OK)
                return hasValidTrack;
            });
            if (!hasScreenShareTrack) {
                console.log('⏭️ Ignoring screen share detection - no valid screen share track found:', {
                    participantId,
                    tracks: tracks.map(pub => ({
                        source: pub?.source,
                        sourceType: typeof pub?.source,
                        sourceValue: pub?.source,
                        trackSourceScreenShare: pub?.source === Track.Source.ScreenShare,
                        hasTrack: !!(pub?.track || pub?.videoTrack),
                        isSubscribed: pub?.isSubscribed,
                        isSubscribedType: typeof pub?.isSubscribed,
                        isActive: !!(pub?.track?.mediaStream?.active || pub?.videoTrack?.mediaStream?.active),
                        kind: pub?.kind
                    })),
                    TrackSourceScreenShare: Track.Source.ScreenShare,
                    TrackSourceScreenShareType: typeof Track.Source.ScreenShare
                });
                // CRITICAL: Even if we can't verify the track, if CustomLayoutManager detected it, trust it
                // This is a fallback for cases where the track is subscribed but not yet in trackPublications
                console.log('🔄 Fallback: Trusting CustomLayoutManager detection and switching layout anyway');
                setScreenSharingParticipantId(participantId);
                setCurrentLayout('screen-share');
                return;
            }
            console.log('🖥️ Screen share detected (verified):', { participantId, userRole, currentLayout });
            // CRITICAL: For remote participants, explicitly subscribe to screen share track
            // This ensures students/guests receive the screen share track
            if (participantId !== room.localParticipant?.identity && participant) {
                const screenSharePub = tracks.find(pub => {
                    // CRITICAL: Check for Track.Source.ScreenShare enum value (not just strings)
                    const isScreenShare = pub.source === Track.Source.ScreenShare
                        || pub.source === 'screen_share'
                        || pub.source === 'screen';
                    return isScreenShare;
                });
                if (screenSharePub && !screenSharePub.isSubscribed && screenSharePub.setSubscribed) {
                    try {
                        console.log('📡 Explicitly subscribing to remote screen share track for:', participantId);
                        screenSharePub.setSubscribed(true);
                    }
                    catch (error) {
                        console.error('❌ Error subscribing to screen share track:', error);
                    }
                }
            }
            // CRITICAL: Switch to screen-share layout for ALL participants (teacher and students)
            // This replicates Zoom/Google Meet behavior - everyone sees the same layout
            console.log('🖥️ Switching to screen-share layout');
            setScreenSharingParticipantId(participantId);
            setCurrentLayout('screen-share');
            // Only set isScreenSharing to true if THIS participant is sharing
            if (participantId === room.localParticipant?.identity) {
                setIsScreenSharing(true);
                // CRITICAL: Persist screen share state to survive refresh (like Google Meet)
                sessionStorage.setItem('meetplus_screen_sharing', 'true');
                startScreenShareWatchdog();
                console.log('🖥️ Local participant is screen sharing');
                // CRITICAL: Clear saved state since we're now actively sharing
                screenShareStateBeforeDisconnectRef.current = null;
            }
            else {
                setIsScreenSharing(false);
                sessionStorage.removeItem('meetplus_screen_sharing');
                console.log('🖥️ Remote participant is screen sharing:', participantId);
            }
        }
        else {
            // Screen sharing stopped - only switch layout if we were in screen-share mode
            console.warn('🔔🔔🔔 SCREEN SHARE STOPPED - DETECTED BY LAYOUT MANAGER 🔔🔔🔔');
            console.warn('📊 DIAGNOSTIC INFO:', {
                timestamp: new Date().toISOString(),
                participantId,
                wasLocalSharing: participantId === lkRoomRef.current?.localParticipant?.identity,
                currentLayout,
                isScreenSharing,
                screenSharingParticipantId,
                userRole,
                documentHidden: document.hidden
            });
            if (currentLayout === 'screen-share') {
                console.error('🛑🛑🛑 STOPPING SCREEN SHARE - Layout manager detected no screen share tracks');
                console.error('📊 STOPPING REASON: CustomLayoutManager detected screen share ended');
                setScreenSharingParticipantId(null);
                setIsScreenSharing(false);
                // CRITICAL: Clear persisted state when screen share stops
                sessionStorage.removeItem('meetplus_screen_sharing');
                // Return to default spotlight layout for everyone
                setCurrentLayout('focus');
            }
            else {
                console.log('🔄 Screen share already stopped, just updating state');
                // Already in correct layout, just update state
                setScreenSharingParticipantId(null);
                setIsScreenSharing(false);
                sessionStorage.removeItem('meetplus_screen_sharing');
            }
            // CRITICAL: Ensure camera track is re-enabled after screen share stops
            // This must run regardless of layout - camera can be lost when screen share stops from browser tab
            const room = lkRoomRef.current;
            if (room?.localParticipant && participantId === room.localParticipant?.identity) {
                // Use multiple attempts with increasing delays to ensure camera is re-enabled
                const reenableCamera = async (attempt = 1) => {
                    try {
                        const localParticipant = room.localParticipant;
                        if (!localParticipant)
                            return;
                        const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                        const hasValidCamera = cameraPub && cameraPub.track && !cameraPub.isMuted && cameraPub.track.state === 'live';
                        // Re-enable camera if it was enabled before and is now missing/muted/inactive
                        if (cameraEnabled && !hasValidCamera) {
                            console.log(`📹 Camera track lost after screen share stopped (attempt ${attempt}) - re-enabling...`);
                            await localParticipant.setCameraEnabled(true);
                            // Verify it worked, retry if needed
                            setTimeout(async () => {
                                const cameraPubAfter = localParticipant.getTrackPublication(Track.Source.Camera);
                                const hasValidCameraAfter = cameraPubAfter && cameraPubAfter.track && !cameraPubAfter.isMuted && cameraPubAfter.track.state === 'live';
                                if (!hasValidCameraAfter && attempt < 3) {
                                    console.log(`📹 Camera still not active, retrying (attempt ${attempt + 1})...`);
                                    reenableCamera(attempt + 1);
                                }
                                else if (hasValidCameraAfter) {
                                    console.log('✅ Camera re-enabled after screen share stopped');
                                }
                            }, 500);
                        }
                        else if (hasValidCamera) {
                            console.log('✅ Camera track is still active after screen share stopped');
                        }
                    }
                    catch (error) {
                        console.error(`❌ Error re-enabling camera after screen share (attempt ${attempt}):`, error);
                        // Retry if not the last attempt
                        if (attempt < 3) {
                            setTimeout(() => reenableCamera(attempt + 1), 500);
                        }
                    }
                };
                // Start re-enabling immediately, then retry if needed
                setTimeout(() => reenableCamera(1), 100);
                setTimeout(() => reenableCamera(2), 600);
                setTimeout(() => reenableCamera(3), 1200);
            }
        }
        // CRITICAL: Reset processing flag after a short delay to allow state to settle
        setTimeout(() => {
            screenShareProcessingRef.current = false;
        }, 500);
    }, [userRole, isConnected, currentLayout, cameraEnabled]);
    // CRITICAL: Check for existing screen shares when joining (for guests joining during active screen share)
    // This ensures guests see screen share immediately when joining during an active session
    // FIX: Added ref to track if we've already detected screen share to prevent repeated calls
    const screenShareDetectedRef = useRef(false);
    useEffect(() => {
        if (!isConnected || !lkRoomRef.current)
            return;
        // CRITICAL: Don't run if already in screen-share layout OR if we've already detected it
        if (currentLayout === 'screen-share' || screenShareDetectedRef.current)
            return;
        const room = lkRoomRef.current;
        const checkForExistingScreenShare = () => {
            try {
                // CRITICAL: Double-check we're not already in screen-share mode
                if (currentLayout === 'screen-share' || screenShareDetectedRef.current)
                    return;
                // Check all remote participants for existing screen share tracks
                const remoteParticipants = room.remoteParticipants || new Map();
                let foundScreenShare = false;
                let sharingParticipantId = null;
                remoteParticipants.forEach((participant) => {
                    if (foundScreenShare)
                        return; // Already found one
                    if (participant && participant.trackPublications) {
                        const tracks = [...participant.trackPublications.values()];
                        const screenSharePub = tracks.find((pub) => {
                            const isScreenShare = pub.source === 'screen_share' || pub.source === 'screen';
                            const isVideo = pub.kind === 'video';
                            // Accept if it's a screen share video publication (even if not subscribed yet)
                            return isScreenShare && isVideo && pub.isSubscribed !== false;
                        });
                        if (screenSharePub) {
                            foundScreenShare = true;
                            sharingParticipantId = participant.identity;
                            console.log('🖥️ Found existing screen share publication from:', participant.identity);
                        }
                    }
                });
                if (foundScreenShare && sharingParticipantId) {
                    // CRITICAL: Mark as detected to prevent repeated calls
                    screenShareDetectedRef.current = true;
                    console.log('🖥️ Found existing screen share on join - switching layout:', sharingParticipantId);
                    // Call handleScreenShareDetected - it will now allow remote participant screen shares during initial join
                    handleScreenShareDetected(true, sharingParticipantId);
                }
            }
            catch (error) {
                console.error('❌ Error checking for existing screen share:', error);
            }
        };
        // Check immediately (CustomLayoutManager's useTracks should have detected it, but as fallback)
        // Also check after a short delay to catch tracks that are still being subscribed
        checkForExistingScreenShare();
        const timeoutId = setTimeout(() => {
            checkForExistingScreenShare();
        }, 2000); // Also check after 2 seconds to catch delayed subscriptions
        return () => clearTimeout(timeoutId);
    }, [isConnected, handleScreenShareDetected]); // Removed currentLayout from dependencies to prevent loop
    // CRITICAL: Sync screen sharing state with actual track publications
    // This ensures UI state matches reality, especially after reconnection
    // FIX: Removed currentLayout from dependencies and added debounce to prevent flickering loop
    const syncScreenShareStateRef = useRef(null);
    useEffect(() => {
        if (!isConnected || !lkRoomRef.current?.localParticipant)
            return;
        // CRITICAL: Debounce sync checks to prevent rapid state changes
        if (syncScreenShareStateRef.current) {
            clearTimeout(syncScreenShareStateRef.current);
        }
        syncScreenShareStateRef.current = setTimeout(() => {
            const localParticipant = lkRoomRef.current?.localParticipant;
            if (!localParticipant)
                return;
            const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
            const hasScreenShareTrack = screenPub?.track && (screenPub.track.state === 'live' || screenPub.track.state === 'starting');
            const isLocalParticipant = true; // We're checking local participant
            // CRITICAL: Only sync if there's a real mismatch - avoid unnecessary state changes
            // Check current layout state without triggering re-renders
            const currentLayoutState = currentLayout;
            // CRITICAL: Don't sync if we're already processing screen share changes
            if (screenShareProcessingRef.current) {
                return;
            }
            if (isScreenSharing && !hasScreenShareTrack) {
                console.log('🔄 Screen sharing state out of sync - track missing, updating state...');
                stopScreenShareWatchdog();
                setIsScreenSharing(false);
                sessionStorage.removeItem('meetplus_screen_sharing');
                setScreenSharingParticipantId(null);
                // Only change layout if we're actually in screen-share mode
                if (currentLayoutState === 'screen-share') {
                    setCurrentLayout('focus');
                }
            }
            else if (!isScreenSharing && hasScreenShareTrack && isLocalParticipant) {
                // CRITICAL: Only update if we're NOT already in screen-share layout to prevent flickering
                if (currentLayoutState !== 'screen-share') {
                    console.log('🔄 Screen sharing state out of sync - track exists but state is false, updating state...');
                    setIsScreenSharing(true);
                    startScreenShareWatchdog();
                    setScreenSharingParticipantId(localParticipant.identity);
                    setCurrentLayout('screen-share');
                }
            }
        }, 300); // Debounce by 300ms to prevent rapid changes
        return () => {
            if (syncScreenShareStateRef.current) {
                clearTimeout(syncScreenShareStateRef.current);
            }
        };
    }, [isConnected, isScreenSharing]); // Removed currentLayout from dependencies to prevent loop
    // CRITICAL: Prevent camera from being disabled during meeting (like Google Meet/Zoom)
    // Camera should NEVER be stopped unless user explicitly turns it off or meeting ends
    // We prevent this by ensuring camera stays enabled when screen sharing starts/stops
    // CRITICAL: Ensure camera track stays active during screen sharing (Zoom-style: show both screen share + camera)
    // This is a backup to the continuous monitor above
    useEffect(() => {
        if (!isConnected || !lkRoomRef.current?.localParticipant)
            return;
        // Only monitor when screen sharing is active (continuous monitor handles general case)
        if (isScreenSharing && cameraEnabled) {
            const checkInterval = setInterval(async () => {
                const localParticipant = lkRoomRef.current?.localParticipant;
                if (!localParticipant)
                    return;
                const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                // If we're screen sharing but camera track is missing/muted, re-enable it
                if (screenPub?.track && (!cameraPub || !cameraPub.track || cameraPub.isMuted)) {
                    console.log('📹 Camera track lost during screen share, re-enabling...');
                    try {
                        await localParticipant.setCameraEnabled(true);
                        console.log('✅ Camera re-enabled during screen share');
                    }
                    catch (error) {
                        console.error('❌ Error re-enabling camera during screen share:', error);
                    }
                }
            }, 2000); // Check every 2 seconds while screen sharing
            return () => clearInterval(checkInterval);
        }
    }, [isScreenSharing, isConnected, cameraEnabled]);
    // Handle teacher identification
    const handleTeacherIdentified = useCallback((teacherId) => {
        setTeacherParticipantId(teacherId);
        console.log('👨‍🏫 Teacher identified:', teacherId);
    }, []);
    // Handle participant pinning
    const handleParticipantPin = useCallback((participantId) => {
        setPinnedParticipant(prev => prev === participantId ? null : participantId);
    }, []);
    // Handle puzzle sharing - completely rewritten
    const handlePuzzleShared = useCallback((data) => {
        console.log('♟️ Puzzle event received:', data);
        // Handle different puzzle events
        if (data.action === 'PUZZLE_SHARED') ;
        else if (data.action === 'STUDENT_MOVE' && userRole === 'teacher') {
            // Teacher receives student moves in real-time
            console.log('♟️ Student move:', {
                student: data.playerName,
                move: data.san,
                isCorrect: data.isCorrect
            });
            // Removed non-critical toast - moves shown in UI
        }
        else if (data.action === 'PUZZLE_COMPLETED' && userRole === 'teacher') {
            // Teacher receives puzzle completion
            console.log('♟️ Puzzle completed by:', data.playerName);
            // Removed non-critical toast - completion shown in UI
        }
    }, [userRole]);
    // Handle poll started
    const handlePollStarted = useCallback(() => {
        setShowPollsQuizzes(true);
        // Removed non-critical toast - UI shows poll panel
    }, []);
    // Handle chat messages
    const handleChatMessage = useCallback((data) => {
        if (!data.name || !data.message) {
            console.warn('⚠️ Invalid chat message:', data);
            return;
        }
        console.log('📨 Received chat message:', {
            from: data.name,
            myName: participantName,
            isTeacher: data.isTeacher,
            message: data.message,
            timestamp: new Date(data.timestamp || Date.now()).toLocaleTimeString()
        });
        const newMessage = {
            name: data.name,
            message: data.message,
            time: new Date(data.timestamp || Date.now()).toLocaleTimeString(),
            id: `${data.name}-${data.timestamp || Date.now()}`,
            isTeacher: data.isTeacher || false,
            isLocal: data.name === participantName
        };
        setChatMessages(prev => {
            if (!showChat) {
                setUnreadMessageCount(prev => prev + 1);
            }
            return [...prev, newMessage];
        });
    }, [showChat, participantName]);
    // Send chat message
    const sendChatMessage = async () => {
        if (!chatInput.trim() || !lkRoomRef.current)
            return;
        const messageText = chatInput.trim();
        try {
            const messageData = {
                type: 'chat_message',
                name: participantName,
                message: messageText,
                timestamp: Date.now(),
                isTeacher: userRole === 'teacher'
            };
            console.log('📤 Sending chat message:', messageData);
            // Add message to local chat immediately for better UX
            const localMessage = {
                name: participantName,
                message: messageText,
                time: new Date().toLocaleTimeString(),
                id: `${participantName}-${Date.now()}`,
                isTeacher: userRole === 'teacher',
                isLocal: true
            };
            setChatMessages(prev => [...prev, localMessage]);
            // Convert JSON string to Uint8Array for LiveKit
            const encoder = new TextEncoder();
            const dataToSend = encoder.encode(JSON.stringify(messageData));
            console.log('📤 Sending data:', {
                type: messageData.type,
                name: messageData.name,
                message: messageData.message,
                asUint8Array: dataToSend,
                length: dataToSend.length
            });
            await lkRoomRef.current.localParticipant.publishData(dataToSend, { reliable: true });
            console.log('✅ Chat message sent successfully');
            // Clear input
            setChatInput('');
        }
        catch (error) {
            console.error('Error sending chat message:', error);
            // Removed toast - no toasts in LiveKit room except Chequered
        }
    };
    // Removed SingleParticipantMonitor component - using 70-minute auto-stop instead
    const finalizeCloudRecordingStop = async (stopMessage, toastMessage, toastVariant = 'success') => {
        try {
            const firestoreDocId = recordingIdRef.firestoreDocId;
            if (firestoreDocId && tenantId) {
                const recordingDocRef = doc(db, 'ServiceProviders', tenantId, 'Recordings', firestoreDocId);
                await updateDoc(recordingDocRef, {
                    status: 'stopped',
                    stopped_at: serverTimestamp(),
                    updated_at: serverTimestamp(),
                    stop_response: stopMessage || 'Recording stopped'
                });
                console.log('✅ Recording status updated in Firestore (stop)');
            }
            else {
                console.log('⚠️ No Firestore doc ID found while finalizing stop, skipping update');
            }
        }
        catch (firestoreError) {
            console.error('⚠️ Error updating recording status in Firestore during stop:', firestoreError);
        }
        setIsRecording(false);
        setRecordingMode(null);
        recordingIdRef.current = null;
        setRecordingStartMs(null);
        recordingIdRef.firestoreDocId = null;
    };
    // Recording functions
    const stopRecordingIfActive = async () => {
        console.log('🛑 stopRecordingIfActive called', { isRecording, recordingMode, recordingId: recordingIdRef.current });
        if (!isRecording) {
            console.log('⚠️ Not recording, returning early');
            return;
        }
        // Comprehensive logging for recording stop - ALL INFO TOGETHER
        const room = lkRoomRef.current;
        const localParticipant = room?.localParticipant;
        const recordingId = recordingIdRef.current;
        const recordingDuration = recordingStartMs ? Date.now() - recordingStartMs : null;
        const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
        const micPub = localParticipant?.getTrackPublication(Track.Source.Microphone);
        const screenPub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
        logLiveKitEvent('RECORDING_STOP', {
            // Recording Information
            recordingId: recordingId,
            recordingMode: recordingMode,
            firestoreDocId: recordingIdRef.firestoreDocId,
            // Room Information
            roomName: room?.name,
            roomSid: room?.sid,
            serverUrl: room?.engine?.ws?.url || serverUrl,
            connectionState: room?.state,
            // Participant Information
            participantIdentity: localParticipant?.identity,
            participantName: localParticipant?.name,
            userRole: userRole,
            // Current State
            isScreenSharing: isScreenSharing,
            screenSharingParticipantId: screenSharingParticipantId,
            cameraEnabled: cameraEnabled,
            micEnabled: micEnabled,
            // Track Status
            cameraTrackState: cameraPub?.track?.state,
            cameraTrackMuted: cameraPub?.isMuted,
            micTrackState: micPub?.track?.state,
            micTrackMuted: micPub?.isMuted,
            screenShareTrackState: screenPub?.track?.state,
            screenShareTrackSid: screenPub?.trackSid,
            // Recording Duration
            recordingDurationMs: recordingDuration,
            recordingDurationMinutes: recordingDuration ? Math.round(recordingDuration / 60000 * 100) / 100 : null,
            recordingDurationHours: recordingDuration ? Math.round(recordingDuration / 3600000 * 100) / 100 : null,
            recordingStartMs: recordingStartMs,
            // Timing
            timestamp: Date.now()
        });
        // CRITICAL: Recording MUST stop regardless of API errors (monetary implications)
        // We'll update UI state immediately to prevent continued billing
        if (recordingMode === 'cloud' && recordingIdRef.current) {
            // Stop cloud recording - try API call but ALWAYS finalize stop
            console.log('☁️ Stopping cloud recording...', { recordingId: recordingIdRef.current });
            let apiSuccess = false;
            let apiResponse = null;
            let apiError = null;
            // Try to call API, but don't block on it
            try {
                const response = await axios.post(`${API_ENDPOINT}/api/meet/stopRecording`, {
                    recordingId: recordingIdRef.current
                });
                apiSuccess = true;
                apiResponse = response.data;
                console.log('☁️ Stop recording API response:', response.data);
            }
            catch (error) {
                apiError = error;
                const errorMessage = error.response?.data?.message ||
                    error.response?.data?.error ||
                    error.message ||
                    'API call failed';
                const statusCode = error.response?.status;
                console.warn('⚠️ Stop recording API call failed (but will still stop):', {
                    statusCode,
                    errorMessage,
                    recordingId: recordingIdRef.current
                });
                // Log warning but continue - recording will still be stopped
                const normalizedMessage = errorMessage.toLowerCase();
                const isAlreadyStopped = normalizedMessage.includes('already stopped') ||
                    normalizedMessage.includes('egress aborted') ||
                    normalizedMessage.includes('not in progress') ||
                    normalizedMessage.includes('no such recording') ||
                    normalizedMessage.includes('precondition failed') ||
                    statusCode === 404 ||
                    statusCode === 412;
                if (isAlreadyStopped) {
                    console.log('ℹ️ Recording appears to already be stopped on server');
                }
            }
            // CRITICAL: ALWAYS finalize stop regardless of API result
            // This ensures UI updates and prevents continued billing
            try {
                await finalizeCloudRecordingStop(apiSuccess
                    ? (apiResponse?.message || 'Recording stopped successfully')
                    : (apiError?.response?.data?.message || 'Recording stop initiated (API call had issues)'), apiSuccess
                    ? '☁️ Cloud recording stopped'
                    : '☁️ Cloud recording stopped (with API warning)', apiSuccess ? 'success' : 'warning');
                console.log('✅ Cloud recording finalized - UI updated');
                // Clear saved recording state since recording was intentionally stopped
                recordingStateBeforeDisconnectRef.current = null;
            }
            catch (finalizeError) {
                console.error('❌ Error finalizing recording stop:', finalizeError);
                // Force state update as absolute last resort
                setIsRecording(false);
                setRecordingMode(null);
                recordingIdRef.current = null;
                setRecordingStartMs(null);
                recordingIdRef.firestoreDocId = null;
                // Clear saved recording state
                recordingStateBeforeDisconnectRef.current = null;
                // Removed non-critical toast - UI shows recording state
            }
        }
        else {
            // Unknown recording mode - force stop anyway
            console.warn('⚠️ Unknown recording mode, forcing stop:', recordingMode);
            setIsRecording(false);
            setRecordingMode(null);
            recordingIdRef.current = null;
            setRecordingStartMs(null);
        }
    };
    // Parse roomName to extract originalRoomName
    // Format: "tenant-abc-myroom-1730373390123" -> originalRoomName: "myroom"
    const parseOriginalRoomName = (roomName) => {
        // Split by '-' and get all parts except first (tenantId) and last (timestamp)
        const parts = roomName.split('-');
        if (parts.length >= 3) {
            // Remove tenantId (first) and timestamp (last), join the rest
            return parts.slice(1, -1).join('-');
        }
        // Fallback: return roomName without tenant prefix if format is unexpected
        return roomName.replace(/^[^-]+-/, '');
    };
    const startCloudRecording = async () => {
        console.log('🚀 startCloudRecording function called!');
        // Note: Loading state is managed by the caller (button handler)
        // CRITICAL: Preserve screen share during recording (like Google Meet)
        // Screen share must continue working reliably for hours during recording
        // DO NOT stop or interfere with screen share tracks when recording starts
        const room = lkRoomRef.current;
        if (room) {
            const localParticipant = room.localParticipant;
            const screenSharePub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
            if (screenSharePub?.track && isScreenSharing) {
                console.log('🖥️ Screen share is active - preserving during recording (like Google Meet)');
                // Ensure screen share track remains active - don't touch it
            }
        }
        // CRITICAL: ALWAYS use pip-circle layout (backgrounds work correctly in this mode)
        // Side-by-side has issues where background effects are not applied to camera feed
        console.log('📐 Using FORCED pip-circle layout for recording (ensures backgrounds are recorded)');
        // ⚠️ BACKEND RECORDING CONFIGURATION ISSUE:
        // When screen sharing is active during recording, the backend recording service
        // currently records both the screen share track AND the camera feed as a PiP overlay.
        // This causes duplicate thumbnails in the recorded video, and the camera PiP thumbnail
        // doesn't have background effects (because backgrounds are applied client-side).
        //
        // BACKEND FIX REQUIRED:
        // The backend recording service (LiveKit Egress) should be configured to:
        // 1. When screen sharing is active: Record ONLY the screen share track (no camera PiP)
        // 2. When screen sharing is NOT active: Record camera feed with pip-circle layout as normal
        // 3. Detect screen sharing from Track.Source.ScreenShare tracks in the room
        // 4. Use different recording presets/templates based on screen share state
        //
        // The backend should NOT include the camera feed as a PiP when screen sharing is active,
        // to avoid duplicate thumbnails and missing background effects.
        // CRITICAL: Check if recording is already active to prevent duplicate recordings
        if (isRecording && recordingIdRef.current) {
            console.warn('⚠️ Recording already active - cannot start new recording:', {
                isRecording,
                recordingId: recordingIdRef.current,
                mode: recordingMode
            });
            // Removed toast - no toasts in LiveKit room except Chequered
            throw new Error('Recording already active');
        }
        if (!tenantId) {
            console.error('❌ tenantId is missing!');
            // Removed toast - no toasts in LiveKit room except Chequered
            throw new Error('Missing tenant ID');
        }
        if (!roomName) {
            console.error('❌ roomName is missing!');
            // Removed toast - no toasts in LiveKit room except Chequered
            throw new Error('Missing room name');
        }
        try {
            const originalRoomName = parseOriginalRoomName(roomName);
            console.log('☁️ Starting cloud recording...', {
                roomName,
                tenantId,
                originalRoomName,
                participantName,
                API_ENDPOINT
            });
            // Map resolution to width/height (cloud recording supports 720p/480p, map 1080p to 720p)
            const cloudResolution = selectedResolution === '1080p' ? '720p' : selectedResolution;
            const resolutionMap = {
                '480p': { width: 854, height: 480 },
                '720p': { width: 1280, height: 720 },
                '1080p': { width: 1920, height: 1080 } // Will be mapped to 720p for cloud
            };
            const { width: videoWidth, height: videoHeight } = resolutionMap[cloudResolution] || resolutionMap['480p'];
            // Calculate bitrates for excellent quality (optimized for memory efficiency like Google Meet)
            // Google Meet uses ~15 FPS for recording to reduce memory usage and ensure reliability
            // 480p: Video 1200 kbps + Audio 128 kbps = 1328 kbps ≈ 600 MB/hour (excellent quality)
            // 720p: Video 2500 kbps + Audio 128 kbps = 2628 kbps ≈ 1.15 GB/hour (high quality)
            // Reduced FPS from 30 to 15 for better memory efficiency and reliability (like Google Meet)
            const videoBitrateKbps = cloudResolution === '720p' ? 2500 : 1200; // 480p: 1200 kbps (excellent), 720p: 2500 kbps
            const audioBitrateKbps = 128; // Always use 128 kbps for better audio quality
            const fps = 15; // Google Meet uses ~15 FPS for recording - reduces memory usage and ensures reliability
            // Build request payload with recording options
            // CRITICAL: Always use 'pip-circle' - ensures backgrounds are properly recorded
            const requestPayload = {
                roomName: roomName,
                options: {
                    tenantId: tenantId,
                    originalRoomName: originalRoomName,
                    createdBy: participantName || 'teacher',
                    // Recording layout (CRITICAL: Always 'pip-circle' - backgrounds work correctly in this mode)
                    layout: FORCED_RECORDING_LAYOUT, // Always pip-circle for proper background recording
                    // Video dimensions (width & height coming from frontend)
                    videoWidth: videoWidth,
                    videoHeight: videoHeight,
                    // Bitrate settings (in kbps as per backend expectation)
                    videoBitrate: videoBitrateKbps,
                    audioBitrate: audioBitrateKbps,
                    // Frame rate and codec
                    fps: fps,
                    videoCodec: 'vp9',
                    // Screen sharing state (BACKEND: Use this to exclude camera PiP when screen sharing is active)
                    // When isScreenSharing is true, backend should record ONLY screen share (no camera PiP)
                    // to avoid duplicate thumbnails and missing background effects
                    isScreenSharing: isScreenSharing
                }
            };
            // Log the exact payload being sent
            console.log('📤 Request payload (EXACT):', JSON.stringify(requestPayload, null, 2));
            console.log('📤 Request payload (structure check):', {
                hasRoomName: !!requestPayload.roomName,
                hasOptions: !!requestPayload.options,
                optionsKeys: requestPayload.options ? Object.keys(requestPayload.options) : [],
                optionsTenantId: requestPayload.options?.tenantId,
                optionsOriginalRoomName: requestPayload.options?.originalRoomName,
                optionsCreatedBy: requestPayload.options?.createdBy,
                optionsLayout: requestPayload.options?.layout,
                currentRecordLayout: recordLayout,
                forcedLayout: 'pip-circle', // Always forced to pip-circle
                videoWidth: requestPayload.options?.videoWidth,
                videoHeight: requestPayload.options?.videoHeight,
                videoBitrate: requestPayload.options?.videoBitrate,
                audioBitrate: requestPayload.options?.audioBitrate,
                fps: requestPayload.options?.fps,
                videoCodec: requestPayload.options?.videoCodec
            });
            console.log('📤 Recording Quality Settings:', {
                resolution: cloudResolution,
                layout: 'pip-circle', // Always pip-circle
                layoutState: recordLayout, // For debugging
                videoBitrate: `${videoBitrateKbps} kbps`,
                audioBitrate: `${audioBitrateKbps} kbps`,
                dimensions: `${videoWidth}x${videoHeight}`,
                fps: fps,
                codec: 'vp9'
            });
            console.log('📤 API URL:', `${API_ENDPOINT}/api/meet/startRecording`);
            // Call API with correct format - DO NOT modify payload
            const response = await axios.post(`${API_ENDPOINT}/api/meet/startRecording`, requestPayload, // Send as-is, no transformation
            {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            console.log('✅ Cloud recording API response:', response.data);
            console.log('✅ Response status:', response.status);
            // Extract recording_id from response
            const recordingId = response.data?.recording_id;
            if (!recordingId) {
                throw new Error('No recording ID received');
            }
            // Store recording data in Firestore
            try {
                const recordingsRef = collection(db, 'ServiceProviders', tenantId, 'Recordings');
                const recordingDocRef = await addDoc(recordingsRef, {
                    recording_id: recordingId,
                    room_name: roomName,
                    status: response.data?.status || 'active',
                    filename: response.data?.filename || null,
                    s3_bucket: response.data?.s3_bucket || null,
                    s3_key: response.data?.s3_key || null,
                    started_at: response.data?.started_at || serverTimestamp(),
                    tenant_id: tenantId,
                    original_room_name: originalRoomName,
                    created_by: participantName || 'teacher',
                    meeting_title: meetingTitle,
                    recording_type: 'cloud',
                    resolution: selectedResolution === '1080p' ? '720p' : selectedResolution, // Cloud only supports 720p/480p, map 1080p to 720p
                    layout: 'pip-circle', // CRITICAL: Always 'pip-circle' - only supported layout
                    created_at: serverTimestamp(),
                    updated_at: serverTimestamp()
                });
                console.log('✅ Recording saved to Firestore:', recordingDocRef.id);
                console.log('📐 Recording layout confirmed:', recordLayout);
                console.log('📹 Recording resolution confirmed:', selectedResolution === '1080p' ? '720p' : selectedResolution);
                // Store Firestore document ID for potential future updates
                recordingIdRef.firestoreDocId = recordingDocRef.id;
            }
            catch (firestoreError) {
                console.error('⚠️ Error saving recording to Firestore:', firestoreError);
                // Don't fail the recording if Firestore save fails
            }
            recordingIdRef.current = recordingId;
            setIsRecording(true);
            setRecordingMode('cloud');
            setRecordingStartMs(Date.now());
            // Comprehensive logging for recording start - ALL INFO TOGETHER
            const room = lkRoomRef.current;
            const localParticipant = room?.localParticipant;
            const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
            const micPub = localParticipant?.getTrackPublication(Track.Source.Microphone);
            const screenPub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
            logLiveKitEvent('RECORDING_START', {
                // Recording Information
                recordingId: recordingId,
                recordingMode: 'cloud',
                firestoreDocId: recordingIdRef.firestoreDocId,
                // Room Information
                roomName: room?.name,
                roomSid: room?.sid,
                serverUrl: room?.engine?.ws?.url || serverUrl,
                connectionState: room?.state,
                // Participant Information
                participantIdentity: localParticipant?.identity,
                participantName: localParticipant?.name,
                userRole: userRole,
                tenantId: tenantId,
                // Current State
                isScreenSharing: isScreenSharing,
                screenSharingParticipantId: screenSharingParticipantId,
                cameraEnabled: cameraEnabled,
                micEnabled: micEnabled,
                // Track Status
                cameraTrackState: cameraPub?.track?.state,
                cameraTrackMuted: cameraPub?.isMuted,
                micTrackState: micPub?.track?.state,
                micTrackMuted: micPub?.isMuted,
                screenShareTrackState: screenPub?.track?.state,
                screenShareTrackSid: screenPub?.trackSid,
                // Recording Configuration
                recordingConfig: {
                    layout: recordLayout,
                    resolution: selectedResolution === '1080p' ? '720p' : selectedResolution,
                    videoBitrate: `${videoBitrateKbps} kbps`,
                    audioBitrate: `${audioBitrateKbps} kbps`,
                    dimensions: `${videoWidth}x${videoHeight}`,
                    fps: fps,
                    codec: 'vp9'
                },
                // Timing
                timestamp: Date.now(),
                recordingStartMs: Date.now()
            });
            // Removed non-critical toast - UI shows recording indicator
            console.log('☁️ Cloud recording started successfully:', recordingId);
            // Clear loading state on success
            setIsRecordingLoading(false);
        }
        catch (error) {
            console.error('❌ Error starting cloud recording:', error);
            console.error('❌ Error details:', {
                message: error.message,
                response: error.response?.data,
                status: error.response?.status,
                statusText: error.response?.statusText,
                config: {
                    url: error.config?.url,
                    method: error.config?.method,
                    data: error.config?.data
                },
                stack: error.stack
            });
            error.response?.data?.error ||
                error.response?.data?.message ||
                error.message ||
                'Failed to start cloud recording';
            // Removed toast - no toasts in LiveKit room except Chequered
            // Clear loading state on error
            setIsRecordingLoading(false);
            // Re-throw so caller can handle if needed
            throw error;
        }
    };
    // Meeting hours tracking - Only track teacher minutes
    const startMeetingHoursTracking = async () => {
        if (userRole !== 'teacher')
            return;
        try {
            const meetingRef = doc(db, 'meetings', `${tenantId}_${roomName}`);
            await setDoc(meetingRef, {
                startTime: serverTimestamp(),
                tenantId,
                roomName,
                status: 'active',
                teacherPresent: true,
                teacherStartTime: serverTimestamp()
            }, { merge: true });
            // Store the current hours balance at meeting start for real-time calculation
            const currentHours = subscriptionHoursRemaining;
            setInitialHoursAtMeetingStart(currentHours);
            setMeetingStartTime(Date.now());
            console.log('📊 Teacher meeting hours tracking started', {
                initialHours: currentHours,
                startTime: new Date().toISOString()
            });
            // CRITICAL: Auto-stop meeting after 1 hour 10 minutes (70 minutes)
            // Show warning at 60 minutes (10 minutes before auto-stop)
            const WARNING_TIME = 60 * 60 * 1000; // 60 minutes in ms
            const AUTO_STOP_TIME = 70 * 60 * 1000; // 70 minutes in ms
            // Warning at 60 minutes
            const warningTimer = setTimeout(() => {
                console.warn('⏰ Meeting has been running for 60 minutes - will auto-stop in 10 minutes');
                // Show toast notification
                if (typeof window !== 'undefined' && window.showToast) {
                    window.showToast('Meeting will automatically end in 10 minutes', 'warning');
                }
            }, WARNING_TIME);
            // Auto-stop at 70 minutes
            const autoStopTimer = setTimeout(async () => {
                console.warn('🛑 Auto-stopping meeting after 70 minutes');
                // Show notification
                if (typeof window !== 'undefined' && window.showToast) {
                    window.showToast('Meeting automatically ended after 1 hour 10 minutes', 'info');
                }
                // Disconnect from meeting
                await stopMeetingHoursTracking();
                onDisconnect(true); // Intentional disconnect
            }, AUTO_STOP_TIME);
            // Store timers for cleanup
            window.__meetingAutoStopTimers = { warningTimer, autoStopTimer };
        }
        catch (error) {
            console.error('Error starting meeting tracking:', error);
        }
    };
    const stopMeetingHoursTracking = async () => {
        if (userRole !== 'teacher' || !meetingStartTime || !tenantId)
            return;
        try {
            const duration = (Date.now() - meetingStartTime) / (1000 * 60 * 60); // hours
            // Save meeting to top-level meetings collection
            if (duration > 0) {
                try {
                    await addDoc(collection(db, 'meetings'), {
                        serviceProviderId: tenantId,
                        roomName: roomName,
                        meetingTitle: meetingTitle,
                        subscriptionId: subscriptionId || null,
                        subscriptionName: subscriptionName || null,
                        startTime: new Date(meetingStartTime),
                        endTime: serverTimestamp(),
                        duration: duration,
                        status: 'ended',
                        createdAt: serverTimestamp()
                    });
                    console.log('📝 Meeting logged to meetings collection');
                }
                catch (meetingError) {
                    console.error('Error saving meeting to collection:', meetingError);
                }
            }
            // Deduct hours from serviceProviders.meetPlusHours (with 5hr trial for new users)
            if (duration > 0) {
                try {
                    const serviceProviderRef = doc(db, 'serviceProviders', tenantId);
                    const serviceProviderSnap = await getDoc(serviceProviderRef);
                    if (serviceProviderSnap.exists()) {
                        const data = serviceProviderSnap.data();
                        let meetPlusHours = data.meetPlusHours || 0;
                        // If no hours configured, grant 5 trial hours
                        if (meetPlusHours === 0 || meetPlusHours === null || meetPlusHours === undefined) {
                            meetPlusHours = 5;
                            console.log('🎁 Granting 5 trial hours for new service provider');
                            // Removed toast - no toasts in LiveKit room except Chequered
                        }
                        const remaining = meetPlusHours - duration;
                        // Update serviceProvider with meetPlusHours (deduct meeting duration)
                        await updateDoc(serviceProviderRef, {
                            meetPlusHours: Math.max(0, remaining), // Don't go below 0
                            lastUpdated: serverTimestamp()
                        });
                        // Update local state
                        setSubscriptionHoursRemaining(Math.max(0, remaining));
                        setInitialHoursAtMeetingStart(null); // Clear initial hours after meeting ends
                        console.log('📊 Hours deducted from service provider (teacher only):', {
                            meetPlusHours: meetPlusHours.toFixed(2),
                            duration: duration.toFixed(2),
                            remaining: remaining.toFixed(2)
                        });
                        // Warn if running low
                        if (remaining <= 1 && remaining > 0) {
                            // Removed toast - no toasts in LiveKit room except Chequered
                        }
                        else if (remaining <= 0) {
                            // Removed toast - no toasts in LiveKit room except Chequered
                            setShowPurchaseHoursDialog(true);
                        }
                    }
                }
                catch (spError) {
                    console.error('Error updating service provider hours:', spError);
                }
            }
            console.log('📊 Teacher meeting hours tracking stopped - Duration:', duration.toFixed(2), 'hours');
        }
        catch (error) {
            console.error('Error stopping meeting tracking:', error);
        }
    };
    // Handle teacher/student leaving - show dialog first, cleanup only after confirmation
    const handleTeacherLeave = (e) => {
        // Prevent event bubbling
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        console.log('🔴 Leave button clicked, userRole:', userRole);
        console.log('🔴 Current dialog state:', showLeaveConfirmDialog);
        // CRITICAL: Don't do any cleanup here - just show dialog for teachers
        // All cleanup happens in executeLeave after user confirms
        // Store the cleanup function to execute after confirmation
        const executeLeave = async (endForAll) => {
            // Mark as intentional disconnect to prevent error toasts
            isIntentionalDisconnectRef.current = true;
            // Reset connection tracking for next session
            hasEverConnectedRef.current = false;
            isInitialConnectionRef.current = true;
            reconnectAttemptCountRef.current = 0;
            // Complete cleanup of media and browser indicators
            await cleanupAllMediaAndIndicators();
            try {
                // CRITICAL: Stop recording FIRST and wait for it to complete before disconnecting
                if (isRecording) {
                    console.log('🛑 Stopping recording before leaving meeting...');
                    // Removed non-critical toast - UI shows recording state
                    try {
                        await stopRecordingIfActive();
                        // Removed non-critical toast - UI shows recording state
                    }
                    catch (recordingError) {
                        console.error('Error stopping recording:', recordingError);
                        // Removed non-critical toast - error already logged
                        // Continue with disconnect even if recording stop fails
                    }
                }
                // Complete cleanup of media and browser indicators (only stops screen share)
                await cleanupAllMediaAndIndicators();
                // CRITICAL: Stop camera and mic tracks ONLY when explicitly leaving the meeting
                // This is the ONLY place where we stop camera/mic tracks (except user explicitly toggling them off)
                if (lkRoomRef.current?.localParticipant) {
                    const localParticipant = lkRoomRef.current.localParticipant;
                    // Stop camera track
                    const videoTrack = localParticipant.getTrackPublication(Track.Source.Camera)?.track;
                    if (videoTrack) {
                        try {
                            await videoTrack.stop();
                            console.log('📹 Camera track stopped (leaving meeting)');
                        }
                        catch (error) {
                            console.warn('⚠️ Error stopping camera track:', error);
                        }
                    }
                    // Stop microphone track
                    const audioTrack = localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
                    if (audioTrack) {
                        try {
                            await audioTrack.stop();
                            console.log('🎤 Microphone track stopped (leaving meeting)');
                        }
                        catch (error) {
                            console.warn('⚠️ Error stopping microphone track:', error);
                        }
                    }
                    // CRITICAL: Stop screen share track (if any) - must use setScreenShareEnabled to clear browser indicator
                    const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                    if (screenPub?.track) {
                        try {
                            console.log('🖥️ Stopping screen share via setScreenShareEnabled to clear browser indicator (leaving meeting)...');
                            // Step 1: Stop via LiveKit API to clear browser indicator
                            await localParticipant.setScreenShareEnabled(false);
                            // Step 2: Stop the underlying MediaStream track directly
                            const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                            if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                console.log('🛑 Stopping underlying MediaStream track to clear browser indicators (leaving meeting)...');
                                mediaStreamTrack.stop();
                                console.log('✅ MediaStream track stopped (leaving meeting)');
                            }
                            // Step 3: Also stop the LiveKit track if it's still active
                            if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                                try {
                                    await screenPub.track.stop();
                                    console.log('✅ LiveKit screen share track stopped (leaving meeting)');
                                }
                                catch (trackError) {
                                    console.warn('⚠️ Error stopping LiveKit track (leaving meeting):', trackError);
                                }
                            }
                            // Step 4: Find and stop any remaining screen share MediaStreams
                            try {
                                const allVideoElements = document.querySelectorAll('video');
                                allVideoElements.forEach(video => {
                                    if (video.srcObject && video.srcObject instanceof MediaStream) {
                                        const stream = video.srcObject;
                                        stream.getVideoTracks().forEach(track => {
                                            const label = track.label.toLowerCase();
                                            if (track.kind === 'video' && (label.includes('screen') ||
                                                label.includes('window') ||
                                                label.includes('display') ||
                                                label.includes('monitor'))) {
                                                console.log('🛑 Stopping screen share MediaStream from video element (leaving meeting):', track.label);
                                                try {
                                                    track.stop();
                                                }
                                                catch (e) {
                                                    console.warn('⚠️ Error stopping track from video element (leaving meeting):', e);
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                            catch (streamError) {
                                console.warn('⚠️ Error stopping screen share streams (leaving meeting):', streamError);
                            }
                            console.log('✅ Screen share stopped and browser indicator should be cleared (leaving meeting)');
                        }
                        catch (error) {
                            console.warn('⚠️ Error stopping screen share track (leaving meeting):', error);
                            // Fallback: try to stop the track directly
                            try {
                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                if (mediaStreamTrack) {
                                    mediaStreamTrack.stop();
                                }
                                await screenPub.track.stop();
                            }
                            catch (fallbackError) {
                                console.warn('⚠️ Error in fallback screen share stop (leaving meeting):', fallbackError);
                            }
                        }
                    }
                }
                // CRITICAL: Stop initialMediaStream camera/mic tracks when leaving
                if (initialMediaStream) {
                    initialMediaStream.getTracks().forEach(track => {
                        // Stop ALL tracks from initialMediaStream when leaving (camera, mic, screen share)
                        console.log(`🛑 Stopping initial media stream ${track.kind} track (leaving meeting):`, track.label);
                        try {
                            track.stop();
                        }
                        catch (error) {
                            console.warn(`⚠️ Error stopping ${track.kind} track from initialMediaStream:`, error);
                        }
                    });
                }
                // CRITICAL: Stop any remaining camera/mic tracks from video/audio elements when leaving
                if (navigator.mediaDevices && 'getUserMedia' in navigator.mediaDevices) {
                    try {
                        const allVideoElements = document.querySelectorAll('video');
                        allVideoElements.forEach(video => {
                            if (video.srcObject && video.srcObject instanceof MediaStream) {
                                const stream = video.srcObject;
                                stream.getTracks().forEach(track => {
                                    // Stop ALL tracks when leaving (camera, mic, screen share)
                                    console.log(`🛑 Stopping ${track.kind} track from video element (leaving meeting):`, track.label);
                                    try {
                                        track.stop();
                                    }
                                    catch (error) {
                                        console.warn(`⚠️ Error stopping ${track.kind} track from video element:`, error);
                                    }
                                });
                                video.srcObject = null;
                            }
                        });
                        const allAudioElements = document.querySelectorAll('audio');
                        allAudioElements.forEach(audio => {
                            if (audio.srcObject && audio.srcObject instanceof MediaStream) {
                                const stream = audio.srcObject;
                                stream.getTracks().forEach(track => {
                                    // Stop ALL audio tracks when leaving
                                    console.log(`🛑 Stopping ${track.kind} track from audio element (leaving meeting):`, track.label);
                                    try {
                                        track.stop();
                                    }
                                    catch (error) {
                                        console.warn(`⚠️ Error stopping ${track.kind} track from audio element:`, error);
                                    }
                                });
                                audio.srcObject = null;
                            }
                        });
                    }
                    catch (error) {
                        console.warn('⚠️ Error stopping tracks from media elements:', error);
                    }
                }
                if (userRole === 'teacher') {
                    if (endForAll) {
                        try {
                            // CRITICAL: Broadcast "END_MEETING" message to all participants BEFORE disconnecting
                            // This ensures all participants receive the disconnect signal
                            if (lkRoomRef.current?.localParticipant) {
                                try {
                                    const endMeetingData = {
                                        type: 'meeting',
                                        action: 'END_MEETING',
                                        timestamp: Date.now(),
                                        message: 'The meeting has been ended by the host'
                                    };
                                    const encoder = new TextEncoder();
                                    const payload = encoder.encode(JSON.stringify(endMeetingData));
                                    await lkRoomRef.current.localParticipant.publishData(payload, { reliable: true });
                                    console.log('📢 End meeting message broadcasted to all participants');
                                    // Give a small delay to ensure message is sent before disconnecting
                                    await new Promise(resolve => setTimeout(resolve, 500));
                                }
                                catch (broadcastError) {
                                    console.error('❌ Error broadcasting end meeting message:', broadcastError);
                                    // Continue even if broadcast fails
                                }
                            }
                            // End meeting for all participants (optional API call)
                            try {
                                await axios.post(`${API_ENDPOINT}/api/meet/endRoom`, {
                                    roomName,
                                    tenantId
                                });
                                console.log('✅ Meeting ended via API');
                            }
                            catch (apiError) {
                                console.warn('⚠️ endRoom API not available, continuing with local cleanup:', apiError);
                            }
                            // Stop meeting hours tracking
                            await stopMeetingHoursTracking();
                            // Removed toast - no toasts in LiveKit room except Chequered
                        }
                        catch (error) {
                            console.error('Error ending meeting:', error);
                            // Don't show error toast on intentional exit
                        }
                    }
                    else {
                        // Just leave without ending for others
                        await stopMeetingHoursTracking();
                    }
                }
                else {
                    // Student leaving - just stop tracking
                    await stopMeetingHoursTracking();
                }
                // CRITICAL: Stop screen share before disconnecting to clear browser indicator
                if (lkRoomRef.current?.localParticipant) {
                    const localParticipant = lkRoomRef.current.localParticipant;
                    const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                    if (screenPub?.track) {
                        try {
                            console.log('🖥️ Stopping screen share before disconnect to clear browser indicator...');
                            // Step 1: Stop via LiveKit API to clear browser indicator
                            await localParticipant.setScreenShareEnabled(false);
                            // Step 2: Stop the underlying MediaStream track directly
                            const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                            if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                console.log('🛑 Stopping underlying MediaStream track to clear browser indicators...');
                                mediaStreamTrack.stop();
                                console.log('✅ MediaStream track stopped');
                            }
                            // Step 3: Also stop the LiveKit track if it's still active
                            if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                                try {
                                    await screenPub.track.stop();
                                    console.log('✅ LiveKit screen share track stopped');
                                }
                                catch (trackError) {
                                    console.warn('⚠️ Error stopping LiveKit track:', trackError);
                                }
                            }
                            // Step 4: Find and stop any remaining screen share MediaStreams
                            try {
                                const allVideoElements = document.querySelectorAll('video');
                                allVideoElements.forEach(video => {
                                    if (video.srcObject && video.srcObject instanceof MediaStream) {
                                        const stream = video.srcObject;
                                        stream.getVideoTracks().forEach(track => {
                                            const label = track.label.toLowerCase();
                                            if (track.kind === 'video' && (label.includes('screen') ||
                                                label.includes('window') ||
                                                label.includes('display') ||
                                                label.includes('monitor'))) {
                                                console.log('🛑 Stopping screen share MediaStream from video element:', track.label);
                                                try {
                                                    track.stop();
                                                }
                                                catch (e) {
                                                    console.warn('⚠️ Error stopping track from video element:', e);
                                                }
                                            }
                                        });
                                    }
                                });
                            }
                            catch (streamError) {
                                console.warn('⚠️ Error stopping screen share streams:', streamError);
                            }
                            console.log('✅ Screen share stopped and browser indicator should be cleared');
                        }
                        catch (screenShareError) {
                            console.warn('⚠️ Error stopping screen share before disconnect:', screenShareError);
                            // Fallback: try to stop the track directly
                            try {
                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                if (mediaStreamTrack) {
                                    mediaStreamTrack.stop();
                                }
                                await screenPub.track.stop();
                            }
                            catch (fallbackError) {
                                console.warn('⚠️ Error in fallback screen share stop:', fallbackError);
                            }
                        }
                    }
                }
                // Disconnect from room gracefully
                if (lkRoomRef.current) {
                    try {
                        console.log('🔌 Disconnecting from LiveKit room...');
                        await lkRoomRef.current.disconnect();
                        console.log('✅ Disconnected from LiveKit room gracefully');
                    }
                    catch (disconnectError) {
                        console.warn('⚠️ Disconnect warning (non-critical):', disconnectError);
                        // Continue with cleanup even if disconnect throws
                    }
                }
                // CRITICAL: Final aggressive cleanup before calling onDisconnect
                // This ensures all screen share tracks are stopped even if previous cleanup missed some
                try {
                    console.log('🧹 Final aggressive cleanup before onDisconnect...');
                    // Stop all screen share tracks from all video elements one more time
                    const allVideoElements = document.querySelectorAll('video');
                    allVideoElements.forEach(video => {
                        if (video.srcObject && video.srcObject instanceof MediaStream) {
                            const stream = video.srcObject;
                            stream.getVideoTracks().forEach(track => {
                                const label = track.label.toLowerCase();
                                if (track.kind === 'video' && (label.includes('screen') ||
                                    label.includes('window') ||
                                    label.includes('display') ||
                                    label.includes('monitor'))) {
                                    console.log('🛑 Final cleanup - stopping screen share track:', track.label);
                                    try {
                                        track.stop();
                                    }
                                    catch (e) {
                                        console.warn('⚠️ Error in final cleanup:', e);
                                    }
                                }
                            });
                        }
                    });
                    // Also try to stop via LiveKit one more time if room still exists
                    if (lkRoomRef.current?.localParticipant) {
                        const localParticipant = lkRoomRef.current.localParticipant;
                        const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                        if (screenPub?.track) {
                            try {
                                await localParticipant.setScreenShareEnabled(false);
                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                    mediaStreamTrack.stop();
                                }
                            }
                            catch (e) {
                                console.warn('⚠️ Error in final LiveKit cleanup:', e);
                            }
                        }
                    }
                    console.log('✅ Final cleanup completed');
                }
                catch (finalCleanupError) {
                    console.warn('⚠️ Error in final cleanup:', finalCleanupError);
                }
                // Wait for animation to play (2 seconds total) then call onDisconnect
                setTimeout(() => {
                    console.log('✅ Meeting left gracefully - all cleanup completed');
                    onDisconnect(true);
                }, 1900); // 100ms already waited + 1900ms = 2 seconds total
            }
            catch (error) {
                console.error('Error leaving meeting:', error);
                // Don't show error toast on intentional exit
                // Force disconnect anyway
                onDisconnect(true);
            }
        };
        // Show confirmation dialog for teachers, or leave directly for students
        if (userRole === 'teacher') {
            console.log('🔴 Setting dialog state for teacher');
            setLeaveAction(() => executeLeave);
            setShowLeaveConfirmDialog(true);
            console.log('🔴 Dialog state set, should show dialog');
        }
        else {
            console.log('🔴 Student leaving directly');
            // Students can leave directly
            executeLeave(false);
        }
    };
    // Control visibility management - DISABLED to prevent dark overlay
    // Controls are now always visible
    useCallback(() => {
        // Do nothing - controls are always visible
        setShowControls(true);
    }, []);
    const handleMouseMove = useCallback(() => {
        // Do nothing - no auto-hide to prevent dark overlay
    }, []);
    // Mirror toggle
    const toggleMirror = useCallback(() => {
        const newMirrored = !isMirrored;
        setIsMirrored(newMirrored);
        localStorage.setItem('meetplus_mirror_video', newMirrored.toString());
        console.log(`🔄 Video mirror ${newMirrored ? 'enabled' : 'disabled'}`);
    }, [isMirrored]);
    // Update participant count callback
    const updateParticipantCount = useCallback((count) => {
        participantCountRef.current = count;
    }, []);
    // Handler for chess panel with validation
    const handleChessPanelToggle = useCallback(() => {
        // Check if already open - if so, close it
        if (showChessBoard) {
            setShowChessBoard(false);
            return;
        }
        // Validate chequeredEnabled first
        if (!chequeredEnabled) {
            toast$1.error('Chequered is not enabled for this service provider', { containerId: 'livekit-meeting' });
            return;
        }
        // Validate participant count (must be < 10, so max 9 participants)
        const participantCount = participantCountRef.current;
        if (participantCount >= 10) {
            toast$1.error('Chequered is only available for meetings with fewer than 10 participants', { containerId: 'livekit-meeting' });
            return;
        }
        // All checks passed, open the chess panel and close others
        setShowChessBoard(true);
        setShowChat(false);
        setShowBackgroundEffects(false);
        setShowPollsQuizzes(false);
        setShowMeetingInfo(false);
    }, [showChessBoard, chequeredEnabled]);
    // Exclusive panel management - closes other panels and toggles the requested one
    const openExclusivePanel = (panelType) => {
        console.log(`🔧 Opening exclusive panel: ${panelType}`);
        // Check if the requested panel is already open - if so, close it
        let shouldOpen = true;
        switch (panelType) {
            case 'chat':
                if (showChat) {
                    shouldOpen = false;
                    setShowChat(false);
                }
                break;
            case 'background':
                if (showBackgroundEffects) {
                    shouldOpen = false;
                    setShowBackgroundEffects(false);
                }
                break;
            case 'chess':
                // Use the handler that validates conditions
                handleChessPanelToggle();
                return; // Return early since handleChessPanelToggle handles the logic
            case 'polls':
                if (showPollsQuizzes) {
                    shouldOpen = false;
                    setShowPollsQuizzes(false);
                }
                break;
            case 'meetingInfo':
                if (showMeetingInfo) {
                    shouldOpen = false;
                    setShowMeetingInfo(false);
                }
                break;
            case 'participants':
                if (showParticipants) {
                    shouldOpen = false;
                    setShowParticipants(false);
                }
                break;
        }
        // If we're closing the panel, don't do anything else
        if (!shouldOpen) {
            console.log(`🔧 Closing panel: ${panelType}`);
            return;
        }
        // Close all other panels first
        setShowChat(false);
        setShowChessBoard(false);
        setShowPollsQuizzes(false);
        setShowBackgroundEffects(false);
        setShowMeetingInfo(false);
        setShowParticipants(false);
        setChatExpanded(false); // Reset chat expansion
        // Then open the requested panel
        switch (panelType) {
            case 'chat':
                console.log('💬 Opening chat panel');
                setShowChat(true);
                setUnreadMessageCount(0); // Reset unread count when chat is opened
                break;
            case 'background':
                console.log('🎨 Opening background effects panel');
                setShowBackgroundEffects(true);
                break;
            case 'chess':
                console.log('♟️ Opening chess panel');
                setShowChessBoard(true);
                break;
            case 'polls':
                console.log('📊 Opening polls panel');
                setShowPollsQuizzes(true);
                break;
            case 'meetingInfo':
                console.log('ℹ️ Opening meeting info panel');
                setShowMeetingInfo(true);
                break;
            case 'participants':
                console.log('👥 Opening participants panel');
                setShowParticipants(true);
                break;
            default:
                console.log(`❓ Unknown panel type: ${panelType}`);
        }
    };
    // Toggle chat expansion
    const toggleChatExpansion = () => {
        setChatExpanded(!chatExpanded);
        console.log('💬 Chat expansion toggled:', !chatExpanded);
    };
    // Initial permissions request - SKIP if we have prejoin stream (GOOGLE MEET OPTIMIZATION)
    useEffect(() => {
        // If we have a prejoin media stream, permissions are already granted - skip!
        if (initialMediaStream) {
            console.log('⚡ Skipping permission request - using prejoin stream for instant start!');
            return;
        }
        const requestPermissions = async () => {
            try {
                // Request permissions first (only if no prejoin stream)
                const stream = await navigator.mediaDevices.getUserMedia({
                    video: {
                        width: { ideal: 1280 },
                        height: { ideal: 720 },
                        frameRate: { ideal: 30 }
                    },
                    audio: true
                });
                // Stop tracks immediately - just wanted permissions
                stream.getTracks().forEach(track => track.stop());
                console.log('✅ Camera/mic permissions granted');
            }
            catch (error) {
                console.error('Error requesting permissions:', error);
                // Removed toast - no toasts in LiveKit room except Chequered
            }
        };
        requestPermissions();
    }, [initialMediaStream]);
    // ============================================================================
    // NETWORK BLIPS & TAB VISIBILITY: What LiveKit SDK Handles Automatically
    // ============================================================================
    // 
    // LiveKit SDK automatically handles:
    // ✅ Tab visibility changes (tracks pause/resume)
    // ✅ Network disconnections and reconnections
    // ✅ Track republishing after reconnection (via republishOnReconnect: true)
    // ✅ Connection keep-alive during network blips
    // ✅ Screen share persistence during brief disconnections
    // 
    // What happens during a network blip:
    // 1. SDK detects disconnection (onDisconnected fires)
    // 2. SDK automatically attempts reconnection (up to 20 attempts)
    // 3. SDK republishes all tracks when reconnected (onConnected fires)
    // 4. Recording continues on server-side (egress is independent)
    // 5. Meeting state is preserved (participants, layout, etc.)
    // 
    // REMOVED: Manual tab visibility handling
    // Our previous manual intervention was causing:
    // ❌ Unnecessary track republishing (causing "Cannot pipe a locked stream" errors)
    // ❌ Background processor conflicts (WebGL context errors)
    // ❌ Disconnection/reconnection loops
    // 
    // Solution: Trust LiveKit's built-in handling via SDK options:
    // - reconnectAttempts: 20
    // - republishOnReconnect: true
    // - autoSubscribe: true
    // - suspendLocalVideoOnFocus: false
    // ============================================================================
    // Manage idle and max-duration timers; ensure stop recording on unload
    useEffect(() => {
        if (!isConnected)
            return;
        const handleActivity = () => {
            // Reset idle timer logic would go here
        };
        const handleBeforeUnload = (e) => {
            try {
                if (isRecording) {
                    console.log('⚠️ Page unloading while recording is active');
                    // For cloud recording, try to stop via beacon
                    if (recordingMode === 'cloud' && recordingIdRef.current && navigator.sendBeacon) {
                        try {
                            const blob = new Blob([JSON.stringify({ recordingId: recordingIdRef.current })], { type: 'application/json' });
                            navigator.sendBeacon(`${API_ENDPOINT}/api/meet/stopRecording`, blob);
                            console.log('📤 Sent stop recording beacon for cloud recording');
                        }
                        catch (beaconError) {
                            console.error('Error sending stop recording beacon:', beaconError);
                        }
                    }
                    // Local recording cleanup removed - only cloud recording is supported
                    // Warn user if they're leaving during recording
                    const message = 'Recording is active. Are you sure you want to leave?';
                    e.preventDefault();
                    e.returnValue = message;
                    return message;
                }
            }
            catch (error) {
                console.error('Error in beforeunload handler:', error);
            }
        };
        window.addEventListener('keydown', handleActivity);
        window.addEventListener('click', handleActivity);
        window.addEventListener('mousemove', handleActivity);
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('mousemove', handleActivity);
            window.removeEventListener('beforeunload', handleBeforeUnload);
            const idleTimer = idleTimerRef.current;
            const maxTimer = maxTimerRef.current;
            if (idleTimer)
                clearTimeout(idleTimer);
            if (maxTimer)
                clearTimeout(maxTimer);
        };
    }, [isConnected, isRecording]);
    // Draggable minimized window state
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const minimizedWindowRef = useRef(null);
    // Handle dragging for minimized window
    const handleMinimizedMouseDown = (e) => {
        if (!minimizedWindowRef.current)
            return;
        const rect = minimizedWindowRef.current.getBoundingClientRect();
        setDragOffset({
            x: e.clientX - rect.left,
            y: e.clientY - rect.top
        });
        setIsDragging(true);
    };
    useEffect(() => {
        if (!isDragging)
            return;
        const handleMouseMove = (e) => {
            if (!isDragging)
                return;
            const newX = e.clientX - dragOffset.x;
            const newY = e.clientY - dragOffset.y;
            // Keep window within viewport bounds
            const maxX = window.innerWidth - 360;
            const maxY = window.innerHeight - 200;
            const boundedX = Math.max(0, Math.min(newX, maxX));
            const boundedY = Math.max(0, Math.min(newY, maxY));
            setMinimizedPosition({ x: boundedX, y: boundedY });
        };
        const handleMouseUp = () => {
            setIsDragging(false);
        };
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isDragging, dragOffset]);
    const meetingContent = (jsxs(Fragment, { children: [isMinimized && isScreenSharing && (jsxs("div", { ref: minimizedWindowRef, className: "fixed z-[10000] bg-gray-800 rounded-lg shadow-2xl border border-gray-700", style: {
                    left: `${minimizedPosition.x}px`,
                    top: `${minimizedPosition.y}px`,
                    width: '360px',
                    height: '200px',
                    cursor: isDragging ? 'grabbing' : 'grab'
                }, onMouseDown: handleMinimizedMouseDown, children: [jsxs("div", { className: "flex items-center justify-between px-3 py-2 bg-gray-900 border-b border-gray-700 rounded-t-lg cursor-grab active:cursor-grabbing", children: [jsxs("div", { className: "flex items-center gap-2 flex-1 min-w-0", children: [jsx("div", { className: "w-2 h-2 bg-red-500 rounded-full" }), jsx("span", { className: "text-white text-xs font-medium truncate", children: meetingTitle })] }), jsx("button", { onClick: () => setIsMinimized(false), className: "text-gray-400 hover:text-white transition-colors ml-2", title: "Restore Meeting", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" }) }) })] }), jsx("div", { className: "relative h-[120px] bg-black overflow-hidden", children: jsx("div", { className: "absolute inset-0 flex items-center justify-center", children: jsxs("div", { className: "text-gray-500 text-xs text-center", children: [jsx("div", { className: "mb-2", children: "\uD83D\uDCF9" }), jsx("div", { children: "Meeting Active" })] }) }) }), jsxs("div", { className: "flex items-center justify-center gap-2 px-3 py-2 bg-gray-900 rounded-b-lg", children: [jsx("button", { onClick: async () => {
                                    try {
                                        if (lkRoomRef.current?.localParticipant) {
                                            await lkRoomRef.current.localParticipant.setMicrophoneEnabled(!micEnabled);
                                            setMicEnabled(!micEnabled);
                                        }
                                    }
                                    catch (error) {
                                        console.error('Error toggling mic:', error);
                                    }
                                }, className: `w-8 h-8 rounded-full flex items-center justify-center transition-colors ${micEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`, title: micEnabled ? 'Mute' : 'Unmute', children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: micEnabled ? (jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" })) : (jsx("path", { d: "M1 1l22 22M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" })) }) }), jsx("button", { onClick: async () => {
                                    try {
                                        if (lkRoomRef.current?.localParticipant) {
                                            await lkRoomRef.current.localParticipant.setCameraEnabled(!cameraEnabled);
                                            setCameraEnabled(!cameraEnabled);
                                        }
                                    }
                                    catch (error) {
                                        console.error('Error toggling camera:', error);
                                    }
                                }, className: `w-8 h-8 rounded-full flex items-center justify-center transition-colors ${cameraEnabled ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}`, title: cameraEnabled ? 'Turn Off Camera' : 'Turn On Camera', children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: cameraEnabled ? (jsx("path", { d: "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" })) : (jsx("path", { d: "M1 1l22 22M23 19V8a2 2 0 0 0-2-2h-4l-2-3H9l-2 3H3a2 2 0 0 0-2 2v11" })) }) })] })] })), jsxs("div", { className: `bg-gray-900 overflow-hidden shadow-2xl fixed inset-0 z-[9999] rounded-none ${isMinimized ? 'opacity-0 pointer-events-none' : ''}`, onMouseMove: handleMouseMove, style: isMinimized ? { visibility: 'hidden' } : { visibility: 'visible' }, children: [jsxs(LiveKitRoom, { video: true, audio: true, token: typeof initialToken === 'string' ? initialToken : initialToken.token, serverUrl: serverUrl, connect: true, options: {
                            // Basic connection options
                            dynacast: true,
                            // CRITICAL: For Managed LiveKit - prevent shard hopping
                            // Ensure serverUrl points to a specific region endpoint (e.g., wss://us-east-1.livekit.cloud)
                            // to prevent Managed backend from migrating between US/EU shards during screen sharing
                            // Shard migrations cause disconnect reason 10 and can interrupt screen share if not handled properly
                            // CRITICAL: Enable active speaker detection for automatic video switching
                            // This is required for isSpeaking property to work
                            expWebAudioMix: true,
                            // Video capture settings
                            videoCaptureDefaults: {
                                resolution: {
                                    width: 1280,
                                    height: 720,
                                    frameRate: 30
                                }
                            },
                            // Audio capture settings
                            audioCaptureDefaults: {
                                autoGainControl: true,
                                echoCancellation: true,
                                noiseSuppression: true
                            },
                            // Publishing settings
                            publishDefaults: {
                                simulcast: false,
                                stopMicTrackOnMute: false, // Don't stop tracks on mute
                                videoSimulcastLayers: [],
                                videoCodec: 'vp9', // Using VP9 (same as Google Meet) for 30-50% better compression
                                videoEncoding: {
                                    maxBitrate: 1500000, // Reduced for better stability
                                    maxFramerate: 30
                                },
                                screenShareEncoding: {
                                    maxBitrate: 2000000, // Reduced for better stability
                                    maxFramerate: 30
                                },
                                dtx: true,
                                red: true
                            },
                            // Connection behavior
                            disconnectOnPageLeave: false,
                            stopLocalTrackOnUnpublish: false,
                            // Reconnection settings - Aggressive reconnection for network blips
                            reconnectAttempts: 20, // Increased to 20 for aggressive reconnection (WiFi disconnect scenarios)
                            reconnectBackoffMs: 500, // Faster initial reconnection (500ms instead of 1000ms)
                            maxReconnectRetries: 20, // Maximum number of reconnection attempts
                            // CRITICAL: Auto-subscribe settings for network resilience
                            autoSubscribe: true, // Automatically subscribe to tracks after reconnection
                            // CRITICAL: Performance and visibility settings
                            // These prevent LiveKit from interfering with screen sharing during tab switches
                            suspendLocalVideoOnFocus: false, // Don't suspend video when tab loses focus
                            // stopLocalTrackOnUnpublish: false, // Removed duplicate - already set above
                            // CRITICAL: Adaptive stream settings for network resilience (Gemini recommendation)
                            adaptiveStream: true, // Enable adaptive streaming for better network handling
                            dynacast: true, // Enable dynamic broadcasting for better quality adaptation
                            // CRITICAL: Track publication settings for network blips
                            // These ensure tracks are automatically republished after reconnection
                            republishOnReconnect: true, // Auto-republish local tracks after reconnection (LiveKit handles this internally)
                            // CRITICAL: Publish defaults to prevent track stopping (Gemini recommendation)
                            publishDefaults: {
                                stopMicTrackOnMute: false, // Keep mic hardware active during mute (prevents re-initialization delays)
                            }
                        }, onConnected: async () => {
                            console.log('🎉 LiveKit connected successfully!');
                            setIsConnected(true);
                            setIsLoading(false);
                            // Comprehensive logging for join meeting - ALL INFO TOGETHER
                            const room = lkRoomRef.current;
                            const localParticipant = room?.localParticipant;
                            const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
                            const micPub = localParticipant?.getTrackPublication(Track.Source.Microphone);
                            const screenPub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
                            logLiveKitEvent('JOIN_MEETING', {
                                // Room Information
                                roomName: room?.name,
                                roomSid: room?.sid,
                                serverUrl: room?.engine?.ws?.url || serverUrl,
                                connectionState: room?.state,
                                // Participant Information
                                participantIdentity: localParticipant?.identity,
                                participantName: localParticipant?.name,
                                userRole: userRole,
                                // Current State
                                isScreenSharing: isScreenSharing,
                                isRecording: isRecording,
                                recordingId: recordingIdRef.current,
                                recordingMode: recordingMode,
                                cameraEnabled: cameraEnabled,
                                micEnabled: micEnabled,
                                // Track Status
                                cameraTrackState: cameraPub?.track?.state,
                                cameraTrackMuted: cameraPub?.isMuted,
                                micTrackState: micPub?.track?.state,
                                micTrackMuted: micPub?.isMuted,
                                screenShareTrackState: screenPub?.track?.state,
                                screenShareTrackSid: screenPub?.trackSid,
                                // Timing
                                timestamp: Date.now(),
                                joinTime: joinTimeRef.current,
                                isInitialConnection: isInitialConnectionRef.current,
                                hasEverConnected: hasEverConnectedRef.current
                            });
                            // CRITICAL: Track join time to prevent layout switches during initial join
                            joinTimeRef.current = Date.now();
                            // Reset loading state on new connection
                            // Reset intentional disconnect flag for new session
                            isIntentionalDisconnectRef.current = false;
                            // Mark that we've successfully connected at least once
                            hasEverConnectedRef.current = true;
                            // Mark that initial connection phase is complete
                            isInitialConnectionRef.current = false;
                            // Reset reconnection attempt count on successful connection
                            reconnectAttemptCountRef.current = 0;
                            // CRITICAL: Store token and verify participant identity is maintained (Zoom-like behavior)
                            // This ensures recording sees the same participant on reconnection, not a new one
                            if (lkRoomRef.current?.localParticipant) {
                                const currentIdentity = lkRoomRef.current.localParticipant.identity;
                                if (participantIdentityRef.current && participantIdentityRef.current !== currentIdentity) {
                                    console.warn('⚠️ Participant identity changed on reconnection:', {
                                        previous: participantIdentityRef.current,
                                        current: currentIdentity
                                    });
                                }
                                else if (!participantIdentityRef.current) {
                                    // First connection - store identity
                                    participantIdentityRef.current = currentIdentity;
                                    tokenRef.current = typeof initialToken === 'string' ? initialToken : initialToken.token; // Store token for reconnection
                                    console.log('✅ Stored participant identity for reconnection:', currentIdentity);
                                }
                                else {
                                    console.log('✅ Participant identity maintained on reconnection:', currentIdentity);
                                }
                                // CRITICAL: Prevent LiveKit from unpublishing screen share on visibility change (like Google Meet)
                                // LiveKit's internal handleAppVisibilityChanged unpublishes screen share when tab is hidden
                                // We override this behavior to keep screen share active during tab switches
                                const localParticipant = lkRoomRef.current.localParticipant;
                                const originalSetScreenShareEnabled = localParticipant.setScreenShareEnabled.bind(localParticipant);
                                // Intercept visibility change events BEFORE LiveKit processes them
                                const visibilityChangeHandler = () => {
                                    lastVisibilityChangeTimeRef.current = Date.now(); // Record when visibility change happened
                                    // CRITICAL FIX: If we are reconnecting, tell the browser to ignore visibility changes entirely
                                    // This prevents the visibility handler from hammering the processor during a reconnection
                                    const roomState = lkRoomRef.current?.state;
                                    if (roomState === ConnectionState.Reconnecting || roomState === ConnectionState.Connecting) {
                                        console.log('🔇 Ignoring visibility change during reconnection.');
                                        return;
                                    }
                                    if (document.hidden) {
                                        // Tab is being hidden - remember if we're screen sharing
                                        isVisibilityChangingRef.current = true;
                                        const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                        wasScreenSharingBeforeHiddenRef.current = !!(screenPub && screenPub.track);
                                        if (wasScreenSharingBeforeHiddenRef.current) {
                                            console.log('👁️ Tab hidden while screen sharing - will prevent LiveKit from stopping it');
                                            // CRITICAL: Ensure screen share track has no processor (causes browser throttling)
                                            if (screenPub?.track && screenPub.track.processor) {
                                                console.warn('⚠️ Screen share track has processor - this can cause issues during tab switch');
                                            }
                                        }
                                        // Don't block processor restarts - allow them to remain active/idle
                                        // Blocking causes conflicts with browser throttling
                                        console.log('👁️ Tab hidden - allowing processors to remain active (not blocking)');
                                    }
                                    else {
                                        // Tab is becoming visible again
                                        if (wasScreenSharingBeforeHiddenRef.current) {
                                            console.log('👁️ Tab visible again - screen share should still be active');
                                        }
                                        // Keep protection active for a short time after becoming visible to prevent race conditions
                                        setTimeout(() => {
                                            isVisibilityChangingRef.current = false;
                                            console.log('🔓 Re-enabled full processor operations after tab switch');
                                        }, 2000); // Extended to 2 seconds to handle slower tab switches
                                    }
                                };
                                // Add our handler BEFORE LiveKit's handler (capture phase)
                                document.addEventListener('visibilitychange', visibilityChangeHandler, true);
                                // CRITICAL PRIVACY PROTECTION: Prevent camera track processor from being stopped during visibility changes
                                // Allow restart but prevent stop - this keeps background effect active while allowing browser to manage resources
                                const cameraTrackPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                if (cameraTrackPub?.track) {
                                    const cameraTrack = cameraTrackPub.track;
                                    // Override stopProcessor to block during visibility changes (but allow restart)
                                    // This prevents privacy breach while allowing processor to handle browser throttling
                                    if (cameraTrack.stopProcessor) {
                                        const originalStopProcessor = cameraTrack.stopProcessor.bind(cameraTrack);
                                        cameraTrack.stopProcessor = async (...args) => {
                                            if (isVisibilityChangingRef.current) {
                                                console.log('🚫 PRIVACY PROTECTION: Blocked stopProcessor during tab switch - background stays active');
                                                return; // Block stop to keep background active
                                            }
                                            return originalStopProcessor(...args);
                                        };
                                    }
                                    console.log('🔒 PRIVACY PROTECTION: Installed camera processor protection (stop only) against tab switches');
                                }
                                // CRITICAL: Ensure screen share track NEVER has processors (causes browser throttling issues)
                                const screenSharePub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                if (screenSharePub?.track && screenSharePub.track.processor) {
                                    console.warn('⚠️ Screen share track has processor - removing to prevent tab switching issues');
                                    // Remove processor asynchronously (non-blocking)
                                    screenSharePub.track.stopProcessor?.().then(() => {
                                        console.log('✅ Removed processor from screen share track');
                                    }).catch((e) => {
                                        console.warn('⚠️ Could not remove processor from screen share track:', e);
                                    });
                                }
                                // Override setScreenShareEnabled to prevent auto-disable during tab switches
                                localParticipant.setScreenShareEnabled = async (enabled, options) => {
                                    // If trying to disable screen share while tab is hidden OR just after it was hidden, ignore it
                                    // This prevents LiveKit's handleAppVisibilityChanged from stopping screen share
                                    const timeSinceVisibilityChange = Date.now() - lastVisibilityChangeTimeRef.current;
                                    const recentVisibilityChange = timeSinceVisibilityChange < 5000; // Extended to 5 seconds to handle slow tab switches
                                    // Check if we're currently in a visibility change state (tab switching)
                                    const isDuringVisibilityChange = isVisibilityChangingRef.current || recentVisibilityChange;
                                    // Prevent disabling screen share if:
                                    // 1. Tab is currently hidden, OR
                                    // 2. We recently had a visibility change AND screen share was active before, OR
                                    // 3. We're in the middle of a visibility change transition
                                    if (!enabled && (document.hidden || (recentVisibilityChange && wasScreenSharingBeforeHiddenRef.current) || isDuringVisibilityChange) && isScreenSharing) {
                                        console.log('🚫 Prevented LiveKit from unpublishing screen share during tab switch (like Google Meet)', {
                                            documentHidden: document.hidden,
                                            recentVisibilityChange,
                                            timeSinceVisibilityChange,
                                            isDuringVisibilityChange,
                                            wasScreenSharingBeforeHidden: wasScreenSharingBeforeHiddenRef.current
                                        });
                                        return; // Don't disable - keep screen share active
                                    }
                                    // Update internal screen sharing state (setIsScreenSharing)
                                    if (enabled) {
                                        setIsScreenSharing(true);
                                        wasScreenSharingBeforeHiddenRef.current = true; // Mark as sharing
                                    }
                                    else {
                                        // Only set to false if it's an intentional stop (not during visibility change)
                                        if (!document.hidden && !recentVisibilityChange && !isDuringVisibilityChange) {
                                            setIsScreenSharing(false);
                                            wasScreenSharingBeforeHiddenRef.current = false;
                                        }
                                        else {
                                            console.log('⏸️ Preserving screen share state - intentional stop not confirmed', {
                                                documentHidden: document.hidden,
                                                recentVisibilityChange,
                                                isDuringVisibilityChange
                                            });
                                        }
                                    }
                                    // Call original method
                                    return originalSetScreenShareEnabled(enabled, options);
                                };
                                // CRITICAL: Also intercept the track publication's unpublish method directly
                                // LiveKit might call unpublish directly without going through setScreenShareEnabled
                                const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                if (screenPub) {
                                    const originalUnpublish = screenPub.unpublish;
                                    if (originalUnpublish && typeof originalUnpublish === 'function') {
                                        screenPub.unpublish = async (...args) => {
                                            const timeSinceVisibilityChange = Date.now() - lastVisibilityChangeTimeRef.current;
                                            const recentVisibilityChange = timeSinceVisibilityChange < 5000;
                                            const isDuringVisibilityChange = isVisibilityChangingRef.current || recentVisibilityChange;
                                            // Prevent unpublish if it's during a visibility change
                                            if ((document.hidden || recentVisibilityChange || isDuringVisibilityChange) && wasScreenSharingBeforeHiddenRef.current) {
                                                console.log('🚫 Prevented direct track unpublish during tab switch (like Google Meet)', {
                                                    documentHidden: document.hidden,
                                                    recentVisibilityChange,
                                                    timeSinceVisibilityChange,
                                                    isDuringVisibilityChange
                                                });
                                                return; // Don't unpublish - keep screen share active
                                            }
                                            return originalUnpublish.apply(screenPub, args);
                                        };
                                        console.log('✅ Installed track unpublish protection for screen share');
                                    }
                                }
                                console.log('✅ Installed screen share protection against tab visibility changes (like Google Meet)');
                            }
                            // Removed non-critical toast - connection state visible in UI
                            // CRITICAL: Restore screen share state from sessionStorage if it was active before refresh (like Google Meet)
                            const savedScreenSharing = sessionStorage.getItem('meetplus_screen_sharing');
                            if (savedScreenSharing === 'true' && lkRoomRef.current?.localParticipant) {
                                console.log('🔄 Restoring screen share state from sessionStorage after refresh...');
                                const localParticipant = lkRoomRef.current.localParticipant;
                                const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                // Check if screen share track already exists (from LiveKit reconnection)
                                if (screenPub && screenPub.track && screenPub.track.state === 'live') {
                                    console.log('✅ Screen share track already active - restoring UI state');
                                    setIsScreenSharing(true);
                                    setScreenSharingParticipantId(localParticipant.identity);
                                    setCurrentLayout('screen-share');
                                    // CRITICAL: Add 'ended' event listener to restored track
                                    const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                    if (mediaStreamTrack) {
                                        const handleTrackEnded = () => {
                                            // CRITICAL: Only handle track ended if it's truly ended
                                            // Check readyState to avoid false positives during tab switches
                                            if (mediaStreamTrack.readyState !== 'ended') {
                                                console.log('⏸️ Restored track ended event but readyState is not ended - ignoring (tab switch)');
                                                return;
                                            }
                                            console.warn('🛑 Restored screen share track truly ended (browser stopped sharing)');
                                            stopScreenShareWatchdog();
                                            setIsScreenSharing(false);
                                            sessionStorage.removeItem('meetplus_screen_sharing');
                                            setScreenSharingParticipantId(null);
                                            setCurrentLayout(prev => prev === 'screen-share' ? 'focus' : prev);
                                            // Clean up the track publication
                                            try {
                                                localParticipant.setScreenShareEnabled(false).catch(() => { });
                                            }
                                            catch (e) {
                                                console.warn('⚠️ Error cleaning up after restored track ended:', e);
                                            }
                                        };
                                        mediaStreamTrack.addEventListener('ended', handleTrackEnded, { once: true });
                                        console.log('✅ Added "ended" event listener to restored screen share track');
                                    }
                                    startScreenShareWatchdog();
                                }
                                else {
                                    // Screen share was active but track was lost - attempt to restore
                                    console.log('⚠️ Screen share state was active but track lost - attempting to restore...');
                                    // Note: This may require user permission, so we'll attempt but not force
                                    try {
                                        // Ensure camera is enabled (required for screen share)
                                        if (!cameraEnabled) {
                                            await localParticipant.setCameraEnabled(true);
                                            await new Promise(resolve => setTimeout(resolve, 200));
                                        }
                                        await localParticipant.setScreenShareEnabled(true);
                                        // Comprehensive logging for screen share start - ALL INFO TOGETHER
                                        const room = lkRoomRef.current;
                                        const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                        logLiveKitEvent('SCREEN_SHARE_START', {
                                            // Room Information
                                            roomName: room?.name,
                                            roomSid: room?.sid,
                                            serverUrl: room?.engine?.ws?.url || serverUrl,
                                            connectionState: room?.state,
                                            // Participant Information
                                            participantIdentity: localParticipant?.identity,
                                            participantName: localParticipant?.name,
                                            userRole: userRole,
                                            // Current State
                                            isRecording: isRecording,
                                            recordingId: recordingIdRef.current,
                                            recordingMode: recordingMode,
                                            cameraEnabled: cameraEnabled,
                                            micEnabled: micEnabled,
                                            // Screen Share Track Info
                                            trackSid: screenPub?.trackSid,
                                            trackName: screenPub?.trackName,
                                            trackState: screenPub?.track?.state,
                                            // Context
                                            source: 'restore_after_refresh',
                                            timestamp: Date.now()
                                        });
                                        console.log('✅ Screen share restoration attempted after refresh');
                                    }
                                    catch (restoreError) {
                                        console.warn('⚠️ Could not restore screen share after refresh (may need user permission):', restoreError);
                                        // Clear saved state if restoration fails
                                        sessionStorage.removeItem('meetplus_screen_sharing');
                                    }
                                }
                            }
                            // CRITICAL: Final sanity-check for microphone state on join
                            // In some edge cases (guest join, reconnection, device changes),
                            // LiveKit can end up with microphone enabled even when the
                            // pre-join UI shows "muted". To guarantee that a guest who
                            // joined with mic muted is truly muted, we aggressively
                            // re-apply the intended mic state here using the original
                            // initialMicEnabled flag from pre-join.
                            try {
                                if (lkRoomRef.current?.localParticipant) {
                                    const intendedMicEnabled = initialMicEnabled !== false;
                                    const currentMicState = lkRoomRef.current.localParticipant.isMicrophoneEnabled;
                                    console.log('🎤 Mic state sanity check on connect:', {
                                        intendedMicEnabled,
                                        currentMicState,
                                        source: 'onConnected'
                                    });
                                    // Only force a change if there's a mismatch between
                                    // intended state and actual LiveKit state.
                                    if (currentMicState !== intendedMicEnabled) {
                                        console.warn('⚠️ Mic state mismatch on join – forcing correct state', {
                                            fixingTo: intendedMicEnabled
                                        });
                                        await lkRoomRef.current.localParticipant.setMicrophoneEnabled(intendedMicEnabled);
                                        setMicEnabled(intendedMicEnabled);
                                        console.log(`✅ Mic state corrected on join: ${intendedMicEnabled ? 'enabled' : 'disabled'}`);
                                    }
                                }
                            }
                            catch (micError) {
                                console.warn('⚠️ Failed to enforce mic state on connect:', micError);
                            }
                            // CRITICAL: Resume recording if it was active before disconnection (Zoom-like behavior)
                            if (recordingStateBeforeDisconnectRef.current?.wasRecording) {
                                const savedState = recordingStateBeforeDisconnectRef.current;
                                console.log('🔄 Resuming recording after reconnection:', savedState);
                                if (savedState.mode === 'cloud' && savedState.recordingId) {
                                    // For cloud recording, verify it's still active and restore state
                                    // LiveKit cloud recording continues server-side during client disconnections
                                    try {
                                        // Restore recording state
                                        recordingIdRef.current = savedState.recordingId;
                                        setIsRecording(true);
                                        setRecordingMode('cloud');
                                        setRecordingStartMs(savedState.recordingStartMs);
                                        if (savedState.firestoreDocId) {
                                            recordingIdRef.firestoreDocId = savedState.firestoreDocId;
                                        }
                                        // CRITICAL: Restore layout and resolution to match what was used when recording started
                                        // CRITICAL: Only 'pip-circle' layout is supported - always use it
                                        setRecordLayout('pip-circle');
                                        localStorage.setItem('meetplus_recording_layout', 'pip-circle');
                                        localStorage.setItem('meetplus_cloud_layout', 'pip-circle');
                                        console.log('📐 Enforced pip-circle layout after reconnection (only supported layout)');
                                        if (savedState.resolution) {
                                            setSelectedResolution(savedState.resolution);
                                            console.log('📹 Restored recording resolution after reconnection:', savedState.resolution);
                                        }
                                        console.log('✅ Cloud recording resumed - recording continues server-side with layout:', savedState.layout, 'resolution:', savedState.resolution);
                                    }
                                    catch (error) {
                                        console.error('⚠️ Error resuming cloud recording:', error);
                                        // Clear saved state if resume fails
                                        recordingStateBeforeDisconnectRef.current = null;
                                    }
                                }
                                // Clear saved state after resume attempt
                                recordingStateBeforeDisconnectRef.current = null;
                            }
                            // CRITICAL: LiveKit's republishOnReconnect: true handles track republishing automatically
                            // DO NOT manually toggle camera on/off as it destroys the background processor
                            // and causes video freeze/flicker
                            try {
                                if (lkRoomRef.current?.localParticipant) {
                                    const localParticipant = lkRoomRef.current.localParticipant;
                                    // Check camera track status after reconnection
                                    const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                    const shouldHaveCamera = cameraEnabled || initialCameraEnabled !== false;
                                    console.log('📹 Camera track status after reconnection:', {
                                        shouldHaveCamera,
                                        hasPub: !!cameraPub,
                                        hasTrack: !!cameraPub?.track,
                                        isSubscribed: cameraPub?.isSubscribed,
                                        trackState: cameraPub?.track?.mediaStreamTrack?.readyState
                                    });
                                    // LiveKit's republishOnReconnect: true will automatically republish the track
                                    // We just need to wait for it to complete
                                    if (shouldHaveCamera && cameraPub?.track) {
                                        console.log('✅ Camera track exists after reconnection - LiveKit auto-republished it');
                                    }
                                    else if (shouldHaveCamera) {
                                        console.warn('⚠️ Camera track missing after reconnection - may need manual intervention');
                                    }
                                    // Check microphone track status after reconnection
                                    const micPub = localParticipant.getTrackPublication(Track.Source.Microphone);
                                    const shouldHaveMic = micEnabled || initialMicEnabled !== false;
                                    console.log('🎤 Microphone track status after reconnection:', {
                                        shouldHaveMic,
                                        hasPub: !!micPub,
                                        hasTrack: !!micPub?.track,
                                        isSubscribed: micPub?.isSubscribed,
                                        trackState: micPub?.track?.mediaStreamTrack?.readyState
                                    });
                                    // LiveKit's republishOnReconnect: true will automatically republish the mic track
                                    if (shouldHaveMic && micPub?.track) {
                                        console.log('✅ Microphone track exists after reconnection - LiveKit auto-republished it');
                                    }
                                    else if (shouldHaveMic) {
                                        console.warn('⚠️ Microphone track missing after reconnection - may need manual intervention');
                                    }
                                    // CRITICAL: DO NOT try to restore screen share automatically
                                    // Screen share MediaStreamTrack is gone after disconnection and cannot be restored
                                    // Attempting to restore will fail with "AbortError: Invalid state" and cause issues
                                    // Instead, clear the state and let user manually restart if needed
                                    if (screenShareStateBeforeDisconnectRef.current?.wasSharing) {
                                        console.warn('⚠️ Screen share was active before disconnection but cannot be restored automatically');
                                        console.log('ℹ️ User will need to manually restart screen share if needed');
                                        // Clear screen share state
                                        setIsScreenSharing(false);
                                        sessionStorage.removeItem('meetplus_screen_sharing');
                                        setScreenSharingParticipantId(null);
                                        setCurrentLayout('focus');
                                        // Clear saved state
                                        screenShareStateBeforeDisconnectRef.current = null;
                                        // TODO: Show a toast notification to user that screen share stopped
                                        // and they need to restart it manually if they want to continue sharing
                                    }
                                }
                            }
                            catch (trackRepublishError) {
                                console.error('⚠️ Failed to republish tracks after reconnection:', trackRepublishError);
                            }
                            // Enumerate available devices
                            await enumerateDevices();
                            // Check subscription hours and start meeting tracking for teachers
                            if (userRole === 'teacher') {
                                const { canStart: canStartMeeting, hours: remainingHours } = await fetchSubscriptionHours();
                                if (canStartMeeting) {
                                    startMeetingHoursTracking();
                                    // Show hours remaining banner at meeting start (only if less than 5 hours)
                                    if (remainingHours !== null && remainingHours > 0 && remainingHours < 5) {
                                        setShowHoursInfoDialog(true);
                                        // Auto-hide after 5 seconds
                                        setTimeout(() => {
                                            setShowHoursInfoDialog(false);
                                        }, 5000);
                                    }
                                }
                            }
                            // Restore persisted background effect
                            const saved = localStorage.getItem('meetplus_background_effect');
                            if (saved === 'blur' || saved === 'image' || saved === 'none') {
                                setCurrentEffect(saved);
                            }
                        }, onDisconnected: async (reason) => {
                            const reasonCode = reason?.code || reason;
                            console.log('🔌 LiveKit disconnected:', reason);
                            console.log('🔍 Disconnect details:', {
                                reason,
                                reasonType: typeof reason,
                                reasonCode,
                                wasIntentional: isIntentionalDisconnectRef.current,
                                isScreenSharing,
                                connectionState: lkRoomRef.current?.state
                            });
                            // Comprehensive logging for disconnection - ALL INFO TOGETHER
                            const room = lkRoomRef.current;
                            const localParticipant = room?.localParticipant;
                            const cameraPub = localParticipant?.getTrackPublication(Track.Source.Camera);
                            const micPub = localParticipant?.getTrackPublication(Track.Source.Microphone);
                            const screenPub = localParticipant?.getTrackPublication(Track.Source.ScreenShare);
                            const timeSinceJoin = joinTimeRef.current ? Date.now() - joinTimeRef.current : null;
                            // CRITICAL: Disconnect reason 10 during screen sharing is likely a shard migration
                            // Managed LiveKit uses global load balancing and may migrate sessions between US/EU shards
                            // During high-bandwidth tasks like screen sharing, the backend may initiate migration
                            const isLikelyShardMigration = reasonCode === 10 && isScreenSharing && !isIntentionalDisconnectRef.current;
                            const isRegionSwitch = reasonCode === 10;
                            const disconnectData = {
                                // Disconnect Information
                                reason,
                                reasonCode,
                                reasonType: typeof reason,
                                wasIntentional: isIntentionalDisconnectRef.current,
                                isRegionSwitch: isRegionSwitch,
                                isShardMigration: isLikelyShardMigration,
                                description: isRegionSwitch
                                    ? 'Managed LiveKit is switching between regions (US/EU shards)'
                                    : 'Disconnection event',
                                // Room Information
                                roomName: room?.name,
                                roomSid: room?.sid,
                                serverUrl: room?.engine?.ws?.url || serverUrl,
                                connectionState: room?.state,
                                // Participant Information
                                participantIdentity: localParticipant?.identity,
                                participantName: localParticipant?.name,
                                userRole: userRole,
                                // Current State
                                isScreenSharing,
                                isRecording,
                                recordingId: recordingIdRef.current,
                                recordingMode: recordingMode,
                                cameraEnabled: cameraEnabled,
                                micEnabled: micEnabled,
                                // Track Status at Disconnect
                                cameraTrackState: cameraPub?.track?.state,
                                cameraTrackMuted: cameraPub?.isMuted,
                                micTrackState: micPub?.track?.state,
                                micTrackMuted: micPub?.isMuted,
                                screenShareTrackState: screenPub?.track?.state,
                                screenShareTrackSid: screenPub?.trackSid,
                                screenSharePreserved: isLikelyShardMigration,
                                recordingPreserved: isRecording && isLikelyShardMigration,
                                // Timing
                                timestamp: Date.now(),
                                timeSinceJoin: timeSinceJoin,
                                timeSinceJoinMinutes: timeSinceJoin ? Math.round(timeSinceJoin / 60000 * 100) / 100 : null,
                                joinTime: joinTimeRef.current,
                                hasEverConnected: hasEverConnectedRef.current
                            };
                            // Log with appropriate level based on reason
                            if (isRegionSwitch) {
                                logLiveKitEvent('DISCONNECT_REASON_10_REGION_SWITCH', disconnectData, 'warn');
                                console.log('🌐 Region switch detected (reason 10) - Managed LiveKit is migrating session between shards');
                                if (isLikelyShardMigration) {
                                    console.log('🔄 Screen share active during migration - state will be preserved');
                                }
                            }
                            else {
                                logLiveKitEvent('DISCONNECT', disconnectData, reasonCode ? 'warn' : 'info');
                            }
                            const wasIntentional = isIntentionalDisconnectRef.current;
                            const isInitialConnection = isInitialConnectionRef.current;
                            const hasEverConnected = hasEverConnectedRef.current;
                            setIsConnected(false);
                            // CRITICAL: Reset loading state to prevent overlays on next meeting
                            setIsLoading(false);
                            // CRITICAL: Only stop screen share on INTENTIONAL disconnect (like Google Meet)
                            // During network blips, preserve screen share - it will continue when connection is restored
                            // Google Meet doesn't stop screen share during network issues - it continues seamlessly
                            if (wasIntentional) {
                                // Only stop screen share if disconnect was intentional (user left meeting)
                                if (lkRoomRef.current?.localParticipant) {
                                    const localParticipant = lkRoomRef.current.localParticipant;
                                    const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                    if (screenPub?.track) {
                                        console.log('🖥️ Stopping screen share on intentional disconnect to clear browser indicator (red dot)...');
                                        try {
                                            // Stop via LiveKit API
                                            await localParticipant.setScreenShareEnabled(false);
                                            // CRITICAL: Also stop the underlying MediaStream track to clear browser indicator
                                            const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                            if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                                console.log('🛑 Stopping underlying MediaStream track to clear browser red dot...');
                                                mediaStreamTrack.stop();
                                            }
                                            // Also stop the LiveKit track if still active
                                            if (screenPub.track.state === 'live' || screenPub.track.state === 'starting') {
                                                try {
                                                    await screenPub.track.stop();
                                                }
                                                catch (trackError) {
                                                    console.warn('⚠️ Error stopping LiveKit track:', trackError);
                                                }
                                            }
                                            console.log('✅ Screen share stopped on intentional disconnect - browser indicator should be cleared');
                                        }
                                        catch (screenShareError) {
                                            console.warn('⚠️ Error stopping screen share on disconnect:', screenShareError);
                                            // Fallback: try to stop the track directly
                                            try {
                                                if (screenPub?.track?.mediaStreamTrack) {
                                                    screenPub.track.mediaStreamTrack.stop();
                                                }
                                            }
                                            catch (fallbackError) {
                                                console.warn('⚠️ Fallback stop also failed:', fallbackError);
                                            }
                                        }
                                    }
                                }
                            }
                            else {
                                // Network blip - preserve screen share (like Google Meet)
                                console.log('🌐 Network blip detected - preserving screen share (like Google Meet)');
                                // Screen share will continue when connection is restored
                            }
                            // CRITICAL: Only cleanup screen share tracks on INTENTIONAL disconnect (like Google Meet)
                            // During network blips, preserve screen share tracks - they will continue when connection is restored
                            if (wasIntentional) {
                                // Only stop screen share tracks if disconnect was intentional (user left meeting)
                                try {
                                    const allVideoElements = document.querySelectorAll('video');
                                    allVideoElements.forEach(video => {
                                        if (video.srcObject && video.srcObject instanceof MediaStream) {
                                            const stream = video.srcObject;
                                            stream.getVideoTracks().forEach(track => {
                                                // Only stop screen share tracks to clear browser indicator
                                                const label = track.label.toLowerCase();
                                                if (track.kind === 'video' && (label.includes('screen') ||
                                                    label.includes('window') ||
                                                    label.includes('display') ||
                                                    label.includes('monitor')) && track.readyState !== 'ended') {
                                                    console.log('🛑 Stopping lingering screen share track to clear red dot:', track.label);
                                                    try {
                                                        track.stop();
                                                    }
                                                    catch (e) {
                                                        console.warn('⚠️ Error stopping track:', e);
                                                    }
                                                }
                                            });
                                            // Clear srcObject to help browser recognize sharing has stopped
                                            const activeTracks = stream.getTracks().filter(t => t.readyState === 'live');
                                            if (activeTracks.length === 0) {
                                                video.srcObject = null;
                                            }
                                            else {
                                                // If there are still active tracks but we stopped screen share,
                                                // clear and re-set srcObject to force browser update
                                                const hasScreenShare = stream.getVideoTracks().some(t => {
                                                    const label = t.label.toLowerCase();
                                                    return (label.includes('screen') || label.includes('window') ||
                                                        label.includes('display') || label.includes('monitor')) &&
                                                        t.readyState === 'ended';
                                                });
                                                if (hasScreenShare) {
                                                    const currentSrc = video.srcObject;
                                                    video.srcObject = null;
                                                    setTimeout(() => {
                                                        if (currentSrc) {
                                                            video.srcObject = currentSrc;
                                                        }
                                                    }, 10);
                                                }
                                            }
                                        }
                                    });
                                }
                                catch (streamError) {
                                    console.warn('⚠️ Error stopping lingering screen share streams:', streamError);
                                }
                                // CRITICAL: Aggressive cleanup - stop ALL screen share MediaStream tracks globally
                                // This catches any tracks that might not be attached to video elements
                                // Only do this on intentional disconnect
                                try {
                                    // Get all active MediaStream tracks from getUserMedia/getDisplayMedia
                                    if (navigator.mediaDevices) {
                                        // Additional cleanup: Stop any screen share tracks that might be in memory
                                        // by checking all MediaStreams that might exist
                                        const allMediaStreams = [];
                                        // Check all video/audio elements
                                        document.querySelectorAll('video, audio').forEach(element => {
                                            if (element.srcObject && element.srcObject instanceof MediaStream) {
                                                allMediaStreams.push(element.srcObject);
                                            }
                                        });
                                        // Stop all screen share tracks from all found streams
                                        allMediaStreams.forEach(stream => {
                                            stream.getVideoTracks().forEach(track => {
                                                const label = track.label.toLowerCase();
                                                if (track.kind === 'video' && (label.includes('screen') ||
                                                    label.includes('window') ||
                                                    label.includes('display') ||
                                                    label.includes('monitor'))) {
                                                    console.log('🛑 Stopping screen share track from MediaStream:', track.label);
                                                    try {
                                                        track.stop();
                                                    }
                                                    catch (e) {
                                                        console.warn('⚠️ Error stopping MediaStream track:', e);
                                                    }
                                                }
                                            });
                                        });
                                    }
                                }
                                catch (globalCleanupError) {
                                    console.warn('⚠️ Error in global screen share cleanup:', globalCleanupError);
                                }
                            }
                            else {
                                // Network blip - preserve screen share tracks (like Google Meet)
                                console.log('🌐 Network blip - preserving screen share tracks (like Google Meet)');
                                // Screen share tracks will continue when connection is restored
                            }
                            // Check if screen share was active and if disconnect was intentional
                            if (isScreenSharing && wasIntentional) {
                                console.warn('⚠️ Screen share was active and disconnect was intentional - clearing state');
                                screenShareStateBeforeDisconnectRef.current = null;
                                setIsScreenSharing(false);
                                sessionStorage.removeItem('meetplus_screen_sharing');
                                setScreenSharingParticipantId(null);
                                setCurrentLayout('focus');
                                // TODO: Show notification to user that screen share stopped and needs to be manually restarted
                            }
                            else if (!isScreenSharing && wasIntentional) {
                                // Intentional disconnect, but no screen share active - just clear saved state
                                screenShareStateBeforeDisconnectRef.current = null;
                                sessionStorage.removeItem('meetplus_screen_sharing');
                                setScreenSharingParticipantId(null);
                                setCurrentLayout('focus');
                            }
                            else if (isScreenSharing && !wasIntentional) {
                                // CRITICAL: Preserve screen share state during non-intentional disconnects
                                // This includes:
                                // 1. Network blips (temporary connection loss)
                                // 2. Shard migrations (Managed LiveKit moving session between US/EU shards) - reason code 10
                                // 3. Server-side rebalancing (load balancing during high bandwidth)
                                // LiveKit's auto-republish will restore the track after reconnection/migration
                                if (isLikelyShardMigration) {
                                    console.log('🌐 Shard migration detected - preserving screen share state (Managed LiveKit migration)');
                                    console.log('🔄 Managed LiveKit is migrating session between shards - tracks will auto-republish after migration');
                                }
                                else {
                                    console.log('🌐 Network blip - preserving screen share state (like Google Meet)');
                                }
                                // We rely on LiveKit's auto-republish to restore the track.
                                // DO NOT clear isScreenSharing state here.
                                // DO NOT clear screenShareStateBeforeDisconnectRef.current here.
                            }
                            else {
                                // No screen share active and not intentional disconnect - just clear saved state
                                screenShareStateBeforeDisconnectRef.current = null;
                            }
                            // Don't set isLoading to true during disconnection - only set it during initial connection
                            // This prevents the black overlay from appearing during reconnection attempts
                            // CRITICAL FIX: Only run hard cleanup if user explicitly left
                            // Code 10 is a Shard Migration - CLEANUP MUST BE BLOCKED
                            // Code 1006 is an abnormal closure (network blip) - CLEANUP MUST BE BLOCKED
                            // During shard migrations and network blips, LiveKit automatically tries to reconnect and republish tracks
                            // Running cleanup during reconnection will kill tracks that LiveKit is trying to restore
                            // This destroys WebGL contexts, causing "InvalidStateError: Stream closed" when SDK reconnects
                            const roomState = lkRoomRef.current?.state;
                            const isReconnecting = roomState === ConnectionState.Reconnecting ||
                                roomState === ConnectionState.Connecting;
                            // Block cleanup for shard migrations (Reason 10) and network blips (Reason 1006)
                            // Also block if room is reconnecting or if disconnect was not intentional
                            if (reasonCode === 10 || reasonCode === 1006 || isReconnecting || !wasIntentional) {
                                console.log('🌐 Shard migration or network blip (Reason', reasonCode, '). LOCKING cleanup to preserve WebGL context.');
                                console.log('🔄 Room state:', roomState, '- LiveKit will auto-republish tracks after reconnection');
                                console.log('🔒 WebGL context preserved - tracks will be restored seamlessly');
                                // DO NOT call cleanupAllMediaAndIndicators() here - it destroys WebGL contexts
                                // The SDK will republish tracks after migration/reconnection
                            }
                            else if (wasIntentional) {
                                // Normal path for intentional exit (user explicitly left meeting)
                                console.log('🧹 Intentional disconnect - running cleanup');
                                await cleanupAllMediaAndIndicators();
                            }
                            else {
                                // This should not happen due to the check above, but keep as fallback
                                console.log('🧹 Final disconnect - running cleanup');
                                await cleanupAllMediaAndIndicators();
                            }
                            // Skip reconnection and error toasts if disconnect was intentional
                            if (wasIntentional) {
                                console.log('✅ Intentional disconnect - showing exit animation');
                                // Reset flag for next session
                                isIntentionalDisconnectRef.current = false;
                                // Reset connection tracking for next session
                                hasEverConnectedRef.current = false;
                                isInitialConnectionRef.current = true;
                                reconnectAttemptCountRef.current = 0;
                                // Clear recording state - intentional disconnect means stop recording
                                recordingStateBeforeDisconnectRef.current = null;
                                // Stop recording only on intentional disconnect
                                await stopRecordingIfActive();
                                // Stop meeting hours tracking for teachers
                                if (userRole === 'teacher') {
                                    await stopMeetingHoursTracking();
                                }
                                // CRITICAL: Final aggressive cleanup pass before calling onDisconnect
                                // This ensures the browser's screen sharing indicator (red dot) is cleared
                                try {
                                    console.log('🧹 Final cleanup pass before onDisconnect to clear browser indicator...');
                                    // Step 1: Stop via LiveKit if room still exists
                                    if (lkRoomRef.current?.localParticipant) {
                                        const localParticipant = lkRoomRef.current.localParticipant;
                                        const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                        if (screenPub?.track) {
                                            try {
                                                await localParticipant.setScreenShareEnabled(false);
                                                const mediaStreamTrack = screenPub.track.mediaStreamTrack;
                                                if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                                    mediaStreamTrack.stop();
                                                }
                                                await screenPub.track.stop();
                                            }
                                            catch (e) {
                                                console.warn('⚠️ Error in final LiveKit cleanup:', e);
                                            }
                                        }
                                    }
                                    // Step 2: Stop ALL screen share tracks from ALL video elements
                                    const allVideoElements = document.querySelectorAll('video');
                                    allVideoElements.forEach(video => {
                                        if (video.srcObject && video.srcObject instanceof MediaStream) {
                                            const stream = video.srcObject;
                                            stream.getVideoTracks().forEach(track => {
                                                const label = track.label.toLowerCase();
                                                if (track.kind === 'video' && (label.includes('screen') ||
                                                    label.includes('window') ||
                                                    label.includes('display') ||
                                                    label.includes('monitor')) && track.readyState !== 'ended') {
                                                    try {
                                                        track.stop();
                                                    }
                                                    catch (e) {
                                                        console.warn('⚠️ Error stopping track:', e);
                                                    }
                                                }
                                            });
                                            // Clear srcObject
                                            video.srcObject = null;
                                        }
                                    });
                                    // Step 3: Wait a bit for tracks to fully stop, then force browser to recognize the change
                                    await new Promise(resolve => setTimeout(resolve, 300));
                                    // Dispatch focus/blur events to help browser update its indicator
                                    window.dispatchEvent(new Event('focus'));
                                    setTimeout(() => {
                                        window.dispatchEvent(new Event('blur'));
                                    }, 100);
                                    console.log('✅ Final cleanup completed - browser indicator should be cleared');
                                }
                                catch (finalCleanupError) {
                                    console.warn('⚠️ Error in final cleanup:', finalCleanupError);
                                }
                                onDisconnect(true);
                                return;
                            }
                            // CRITICAL: Suppress disconnect messages during initial connection phase
                            // This prevents false "left meeting" or "disconnection error" messages on first join
                            if (isInitialConnection || !hasEverConnected) {
                                console.log('🔇 Suppressing disconnect message during initial connection phase');
                                // Don't attempt reconnection during initial connection - let the room handle it
                                await stopRecordingIfActive();
                                if (userRole === 'teacher') {
                                    await stopMeetingHoursTracking();
                                }
                                onDisconnect(false);
                                return;
                            }
                            // CRITICAL: Save recording state before disconnection (like Google Meet)
                            // Recording continues on server-side during network blips - we just restore client state after reconnection
                            // This allows us to resume recording UI state after reconnection without creating duplicate videos
                            // Server-side recording is independent of client connection - it continues seamlessly during network issues
                            if (isRecording && !wasIntentional) {
                                console.log('💾 Saving recording state before disconnection for resume (recording continues on server)...', {
                                    layout: recordLayout,
                                    resolution: selectedResolution,
                                    recordingId: recordingIdRef.current
                                });
                                console.log('🌐 Recording continues on server-side during network blip (like Google Meet)');
                                recordingStateBeforeDisconnectRef.current = {
                                    wasRecording: true,
                                    mode: recordingMode || null,
                                    recordingId: recordingIdRef.current,
                                    recordingStartMs: recordingStartMs,
                                    firestoreDocId: recordingIdRef.firestoreDocId || null,
                                    layout: recordLayout,
                                    resolution: selectedResolution
                                };
                            }
                            // Try to reconnect only if it wasn't an intentional disconnect and wasn't a client-initiated disconnect
                            // Only attempt reconnection if we've successfully connected before
                            // Note: LiveKit has built-in reconnection (reconnectAttempts: 10), but we add manual retry with exponential backoff
                            // for better control and handling of sporadic disconnections
                            // CRITICAL: Aggressively try to reconnect on network issues (WiFi disconnect/reconnect scenarios)
                            if (hasEverConnected && reason && reason !== 'Client initiated disconnect' && reason.toString().includes('Client') === false && lkRoomRef.current) {
                                reconnectAttemptCountRef.current += 1;
                                const maxReconnectAttempts = 15; // Increased to 15 for aggressive reconnection (WiFi disconnect scenarios)
                                const baseBackoffMs = 500; // Start with 500ms for faster initial reconnection
                                const maxBackoffMs = 10000; // Cap at 10 seconds for faster reconnection
                                if (reconnectAttemptCountRef.current <= maxReconnectAttempts) {
                                    // Exponential backoff: 1s, 2s, 4s, 8s, 16s (capped at 30s)
                                    const backoffDelay = Math.min(baseBackoffMs * Math.pow(2, reconnectAttemptCountRef.current - 1), maxBackoffMs);
                                    console.log(`🔄 Attempting to reconnect (attempt ${reconnectAttemptCountRef.current}/${maxReconnectAttempts}) after ${backoffDelay}ms delay...`);
                                    console.log('🌐 Recording continues on server-side during network blip (like Google Meet)');
                                    console.log('🖥️ Screen share preserved during network blip (like Google Meet)');
                                    console.log('⏸️ NOT stopping recording - will resume UI state after reconnection...');
                                    // Wait with exponential backoff before attempting reconnection
                                    await new Promise(resolve => setTimeout(resolve, backoffDelay));
                                    try {
                                        // Check if already connected before attempting to reconnect
                                        if (lkRoomRef.current && lkRoomRef.current.state === ConnectionState.Connected) {
                                            console.log('✅ Already connected - skipping reconnect attempt');
                                            setIsLoading(false);
                                            setIsConnected(true);
                                            reconnectAttemptCountRef.current = 0; // Reset on success
                                            return;
                                        }
                                        // Check if room still exists and is valid
                                        if (!lkRoomRef.current) {
                                            console.warn('⚠️ Room reference lost, cannot reconnect');
                                            throw new Error('Room reference lost');
                                        }
                                        // CRITICAL: Use stored token to maintain same participant identity (Zoom-like behavior)
                                        // This ensures recording sees the same participant, not a new one
                                        const reconnectToken = tokenRef.current || (typeof initialToken === 'string' ? initialToken : initialToken.token);
                                        if (!tokenRef.current) {
                                            tokenRef.current = typeof initialToken === 'string' ? initialToken : initialToken.token; // Store token if not already stored
                                        }
                                        console.log('🔄 Reconnecting with same token to maintain participant identity...');
                                        await lkRoomRef.current.connect(serverUrl, reconnectToken);
                                        console.log('✅ Reconnected successfully - participant identity should be maintained');
                                        // Suppress reconnection success toast - it's a recoverable transient event
                                        // toast.success('Reconnected to meeting');
                                        // Reset loading state after reconnection
                                        setIsLoading(false);
                                        setIsConnected(true);
                                        reconnectAttemptCountRef.current = 0; // Reset on success
                                        // Recording will be resumed in onConnected handler
                                    }
                                    catch (error) {
                                        console.error(`❌ Reconnection failed (attempt ${reconnectAttemptCountRef.current}/${maxReconnectAttempts}):`, error);
                                        // Check if we should retry or give up
                                        if (reconnectAttemptCountRef.current < maxReconnectAttempts) {
                                            // Suppress intermediate reconnection failure toasts - they're transient
                                            console.log(`⚠️ Reconnection attempt ${reconnectAttemptCountRef.current} failed, will retry with exponential backoff...`);
                                            // Don't return - let the next disconnect event trigger another retry
                                            // This allows for multiple sporadic disconnections to be handled
                                        }
                                        else {
                                            // Only show toast if we've exhausted all reconnection attempts
                                            console.log('🛑 Reconnection attempts exhausted - stopping recording...');
                                            // Removed toast - no toasts in LiveKit room except Chequered
                                            await stopRecordingIfActive();
                                            // Stop meeting hours tracking for teachers
                                            if (userRole === 'teacher') {
                                                await stopMeetingHoursTracking();
                                            }
                                            onDisconnect(false);
                                        }
                                    }
                                }
                                else {
                                    // Exhausted reconnection attempts - this is non-recoverable
                                    console.log('🛑 Reconnection attempts exhausted - stopping recording...');
                                    // Removed toast - no toasts in LiveKit room except Chequered
                                    await stopRecordingIfActive();
                                    // Stop meeting hours tracking for teachers
                                    if (userRole === 'teacher') {
                                        await stopMeetingHoursTracking();
                                    }
                                    onDisconnect(false);
                                }
                            }
                            else {
                                // Normal disconnect (no reconnection attempt) - stop recording
                                console.log('🛑 Final disconnect - stopping recording...');
                                await stopRecordingIfActive();
                                // Stop meeting hours tracking for teachers
                                if (userRole === 'teacher') {
                                    await stopMeetingHoursTracking();
                                }
                                // Normal disconnect - call callback silently
                                onDisconnect(true);
                            }
                        }, onError: (error) => {
                            // Skip error toasts if disconnect was intentional
                            if (isIntentionalDisconnectRef.current) {
                                console.log('⚠️ LiveKit error during intentional disconnect (silent):', error.message);
                                return;
                            }
                            const isInitialConnection = isInitialConnectionRef.current;
                            const hasEverConnected = hasEverConnectedRef.current;
                            const errorMessage = error.message?.toLowerCase() || '';
                            console.error('LiveKit error:', error);
                            // CRITICAL: Suppress errors during initial connection phase to prevent false error messages
                            // Only show errors if we've successfully connected before, or if it's a truly non-recoverable error
                            if (isInitialConnection && !hasEverConnected) {
                                // During initial connection, only show truly non-recoverable errors
                                const isNonRecoverable = errorMessage.includes('token') ||
                                    errorMessage.includes('room') ||
                                    errorMessage.includes('permission') ||
                                    errorMessage.includes('unauthorized') ||
                                    errorMessage.includes('forbidden');
                                if (!isNonRecoverable) {
                                    console.log('🔇 Suppressing transient error during initial connection:', error.message);
                                    // Log detailed error info for debugging
                                    console.log('🔍 Detailed error info (suppressed):', {
                                        name: error.name,
                                        message: error.message,
                                        token: initialToken ? 'present' : 'missing',
                                        serverUrl: serverUrl ? 'present' : 'missing',
                                        roomName: roomName ? 'present' : 'missing'
                                    });
                                    if (onError)
                                        onError(error);
                                    return;
                                }
                            }
                            // Filter out recoverable/transient errors - only show non-recoverable errors
                            // CRITICAL: Also suppress "already connected" errors from LiveKit SDK
                            const isRecoverableError = errorMessage.includes('disconnect') ||
                                errorMessage.includes('disconnected') ||
                                errorMessage.includes('connection lost') ||
                                errorMessage.includes('network') ||
                                errorMessage.includes('timeout') ||
                                errorMessage.includes('websocket') ||
                                errorMessage.includes('reconnect') ||
                                errorMessage.includes('already connected') ||
                                errorMessage.includes('publishing rejected') ||
                                errorMessage.includes('engine not connected');
                            if (isRecoverableError && hasEverConnected) {
                                // These are recoverable - LiveKit will attempt to reconnect automatically
                                // Don't show toast, just log for debugging
                                console.log('🔇 Suppressing recoverable error (will auto-reconnect):', error.message);
                                if (onError)
                                    onError(error);
                                return;
                            }
                            // Handle specific non-recoverable error types
                            if (errorMessage.includes('token') || errorMessage.includes('unauthorized')) ;
                            else if (errorMessage.includes('room') || errorMessage.includes('not found')) ;
                            else if (errorMessage.includes('permission') || errorMessage.includes('denied')) ;
                            else if (errorMessage.includes('forbidden')) ;
                            else ;
                            // Log detailed error info
                            console.log('🔍 Detailed error info:', {
                                name: error.name,
                                message: error.message,
                                stack: error.stack,
                                token: initialToken ? 'present' : 'missing',
                                serverUrl: serverUrl ? 'present' : 'missing',
                                roomName: roomName ? 'present' : 'missing',
                                isInitialConnection,
                                hasEverConnected
                            });
                            if (onError)
                                onError(error);
                        }, style: { height: '100vh', width: '100%' }, children: [jsx(RoomRefCollector, { onRoom: (room) => (lkRoomRef.current = room), serverUrl: serverUrl, token: typeof initialToken === 'string' ? initialToken : initialToken.token }), jsx(MediaEnabler, { initialCameraEnabled: initialCameraEnabled !== false, initialMicEnabled: initialMicEnabled !== false, initialBackgroundEffect: initialBackgroundEffect, initialMediaStream: initialMediaStream || null, selectedAudioDevice: selectedAudioDevice, selectedVideoDevice: selectedVideoDevice, onCameraEnabled: setCameraEnabled, onMicEnabled: setMicEnabled, onCurrentEffect: handleEffectState, isConnected: isConnected }), jsxs("div", { className: "flex flex-col h-full", children: [jsx("div", { className: "absolute top-0 left-0 right-0 z-50 transition-all duration-300 translate-y-0 opacity-100", children: jsxs("div", { className: "bg-gray-800 text-white px-4 py-2 flex items-center justify-between shadow-lg border-b border-gray-700", children: [jsxs("div", { className: "flex items-center gap-3", children: [jsx("div", { className: "w-7 h-7 bg-purple-600 rounded-lg flex items-center justify-center", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M23 7l-7 5 7 5V7z" }), jsx("rect", { x: "1", y: "5", width: "15", height: "14", rx: "2", ry: "2" })] }) }), jsxs("div", { children: [jsx("h3", { className: "text-xs font-semibold", children: meetingTitle }), jsx("p", { className: "text-[10px] text-gray-400", children: participantName })] }), isConnected && (jsxs("span", { className: "flex items-center gap-1.5 text-[10px] text-green-400", children: [jsx("span", { className: "w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" }), "Live", userRole === 'teacher' && meetingStartTime && (jsxs("span", { className: "text-gray-300 ml-2", children: ["(", (() => {
                                                                            const duration = (Date.now() - meetingStartTime) / (1000 * 60); // minutes
                                                                            const hours = Math.floor(duration / 60);
                                                                            const minutes = Math.floor(duration % 60);
                                                                            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                                                                        })(), ")"] }))] }))] }), jsxs("div", { className: "flex items-center gap-2", children: [userRole === 'teacher' && (jsxs("button", { type: "button", onClick: async (e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                // Prevent multiple clicks
                                                                if (isRecordingLoading) {
                                                                    console.log('⏸️ Recording operation already in progress...');
                                                                    return;
                                                                }
                                                                try {
                                                                    setIsRecordingLoading(true);
                                                                    if (isRecording) {
                                                                        console.log('🛑 Stopping recording via top button...');
                                                                        await stopRecordingIfActive();
                                                                        console.log('✅ Recording stopped via top button');
                                                                    }
                                                                    else {
                                                                        console.log('▶️ Starting cloud recording via top button...');
                                                                        await startCloudRecording();
                                                                        console.log('✅ Recording started via top button');
                                                                    }
                                                                }
                                                                catch (error) {
                                                                    console.error('❌ Error in recording button:', error);
                                                                    // Removed toast - no toasts in LiveKit room except Chequered
                                                                }
                                                                finally {
                                                                    setIsRecordingLoading(false);
                                                                }
                                                            }, disabled: isLoading || isRecordingLoading, className: `flex items-center gap-1.5 px-2.5 py-1.5 rounded-full transition-colors ${isRecording
                                                                ? 'bg-red-600 hover:bg-red-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'} ${(isLoading || isRecordingLoading) ? 'opacity-50 cursor-not-allowed' : ''}`, title: isRecording ? 'Stop Recording (CLOUD)' : 'Start Recording (CLOUD)', children: [isRecordingLoading ? (jsx("div", { className: "w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" })) : isRecording ? (jsx("div", { className: "w-3 h-3 bg-white rounded-sm" })) : (jsx("div", { className: "w-2.5 h-2.5 bg-red-500 rounded-full" })), jsx("span", { className: "text-[10px] font-medium text-white/90", children: "CLOUD" })] })), jsx("button", { onClick: () => openExclusivePanel('meetingInfo'), className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showMeetingInfo
                                                                ? 'bg-purple-600 hover:bg-purple-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: "Meeting Info", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "10" }), jsx("line", { x1: "12", y1: "16", x2: "12", y2: "12" }), jsx("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })] }) }), showMeetingInfo && (jsx("div", { className: "absolute top-12 right-4 z-50 bg-gray-800 rounded-lg shadow-2xl border border-gray-700 w-[400px]", children: jsxs("div", { className: "p-4", children: [jsxs("div", { className: "flex justify-between items-center mb-4", children: [jsx("h3", { className: "text-white font-semibold", children: "Meeting Information" }), jsx("button", { onClick: () => setShowMeetingInfo(false), className: "text-gray-400 hover:text-white transition-colors", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })] }), jsxs("div", { className: "space-y-4", children: [jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Meeting Name" }), jsx("p", { className: "text-white text-sm font-medium", children: meetingTitle })] }), jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Meeting ID" }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("p", { className: "text-white text-sm font-medium", children: roomName }), jsx("button", { onClick: () => {
                                                                                                    navigator.clipboard.writeText(roomName);
                                                                                                    // Removed toast - no toasts in LiveKit room except Chequered
                                                                                                }, className: "text-blue-400 hover:text-blue-300", title: "Copy Meeting ID", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: [jsx("rect", { x: "9", y: "9", width: "13", height: "13", rx: "2", ry: "2" }), jsx("path", { d: "M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" })] }) })] })] }), jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Your Info" }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("p", { className: "text-white text-sm font-medium", children: participantName }), jsx("span", { className: "bg-purple-600 text-white text-xs px-2 py-0.5 rounded-full", children: userRole === 'teacher' ? 'Teacher' : 'Student' })] })] }), jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Joined At" }), jsx("p", { className: "text-white text-sm font-medium", children: new Date().toLocaleTimeString() })] }), userRole === 'teacher' && subscriptionHoursRemaining !== null && (jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Hours Remaining" }), jsxs("p", { className: `text-sm font-medium ${subscriptionHoursRemaining <= 1 ? 'text-red-400' :
                                                                                            subscriptionHoursRemaining <= 5 ? 'text-yellow-400' : 'text-green-400'}`, children: [subscriptionHoursRemaining.toFixed(1), " hours"] })] })), userRole === 'teacher' && meetingStartTime && (jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Duration" }), jsx("p", { className: "text-white text-sm font-medium", children: (() => {
                                                                                            const duration = (Date.now() - meetingStartTime) / (1000 * 60);
                                                                                            const hours = Math.floor(duration / 60);
                                                                                            const minutes = Math.floor(duration % 60);
                                                                                            return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
                                                                                        })() })] })), jsxs("div", { children: [jsx("label", { className: "block text-gray-400 text-xs mb-1", children: "Invite Link" }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("input", { type: "text", value: `${window.location.origin}/meet/join?room=${roomName}&title=${encodeURIComponent(meetingTitle || 'Meeting')}&subscription=${encodeURIComponent(subscriptionName || '')}&tenant=${tenantId}`, readOnly: true, className: "flex-1 bg-gray-700 text-white px-3 py-1.5 rounded text-sm border border-gray-600" }), jsx("button", { onClick: () => {
                                                                                                    const inviteLink = `${window.location.origin}/meet/join?room=${roomName}&title=${encodeURIComponent(meetingTitle || 'Meeting')}&subscription=${encodeURIComponent(subscriptionName || '')}&tenant=${tenantId}`;
                                                                                                    navigator.clipboard.writeText(inviteLink);
                                                                                                    // Removed toast - no toasts in LiveKit room except Chequered
                                                                                                }, className: "bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded text-sm font-medium transition-colors", children: "Copy" })] })] })] })] }) })), jsxs("button", { onClick: () => {
                                                                console.log('💬 Chat button clicked');
                                                                openExclusivePanel('chat');
                                                            }, className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors relative ${showChat
                                                                ? 'bg-purple-600 hover:bg-purple-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: "Chat", children: [jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }) }), unreadMessageCount > 0 && (jsx("span", { className: "absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center", children: unreadMessageCount > 9 ? '9+' : unreadMessageCount }))] }), jsx("button", { onClick: () => {
                                                                console.log('👥 Participants button clicked');
                                                                openExclusivePanel('participants');
                                                            }, className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showParticipants
                                                                ? 'bg-purple-600 hover:bg-purple-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: "Participants", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" }), jsx("circle", { cx: "9", cy: "7", r: "4" }), jsx("path", { d: "M23 21v-2a4 4 0 0 0-3-3.87" }), jsx("path", { d: "M16 3.13a4 4 0 0 1 0 7.75" })] }) }), jsx("button", { onClick: () => {
                                                                console.log('⚙️ Settings button clicked');
                                                                openExclusivePanel('background');
                                                            }, className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showBackgroundEffects
                                                                ? 'bg-purple-600 hover:bg-purple-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: "Video Settings", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "3" }), jsx("path", { d: "M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" })] }) }), userRole === 'teacher' && (jsx("button", { onClick: handleChessPanelToggle, className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showChessBoard
                                                                ? 'bg-purple-600 hover:bg-purple-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: showChessBoard ? 'Close Puzzle Mode' : 'Open Puzzle Library', children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M12 2l2 4h4l-3 3 1 4-4-2-4 2 1-4-3-3h4z" }), jsx("rect", { x: "2", y: "14", width: "20", height: "8", rx: "1" })] }) })), userRole === 'teacher' && (jsx("button", { onClick: () => openExclusivePanel('polls'), className: `w-7 h-7 rounded-full flex items-center justify-center transition-colors ${showPollsQuizzes
                                                                ? 'bg-blue-600 hover:bg-blue-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: showPollsQuizzes ? 'Close Polls' : 'Open Polls & Quizzes', children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("rect", { x: "3", y: "3", width: "18", height: "18", rx: "2" }), jsx("path", { d: "M9 9h6v6H9z" })] }) }))] })] }) }), jsxs("div", { className: `flex-1 relative overflow-hidden ${currentEffect !== 'none' ? 'lk-background-active' : ''}`, children: [isLoading && !isConnected ? (jsx("div", { className: "absolute inset-0 flex items-center justify-center bg-gray-900 z-10 transition-opacity duration-200", style: {
                                                    display: isConnected ? 'none' : 'flex',
                                                    opacity: isConnected ? 0 : 1,
                                                    pointerEvents: isConnected ? 'none' : 'auto'
                                                }, children: jsxs("div", { className: "text-center", children: [jsx("div", { className: "animate-spin rounded-full h-10 w-10 border-3 border-purple-500 border-t-transparent mx-auto mb-3" }), jsx("p", { className: "text-white text-xs font-medium", children: "Connecting..." }), jsx("p", { className: "text-gray-500 text-[10px] mt-1", children: "This may take a few seconds" })] }) }, "loading-overlay")) : null, showHoursInfoDialog && subscriptionHoursRemaining !== null && userRole === 'teacher' && (jsx("div", { className: "absolute top-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-down", children: jsxs("div", { className: `px-6 py-3 rounded-lg shadow-2xl border-2 flex items-center gap-3 ${subscriptionHoursRemaining <= 1
                                                        ? 'bg-red-900/95 border-red-600 text-red-100'
                                                        : subscriptionHoursRemaining <= 5
                                                            ? 'bg-yellow-900/95 border-yellow-600 text-yellow-100'
                                                            : 'bg-green-900/95 border-green-600 text-green-100'} backdrop-blur-sm`, children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "10" }), jsx("polyline", { points: "12 6 12 12 16 14" })] }), jsxs("div", { children: [jsxs("div", { className: "text-sm font-bold", children: [subscriptionHoursRemaining.toFixed(1), " hours remaining"] }), subscriptionHoursRemaining <= 5 && (jsx("div", { className: "text-xs opacity-90 mt-0.5", children: subscriptionHoursRemaining <= 1 ? 'Consider purchasing more hours' : 'Running low - purchase more soon' }))] }), jsx("button", { onClick: () => setShowHoursInfoDialog(false), className: "ml-2 text-current opacity-70 hover:opacity-100 transition-opacity", title: "Dismiss", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }) })), jsxs("div", { className: showPollsQuizzes ? 'lk-video-with-polls' :
                                                    showChat ? (chatExpanded ? 'lk-video-with-chat-expanded' : 'lk-video-with-chat') :
                                                        showParticipants ? 'lk-video-with-chat' :
                                                            showChessBoard ? 'lk-video-with-chess' :
                                                                'lk-video-full', style: showPollsQuizzes ? { width: `calc(100% - ${pollsPanelWidth}px)` } : {}, children: [jsxs("div", { className: `w-full h-full relative ${currentEffect !== 'none' && cameraEnabled ? 'lk-background-active' : ''}`, children: [jsx(VideoConference, {}), jsx("style", { children: `
                                        /* Global prevention - must be very aggressive */
                                        .lk-loading,
                                        .lk-connecting,
                                        [class*="lk-loading"],
                                        [class*="lk-connecting"],
                                        .lk-room-container .lk-loading,
                                        .lk-room-container .lk-connecting,
                                        .lk-video-conference .lk-loading,
                                        .lk-video-conference .lk-connecting,
                                        .lk-video-conference::before,
                                        .lk-video-conference::after {
                                            display: none !important;
                                            opacity: 0 !important;
                                            visibility: hidden !important;
                                            pointer-events: none !important;
                                            z-index: -9999 !important;
                                            position: absolute !important;
                                            width: 0 !important;
                                            height: 0 !important;
                                        }
                                        /* Prevent dark overlays during track subscriptions */
                                        .lk-room-container .absolute[class*="bg-gray-900"]:not(.lk-control-bar),
                                        .lk-room-container .absolute[class*="bg-black"]:not(.lk-control-bar),
                                        .lk-room-container .fixed[class*="bg-gray-900"]:not(.lk-control-bar),
                                        .lk-room-container .fixed[class*="bg-black"]:not(.lk-control-bar),
                                        .lk-video-conference .absolute[class*="bg-gray-900"]:not(.lk-control-bar),
                                        .lk-video-conference .absolute[class*="bg-black"]:not(.lk-control-bar) {
                                            display: none !important;
                                            opacity: 0 !important;
                                            visibility: hidden !important;
                                            pointer-events: none !important;
                                        }
                                        /* Hide default LiveKit participant tiles ONLY when NOT in screen-share mode */
                                        /* When screen sharing, show LiveKit's built-in layout */
                                        .lk-video-conference {
                                            position: absolute !important;
                                            top: 0 !important;
                                            left: 0 !important;
                                            width: 100% !important;
                                            height: 100% !important;
                                            z-index: 1 !important;
                                            background: #1a1a1a !important;
                                        }
                                        
                                        /* CRITICAL: Show LiveKit tiles when in screen-share mode */
                                        body[data-screen-sharing="true"] .lk-video-conference {
                                            display: block !important;
                                            visibility: visible !important;
                                            opacity: 1 !important;
                                            pointer-events: auto !important;
                                            z-index: 100 !important;
                                            position: relative !important;
                                        }
                                        
                                        body[data-screen-sharing="true"] .lk-video-conference *,
                                        body[data-screen-sharing="true"] .lk-video-conference .lk-participant-tile,
                                        body[data-screen-sharing="true"] .lk-video-conference .lk-focus-layout,
                                        body[data-screen-sharing="true"] .lk-video-conference .lk-focus-layout-wrapper,
                                        body[data-screen-sharing="true"] .lk-video-conference .lk-grid-layout,
                                        body[data-screen-sharing="true"] .lk-video-conference .lk-grid-layout-wrapper,
                                        body[data-screen-sharing="true"] .lk-video-conference video {
                                            display: block !important;
                                            visibility: visible !important;
                                            opacity: 1 !important;
                                            pointer-events: auto !important;
                                            max-width: none !important;
                                            max-height: none !important;
                                        }
                                        
                                        /* CRITICAL: Ensure VideoConference has proper z-index when screen sharing */
                                        body[data-screen-sharing="true"] .lk-video-conference {
                                            z-index: 100 !important;
                                            position: relative !important;
                                            width: 100% !important;
                                            height: 100% !important;
                                        }
                                        
                                        /* CRITICAL: Ensure all container elements are visible */
                                        body[data-screen-sharing="true"] .lk-room-container {
                                            display: block !important;
                                            visibility: visible !important;
                                            opacity: 1 !important;
                                        }
                                        
                                        /* CRITICAL: Auto-maximize screen share - replicate Zoom/Meet behavior */
                                        /* Force focus layout to be in maximized state by default */
                                        body[data-screen-sharing="true"] .lk-focus-layout {
                                            display: flex !important;
                                            flex-direction: row !important;
                                            gap: 16px !important;
                                            padding: 16px !important;
                                        }
                                        
                                        /* CRITICAL: Make screen share take most of the space */
                                        body[data-screen-sharing="true"] .lk-focus-layout > div:first-child {
                                            flex: 1 1 auto !important;
                                            min-width: 0 !important;
                                            display: flex !important;
                                        }
                                        
                                        /* CRITICAL: Side panel for participant videos */
                                        body[data-screen-sharing="true"] .lk-focus-layout > div:last-child {
                                            flex: 0 0 300px !important;
                                            width: 300px !important;
                                            display: flex !important;
                                            flex-direction: column !important;
                                            gap: 12px !important;
                                            overflow-y: auto !important;
                                        }
                                        
                                        /* CRITICAL: Hide maximize/minimize buttons - screen share should be auto-maximized */
                                        body[data-screen-sharing="true"] button[data-lk-source="screen_share"],
                                        body[data-screen-sharing="true"] button[data-lk-source="screen"],
                                        body[data-screen-sharing="true"] .lk-button[aria-label*="maximize"],
                                        body[data-screen-sharing="true"] .lk-button[aria-label*="Maximize"],
                                        body[data-screen-sharing="true"] .lk-participant-tile button[title*="maximize"],
                                        body[data-screen-sharing="true"] .lk-participant-tile button[title*="Maximize"] {
                                            display: none !important;
                                        }
                                        
                                        /* CRITICAL: Hide ALL background avatars/placeholders/overlays on screen share */
                                        body[data-screen-sharing="true"] .lk-participant-placeholder,
                                        body[data-screen-sharing="true"] [class*="placeholder"],
                                        body[data-screen-sharing="true"] .lk-participant-tile > div:not(video):not([class*="metadata"]),
                                        body[data-screen-sharing="true"] .lk-focus-layout img,
                                        body[data-screen-sharing="true"] .lk-focus-layout svg:not(.lk-participant-metadata svg),
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen_share"] > *:not(video):not([class*="metadata"]),
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen"] > *:not(video):not([class*="metadata"]) {
                                            display: none !important;
                                            opacity: 0 !important;
                                            visibility: hidden !important;
                                        }
                                        
                                        /* CRITICAL: Hide any background elements/overlays on all tiles */
                                        body[data-screen-sharing="true"] .lk-participant-tile video::before,
                                        body[data-screen-sharing="true"] .lk-participant-tile video::after,
                                        body[data-screen-sharing="true"] .lk-participant-tile::before,
                                        body[data-screen-sharing="true"] .lk-participant-tile::after,
                                        body[data-screen-sharing="true"] .lk-focus-layout::before,
                                        body[data-screen-sharing="true"] .lk-focus-layout::after {
                                            display: none !important;
                                        }
                                        
                                        /* CRITICAL: Screen share tile should ONLY show video, nothing else */
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen_share"],
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen"] {
                                            background: #000 !important;
                                            position: relative !important;
                                        }
                                        
                                        /* CRITICAL: Ensure screen share video is fully opaque with no background */
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen_share"] video,
                                        body[data-screen-sharing="true"] .lk-participant-tile[data-lk-source="screen"] video {
                                            background: #000 !important;
                                            opacity: 1 !important;
                                            z-index: 10 !important;
                                            position: relative !important;
                                            width: 100% !important;
                                            height: 100% !important;
                                            object-fit: contain !important;
                                        }
                                        
                                        /* CRITICAL: Hide participant metadata/name overlays on large screen share view (but keep it subtle) */
                                        body[data-screen-sharing="true"] .lk-focus-layout > div:first-child .lk-participant-metadata {
                                            opacity: 0.7 !important;
                                            background: rgba(0, 0, 0, 0.5) !important;
                                            position: absolute !important;
                                            bottom: 8px !important;
                                            left: 8px !important;
                                        }
                                        
                                        /* CRITICAL: Always hide LiveKit's native components - we use custom layouts */
                                        .lk-video-conference {
                                            display: none !important;
                                            visibility: hidden !important;
                                            pointer-events: none !important;
                                            z-index: -1 !important;
                                        }
                                        
                                        .lk-video-conference * {
                                            display: none !important;
                                        }
                                    ` })] }), jsx(RoomAudioRenderer, {}), userRole === 'teacher' && raisedHands.size > 0 && (jsxs("div", { className: "absolute top-4 right-4 z-[2000] bg-yellow-600/95 backdrop-blur-sm rounded-lg px-4 py-3 shadow-lg border border-yellow-500 max-w-sm", children: [jsxs("div", { className: "flex items-center gap-2 mb-2", children: [jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-white", children: jsx("path", { d: "M18 11v-1a2 2 0 0 0-2-2h-1M4 13a2 2 0 0 0 2-2v-1M18 11a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1M18 11h-1M4 13h1m13-2v1a2 2 0 0 1-2 2h-1M4 13v-1a2 2 0 0 1 2-2h1m0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1M7 11h10" }) }), jsxs("h3", { className: "text-white font-semibold text-sm", children: ["Raised Hands (", raisedHands.size, ")"] })] }), jsx("div", { className: "space-y-2", children: Array.from(raisedHands.entries()).map(([participantId, { name }]) => (jsxs("div", { className: "flex items-center justify-between bg-yellow-700/50 rounded px-3 py-2", children: [jsx("span", { className: "text-white text-sm", children: name }), jsx("button", { onClick: async () => {
                                                                                if (!lkRoomRef.current)
                                                                                    return;
                                                                                try {
                                                                                    // Send acknowledge message to lower the hand
                                                                                    const messageData = {
                                                                                        type: 'acknowledge_hand',
                                                                                        participantId: participantId,
                                                                                        timestamp: Date.now()
                                                                                    };
                                                                                    const encoder = new TextEncoder();
                                                                                    const dataToSend = encoder.encode(JSON.stringify(messageData));
                                                                                    await lkRoomRef.current.localParticipant.publishData(dataToSend, { reliable: true });
                                                                                    // Also send a raise_hand message with isRaised: false to lower it
                                                                                    const lowerHandData = {
                                                                                        type: 'raise_hand',
                                                                                        participantId: participantId,
                                                                                        participantName: name,
                                                                                        isRaised: false,
                                                                                        timestamp: Date.now()
                                                                                    };
                                                                                    const lowerHandPayload = encoder.encode(JSON.stringify(lowerHandData));
                                                                                    await lkRoomRef.current.localParticipant.publishData(lowerHandPayload, { reliable: true });
                                                                                    setRaisedHands(prev => {
                                                                                        const newMap = new Map(prev);
                                                                                        newMap.delete(participantId);
                                                                                        return newMap;
                                                                                    });
                                                                                    console.log(`✅ Acknowledged and lowered hand for ${name}`);
                                                                                }
                                                                                catch (error) {
                                                                                    console.error('Error acknowledging hand:', error);
                                                                                }
                                                                            }, className: "text-white hover:text-yellow-200 text-xs px-2 py-1 bg-yellow-800/50 rounded hover:bg-yellow-800/70 transition-colors", children: "Acknowledge" })] }, participantId))) })] })), jsx(CustomLayoutManager, { currentLayout: currentLayout, userRole: userRole, teacherParticipantId: teacherParticipantId, pinnedParticipant: pinnedParticipant, onParticipantPin: handleParticipantPin, onTeacherIdentified: handleTeacherIdentified, onScreenShareChange: handleScreenShareDetected, isMirrored: isMirrored, selectedAvatar: selectedAvatar, disableActiveSpeakerSwitching: showChessBoard || currentLayout === 'screen-share', roomRef: lkRoomRef }), minimizeVideos && (jsx("style", { children: `
                                    .lk-participant-tile:not([data-lk-source="screen_share"]) {
                                        display: none !important;
                                    }
                                    .lk-participant-tile[data-lk-source="screen_share"] {
                                        width: 100% !important;
                                        height: 100% !important;
                                    }
                                ` })), isRecording && (jsx("div", { className: "absolute top-3 left-1/2 -translate-x-1/2 z-50", children: jsxs("div", { className: "flex items-center gap-2 bg-red-600 text-white rounded-full px-3 py-1 shadow-lg border border-red-400", children: [jsx("span", { className: "inline-block w-2.5 h-2.5 bg-white rounded-full animate-pulse" }), jsx("span", { className: "text-xs font-semibold tracking-wide", children: "REC" }), jsx("span", { className: "text-[10px] opacity-90", children: (() => {
                                                                        const started = recordingStartMs ?? Date.now();
                                                                        const secs = Math.max(0, Math.floor((Date.now() - started) / 1000));
                                                                        const mm = String(Math.floor(secs / 60)).padStart(2, '0');
                                                                        const ss = String(secs % 60).padStart(2, '0');
                                                                        return `${mm}:${ss}`;
                                                                    })() }), jsx("span", { className: "text-[10px] opacity-90", children: recordingMode?.toUpperCase() })] }) })), jsx(ParticipantCountTracker, { onCountChange: updateParticipantCount }), jsx(ActivityNotificationListener, { userRole: userRole, onPuzzleShared: handlePuzzleShared, onPollStarted: handlePollStarted, onChatMessage: handleChatMessage, onShowChessBoard: setShowChessBoard, onShowPollsQuizzes: setShowPollsQuizzes, onDisconnect: onDisconnect, stopRecordingIfActive: stopRecordingIfActive, setIntentionalDisconnect: (value) => {
                                                            isIntentionalDisconnectRef.current = value;
                                                            if (value) {
                                                                // Reset connection tracking when marking as intentional disconnect
                                                                hasEverConnectedRef.current = false;
                                                                isInitialConnectionRef.current = true;
                                                                reconnectAttemptCountRef.current = 0;
                                                            }
                                                        }, onMicStateChange: (enabled) => {
                                                            // Sync control panel state when teacher mutes/unmutes
                                                            setMicEnabled(enabled);
                                                            console.log(`🎤 Mic state updated from teacher action: ${enabled ? 'enabled' : 'disabled'}`);
                                                        }, onRaiseHand: (participantId, participantName, isRaised) => {
                                                            if (userRole === 'teacher') {
                                                                setRaisedHands(prev => {
                                                                    const newMap = new Map(prev);
                                                                    if (isRaised) {
                                                                        newMap.set(participantId, { name: participantName, timestamp: Date.now() });
                                                                    }
                                                                    else {
                                                                        newMap.delete(participantId);
                                                                    }
                                                                    return newMap;
                                                                });
                                                                if (isRaised) {
                                                                    console.log(`✋ ${participantName} raised their hand`);
                                                                }
                                                            }
                                                            else if (participantId === lkRoomRef.current?.localParticipant?.identity) {
                                                                // Update local state when hand is acknowledged
                                                                setIsHandRaised(isRaised);
                                                            }
                                                        }, onAcknowledgeHand: (participantId) => {
                                                            if (participantId === lkRoomRef.current?.localParticipant?.identity) {
                                                                setIsHandRaised(false);
                                                                console.log('✅ Hand acknowledged by teacher');
                                                            }
                                                        } })] }), jsx("div", { className: "absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[2001] transition-all duration-300 translate-y-0 opacity-100", children: jsxs("div", { className: "bg-gray-800 rounded-full px-4 py-2 flex items-center gap-3 shadow-lg border border-gray-700", children: [userRole === 'teacher' && subscriptionHoursRemaining !== null && (jsxs("div", { className: `px-3 py-1.5 rounded-full flex items-center gap-2 border ${subscriptionHoursRemaining <= 1
                                                                ? 'bg-red-900/50 border-red-600 text-red-200'
                                                                : subscriptionHoursRemaining <= 5
                                                                    ? 'bg-yellow-900/50 border-yellow-600 text-yellow-200'
                                                                    : 'bg-green-900/50 border-green-600 text-green-200'}`, children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "10" }), jsx("polyline", { points: "12 6 12 12 16 14" })] }), jsxs("span", { className: "text-xs font-semibold whitespace-nowrap", children: [subscriptionHoursRemaining.toFixed(1), "h left"] })] })), jsx("button", { onClick: async (e) => {
                                                                // Prevent double-click and event bubbling
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                console.log('🎤 Mute button clicked');
                                                                if (!lkRoomRef.current?.localParticipant) {
                                                                    console.log('❌ No room or participant available');
                                                                    return;
                                                                }
                                                                const participant = lkRoomRef.current.localParticipant;
                                                                const currentState = participant.isMicrophoneEnabled;
                                                                const newState = !currentState;
                                                                // GMEET OPTIMIZATION: Optimistic UI update for instant feedback
                                                                setMicEnabled(newState);
                                                                console.log(`⚡ INSTANT UI update: mic ${newState ? 'enabled' : 'disabled'}`);
                                                                // Disable button temporarily to prevent double-clicks
                                                                const button = e.currentTarget;
                                                                button.disabled = true;
                                                                try {
                                                                    console.log(`🎤 Current mic state: ${currentState}, toggling to: ${newState}`);
                                                                    // Toggle microphone state in background (non-blocking)
                                                                    participant.setMicrophoneEnabled(newState).then(() => {
                                                                        console.log(`✅ Microphone ${newState ? 'enabled' : 'disabled'} - confirmed`);
                                                                    }).catch((error) => {
                                                                        // Rollback on error
                                                                        console.error('❌ Error toggling microphone:', error);
                                                                        setMicEnabled(currentState);
                                                                        toast$1.error('Failed to toggle microphone');
                                                                    });
                                                                }
                                                                catch (error) {
                                                                    console.error('Error toggling microphone:', error);
                                                                    // Rollback on error
                                                                    setMicEnabled(currentState);
                                                                }
                                                                finally {
                                                                    // Re-enable button immediately for instant responsiveness
                                                                    setTimeout(() => {
                                                                        button.disabled = false;
                                                                    }, 100); // Reduced from 300ms to 100ms
                                                                }
                                                            }, className: `w-10 h-10 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${micEnabled
                                                                ? 'bg-gray-700 hover:bg-gray-600'
                                                                : 'bg-red-600 hover:bg-red-700'}`, title: "Mute/Unmute", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: micEnabled ? (jsxs(Fragment, { children: [jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] })) : (jsxs(Fragment, { children: [jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), jsx("path", { d: "M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" }), jsx("path", { d: "M17 16.95A7 7 0 0 1 5 12v-2m14 0v2" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] })) }) }), audioDevices.length > 1 && (jsxs("div", { className: "relative", children: [jsx("button", { onClick: () => setShowAudioDevices(!showAudioDevices), className: "w-6 h-6 rounded-full bg-gray-600 hover:bg-gray-500 flex items-center justify-center transition-colors", title: "Select Microphone", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "10", height: "10", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("polyline", { points: "6,9 12,15 18,9" }) }) }), showAudioDevices && (jsx("div", { className: "absolute bottom-full left-0 mb-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-32 overflow-y-auto", children: audioDevices.map((device) => (jsx("button", { onClick: () => switchAudioDevice(device.deviceId), className: `w-full px-3 py-2 text-left text-xs hover:bg-gray-700 transition-colors ${device.deviceId === selectedAudioDevice ? 'bg-purple-600 text-white' : 'text-gray-300'}`, children: device.label || `Mic ${device.deviceId.slice(0, 8)}` }, device.deviceId))) }))] })), jsx("button", { onClick: async () => {
                                                                console.log('📹 Camera button clicked');
                                                                if (!lkRoomRef.current?.localParticipant) {
                                                                    console.log('❌ No room or participant available');
                                                                    return;
                                                                }
                                                                const participant = lkRoomRef.current.localParticipant;
                                                                const currentState = participant.isCameraEnabled;
                                                                const newState = !currentState;
                                                                // GMEET OPTIMIZATION: Optimistic UI update for instant feedback
                                                                setCameraEnabled(newState);
                                                                setForceUpdate(prev => prev + 1);
                                                                console.log(`⚡ INSTANT UI update: camera ${newState ? 'enabled' : 'disabled'}`);
                                                                try {
                                                                    console.log(`📹 Current camera state: ${currentState}, toggling to: ${newState}`);
                                                                    // Toggle camera state in background (non-blocking)
                                                                    participant.setCameraEnabled(newState).then(() => {
                                                                        console.log(`✅ Camera ${newState ? 'enabled' : 'disabled'} - confirmed`);
                                                                    }).catch((error) => {
                                                                        // Rollback on error
                                                                        console.error('❌ Error toggling camera:', error);
                                                                        setCameraEnabled(currentState);
                                                                        setForceUpdate(prev => prev + 1);
                                                                    });
                                                                }
                                                                catch (error) {
                                                                    console.error('Error toggling camera:', error);
                                                                    // Revert UI state on error
                                                                    setCameraEnabled(currentState);
                                                                    setForceUpdate(prev => prev + 1);
                                                                }
                                                            }, className: `w-10 h-10 rounded-full flex items-center justify-center transition-colors ${cameraEnabled
                                                                ? 'bg-gray-700 hover:bg-gray-600'
                                                                : 'bg-red-600 hover:bg-red-700'}`, title: "Turn Camera On/Off", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: cameraEnabled ? (jsxs(Fragment, { children: [jsx("path", { d: "M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" }), jsx("circle", { cx: "12", cy: "13", r: "4" })] })) : (jsxs(Fragment, { children: [jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), jsx("path", { d: "M21 21H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h.93L16 18.11V19" }), jsx("path", { d: "M7 7h.01" })] })) }) }), videoDevices.length > 1 && (jsxs("div", { className: "relative", children: [jsx("button", { onClick: () => setShowVideoDevices(!showVideoDevices), className: "w-6 h-6 rounded-full bg-gray-600 hover:bg-gray-500 flex items-center justify-center transition-colors", title: "Select Camera", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "10", height: "10", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("polyline", { points: "6,9 12,15 18,9" }) }) }), showVideoDevices && (jsx("div", { className: "absolute bottom-full left-0 mb-2 w-48 bg-gray-800 rounded-lg shadow-lg border border-gray-700 max-h-32 overflow-y-auto", children: videoDevices.map((device) => (jsx("button", { onClick: () => switchVideoDevice(device.deviceId), className: `w-full px-3 py-2 text-left text-xs hover:bg-gray-700 transition-colors ${device.deviceId === selectedVideoDevice ? 'bg-purple-600 text-white' : 'text-gray-300'}`, children: device.label || `Cam ${device.deviceId.slice(0, 8)}` }, device.deviceId))) }))] })), jsx("button", { onClick: async () => {
                                                                if (!lkRoomRef.current?.localParticipant)
                                                                    return;
                                                                // CRITICAL: Prevent concurrent operations
                                                                if (screenShareOperationInProgressRef.current) {
                                                                    console.log('⏸️ Screen share operation already in progress, skipping...');
                                                                    return;
                                                                }
                                                                const localParticipant = lkRoomRef.current.localParticipant;
                                                                if (isScreenSharing) {
                                                                    // Stop screen sharing - CRITICAL: Must stop both LiveKit track AND underlying MediaStream
                                                                    console.log('🖥️ Stopping screen share...');
                                                                    stopScreenShareWatchdog();
                                                                    screenShareOperationInProgressRef.current = true;
                                                                    try {
                                                                        // CRITICAL: Get MediaStream track BEFORE calling setScreenShareEnabled(false)
                                                                        // Once setScreenShareEnabled is called, the track might be removed
                                                                        const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                                                        let mediaStreamTrack = null;
                                                                        let liveKitTrack = null;
                                                                        if (screenPub?.track) {
                                                                            // Capture the MediaStream track before it's removed
                                                                            mediaStreamTrack = screenPub.track.mediaStreamTrack || null;
                                                                            liveKitTrack = screenPub.track;
                                                                            console.log('📸 Captured screen share track before stopping:', {
                                                                                hasMediaStreamTrack: !!mediaStreamTrack,
                                                                                trackState: screenPub.track.state,
                                                                                trackLabel: mediaStreamTrack?.label
                                                                            });
                                                                        }
                                                                        // Step 1: Stop via LiveKit API
                                                                        await localParticipant.setScreenShareEnabled(false);
                                                                        // Stop watchdog when screen share stops
                                                                        stopScreenShareWatchdog();
                                                                        // Comprehensive logging for screen share stop - ALL INFO TOGETHER
                                                                        const room = lkRoomRef.current;
                                                                        logLiveKitEvent('SCREEN_SHARE_STOP', {
                                                                            // Room Information
                                                                            roomName: room?.name,
                                                                            roomSid: room?.sid,
                                                                            serverUrl: room?.engine?.ws?.url || serverUrl,
                                                                            connectionState: room?.state,
                                                                            // Participant Information
                                                                            participantIdentity: localParticipant?.identity,
                                                                            participantName: localParticipant?.name,
                                                                            userRole: userRole,
                                                                            // Current State
                                                                            isRecording: isRecording,
                                                                            recordingId: recordingIdRef.current,
                                                                            recordingMode: recordingMode,
                                                                            cameraEnabled: cameraEnabled,
                                                                            micEnabled: micEnabled,
                                                                            // Screen Share Track Info (before stop)
                                                                            trackSid: screenPub?.trackSid,
                                                                            trackName: screenPub?.trackName,
                                                                            trackState: screenPub?.track?.state,
                                                                            mediaStreamTrackLabel: mediaStreamTrack?.label,
                                                                            mediaStreamTrackReadyState: mediaStreamTrack?.readyState,
                                                                            // Context
                                                                            source: 'user_initiated',
                                                                            timestamp: Date.now()
                                                                        });
                                                                        // Step 2: CRITICAL - Stop the underlying MediaStream track directly
                                                                        // This ensures all browser indicators are cleared (tab notification, floating notification, control panel)
                                                                        if (mediaStreamTrack) {
                                                                            try {
                                                                                console.log('🛑 Stopping underlying MediaStream track to clear browser indicators...', {
                                                                                    label: mediaStreamTrack.label,
                                                                                    readyState: mediaStreamTrack.readyState,
                                                                                    enabled: mediaStreamTrack.enabled
                                                                                });
                                                                                mediaStreamTrack.stop();
                                                                                console.log('✅ MediaStream track stopped');
                                                                            }
                                                                            catch (mediaTrackError) {
                                                                                console.warn('⚠️ Error stopping MediaStream track:', mediaTrackError);
                                                                            }
                                                                        }
                                                                        // Step 3: Also stop the LiveKit track if it's still active
                                                                        if (liveKitTrack && (liveKitTrack.state === 'live' || liveKitTrack.state === 'starting')) {
                                                                            try {
                                                                                await liveKitTrack.stop();
                                                                                console.log('✅ LiveKit screen share track stopped');
                                                                            }
                                                                            catch (trackError) {
                                                                                console.warn('⚠️ Error stopping LiveKit track:', trackError);
                                                                            }
                                                                        }
                                                                        // Step 4: Find and stop any remaining screen share MediaStreams
                                                                        // This catches any streams that might not be tracked by LiveKit
                                                                        try {
                                                                            // Get all active MediaStreams from navigator
                                                                            if (navigator.mediaDevices && navigator.mediaDevices.getDisplayMedia) {
                                                                                // Find all video tracks that are screen shares
                                                                                const allVideoElements = document.querySelectorAll('video');
                                                                                allVideoElements.forEach(video => {
                                                                                    if (video.srcObject && video.srcObject instanceof MediaStream) {
                                                                                        const stream = video.srcObject;
                                                                                        stream.getVideoTracks().forEach(track => {
                                                                                            const label = track.label.toLowerCase();
                                                                                            if (track.kind === 'video' && (label.includes('screen') ||
                                                                                                label.includes('window') ||
                                                                                                label.includes('display') ||
                                                                                                label.includes('monitor'))) {
                                                                                                console.log('🛑 Stopping screen share MediaStream from video element:', track.label);
                                                                                                try {
                                                                                                    track.stop();
                                                                                                }
                                                                                                catch (e) {
                                                                                                    console.warn('⚠️ Error stopping track from video element:', e);
                                                                                                }
                                                                                            }
                                                                                        });
                                                                                    }
                                                                                });
                                                                                // Also check for any active MediaStreamTracks that might be screen shares
                                                                                // This is a fallback to catch any tracks that aren't attached to video elements
                                                                                if (mediaStreamTrack && mediaStreamTrack.readyState !== 'ended') {
                                                                                    try {
                                                                                        mediaStreamTrack.stop();
                                                                                    }
                                                                                    catch (e) {
                                                                                        // Track might already be stopped
                                                                                    }
                                                                                }
                                                                                // CRITICAL: Also enumerate all media devices and stop any screen share tracks
                                                                                // This ensures the red dot is cleared even if tracks aren't in video elements
                                                                                try {
                                                                                    const streams = await navigator.mediaDevices.enumerateDevices();
                                                                                    // Note: enumerateDevices doesn't give us MediaStreamTracks directly,
                                                                                    // but stopping tracks from video elements above should handle most cases
                                                                                }
                                                                                catch (enumError) {
                                                                                    // Ignore enumeration errors
                                                                                }
                                                                            }
                                                                        }
                                                                        catch (streamError) {
                                                                            console.warn('⚠️ Error stopping screen share streams:', streamError);
                                                                        }
                                                                        // Step 5: CRITICAL - Force clear any browser indicators by dispatching events
                                                                        // This helps clear the red dot indicator in the browser tab
                                                                        try {
                                                                            // Dispatch focus/blur events to refresh browser state
                                                                            window.dispatchEvent(new Event('blur'));
                                                                            setTimeout(() => {
                                                                                window.dispatchEvent(new Event('focus'));
                                                                            }, 100);
                                                                            console.log('🔄 Dispatched focus/blur events to refresh browser indicators');
                                                                        }
                                                                        catch (eventError) {
                                                                            console.warn('⚠️ Error dispatching focus/blur events:', eventError);
                                                                        }
                                                                        // CRITICAL: Ensure camera track is still enabled after stopping screen share
                                                                        const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                        if (!cameraPub || !cameraPub.track) {
                                                                            console.log('📹 Camera track missing after screen share stop, re-enabling...');
                                                                            if (cameraEnabled) {
                                                                                await localParticipant.setCameraEnabled(true);
                                                                                console.log('✅ Camera re-enabled after screen share stop');
                                                                                // CRITICAL FIX: Reapply background effect after re-enabling camera
                                                                                if (currentEffect !== 'none') {
                                                                                    console.log(`🎨 Reapplying background effect after screen share stop: ${currentEffect}`);
                                                                                    setTimeout(async () => {
                                                                                        try {
                                                                                            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                                            if (cameraPub?.track) {
                                                                                                const track = cameraPub.track;
                                                                                                await ensureBackgroundProcessor(track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                                                                console.log('✅ Background effect reapplied after screen share stop');
                                                                                            }
                                                                                        }
                                                                                        catch (bgError) {
                                                                                            console.error('❌ Error reapplying background after screen share stop:', bgError);
                                                                                        }
                                                                                    }, 500); // Wait for track to be fully ready
                                                                                }
                                                                            }
                                                                        }
                                                                        else {
                                                                            // Ensure track is not muted
                                                                            if (cameraPub.isMuted && cameraEnabled) {
                                                                                console.log('📹 Camera track was muted, unmuting...');
                                                                                await localParticipant.setCameraEnabled(true);
                                                                                console.log('✅ Camera unmuted after screen share stop');
                                                                                // CRITICAL FIX: Reapply background effect after unmuting
                                                                                if (currentEffect !== 'none') {
                                                                                    console.log(`🎨 Reapplying background effect after unmuting: ${currentEffect}`);
                                                                                    setTimeout(async () => {
                                                                                        try {
                                                                                            const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                                            if (cameraPub?.track) {
                                                                                                const track = cameraPub.track;
                                                                                                await ensureBackgroundProcessor(track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                                                                console.log('✅ Background effect reapplied after unmuting');
                                                                                            }
                                                                                        }
                                                                                        catch (bgError) {
                                                                                            console.error('❌ Error reapplying background after unmuting:', bgError);
                                                                                        }
                                                                                    }, 500);
                                                                                }
                                                                            }
                                                                            else {
                                                                                // Camera is active, verify background is still applied
                                                                                if (currentEffect !== 'none' && cameraPub?.track) {
                                                                                    console.log('📹 Camera active after screen share stop, verifying background...');
                                                                                    const track = cameraPub.track;
                                                                                    const hasProcessor = track.getProcessor && track.getProcessor();
                                                                                    if (!hasProcessor) {
                                                                                        console.log('🎨 Background lost during screen share stop, reapplying...');
                                                                                        setTimeout(async () => {
                                                                                            try {
                                                                                                await ensureBackgroundProcessor(cameraPub.track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                                                                console.log('✅ Background effect reapplied');
                                                                                            }
                                                                                            catch (bgError) {
                                                                                                console.error('❌ Error reapplying background:', bgError);
                                                                                            }
                                                                                        }, 500);
                                                                                    }
                                                                                }
                                                                            }
                                                                        }
                                                                        console.log('✅ Screen share stopped and all browser indicators should be cleared');
                                                                        // CRITICAL: Reset state and wait a bit before allowing new operations
                                                                        setIsScreenSharing(false);
                                                                        sessionStorage.removeItem('meetplus_screen_sharing');
                                                                        setScreenSharingParticipantId(null);
                                                                        if (currentLayout === 'screen-share') {
                                                                            setCurrentLayout('focus');
                                                                        }
                                                                        // Wait a short time to ensure all cleanup is complete
                                                                        await new Promise(resolve => setTimeout(resolve, 300));
                                                                    }
                                                                    catch (error) {
                                                                        console.error('❌ Error stopping screen share:', error);
                                                                        // Removed toast - no toasts in LiveKit room except Chequered
                                                                        // Still reset state on error
                                                                        setIsScreenSharing(false);
                                                                        sessionStorage.removeItem('meetplus_screen_sharing');
                                                                        setScreenSharingParticipantId(null);
                                                                        if (currentLayout === 'screen-share') {
                                                                            setCurrentLayout('focus');
                                                                        }
                                                                    }
                                                                    finally {
                                                                        screenShareOperationInProgressRef.current = false;
                                                                    }
                                                                }
                                                                else {
                                                                    // Start screen sharing with preferCurrentTab option (like Google Meet)
                                                                    // This allows tab sharing to persist when navigating away from the shared tab
                                                                    console.log('🖥️ Starting screen share with persistent tab sharing...');
                                                                    screenShareOperationInProgressRef.current = true;
                                                                    try {
                                                                        // CRITICAL: Like Google Meet/Zoom - camera MUST stay on when screen sharing
                                                                        // Ensure camera is explicitly enabled BEFORE starting screen share
                                                                        if (cameraEnabled) {
                                                                            const cameraPubBefore = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                            const hasValidCamera = cameraPubBefore && cameraPubBefore.track && !cameraPubBefore.isMuted;
                                                                            // If camera is not active, enable it BEFORE screen share
                                                                            if (!hasValidCamera) {
                                                                                console.log('📹 Ensuring camera is enabled before starting screen share...');
                                                                                await localParticipant.setCameraEnabled(true);
                                                                            }
                                                                        }
                                                                        // CRITICAL: Wait a moment to ensure any previous stop operations are complete
                                                                        await new Promise(resolve => setTimeout(resolve, 200));
                                                                        // Start screen share with advanced options (like Google Meet)
                                                                        // Gemini recommendations for robust screen sharing
                                                                        await localParticipant.setScreenShareEnabled(true, {
                                                                            video: {
                                                                                // CRITICAL: preferCurrentTab allows persistent tab sharing
                                                                                // When enabled, browser won't stop sharing when you navigate away from the tab
                                                                                // This matches Google Meet's behavior
                                                                                preferCurrentTab: true,
                                                                                // CRITICAL: surfaceSwitching allows users to switch what they're sharing
                                                                                // without ending the stream (Gemini recommendation)
                                                                                // @ts-ignore - surfaceSwitching is a newer API
                                                                                surfaceSwitching: 'include',
                                                                                // CRITICAL: selfBrowserSurface allows capturing the current tab
                                                                                // This enables "Return to tab" functionality like Google Meet
                                                                                // @ts-ignore - selfBrowserSurface is a newer API
                                                                                selfBrowserSurface: 'include'
                                                                            }
                                                                        });
                                                                        // CRITICAL: Wait for track to be published before updating state
                                                                        // This ensures the track is actually available
                                                                        let trackPublished = false;
                                                                        let publishedScreenPub = null;
                                                                        for (let i = 0; i < 10; i++) {
                                                                            await new Promise(resolve => setTimeout(resolve, 100));
                                                                            const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                                                            if (screenPub?.track && screenPub.track.state === 'live') {
                                                                                trackPublished = true;
                                                                                publishedScreenPub = screenPub;
                                                                                break;
                                                                            }
                                                                        }
                                                                        if (trackPublished) {
                                                                            console.log('✅ Screen share track published successfully');
                                                                            // Comprehensive logging for screen share start - ALL INFO TOGETHER
                                                                            const room = lkRoomRef.current;
                                                                            logLiveKitEvent('SCREEN_SHARE_START', {
                                                                                // Room Information
                                                                                roomName: room?.name,
                                                                                roomSid: room?.sid,
                                                                                serverUrl: room?.engine?.ws?.url || serverUrl,
                                                                                connectionState: room?.state,
                                                                                // Participant Information
                                                                                participantIdentity: localParticipant?.identity,
                                                                                participantName: localParticipant?.name,
                                                                                userRole: userRole,
                                                                                // Current State
                                                                                isRecording: isRecording,
                                                                                recordingId: recordingIdRef.current,
                                                                                recordingMode: recordingMode,
                                                                                cameraEnabled: cameraEnabled,
                                                                                micEnabled: micEnabled,
                                                                                // Screen Share Track Info
                                                                                trackSid: publishedScreenPub?.trackSid,
                                                                                trackName: publishedScreenPub?.trackName,
                                                                                trackState: publishedScreenPub?.track?.state,
                                                                                trackPublished: true,
                                                                                // Options Used
                                                                                options: {
                                                                                    preferCurrentTab: true,
                                                                                    surfaceSwitching: 'include',
                                                                                    selfBrowserSurface: 'include'
                                                                                },
                                                                                // Context
                                                                                source: 'user_initiated',
                                                                                timestamp: Date.now()
                                                                            });
                                                                            // CRITICAL: Add 'ended' event listener to MediaStreamTrack
                                                                            // This detects when browser stops screen share (tab closed, navigated away, etc.)
                                                                            const screenPub = localParticipant.getTrackPublication(Track.Source.ScreenShare);
                                                                            const mediaStreamTrack = screenPub?.track?.mediaStreamTrack;
                                                                            if (mediaStreamTrack) {
                                                                                const handleTrackEnded = () => {
                                                                                    console.warn('🔔🔔🔔 BROWSER TRACK ENDED EVENT FIRED 🔔🔔🔔');
                                                                                    console.warn('📊 DIAGNOSTIC INFO:', {
                                                                                        timestamp: new Date().toISOString(),
                                                                                        mediaTrackReadyState: mediaStreamTrack.readyState,
                                                                                        mediaTrackEnabled: mediaStreamTrack.enabled,
                                                                                        mediaTrackMuted: mediaStreamTrack.muted,
                                                                                        mediaTrackId: mediaStreamTrack.id,
                                                                                        mediaTrackLabel: mediaStreamTrack.label,
                                                                                        documentHidden: document.hidden,
                                                                                        documentVisibility: document.visibilityState,
                                                                                        currentLayout,
                                                                                        isScreenSharing,
                                                                                        isRecording
                                                                                    });
                                                                                    // CRITICAL: Only handle track ended if it's truly ended
                                                                                    // Check readyState to avoid false positives during tab switches
                                                                                    if (mediaStreamTrack.readyState !== 'ended') {
                                                                                        console.log('⏸️ Track ended event but readyState is not ended - ignoring (tab switch)');
                                                                                        return;
                                                                                    }
                                                                                    console.error('🛑🛑🛑 BROWSER STOPPED SCREEN SHARE - MediaStreamTrack truly ended');
                                                                                    console.error('📊 STOPPING REASON: Browser ended event (user clicked stop or closed tab)');
                                                                                    // Stop watchdog
                                                                                    stopScreenShareWatchdog();
                                                                                    // Reset screen share state
                                                                                    setIsScreenSharing(false);
                                                                                    sessionStorage.removeItem('meetplus_screen_sharing');
                                                                                    setScreenSharingParticipantId(null);
                                                                                    setCurrentLayout(prev => prev === 'screen-share' ? 'focus' : prev);
                                                                                    screenShareOperationInProgressRef.current = false;
                                                                                    // Clean up the track publication
                                                                                    if (screenPub?.track) {
                                                                                        try {
                                                                                            localParticipant.setScreenShareEnabled(false).catch(() => { });
                                                                                        }
                                                                                        catch (e) {
                                                                                            console.warn('⚠️ Error cleaning up after track ended:', e);
                                                                                        }
                                                                                    }
                                                                                };
                                                                                // Add event listener
                                                                                mediaStreamTrack.addEventListener('ended', handleTrackEnded, { once: true });
                                                                                console.log('✅ Added "ended" event listener to screen share track');
                                                                            }
                                                                            // Start watchdog to detect silent drop/stall (no wake lock usage)
                                                                            startScreenShareWatchdog();
                                                                        }
                                                                        else {
                                                                            console.warn('⚠️ Screen share track not published yet, but continuing...');
                                                                        }
                                                                        // CRITICAL: Immediately verify camera is still on after screen share starts
                                                                        // This prevents any race condition where camera might be disabled
                                                                        setTimeout(async () => {
                                                                            if (cameraEnabled) {
                                                                                const cameraPubAfter = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                                if (!cameraPubAfter || !cameraPubAfter.track || cameraPubAfter.isMuted) {
                                                                                    console.log('📹 Camera track lost during screen share start - re-enabling IMMEDIATELY...');
                                                                                    try {
                                                                                        await localParticipant.setCameraEnabled(true);
                                                                                        console.log('✅ Camera re-enabled during screen share');
                                                                                        // CRITICAL FIX: Reapply background effect after re-enabling camera
                                                                                        if (currentEffect !== 'none') {
                                                                                            console.log(`🎨 Reapplying background effect after screen share start: ${currentEffect}`);
                                                                                            setTimeout(async () => {
                                                                                                try {
                                                                                                    const cameraPub = localParticipant.getTrackPublication(Track.Source.Camera);
                                                                                                    if (cameraPub?.track) {
                                                                                                        const track = cameraPub.track;
                                                                                                        await ensureBackgroundProcessor(track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                                                                        console.log('✅ Background effect reapplied after screen share start');
                                                                                                    }
                                                                                                }
                                                                                                catch (bgError) {
                                                                                                    console.error('❌ Error reapplying background after screen share start:', bgError);
                                                                                                }
                                                                                            }, 500); // Wait for track to be fully ready
                                                                                        }
                                                                                    }
                                                                                    catch (camError) {
                                                                                        console.error('❌ Error re-enabling camera:', camError);
                                                                                    }
                                                                                }
                                                                                else {
                                                                                    // Camera is active, but verify background is still applied
                                                                                    if (currentEffect !== 'none' && cameraPubAfter?.track) {
                                                                                        console.log('📹 Camera active after screen share start, verifying background...');
                                                                                        const track = cameraPubAfter.track;
                                                                                        const hasProcessor = track.getProcessor && track.getProcessor();
                                                                                        if (!hasProcessor) {
                                                                                            console.log('🎨 Background lost during screen share start, reapplying...');
                                                                                            setTimeout(async () => {
                                                                                                try {
                                                                                                    await ensureBackgroundProcessor(cameraPubAfter.track, currentEffect, currentEffect === 'image' ? backgroundConfig.imageUrl : undefined);
                                                                                                    console.log('✅ Background effect reapplied');
                                                                                                }
                                                                                                catch (bgError) {
                                                                                                    console.error('❌ Error reapplying background:', bgError);
                                                                                                }
                                                                                            }, 500);
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }
                                                                        }, 100); // Very short delay - just to verify
                                                                    }
                                                                    catch (error) {
                                                                        console.error('❌ Error starting screen share:', error);
                                                                        // CRITICAL: Handle permission errors specifically
                                                                        const errorName = error?.name || '';
                                                                        const errorMessage = error?.message || '';
                                                                        if (errorName === 'NotAllowedError' ||
                                                                            errorName === 'PermissionDeniedError' ||
                                                                            errorMessage.includes('Permission denied') ||
                                                                            errorMessage.includes('NotAllowedError') ||
                                                                            errorMessage.includes('user activation')) {
                                                                            console.warn('⚠️ Screen share permission denied by user or browser');
                                                                            // Removed toast - no toasts in LiveKit room except Chequered
                                                                        }
                                                                        else if (errorName === 'NotReadableError' || errorMessage.includes('NotReadableError')) {
                                                                            console.warn('⚠️ Screen share device not readable');
                                                                            // Removed toast - no toasts in LiveKit room except Chequered
                                                                        }
                                                                        else if (errorName === 'AbortError' || errorMessage.includes('AbortError')) {
                                                                            console.warn('⚠️ Screen share aborted by user');
                                                                            // User cancelled - don't show error, just log
                                                                            console.log('ℹ️ Screen share cancelled by user');
                                                                        }
                                                                        else ;
                                                                    }
                                                                    finally {
                                                                        stopScreenShareWatchdog();
                                                                        screenShareOperationInProgressRef.current = false;
                                                                    }
                                                                }
                                                            }, className: `w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isScreenSharing
                                                                ? 'bg-red-600 hover:bg-red-700'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: isScreenSharing ? "Stop Sharing" : "Share Screen", children: isScreenSharing ? (
                                                            // Stop sharing icon
                                                            jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }), jsx("line", { x1: "8", y1: "21", x2: "16", y2: "21" }), jsx("line", { x1: "12", y1: "17", x2: "12", y2: "21" }), jsx("line", { x1: "15", y1: "9", x2: "9", y2: "15" }), jsx("line", { x1: "9", y1: "9", x2: "15", y2: "15" })] })) : (
                                                            // Start sharing icon
                                                            jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }), jsx("line", { x1: "8", y1: "21", x2: "16", y2: "21" }), jsx("line", { x1: "12", y1: "17", x2: "12", y2: "21" })] })) }), userRole === 'student' && currentLayout === 'screen-share' && !isScreenSharing && (jsx("button", { onClick: () => setMinimizeVideos(!minimizeVideos), className: `w-10 h-10 rounded-full flex items-center justify-center transition-colors ${minimizeVideos ? 'bg-purple-600 hover:bg-purple-700' : 'bg-gray-700 hover:bg-gray-600'}`, title: minimizeVideos ? 'Show Videos' : 'Hide Videos', children: minimizeVideos ? (jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }), jsx("polyline", { points: "6,9 12,15 18,9" })] })) : (jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("rect", { x: "2", y: "3", width: "20", height: "14", rx: "2", ry: "2" }), jsx("polyline", { points: "6,15 12,9 18,15" })] })) })), (userRole === 'student' || !userRole) && (jsx("button", { onClick: async () => {
                                                                if (!lkRoomRef.current)
                                                                    return;
                                                                const newRaisedState = !isHandRaised;
                                                                setIsHandRaised(newRaisedState);
                                                                try {
                                                                    const messageData = {
                                                                        type: 'raise_hand',
                                                                        participantId: lkRoomRef.current.localParticipant.identity,
                                                                        participantName: participantName,
                                                                        isRaised: newRaisedState,
                                                                        timestamp: Date.now()
                                                                    };
                                                                    const encoder = new TextEncoder();
                                                                    const dataToSend = encoder.encode(JSON.stringify(messageData));
                                                                    await lkRoomRef.current.localParticipant.publishData(dataToSend, { reliable: true });
                                                                    console.log(`✋ Hand ${newRaisedState ? 'raised' : 'lowered'}`);
                                                                }
                                                                catch (error) {
                                                                    console.error('Error sending raise hand:', error);
                                                                    setIsHandRaised(!newRaisedState); // Revert on error
                                                                }
                                                            }, className: `w-10 h-10 rounded-full flex items-center justify-center transition-colors ${isHandRaised
                                                                ? 'bg-yellow-600 hover:bg-yellow-700 animate-pulse'
                                                                : 'bg-gray-700 hover:bg-gray-600'}`, title: isHandRaised ? 'Lower Hand' : 'Raise Hand', children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("path", { d: "M18 11v-1a2 2 0 0 0-2-2h-1M4 13a2 2 0 0 0 2-2v-1M18 11a2 2 0 0 1 2 2v1a2 2 0 0 1-2 2h-1M18 11h-1M4 13h1m13-2v1a2 2 0 0 1-2 2h-1M4 13v-1a2 2 0 0 1 2-2h1m0 0V9a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v1M7 11h10" }) }) })), jsx("button", { onClick: (e) => {
                                                                e.preventDefault();
                                                                e.stopPropagation();
                                                                console.log('🔴 Leave button clicked in control panel');
                                                                handleTeacherLeave(e);
                                                            }, className: "w-10 h-10 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center transition-colors", title: "Leave Meeting", type: "button", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "18", height: "18", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("path", { d: "M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" }), jsx("polyline", { points: "16,17 21,12 16,7" }), jsx("line", { x1: "21", y1: "12", x2: "9", y2: "12" })] }) })] }) }), showChat && (jsxs("div", { className: `lk-custom-chat-panel ${chatExpanded ? 'expanded' : ''}`, children: [jsxs("div", { children: [jsx("h4", { children: "Chat" }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("button", { onClick: toggleChatExpansion, className: "text-gray-400 hover:text-white transition-colors", title: chatExpanded ? 'Collapse Chat' : 'Expand Chat', children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: chatExpanded ? (jsx("path", { d: "M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" })) : (jsx("path", { d: "M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" })) }) }), jsx("button", { onClick: () => {
                                                                            setShowChat(false);
                                                                            setUnreadMessageCount(0);
                                                                        }, className: "text-gray-400 hover:text-white transition-colors", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })] })] }), jsxs("div", { children: [console.log('🔍 Current chat messages:', chatMessages), chatMessages.length === 0 ? (jsxs("div", { className: "text-center mt-8", children: [jsx("div", { className: "w-16 h-16 bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" }) }) }), jsx("p", { className: "text-gray-400 text-sm", children: "No messages yet" }), jsx("p", { className: "text-gray-500 text-xs mt-1", children: "Start a conversation!" })] })) : (jsx("div", { className: "space-y-3", children: chatMessages.map((msg, idx) => (jsxs("div", { className: "bg-gray-800 rounded-lg p-3 border border-gray-700", children: [jsxs("div", { className: "flex items-center justify-between mb-2", children: [jsx("span", { className: "text-blue-400 font-semibold text-sm", children: msg.name }), jsx("span", { className: "text-gray-500 text-xs", children: msg.time })] }), jsx("p", { className: "text-gray-200 text-sm leading-relaxed", children: msg.message })] }, idx))) }))] }), jsx("div", { children: jsxs("div", { className: "flex gap-2", children: [jsx("input", { type: "text", value: chatInput, onChange: (e) => setChatInput(e.target.value), onKeyPress: (e) => e.key === 'Enter' && sendChatMessage(), placeholder: "Type a message...", className: "flex-1 bg-gray-800 text-white px-4 py-3 rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none text-sm placeholder-gray-400" }), jsx("button", { onClick: sendChatMessage, disabled: !chatInput.trim(), className: "px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors text-sm font-medium", children: "Send" })] }) })] })), showParticipants && (jsx(ParticipantsPanel, { roomRef: lkRoomRef, userRole: userRole, onClose: () => setShowParticipants(false) })), showBackgroundEffects && (jsx(BackgroundEffectsControl, { onEffectChange: (effect, imageUrl) => {
                                                    console.log('🎨 Effect changed:', effect, imageUrl);
                                                    handleEffectState(effect, imageUrl);
                                                }, currentEffect: currentEffect, showPanel: showBackgroundEffects, onClose: () => setShowBackgroundEffects(false), onProcessingChange: () => {
                                                    // No-op: Overlay removed - processing is fast enough that we don't need visual feedback
                                                    // Toast notifications already provide user feedback
                                                }, onVideoEnhancementChange: () => {
                                                    // Handle video enhancement change
                                                }, initialBackgroundEffect: initialBackgroundEffect, isMirrored: isMirrored, onMirrorToggle: toggleMirror, selectedAvatar: selectedAvatar, onAvatarChange: handleAvatarSelection })), showChessBoard && (jsxs("div", { className: "lk-custom-chess-panel", children: [jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-b border-gray-700", children: [jsxs("div", { className: "flex items-center gap-2", children: [jsx("h4", { className: "text-white text-base font-semibold", children: "\u265F\uFE0F Chess Puzzle Mode" }), userRole === 'teacher' && (jsx("span", { className: "text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full", children: "Teacher Mode" })), userRole === 'student' && (jsx("span", { className: "text-xs bg-blue-600 text-white px-2 py-0.5 rounded-full", children: "Student Mode" }))] }), jsx("button", { onClick: () => setShowChessBoard(false), className: "text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })] }), jsx("div", { className: "flex-1 overflow-y-auto p-4", children: jsx(ChessInMeeting, { subscriptionId: subscriptionId, subscriptionName: subscriptionName, userRole: userRole, participantName: participantName, serviceProviderId: tenantId }) })] })), showPollsQuizzes && (jsxs("div", { className: "lk-custom-polls-panel", style: { width: `${pollsPanelWidth}px` }, children: [jsxs("div", { className: "flex items-center justify-between px-4 py-3 border-b border-gray-700", children: [jsxs("div", { className: "flex items-center gap-2", children: [jsx("h4", { className: "text-white text-base font-semibold", children: "\uD83D\uDCCA Polls & Quizzes" }), userRole === 'teacher' && (jsx("span", { className: "text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full", children: "Teacher Mode" }))] }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("button", { onClick: () => setPollsPanelWidth(300), className: "text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700", title: "Narrower", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M5 12h14M12 19l-7-7 7-7" }) }) }), jsx("button", { onClick: () => setPollsPanelWidth(500), className: "text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700", title: "Wider", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M12 5l7 7-7 7M19 12H5" }) }) }), jsx("button", { onClick: () => setShowPollsQuizzes(false), className: "text-gray-400 hover:text-white transition-colors p-1 rounded-lg hover:bg-gray-700", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })] })] }), jsx("div", { className: "flex-1 overflow-y-auto p-4", children: jsx(PollsInMeeting, { subscriptionId: subscriptionId, subscriptionName: subscriptionName, userRole: userRole, participantName: participantName, tenantId: tenantId }) })] }))] })] })] }), jsx(ToastContainer, { containerId: "livekit-meeting", position: "top-right", autoClose: 3000, hideProgressBar: false, newestOnTop: true, closeOnClick: true, rtl: false, pauseOnFocusLoss: true, draggable: true, pauseOnHover: true, theme: "dark", style: { zIndex: 100000 } })] })] }));
    return (jsxs(Fragment, { children: [createPortal(meetingContent, document.body), showLeaveConfirmDialog && createPortal(jsx("div", { className: "fixed inset-0 bg-black/50 backdrop-blur-sm z-[99999] flex items-center justify-center p-4", onClick: (e) => {
                    // Close on backdrop click
                    if (e.target === e.currentTarget) {
                        console.log('🔴 Dialog backdrop clicked, closing');
                        setShowLeaveConfirmDialog(false);
                        setLeaveAction(null);
                    }
                }, children: jsxs("div", { className: "bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden", onClick: (e) => e.stopPropagation(), children: [jsx("div", { className: "bg-gradient-to-r from-purple-600 to-blue-600 px-6 py-4", children: jsxs("div", { className: "flex items-center justify-between", children: [jsxs("div", { children: [jsx("h3", { className: "text-white text-xl font-semibold", children: "Leave Meeting" }), jsx("p", { className: "text-purple-100 text-sm mt-1", children: "Choose your exit option" })] }), jsx("button", { onClick: () => {
                                            setShowLeaveConfirmDialog(false);
                                            setLeaveAction(null);
                                        }, className: "text-white/80 hover:text-white transition-colors p-1 rounded-lg hover:bg-white/20", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }) }), jsxs("div", { className: "p-6", children: [jsxs("div", { className: "mb-6", children: [jsx("p", { className: "text-gray-700 mb-4", children: "Would you like to end the meeting for all participants?" }), jsx("div", { className: "bg-purple-50 border border-purple-200 rounded-lg p-4", children: jsxs("div", { className: "flex items-start gap-3", children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-purple-600 mt-0.5 flex-shrink-0", children: [jsx("circle", { cx: "12", cy: "12", r: "10" }), jsx("line", { x1: "12", y1: "16", x2: "12", y2: "12" }), jsx("line", { x1: "12", y1: "8", x2: "12.01", y2: "8" })] }), jsxs("div", { className: "text-sm text-gray-700", children: [jsx("p", { className: "font-semibold text-purple-700 mb-1", children: "End for all" }), jsx("p", { className: "text-gray-600", children: "All participants will be disconnected from the meeting." })] })] }) })] }), jsxs("div", { className: "flex gap-3", children: [jsx("button", { onClick: () => {
                                                setShowLeaveConfirmDialog(false);
                                                setLeaveAction(null);
                                            }, className: "flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors font-medium", children: "Cancel" }), jsx("button", { onClick: async () => {
                                                setShowLeaveConfirmDialog(false);
                                                if (leaveAction) {
                                                    // Just leave without ending for others
                                                    await leaveAction(false);
                                                }
                                                setLeaveAction(null);
                                            }, className: "flex-1 px-4 py-2.5 bg-white hover:bg-gray-100 text-gray-900 border border-gray-200 rounded-lg transition-colors font-medium", children: "Leave Only" }), jsx("button", { onClick: async () => {
                                                setShowLeaveConfirmDialog(false);
                                                if (leaveAction) {
                                                    // End meeting for all participants
                                                    await leaveAction(true);
                                                }
                                                setLeaveAction(null);
                                            }, className: "flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-colors font-medium shadow-lg", children: "End for All" })] })] })] }) }), document.body), showPurchaseHoursDialog && (jsx("div", { className: "fixed inset-0 bg-black/70 z-[9999] flex items-center justify-center p-4", children: jsx("div", { className: "bg-gray-900 rounded-lg border border-gray-700 shadow-2xl max-w-md w-full", children: jsxs("div", { className: "p-6", children: [jsxs("div", { className: "flex items-center justify-between mb-4", children: [jsx("h3", { className: "text-xl font-bold text-white", children: "\u23F0 Meeting Hours Required" }), jsx("button", { onClick: () => setShowPurchaseHoursDialog(false), className: "text-gray-400 hover:text-white transition-colors", children: jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "24", height: "24", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "18", y1: "6", x2: "6", y2: "18" }), jsx("line", { x1: "6", y1: "6", x2: "18", y2: "18" })] }) })] }), jsxs("div", { className: "mb-6", children: [jsx("p", { className: "text-gray-300 mb-4", children: "You need more meeting hours to continue. Your current balance is insufficient to start or continue meetings." }), jsxs("div", { className: "bg-red-900/20 border border-red-800 rounded-lg p-4 mb-4", children: [jsxs("div", { className: "flex items-center gap-2 text-red-300", children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "12", cy: "12", r: "10" }), jsx("line", { x1: "12", y1: "8", x2: "12", y2: "12" }), jsx("line", { x1: "12", y1: "16", x2: "12.01", y2: "16" })] }), jsx("span", { className: "font-semibold", children: "Low Balance" })] }), subscriptionHoursRemaining !== null && subscriptionHoursRemaining > 0 && (jsxs("p", { className: "text-sm text-gray-400 mt-2", children: ["Current balance: ", subscriptionHoursRemaining.toFixed(2), " hours (minimum 1 hour required)"] })), subscriptionHoursRemaining !== null && subscriptionHoursRemaining <= 0 && (jsx("p", { className: "text-sm text-gray-400 mt-2", children: "Current balance: 0 hours (minimum 1 hour required)" }))] })] }), jsxs("div", { className: "flex gap-3", children: [jsx("button", { onClick: () => setShowPurchaseHoursDialog(false), className: "flex-1 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors font-medium", children: "Cancel" }), jsx("button", { onClick: () => {
                                            setShowPurchaseHoursDialog(false);
                                            // Navigate to purchase page or open purchase modal
                                            // TODO: Implement navigation to purchase hours page
                                            window.location.href = '/subscriptions/purchase-hours'; // Update this path as needed
                                            // Removed toast - no toasts in LiveKit room except Chequered
                                        }, className: "flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium", children: "Purchase Hours" })] })] }) }) }))] }));
};
// Participants Panel Component - Zoom-style UI (Dark mode + search)
const ParticipantsPanel = ({ roomRef, userRole, onClose }) => {
    const participants = useParticipants();
    const { localParticipant } = useLocalParticipant();
    const [mutingParticipant, setMutingParticipant] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const isTeacher = userRole === 'teacher';
    // Log panel state for debugging
    useEffect(() => {
        console.log('👥 ParticipantsPanel rendered:', {
            participantCount: participants.length,
            hasLocalParticipant: !!localParticipant,
            participants: participants.map(p => p.name || p.identity),
            roomRefExists: !!roomRef.current
        });
    }, [participants.length, localParticipant]);
    // Mute/unmute participant using data channel (like raise hand)
    const handleMuteToggle = async (p) => {
        if (!roomRef.current) {
            console.error('❌ Room ref not available');
            return;
        }
        const participantId = p.identity;
        const isCurrentlyMuted = !p.isMicrophoneEnabled;
        const targetMuted = !isCurrentlyMuted; // Toggle: mute if not muted, unmute if muted
        console.log(`🎤 Teacher sending mute request to ${targetMuted ? 'mute' : 'unmute'} participant:`, {
            participantId,
            participantName: p.name || p.identity,
            isCurrentlyMuted,
            targetMuted
        });
        setMutingParticipant(participantId);
        try {
            const room = roomRef.current;
            const encoder = new TextEncoder();
            // Send mute request via data channel (like raise hand)
            const muteRequestData = {
                type: 'mute_request',
                targetParticipantId: participantId,
                muted: targetMuted,
                timestamp: Date.now(),
                fromTeacher: true
            };
            const dataToSend = encoder.encode(JSON.stringify(muteRequestData));
            await room.localParticipant.publishData(dataToSend, { reliable: true });
            console.log(`✅ Mute request sent to participant ${participantId} (${targetMuted ? 'mute' : 'unmute'})`);
            // Clear muting state after a short delay to allow the request to be processed
            setTimeout(() => {
                setMutingParticipant(null);
            }, 500);
        }
        catch (error) {
            console.error('❌ Error sending mute request:', error);
            setMutingParticipant(null);
        }
    };
    // Sort participants: teacher first, then local participant, then others
    const allParticipants = localParticipant
        ? [localParticipant, ...participants.filter(p => p.identity !== localParticipant.identity)]
        : participants;
    // Sort: teacher first, then alphabetically
    const sortedParticipants = [...allParticipants].sort((a, b) => {
        const aIsTeacher = a.name?.toLowerCase().includes('teacher') || a.name === 'teacher';
        const bIsTeacher = b.name?.toLowerCase().includes('teacher') || b.name === 'teacher';
        if (aIsTeacher && !bIsTeacher)
            return -1;
        if (!aIsTeacher && bIsTeacher)
            return 1;
        return (a.name || a.identity || '').localeCompare(b.name || b.identity || '');
    });
    const filteredParticipants = useMemo(() => {
        if (!searchQuery.trim()) {
            return sortedParticipants;
        }
        const query = searchQuery.toLowerCase().trim();
        return sortedParticipants.filter((p) => {
            const name = (p.name || p.identity || '').toLowerCase();
            return name.includes(query);
        });
    }, [sortedParticipants, searchQuery]);
    return (jsxs("div", { className: "lk-custom-chat-panel", children: [jsxs("div", { children: [jsx("h4", { children: "Participants" }), jsxs("div", { className: "flex items-center gap-2", children: [jsx("div", { className: "text-sm text-gray-200 bg-gray-700 px-2 py-0.5 rounded-full", children: filteredParticipants.length }), jsx("button", { onClick: onClose, className: "text-gray-400 hover:text-white transition-colors", title: "Close", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "20", height: "20", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })] })] }), jsxs("div", { className: "flex flex-col h-full", children: [sortedParticipants.length > 1 && (jsx("div", { className: "px-3 py-2 border-b border-gray-700 flex-shrink-0", children: jsxs("div", { className: "relative", children: [jsx("input", { type: "text", value: searchQuery, onChange: (e) => {
                                        const value = e.target.value;
                                        console.log('🔍 Search query changed:', value);
                                        setSearchQuery(value);
                                    }, placeholder: "Search participants", className: "w-full px-9 py-2 bg-gray-800 text-gray-200 placeholder-gray-400 rounded-md border border-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" }), searchQuery && (jsx("button", { onClick: () => {
                                        console.log('🔍 Clearing search');
                                        setSearchQuery('');
                                    }, className: "absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white transition-colors", title: "Clear search", children: jsx("svg", { xmlns: "http://www.w3.org/2000/svg", width: "14", height: "14", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: jsx("path", { d: "M18 6L6 18M6 6l12 12" }) }) })), jsxs("svg", { className: "absolute left-3 top-1/2 -translate-y-1/2 text-gray-400", xmlns: "http://www.w3.org/2000/svg", width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("circle", { cx: "11", cy: "11", r: "8" }), jsx("line", { x1: "21", y1: "21", x2: "16.65", y2: "16.65" })] })] }) })), jsx("div", { className: "flex-1 overflow-y-auto p-4", children: filteredParticipants.length === 0 ? (jsxs("div", { className: "flex flex-col items-center justify-center h-full p-8 text-center", children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "48", height: "48", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-gray-500 mb-4", children: [jsx("path", { d: "M16 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" }), jsx("circle", { cx: "12", cy: "7", r: "4" })] }), jsx("p", { className: "text-gray-400 text-sm", children: "No participants found" }), searchQuery && (jsx("p", { className: "text-gray-500 text-xs mt-2", children: "Try adjusting your search" }))] })) : (filteredParticipants.map((p) => {
                            const isLocal = localParticipant && p.identity === localParticipant.identity;
                            const name = p.name || p.identity || 'Participant';
                            const audioEnabled = typeof p.isMicrophoneEnabled === 'boolean' ? p.isMicrophoneEnabled : (p.audioTrackPublications?.size > 0);
                            const isMuting = mutingParticipant === p.identity;
                            const isName = name.length > 0;
                            return (jsx("div", { className: "px-4 py-2.5 hover:bg-gray-800 transition-colors border-b border-gray-800 last:border-b-0", children: jsxs("div", { className: "flex items-center gap-3", children: [jsx("div", { className: `w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 ${isLocal ? 'bg-blue-600 text-white' : audioEnabled ? 'bg-green-700/30 text-green-300' : 'bg-gray-700 text-gray-300'}`, children: isName ? name.slice(0, 1).toUpperCase() : '?' }), jsxs("div", { className: "flex-1 min-w-0", children: [jsxs("div", { className: "flex items-center gap-2", children: [jsx("div", { className: "text-sm font-medium text-gray-100 truncate", children: name }), isLocal && (jsx("span", { className: "text-xs text-blue-400 font-medium", children: "(You)" }))] }), jsx("div", { className: "flex items-center gap-2 mt-0.5", children: jsx("div", { className: `flex items-center gap-1 text-xs ${audioEnabled ? 'text-green-400' : 'text-red-400'}`, children: audioEnabled ? (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-green-400", children: [jsx("path", { d: "M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" }), jsx("path", { d: "M19 10v2a7 7 0 0 1-14 0v-2" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }), jsx("span", { children: "Unmuted" })] })) : (jsxs(Fragment, { children: [jsxs("svg", { xmlns: "http://www.w3.org/2000/svg", width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", className: "text-red-400", children: [jsx("line", { x1: "1", y1: "1", x2: "23", y2: "23" }), jsx("path", { d: "M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6" }), jsx("path", { d: "M17 16.95A7 7 0 0 1 5 12v-2" }), jsx("line", { x1: "12", y1: "19", x2: "12", y2: "23" }), jsx("line", { x1: "8", y1: "23", x2: "16", y2: "23" })] }), jsx("span", { children: "Muted" })] })) }) })] }), isTeacher && !isLocal && (jsx("button", { onClick: () => handleMuteToggle(p), disabled: isMuting, className: `flex-shrink-0 px-3 py-1.5 text-xs font-medium rounded-md transition-all ${audioEnabled
                                                ? 'bg-red-900/40 text-red-300 hover:bg-red-900/60 border border-red-800'
                                                : 'bg-green-900/40 text-green-300 hover:bg-green-900/60 border border-green-800'} disabled:opacity-50 disabled:cursor-not-allowed`, title: audioEnabled ? 'Mute participant' : 'Unmute participant', children: isMuting ? (jsxs("div", { className: "flex items-center gap-1.5", children: [jsxs("svg", { className: "animate-spin text-gray-300", xmlns: "http://www.w3.org/2000/svg", width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2", strokeLinecap: "round", strokeLinejoin: "round", children: [jsx("line", { x1: "12", y1: "2", x2: "12", y2: "6" }), jsx("line", { x1: "12", y1: "18", x2: "12", y2: "22" }), jsx("line", { x1: "4.93", y1: "4.93", x2: "7.76", y2: "7.76" }), jsx("line", { x1: "16.24", y1: "16.24", x2: "19.07", y2: "19.07" }), jsx("line", { x1: "2", y1: "12", x2: "6", y2: "12" }), jsx("line", { x1: "18", y1: "12", x2: "22", y2: "12" }), jsx("line", { x1: "4.93", y1: "19.07", x2: "7.76", y2: "16.24" }), jsx("line", { x1: "16.24", y1: "7.76", x2: "19.07", y2: "4.93" })] }), jsx("span", { className: "text-gray-300", children: "..." })] })) : (audioEnabled ? 'Mute' : 'Unmute') }))] }) }, p.identity));
                        })) })] }), jsx("div", { className: "px-4 py-2 border-t border-gray-700 bg-gray-800", children: jsx("div", { className: "text-xs text-gray-400 text-center", children: isTeacher ? 'Click Mute/Unmute to control participant audio' : 'View participants list' }) })] }));
};

/**
 * React Hook for Recording Management
 */
function useRecording(config, options = {}) {
    const [recordingSDK, setRecordingSDK] = useState(null);
    const [isRecording, setIsRecording] = useState(false);
    const [currentRecording, setCurrentRecording] = useState(null);
    const [error, setError] = useState(null);
    // Initialize SDK
    useState(() => {
        if (!config) {
            setRecordingSDK(null);
            return;
        }
        const sdk = new RecordingSDK(config);
        sdk.setCallbacks({
            onRecordingStarted: (recording) => {
                setCurrentRecording(recording);
                setIsRecording(true);
                setError(null);
                options.onRecordingStarted?.(recording);
            },
            onRecordingStopped: (recording) => {
                setIsRecording(false);
                setCurrentRecording(null);
                options.onRecordingStopped?.(recording);
            },
            onError: (err) => {
                setError(err);
                options.onError?.(err);
            },
        });
        setRecordingSDK(sdk);
    });
    const startRecording = useCallback(async (recordingOptions) => {
        if (!recordingSDK) {
            throw new Error('Recording SDK not initialized');
        }
        try {
            setError(null);
            const recording = await recordingSDK.startCloudRecording(recordingOptions);
            return recording;
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [recordingSDK]);
    const stopRecording = useCallback(async (recordingId) => {
        if (!recordingSDK) {
            throw new Error('Recording SDK not initialized');
        }
        try {
            setError(null);
            await recordingSDK.stopCloudRecording(recordingId);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [recordingSDK]);
    const getPlaybackUrl = useCallback(async (recordingDocId) => {
        if (!recordingSDK) {
            throw new Error('Recording SDK not initialized');
        }
        try {
            setError(null);
            return await recordingSDK.getPlaybackUrl(recordingDocId);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [recordingSDK]);
    const deleteRecording = useCallback(async (recordingDocId) => {
        if (!recordingSDK) {
            throw new Error('Recording SDK not initialized');
        }
        try {
            setError(null);
            await recordingSDK.deleteRecording(recordingDocId);
        }
        catch (err) {
            setError(err);
            throw err;
        }
    }, [recordingSDK]);
    return {
        recordingSDK,
        isRecording,
        currentRecording,
        startRecording,
        stopRecording,
        getPlaybackUrl,
        deleteRecording,
        error,
    };
}

/**
 * React Hook for Recordings List
 */
function useRecordingsList(config, options = {}) {
    const [recordings, setRecordings] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [recordingSDK, setRecordingSDK] = useState(null);
    const unsubscribeRef = useState(null);
    // Initialize SDK
    useEffect(() => {
        if (!config) {
            setRecordingSDK(null);
            setIsLoading(false);
            return;
        }
        const sdk = new RecordingSDK(config);
        setRecordingSDK(sdk);
        setIsLoading(true);
        // Subscribe to recordings if auto-subscribe is enabled
        if (options.autoSubscribe !== false) {
            const unsubscribe = sdk.subscribeToRecordings((recordingsList) => {
                setRecordings(recordingsList);
                setIsLoading(false);
                setError(null);
            }, {
                maxItems: options.maxItems,
                statusFilter: options.statusFilter || 'all',
            });
            unsubscribeRef[0] = unsubscribe;
        }
        return () => {
            if (unsubscribeRef[0]) {
                unsubscribeRef[0]();
            }
            if (sdk) {
                sdk.unsubscribeFromRecordings();
            }
        };
    }, [config, options.maxItems, options.statusFilter, options.autoSubscribe]);
    const refresh = () => {
        if (recordingSDK && options.autoSubscribe !== false) {
            setIsLoading(true);
            // The subscription will automatically update the list
        }
    };
    return {
        recordings,
        isLoading,
        error,
        refresh,
    };
}

/**
 * Utility function to create a MeetingSDK instance
 */
function createMeetingSDK(config) {
    return new MeetingSDK(config);
}

/**
 * Utility function to create a RecordingSDK instance
 */
function createRecordingSDK(config) {
    return new RecordingSDK(config);
}

/**
 * Flavor Configuration Utility
 *
 * Supports multiple build flavors with different API endpoints
 * while sharing the same LiveKit server
 */
/**
 * Create flavor configuration from environment or provided values
 */
function createFlavorConfig(flavor, configMap, defaultLivekitServerUrl) {
    // Try to get flavor from environment
    const envFlavor = flavor ||
        (typeof window !== 'undefined' && window.__FLAVOR__) ||
        (typeof process !== 'undefined' && process.env?.VITE_FLAVOR) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FLAVOR);
    // If config map provided, use it
    if (configMap && envFlavor && configMap[envFlavor]) {
        return {
            apiEndpoint: configMap[envFlavor].apiEndpoint,
            livekitServerUrl: configMap[envFlavor].livekitServerUrl || defaultLivekitServerUrl || '',
            flavor: envFlavor,
        };
    }
    // Try to get from environment variables
    const apiEndpoint = (typeof process !== 'undefined' && process.env?.VITE_API_END_POINT) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_END_POINT) ||
        (typeof window !== 'undefined' && window.__API_ENDPOINT__);
    const livekitServerUrl = defaultLivekitServerUrl ||
        (typeof process !== 'undefined' && process.env?.VITE_LIVEKIT_SERVER_URL) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LIVEKIT_SERVER_URL) ||
        (typeof window !== 'undefined' && window.__LIVEKIT_SERVER_URL__);
    if (apiEndpoint && livekitServerUrl) {
        return {
            apiEndpoint,
            livekitServerUrl,
            flavor: envFlavor || undefined,
        };
    }
    return null;
}
/**
 * Get default configuration based on flavor
 * Useful when student app has different API endpoints per flavor
 */
function getFlavorConfig(flavor, configMap) {
    if (!configMap[flavor]) {
        console.warn(`Flavor "${flavor}" not found in config map`);
        return null;
    }
    return {
        ...configMap[flavor],
        flavor,
    };
}
/**
 * Create config map from environment
 * Reads from VITE_FLAVOR and constructs config
 */
function createConfigFromEnv() {
    const flavor = (typeof process !== 'undefined' && process.env?.VITE_FLAVOR) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FLAVOR);
    const apiEndpoint = (typeof process !== 'undefined' && process.env?.VITE_API_END_POINT) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_END_POINT);
    const livekitServerUrl = (typeof process !== 'undefined' && process.env?.VITE_LIVEKIT_SERVER_URL) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LIVEKIT_SERVER_URL);
    if (apiEndpoint && livekitServerUrl) {
        return {
            apiEndpoint,
            livekitServerUrl,
            flavor: flavor || undefined,
        };
    }
    return null;
}

/**
 * Flavor Configuration Helper
 *
 * Creates SDK configuration based on build flavors
 * Supports different API endpoints per flavor while using same LiveKit server
 */
/**
 * Create SDK configuration based on flavor
 *
 * Example:
 * ```tsx
 * const config = createSDKConfig({
 *   flavor: 'PRODUCTION',
 *   configMap: {
 *     DEVELOPMENT: {
 *       apiEndpoint: 'https://dev-api.example.com',
 *       livekitServerUrl: 'wss://hosaksham-7o3jzavj.livekit.cloud'
 *     },
 *     PRODUCTION: {
 *       apiEndpoint: 'https://api.example.com',
 *       livekitServerUrl: 'wss://hosaksham-7o3jzavj.livekit.cloud'
 *     }
 *   }
 * });
 * ```
 */
function createSDKConfig(options) {
    const { flavor, configMap, defaultLivekitServerUrl, fallbackApiEndpoint, fallbackLivekitServerUrl, } = options;
    // Try to get flavor from environment if not provided
    const envFlavor = flavor ||
        (typeof window !== 'undefined' && window.__FLAVOR__) ||
        (typeof process !== 'undefined' && process.env?.VITE_FLAVOR) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_FLAVOR);
    // If config map provided and flavor exists, use it
    if (configMap && envFlavor && configMap[envFlavor]) {
        const flavorConfig = configMap[envFlavor];
        return {
            apiEndpoint: flavorConfig.apiEndpoint,
            livekitServerUrl: flavorConfig.livekitServerUrl || defaultLivekitServerUrl || fallbackLivekitServerUrl || '',
            flavor: envFlavor,
        };
    }
    // Try environment variables
    const envApiEndpoint = (typeof process !== 'undefined' && process.env?.VITE_API_END_POINT) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_END_POINT) ||
        (typeof window !== 'undefined' && window.__API_ENDPOINT__);
    const envLivekitUrl = defaultLivekitServerUrl ||
        fallbackLivekitServerUrl ||
        (typeof process !== 'undefined' && process.env?.VITE_LIVEKIT_SERVER_URL) ||
        (typeof import.meta !== 'undefined' && import.meta.env?.VITE_LIVEKIT_SERVER_URL) ||
        (typeof window !== 'undefined' && window.__LIVEKIT_SERVER_URL__);
    // Return configuration
    const apiEndpoint = envApiEndpoint || fallbackApiEndpoint || '';
    const livekitServerUrl = envLivekitUrl || '';
    if (!apiEndpoint || !livekitServerUrl) {
        throw new Error(`SDK configuration incomplete. Missing ${!apiEndpoint ? 'API endpoint' : ''} ${!livekitServerUrl ? 'LiveKit server URL' : ''}. ` +
            `Provide configMap with flavor "${envFlavor || 'unknown'}" or set environment variables.`);
    }
    return {
        apiEndpoint,
        livekitServerUrl,
        flavor: envFlavor || undefined,
    };
}
/**
 * Helper to create config map from common patterns
 */
function createConfigMap(options) {
    const { development, production, staging, livekitServerUrl } = options;
    const map = {};
    if (development) {
        map.DEVELOPMENT = {
            apiEndpoint: development.apiEndpoint,
            livekitServerUrl,
        };
    }
    if (production) {
        map.PRODUCTION = {
            apiEndpoint: production.apiEndpoint,
            livekitServerUrl,
        };
    }
    if (staging) {
        map.STAGING = {
            apiEndpoint: staging.apiEndpoint,
            livekitServerUrl,
        };
    }
    return map;
}

/**
 * LiveKit Meeting SDK
 *
 * Complete SDK for LiveKit meetings, recordings, and playback
 * Supports teachers, students, and guests
 *
 * @version 1.0.0
 */
// Version
const SDK_VERSION = '1.0.0';

export { BLUR_PRESETS, BackgroundEffectsControl, CustomLayoutManager, GuestJoin, LiveKitRoomComponent as LiveKitRoom, MeetingSDK, PerformanceMonitor, PreJoinScreen, RecordingSDK, SDK_VERSION, VideoTile, createBlurProcessor, createConfigFromEnv, createConfigMap, createFlavorConfig, createImageProcessor, createMeetingSDK, createRecordingSDK, createSDKConfig, detectPerformanceTier, ensureBackgroundProcessor, getBuiltInBackgrounds, getDeviceInfo, getDevicePerformanceInfo, getFlavorConfig, getLiveKitSDKConfig, getPerformanceSettings, initializeLiveKitSDK, initializeProcessorPrewarming, isSDKInitialized, logDevicePerformance, prewarmBlurProcessor, prewarmImageProcessor, resetSDKConfig, useMeeting, useRecording, useRecordingsList };
//# sourceMappingURL=index.esm.js.map
